import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('portal.hanscribe.encounter.workspace');
import { AfterViewInit, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { hanscribePrecheckBadgeClass, hanscribeCan, hanscribeRoleLabel, hanscribeStatusBadgeClass } from "src/libs/portal/hanscribe/hanscribe";

type WorkspaceMap = Record<string, any>;
type ChecklistDraft = { status: string; note: string; dirty: boolean };
type SummaryCandidatePoint = {
    label: string;
    text: string;
    source_refs: any[];
    dirty: boolean;
};
type SummaryCandidateDraft = {
    overview: string;
    key_points: SummaryCandidatePoint[];
    limitations: string[];
    source_refs: any[];
    dirty: boolean;
};
type PatientClinicalItem = {
    label: string;
    value: string;
    emphasis?: boolean;
};
type EvidenceSourceItem = {
    category: string;
    label: string;
    value: string;
    note?: string;
    source_ref?: string;
    debug_ref?: string;
};
type SoapWorkflowStep = {
    label: string;
    state: string;
    tone: string;
    active: boolean;
};
type SoapClinicalStructureItem = {
    code: string;
    label: string;
    hint: string;
    keys?: string[];
    labels?: string[];
    keywords?: string[];
    dynamic?: string;
};
type ObjectiveFindingInput = {
    code: string;
    label: string;
    hint: string;
    placeholder: string;
    chips: string[];
    fields: ObjectiveFindingFieldInput[];
};
type ObjectiveFindingFieldInput = {
    code: string;
    label: string;
    placeholder: string;
    options: string[];
};
type ObjectiveFindingDraft = {
    chips: string[];
    fields: Record<string, string>;
    note: string;
};
type ObjectiveFindingPriorityPoint = {
    code: string;
    label: string;
    detail: string;
    meta: string;
    tone: string;
    relatedCodes: string[];
    value: string;
};
type TreatmentPlanInput = {
    code: string;
    label: string;
    hint: string;
    placeholder: string;
    chips: string[];
};
type TreatmentPlanDraft = {
    chips: string[];
    note: string;
};
type SoapCandidateQueueItem = {
    label: string;
    value: string;
    meta: string;
    tone: string;
};
type SoapCandidateQueueStatus = 'new_candidate_ready' | 'previous_version' | 'pending' | 'stale' | 'applied' | 'deferred' | 'excluded';
type SoapCandidateQueueRow = {
    id: string;
    kind: string;
    status: SoapCandidateQueueStatus;
    title: string;
    text: string;
    section: string;
    target: string;
    source: string;
    meta: string;
    updated: string;
    sortKey: number;
    tone: string;
    result?: AiAssistantResult;
    item?: AiAssistantResultItem;
    sentence?: SoapSentenceDraft;
};
type SoapCandidateQueueGroup = {
    code: SoapCandidateQueueStatus;
    label: string;
    description: string;
    count: number;
    tone: string;
    open: boolean;
    latestLabel: string;
    rows: SoapCandidateQueueRow[];
};
type CandidateUnifiedStatusCard = {
    code: string;
    label: string;
    value: string;
    meta: string;
    count: number;
    tone: string;
};
type SoapSentenceDraft = {
    id: string;
    soap_section: string;
    text: string;
    status: string;
    author_type: string;
    source_ai_request_id: string;
    sentence_order: number;
    source_refs: any[];
    doctor_decision_reason: string;
    doctor_decision_status?: string;
    candidate_status?: string;
    candidate_source?: string;
    needs_update?: boolean;
    sentence_meta?: WorkspaceMap;
    updated: string;
    dirty: boolean;
    persisted: boolean;
};
type AiAssistantQuickAction = {
    code: string;
    label: string;
    description: string;
};
type AiAssistantResultItem = {
    kind?: string;
    title: string;
    description: string;
    source: string;
    question?: string;
    reason?: string;
    pattern_code?: string;
    display_name?: string;
    confidence_label?: string;
    confidence_score?: number;
    priority_state?: string;
    evidence_summary?: string;
    supporting_evidence?: string[];
    opposing_evidence?: string[];
    followup_questions?: string[];
    doctor_judgement_note?: string;
    source_labels?: string[];
    decision_status?: string;
    checklist_item_code?: string;
    checklist_item_label?: string;
    checklist_note_text?: string;
    soap_section?: string;
    soap_sentence_text?: string;
    soap_candidates?: WorkspaceMap[];
    source_refs?: any[];
    suggestion_reasons?: string[];
    target_sentence_id?: string;
    target_sentence_preview?: string;
    applied_action?: string;
    answer_target?: string;
    applied_to_checklist?: boolean;
    applied_to_soap?: boolean;
    conversation_signal_key?: string;
    signal_strength?: number;
    chart_value_reason?: string;
    clinical_summary?: string;
    clinical_evidence?: string;
    source_excerpt?: string;
    patient_cues?: string[];
    connection_reasons?: string[];
    recommendation_item_id?: string;
    recommendation_id?: string;
    recommendation_type?: string;
    mcp_managed?: boolean;
    target_field?: string;
    existing_value?: string;
    proposed_value?: string;
    patch_hash?: string;
    pattern_name?: string;
    prescription_name?: string;
    prescription_herbs?: string[];
    prescription_indications?: string[];
    prescription_verification_status?: string;
    contraindication_checks?: string[];
    missing_information?: string[];
    knowledge_evidence?: WorkspaceMap[];
    tool_provenance?: WorkspaceMap[];
    tool_trace?: WorkspaceMap[];
    query_expansion?: WorkspaceMap;
    herb_details?: WorkspaceMap[];
    acupoint_details?: WorkspaceMap[];
    clinical_cases?: WorkspaceMap[];
    routing_summary?: WorkspaceMap;
    authoring_evidence_selected_index?: number;
};
type AiAssistantResult = {
    id: string;
    type: string;
    title: string;
    tone: string;
    status: string;
    summary: string;
    items: AiAssistantResultItem[];
    sources: string[];
    uncertainty: string[];
    createdAt: string;
    requestText: string;
    persisted?: boolean;
    source_turn_id?: string;
    mcp_managed?: boolean;
    disclaimer?: string;
};
type ConversationTurn = {
    role: string;
    text: string;
    createdAt: string;
    status?: string;
    animateEntrance?: boolean;
};
type WorkspaceActionHistoryItem = {
    id: string;
    label: string;
    section: string;
    at: string;
    undoable: boolean;
    row?: SoapCandidateQueueRow;
};
type WorkspaceRecoverySnapshot = {
    encounterId: string;
    updatedAt: string;
    activeSoapSection: string;
    soapDrafts: Record<string, SoapSentenceDraft[]>;
    objectiveFindingDrafts: Record<string, ObjectiveFindingDraft>;
    objectiveFindingManualText: string;
    treatmentPlanDrafts: Record<string, TreatmentPlanDraft>;
};
type ChartQualityIssue = {
    id: string;
    section: string;
    title: string;
    summary: string;
    tone: string;
    code?: string;
    message?: string;
    category?: string;
    sentence_id?: string;
    details?: WorkspaceMap;
};

@Component({
    selector: 'wiz-portal-hanscribe-encounter-workspace',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/portal.hanscribe.encounter.workspace/view.scss */
@charset "UTF-8";
:host {
  display: block;
  height: 100%;
  min-height: 0;
}

.quantitative-observation-panel {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid var(--workspace-border, #d9e2ec);
  border-radius: 10px;
  background: #f8fbfd;
}

.quantitative-observation-panel__header,
.quantitative-observation-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.quantitative-observation-panel__header div,
.quantitative-observation-grid label {
  display: grid;
  gap: 4px;
}

.quantitative-observation-panel__header span,
.quantitative-observation-panel__header em,
.quantitative-observation-grid span,
.quantitative-observation-actions span,
.quantitative-observation-latest {
  color: #526477;
  font-size: 12px;
  font-style: normal;
}

.quantitative-observation-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.quantitative-observation-grid input,
.quantitative-observation-grid select {
  width: 100%;
  min-height: 36px;
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: #fff;
}

.quantitative-observation-actions {
  margin-top: 10px;
}

.quantitative-observation-latest {
  display: grid;
  gap: 3px;
  margin: 10px 0 0;
  padding-left: 18px;
}

@media (max-width: 900px) {
  .quantitative-observation-grid {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }
}
@media (max-width: 780px) {
  .reference-comparison-list,
  .standard-tool-mini-list,
  .standard-tool-list .standard-tool-card {
    grid-template-columns: minmax(0, 1fr);
  }
}
.encounter-workspace {
  --workspace-bottom-bar-height: 60px;
  --workspace-bottom-bar-gap: 32px;
  --workspace-bottom-bar-clearance: calc(var(--workspace-bottom-bar-height) + var(--workspace-bottom-bar-gap));
  --workspace-panel-bottom-space: 32px;
  --workspace-inner-scroll-clearance: calc(var(--workspace-bottom-bar-clearance) + 16px);
  --workspace-focused-grid-offset: 150px;
  --workspace-drawer-width: 520px;
  display: flex;
  height: 100%;
  min-height: 100%;
  flex-direction: column;
  overflow: hidden;
  background: var(--hs-bg-workspace, #f8fafc);
  color: var(--hs-text-strong, #111827);
}

.encounter-workspace,
.encounter-workspace * {
  box-sizing: border-box;
}

.encounter-workspace h1,
.encounter-workspace h2,
.encounter-workspace h3,
.encounter-workspace p {
  margin: 0;
}

@keyframes hs-panel-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hs-turn-enter {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes hs-user-turn-enter {
  0% {
    opacity: 0;
    transform: translate3d(14px, 8px, 0) scale(0.96);
    box-shadow: 0 0 0 0 rgba(23, 100, 93, 0);
  }
  58% {
    opacity: 1;
    transform: translate3d(-2px, -1px, 0) scale(1.012);
    box-shadow: 0 10px 24px rgba(23, 100, 93, 0.16), 0 0 0 3px rgba(23, 100, 93, 0.1);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
    box-shadow: 0 0 0 0 rgba(23, 100, 93, 0);
  }
}
@keyframes hs-soft-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(14, 116, 144, 0);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.12);
  }
}
@keyframes hs-cursor-blink {
  0%, 45% {
    opacity: 1;
  }
  46%, 100% {
    opacity: 0;
  }
}
@keyframes hs-loading-breathe {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(14, 116, 144, 0), 0 8px 22px rgba(15, 23, 42, 0.06);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.1), 0 11px 26px rgba(14, 116, 144, 0.09);
  }
}
@keyframes hs-thinking-spin {
  to {
    transform: rotate(1turn);
  }
}
@keyframes hs-thinking-dot {
  0%, 80%, 100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-4px);
  }
}
@keyframes hs-thinking-scan {
  0% {
    transform: translateX(-120%);
  }
  65%, 100% {
    transform: translateX(340%);
  }
}
@keyframes hs-modal-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes hs-modal-pop {
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}
@keyframes hs-mobile-sheet-enter {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hs-mobile-screen-enter {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
.workspace-header {
  display: flex;
  min-height: 56px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #d9dee3;
  background: #ffffff;
  padding: 9px 14px;
}

.workspace-title {
  min-width: 0;
  flex: 1 1 auto;
}

.screen-kicker,
.panel-kicker {
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
}

.title-row {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 12px;
  margin-top: 4px;
}

.title-row--compact {
  flex-wrap: nowrap;
  margin-top: 0;
  align-items: center;
}

.title-row h1 {
  min-width: 0;
  max-width: min(26vw, 280px);
  color: #101113;
  font-size: 21px;
  font-weight: 800;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace-patient-number {
  flex: 0 1 auto;
  max-width: 180px;
  overflow: hidden;
  border: 1px solid #dce5e2;
  border-radius: 999px;
  background: #f7faf9;
  color: #52615d;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.identity-muted {
  min-width: 0;
  color: #71717a;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
}

.workspace-status,
.status-cluster,
.bottom-actions,
.state-actions,
.workflow-actions,
.candidate-actions > div {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.workspace-status {
  flex: 0 0 auto;
  flex-wrap: nowrap;
  justify-content: flex-end;
  overflow-x: auto;
  scrollbar-width: none;
}

.workspace-status::-webkit-scrollbar {
  display: none;
}

.workspace-main {
  min-height: 0;
  flex: 1 1 auto;
  overflow: auto;
  padding: 12px 14px calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
  scroll-padding-bottom: calc(var(--workspace-inner-scroll-clearance) + env(safe-area-inset-bottom));
  overscroll-behavior: contain;
}

.encounter-workspace textarea,
.encounter-workspace select,
.encounter-workspace button,
.encounter-workspace [tabindex] {
  scroll-margin-bottom: calc(var(--workspace-inner-scroll-clearance) + env(safe-area-inset-bottom));
}

.mobile-panel-tabs {
  display: none;
  width: 100%;
  margin: 0 auto 12px;
  gap: 6px;
}

.mobile-panel-tabs button {
  min-width: 0;
  height: 38px;
  border: 1px solid #cbd5df;
  border-radius: 6px;
  background: #ffffff;
  color: #52525b;
  font-size: 12px;
  font-weight: 850;
  line-height: 1;
  white-space: nowrap;
}

.mobile-panel-tabs button.is-active {
  border-color: #0f766e;
  background: #ecfdf5;
  color: #0f766e;
}

.tab-count {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #0e7490;
  color: #ffffff;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
}

.clinical-grid {
  display: grid;
  width: 100%;
  margin: 0 auto;
  align-items: start;
  gap: 12px;
  grid-template-columns: minmax(300px, 0.74fr) minmax(620px, 1.8fr) minmax(280px, 0.68fr);
}

.workspace-panel {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #d9dee3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  animation: hs-panel-enter 260ms ease both;
  scroll-margin-bottom: calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.workspace-panel:hover {
  border-color: #cbd5df;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.clinical-panel--patient,
.clinical-panel--queue,
.clinical-panel--soap {
  padding-bottom: var(--workspace-panel-bottom-space);
}

.panel-header {
  display: flex;
  min-height: 58px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 11px 12px;
  transition: background 160ms ease, border-color 160ms ease;
}

.panel-header h2 {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
  color: #101113;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.panel-header h2 small {
  border: 1px solid #d9dee3;
  border-radius: 999px;
  background: #ffffff;
  color: #64748b;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.2;
}

.panel-header p:not(.panel-kicker) {
  margin-top: 4px;
  color: #71717a;
  font-size: 12px;
  font-weight: 700;
}

.panel-role-strip {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 9px;
}

.panel-role-strip span {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9dee3;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  padding: 3px 7px;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.2;
  white-space: nowrap;
}

.panel-role-strip--conversation span {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.panel-role-strip--assistant span {
  border-color: #a5f3fc;
  background: #ecfeff;
  color: #0e7490;
}

.panel-role-strip--chart span {
  border-color: #a7f3d0;
  background: #ecfdf5;
  color: #047857;
}

.role-badge,
.status-badge,
.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
}

.role-badge,
.status-badge {
  min-height: 24px;
  padding: 0 8px;
  font-size: 11px;
}

.role-badge {
  border-color: var(--hs-color-primary-100, #cdeee8);
  background: var(--hs-color-primary-50, #e8f7f4);
  color: var(--hs-color-primary-700, #0f766e);
}

.status-chip {
  min-height: 24px;
  padding: 0 8px;
  font-size: 11px;
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease;
}

.status-badge--primary,
.status-chip--primary {
  border-color: var(--hs-color-primary-100, #cdeee8);
  background: var(--hs-color-primary-50, #e8f7f4);
  color: var(--hs-color-primary-700, #0f766e);
}

.status-badge--success,
.status-chip--success {
  border-color: var(--hs-color-success-100, #d1fae5);
  background: var(--hs-color-success-50, #ecfdf5);
  color: var(--hs-color-success-700, #047857);
}

.status-badge--info,
.status-chip--info {
  border-color: var(--hs-color-info-100, #dbeafe);
  background: var(--hs-color-info-50, #eff6ff);
  color: var(--hs-color-info-700, #1d4ed8);
}

.status-badge--ai,
.status-chip--ai {
  border-color: var(--hs-color-ai-100, #cffafe);
  background: var(--hs-color-ai-50, #ecfeff);
  color: var(--hs-color-ai-700, #0e7490);
  animation: hs-soft-pulse 2200ms ease-in-out infinite;
}

.status-badge--warning,
.status-chip--warning {
  border-color: var(--hs-color-warning-100, #fef3c7);
  background: var(--hs-color-warning-50, #fffbeb);
  color: var(--hs-color-warning-700, #b45309);
}

.status-badge--pending,
.status-chip--pending {
  border-color: var(--hs-color-pending-100, #ffedd5);
  background: var(--hs-color-pending-50, #fff7ed);
  color: var(--hs-color-pending-700, #c2410c);
}

.status-badge--danger,
.status-chip--danger {
  border-color: var(--hs-color-danger-100, #fee2e2);
  background: var(--hs-color-danger-50, #fef2f2);
  color: var(--hs-color-danger-700, #b91c1c);
}

.status-badge--neutral,
.status-chip--neutral,
.status-badge--muted,
.status-chip--muted {
  border-color: #d4d4d8;
  background: #f4f4f5;
  color: #52525b;
}

.button {
  display: inline-flex;
  min-width: 72px;
  height: var(--hs-control-md, 36px);
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 6px;
  border: 1px solid transparent;
  padding: 0 13px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.03);
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.button:not(:disabled):hover {
  box-shadow: 0 7px 16px rgba(15, 23, 42, 0.12);
  transform: translateY(-1px);
}

.button:not(:disabled):active {
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
  transform: translateY(0);
}

.button--sm {
  min-width: 60px;
  height: var(--hs-control-sm, 32px);
  padding: 0 10px;
  font-size: 12px;
}

.button--xs {
  min-width: 44px;
  height: var(--hs-control-xs, 28px);
  padding: 0 8px;
  font-size: 11px;
}

.button--primary {
  background: var(--hs-btn-primary-bg, #0f766e);
  color: #ffffff;
}

.button--primary:hover {
  background: var(--hs-btn-primary-bg-hover, #0d9488);
}

.button--secondary {
  border-color: var(--hs-btn-secondary-border, #c7d0da);
  background: var(--hs-btn-secondary-bg, #ffffff);
  color: var(--hs-btn-secondary-text, #334155);
}

.button--secondary:hover {
  background: var(--hs-btn-secondary-bg-hover, #f6f8fb);
}

.button--ghost {
  border-color: transparent;
  background: transparent;
  color: var(--hs-btn-ghost-text, #334155);
}

.button--ghost:hover {
  background: var(--hs-btn-ghost-bg-hover, #eef2f6);
}

.button--danger {
  background: var(--hs-btn-danger-bg, #b91c1c);
  color: #ffffff;
}

.button--danger:hover {
  background: var(--hs-btn-danger-bg-hover, #dc2626);
}

button:disabled,
textarea:disabled,
input:disabled,
select:disabled {
  cursor: not-allowed;
  opacity: 1;
}

button:disabled {
  background: var(--hs-surface-muted, #eef2f6);
  border-color: var(--hs-border-subtle, #dde3ea);
  color: var(--hs-text-disabled, #94a3b8);
}

.field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  color: #52525b;
  font-size: 12px;
  font-weight: 800;
}

.field input,
.field textarea,
.summary-point input,
.summary-point textarea,
.native-select,
.soap-sentence textarea,
.soap-add-row textarea {
  width: 100%;
  border: 1px solid #cbd5df;
  border-radius: 6px;
  background: #ffffff;
  color: #18181b;
  font-size: 14px;
  line-height: 1.5;
  outline: none;
}

.field input,
.summary-point input,
.native-select {
  height: 38px;
  padding: 0 10px;
}

.field textarea,
.summary-point textarea,
.soap-sentence textarea,
.soap-add-row textarea {
  min-height: 72px;
  resize: vertical;
  padding: 9px 10px;
}

.field input:focus,
.field textarea:focus,
.summary-point input:focus,
.summary-point textarea:focus,
.native-select:focus,
.soap-sentence textarea:focus,
.soap-add-row textarea:focus {
  border-color: #0f766e;
  box-shadow: 0 0 0 1px #0f766e;
}

.encounter-workspace textarea::placeholder,
.encounter-workspace input::placeholder {
  color: #a1a1aa;
}

.alert,
.state-panel {
  width: min(100%, 1500px);
  margin: 0 auto;
  border-radius: 8px;
}

.state-panel {
  border: 1px solid #d9dee3;
  background: #ffffff;
  padding: 36px 18px;
  text-align: center;
  color: #52525b;
  font-size: 14px;
  font-weight: 700;
}

.state-panel h2 {
  color: #18181b;
  font-size: 17px;
  font-weight: 800;
}

.state-panel p {
  margin-top: 8px;
}

.state-panel--compact {
  width: 100%;
  padding: 22px 12px;
}

.candidate-empty-card {
  display: grid;
  gap: 4px;
  justify-items: center;
  border-color: #d9dee3;
  background: #f8fafc;
  padding: 16px 14px;
}

.candidate-empty-card strong {
  color: #1f2937;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.25;
}

.candidate-empty-card span {
  max-width: 460px;
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.state-actions {
  justify-content: center;
  margin-top: 14px;
}

.alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 800;
}

.alert-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.alert > div {
  display: grid;
  gap: 8px;
}

.debug-toggle {
  width: fit-content;
  border: 0;
  background: transparent;
  color: #475569;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.debug-detail {
  display: grid;
  gap: 4px;
  border: 1px dashed #cbd5df;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  padding: 10px;
}

.debug-detail span {
  font-size: 12px;
  font-weight: 800;
}

.debug-detail code {
  color: #334155;
  font-size: 12px;
  overflow-wrap: anywhere;
}

.patient-panel {
  position: sticky;
  top: 0;
}

.patient-identity {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  padding: 14px;
}

.patient-identity > div {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #f8fafc;
  padding: 11px 12px;
}

.patient-identity span,
.patient-fact dt {
  display: block;
  color: #71717a;
  font-size: 12px;
  font-weight: 800;
}

.patient-identity strong {
  display: block;
  margin-top: 4px;
  color: #18181b;
  font-size: 15px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.patient-fact-list {
  display: grid;
  gap: 8px;
  padding: 0 14px 14px;
}

.patient-fact {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 11px 12px;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.patient-fact:hover {
  border-color: #cbd5df;
  box-shadow: 0 7px 18px rgba(15, 23, 42, 0.07);
  transform: translateY(-1px);
}

.patient-fact--emphasis {
  border-color: #99f6e4;
  background: #ecfdf5;
}

.patient-fact dd {
  margin: 5px 0 0;
  color: #18181b;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.risk-inline-panel {
  display: grid;
  gap: 9px;
  margin: 0 14px 14px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  padding: 11px 12px;
  color: #991b1b;
}

.risk-inline-panel--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
  color: #047857;
}

.risk-inline-head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.risk-inline-head > div {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.risk-inline-head strong {
  font-size: 13px;
  font-weight: 900;
  line-height: 1.35;
}

.risk-inline-head div > span {
  font-size: 12px;
  font-weight: 750;
  line-height: 1.35;
}

.risk-inline-list,
.risk-detail-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.risk-inline-list span,
.risk-detail-list span {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  border: 1px solid currentColor;
  background: rgba(255, 255, 255, 0.72);
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.35;
}

.risk-inline-list b {
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #b91c1c;
  color: #ffffff;
  font-size: 11px;
  line-height: 1;
}

.patient-ai-summary {
  border-top: 1px solid #e4e7eb;
  padding: 14px;
}

.subsection-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.subsection-header h3 {
  color: #18181b;
  font-size: 14px;
  font-weight: 800;
}

.subsection-header p,
.workflow-label p,
.final-readiness-head p {
  margin-top: 3px;
  color: #71717a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.summary-candidate {
  display: grid;
  gap: 10px;
  margin-top: 10px;
}

.candidate-topline,
.candidate-actions,
.checklist-card-footer,
.soap-sentence-footer,
.final-readiness-head,
.soap-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.candidate-topline {
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
}

.summary-point {
  display: grid;
  gap: 6px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
}

.summary-point small,
.candidate-actions > span,
.checklist-card-footer > span,
.soap-sentence-footer > div:first-child {
  color: #71717a;
  font-size: 12px;
  font-weight: 700;
}

.candidate-actions {
  align-items: flex-start;
  border-top: 1px solid #e4e7eb;
  padding-top: 10px;
}

.inline-message {
  display: flex;
  flex-direction: column;
  gap: 5px;
  border-radius: 8px;
  border: 1px solid transparent;
  margin-top: 10px;
  padding: 10px 11px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
}

.inline-message--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
  color: #047857;
}

.inline-message--info {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.inline-message--warning {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.inline-message--danger {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.risk-chip {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border-radius: 6px;
  border: 1px solid transparent;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 850;
  line-height: 16px;
  white-space: nowrap;
}

.risk-chip::before {
  content: "!";
  display: inline-flex;
  width: 15px;
  height: 15px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: currentColor;
  color: #ffffff;
  font-size: 10px;
  line-height: 1;
}

.risk-chip--warning {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.risk-chip--danger {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.risk-chip--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
  color: #047857;
}

.checklist-progress {
  border-bottom: 1px solid #e4e7eb;
  padding: 14px;
}

.progress-copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #52525b;
  font-size: 13px;
  font-weight: 800;
}

.progress-copy strong {
  color: #0f766e;
  font-size: 16px;
}

.progress-track {
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #e4e7eb;
  margin-top: 8px;
}

.progress-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #0f766e;
}

.disease-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  border-bottom: 1px solid #e4e7eb;
  padding: 12px 14px;
}

.checklist-tab,
.segmented-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid #cbd5df;
  background: #ffffff;
  color: #3f3f46;
  font-weight: 800;
  white-space: nowrap;
}

.checklist-tab {
  min-height: 34px;
  padding: 0 11px;
  font-size: 12px;
}

.checklist-tab--active {
  border-color: #0f766e;
  background: #ecfdf5;
  color: #0f766e;
}

.checklist-start-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  border-bottom: 1px solid #e4e7eb;
  padding: 12px 14px;
}

.native-select {
  display: none;
  max-width: 240px;
}

.checklist-items {
  display: grid;
  gap: 10px;
  padding: 14px;
}

.checklist-card {
  display: grid;
  gap: 12px;
  border: 1px solid #d9dee3;
  border-radius: 8px;
  background: #ffffff;
  padding: 13px;
  scroll-margin-bottom: calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
}

.checklist-card--confirmed {
  border-color: #a7f3d0;
  background: #f0fdf4;
}

.checklist-card--not-applicable {
  background: #f8fafc;
}

.checklist-card--deferred,
.checklist-card--safety {
  border-color: #fde68a;
  background: #fffbeb;
}

.checklist-card--red-flag {
  border-color: #fecaca;
  background: #fff7f7;
}

.checklist-card-main {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 10px;
}

.checklist-mark {
  display: flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid #cbd5df;
  background: #ffffff;
  color: #71717a;
  font-size: 11px;
  font-weight: 900;
}

.checklist-mark--confirmed {
  border-color: #059669;
  background: #059669;
  color: #ffffff;
}

.checklist-mark--not-applicable {
  border-color: #71717a;
  background: #ffffff;
  color: #52525b;
}

.checklist-mark--deferred {
  border-color: #d97706;
  background: #fef3c7;
  color: #92400e;
}

.checklist-mark--risk {
  border-color: #b91c1c;
  background: #b91c1c;
  color: #ffffff;
}

.checklist-card-title {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
}

.checklist-card-title h3 {
  min-width: 0;
  color: #18181b;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.checklist-card-main p {
  margin-top: 5px;
  color: #52525b;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.5;
}

.checklist-card-main .risk-detail-list {
  margin-top: 8px;
  color: #b91c1c;
}

.checklist-soap-link {
  display: grid;
  gap: 8px;
  border: 1px solid #c7d2fe;
  border-radius: 7px;
  background: #f8fafc;
  padding: 10px;
}

.checklist-soap-link-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.checklist-soap-link-head span {
  color: #475569;
  font-size: 11px;
  font-weight: 900;
}

.checklist-soap-link-head strong {
  color: #312e81;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.35;
  text-align: right;
}

.soap-target-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.soap-target-chip {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  background: #eef2ff;
  color: #3730a3;
  padding: 0 7px;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
}

.checklist-soap-link p {
  color: #475569;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.45;
}

.checklist-soap-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.checklist-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
}

.segmented-action {
  min-height: 34px;
  padding: 0 8px;
  font-size: 12px;
}

.segmented-action--confirmed {
  border-color: #059669;
  background: #059669;
  color: #ffffff;
}

.segmented-action--not-applicable {
  border-color: #3f3f46;
  background: #3f3f46;
  color: #ffffff;
}

.segmented-action--deferred {
  border-color: #d97706;
  background: #f59e0b;
  color: #ffffff;
}

.checklist-note textarea {
  min-height: 64px;
}

.support-strip {
  border-top: 1px solid #e4e7eb;
  padding: 14px;
}

.support-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid #eef1f4;
  padding: 10px 0;
  color: #18181b;
  font-size: 13px;
  font-weight: 700;
}

.support-row:first-of-type {
  margin-top: 8px;
}

.support-row strong {
  color: #71717a;
  font-size: 12px;
  white-space: nowrap;
}

.soap-mode-tabs {
  display: none;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 10px 14px;
}

.soap-mode-tabs button {
  display: inline-flex;
  min-width: 0;
  min-height: 36px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #cbd5df;
  border-radius: 6px;
  background: #ffffff;
  color: #52525b;
  font-size: 12px;
  font-weight: 850;
}

.soap-mode-tabs button.is-active {
  border-color: #0e7490;
  background: #ecfeff;
  color: #0e7490;
}

.soap-panel-body {
  display: grid;
  align-items: start;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.82fr);
}

.soap-content-pane {
  min-width: 0;
}

.soap-panel-body > .ai-assistant-panel {
  border-left: 1px solid #e4e7eb;
  background: #fbfdff;
}

.soap-workflow {
  display: grid;
  gap: 10px;
  padding: 14px;
}

.soap-hero-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #99f6e4;
  border-radius: 8px;
  background: #f0fdfa;
  padding: 13px;
}

.soap-hero-copy {
  min-width: 0;
}

.soap-hero-copy h3 {
  margin-top: 3px;
  color: #18181b;
  font-size: 18px;
  font-weight: 850;
}

.soap-hero-copy p:not(.panel-kicker),
.soap-hero-copy small {
  display: block;
  margin-top: 4px;
  color: #52525b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.soap-hero-copy small {
  color: #0f766e;
  font-weight: 800;
}

.soap-hero-bridge {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.soap-hero-bridge span {
  color: #0e7490;
  font-size: 12px;
  font-weight: 850;
}

.soap-hero-metrics {
  display: grid;
  min-width: 230px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.soap-hero-metrics div {
  border: 1px solid #ccfbf1;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px;
}

.soap-hero-metrics strong {
  display: block;
  color: #0f766e;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.1;
}

.soap-hero-metrics span {
  display: block;
  margin-top: 3px;
  color: #475569;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.25;
}

.soap-state-dashboard {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.soap-state-dashboard > div {
  display: grid;
  min-width: 0;
  gap: 5px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
}

.soap-state-dashboard span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.soap-state-dashboard strong {
  color: #18181b;
  font-size: 13px;
  font-weight: 900;
}

.soap-state-dashboard p {
  color: #52525b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.soap-state-dashboard .status-chip {
  width: fit-content;
}

.soap-stage-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.soap-step-pill {
  display: flex;
  min-width: 0;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 8px 9px;
}

.soap-step-pill strong,
.soap-step-pill small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.soap-step-pill strong {
  color: #18181b;
  font-size: 12px;
  font-weight: 850;
}

.soap-step-pill small {
  color: #71717a;
  font-size: 11px;
  font-weight: 800;
}

.soap-step-pill.is-active {
  border-width: 2px;
  padding: 7px 8px;
}

.soap-step-pill--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.soap-step-pill--primary {
  border-color: #99f6e4;
  background: #ecfdf5;
}

.soap-step-pill--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.soap-step-pill--pending {
  border-color: #fed7aa;
  background: #fff7ed;
}

.soap-step-pill--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.soap-step-pill--ai {
  border-color: #bae6fd;
  background: #f0f9ff;
}

.soap-action-row {
  display: grid;
  gap: 10px;
  border-top: 1px solid #e4e7eb;
  padding-top: 12px;
}

.soap-action-group {
  display: grid;
  gap: 8px;
}

.soap-action-group + .soap-action-group {
  border-top: 1px dashed #d4d4d8;
  padding-top: 10px;
}

.soap-action-group h3 {
  color: #18181b;
  font-size: 13px;
  font-weight: 850;
}

.soap-action-group p {
  color: #71717a;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.workflow-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.debug-detail--soap,
.debug-detail--sentence {
  margin-top: 0;
}

.debug-detail--sentence {
  margin-top: 8px;
}

.inline-message code {
  color: inherit;
  font-size: 11px;
  overflow-wrap: anywhere;
}

.message-stack,
.final-readiness,
.soap-editor {
  border-top: 1px solid #e4e7eb;
  padding: 14px;
}

.message-stack .inline-message:first-child {
  margin-top: 0;
}

.final-readiness-head {
  align-items: flex-start;
}

.final-readiness-head h3 {
  color: #18181b;
  font-size: 14px;
  font-weight: 800;
}

.final-counts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.final-counts div {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 9px;
}

.final-counts span {
  display: block;
  color: #71717a;
  font-size: 12px;
  font-weight: 800;
}

.final-counts strong {
  display: block;
  margin-top: 3px;
  color: #047857;
  font-size: 16px;
  font-weight: 900;
}

.final-counts strong.is-empty {
  color: #b91c1c;
}

.final-save-gate-inline {
  display: grid;
  gap: 7px;
}

.final-save-gate-inline__row,
.final-save-gate-confirm {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px 9px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}

.final-save-gate-inline__row strong {
  color: #b91c1c;
}

.final-save-gate-inline__row span,
.final-save-gate-confirm span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.final-save-gate-inline__row--blocking {
  border-color: #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.final-save-gate-confirm {
  justify-content: flex-start;
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
  cursor: pointer;
}

.final-save-gate-confirm input {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  accent-color: #0f766e;
}

.final-gate-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.final-gate-column {
  display: grid;
  gap: 7px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
}

.final-gate-column--blocking,
.final-gate-column--confirmable {
  border-color: #dbe3ea;
  background: #fbfcfd;
}

.final-gate-column__head {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #18181b;
}

.final-gate-column__head strong {
  font-size: 13px;
  font-weight: 900;
}

.final-gate-column__head span {
  flex: 0 0 auto;
  color: #71717a;
  font-size: 12px;
  font-weight: 850;
}

.final-gate-item {
  display: grid;
  gap: 3px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px 9px;
}

.final-gate-item strong {
  color: #18181b;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.4;
}

.final-gate-item span {
  color: #52525b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.final-gate-item--blocking,
.final-gate-item--confirmable {
  border-color: #dbe3ea;
}

.soap-editor {
  display: grid;
  gap: 14px;
}

.chart-authoring-boundary {
  display: grid;
  min-width: 0;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #d9eee9;
  background: #f0fdfa;
  padding: 9px 12px;
}

.chart-authoring-boundary span,
.chart-authoring-boundary strong {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
  white-space: nowrap;
}

.chart-authoring-boundary span {
  border: 1px solid #99f6e4;
  background: #ffffff;
  color: #0f766e;
}

.chart-authoring-boundary strong {
  border: 1px solid #0f766e;
  background: #0f766e;
  color: #ffffff;
}

.chart-authoring-boundary p {
  min-width: 0;
  color: #115e59;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.soap-section-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  border-bottom: 1px solid #d9dee3;
  border-top: 1px solid #edf1f5;
  background: #ffffff;
  padding: 8px 10px;
}

.soap-section-tab {
  display: grid;
  min-width: 0;
  min-height: 58px;
  cursor: pointer;
  align-content: center;
  gap: 2px;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #fbfcfd;
  padding: 8px 9px;
  text-align: left;
  transition: border-color 140ms ease, background 140ms ease, box-shadow 140ms ease;
}

.soap-section-tab span,
.soap-section-tab strong,
.soap-section-tab em {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.soap-section-tab span {
  color: #111827;
  font-size: 13px;
  font-weight: 900;
}

.soap-section-tab strong {
  color: #64748b;
  font-size: 11px;
  font-weight: 750;
}

.soap-section-tab em {
  color: #64748b;
  font-size: 10px;
  font-style: normal;
  font-weight: 850;
}

.soap-section-tab:hover {
  border-color: #b7d7d0;
  background: #f7fcfa;
}

.soap-section-tab.is-active {
  border-color: #0f766e;
  background: #ecfdf5;
  box-shadow: inset 0 0 0 1px rgba(15, 118, 110, 0.12);
}

.soap-section-tab.has-attention:not(.is-active) {
  border-color: #fde68a;
  background: #fffbeb;
}

.soap-section {
  border: 1px solid #d9dee3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: none;
  scroll-margin-bottom: calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.soap-section:hover {
  border-color: #cbd5df;
  box-shadow: none;
  transform: none;
}

.soap-section-head {
  align-items: flex-start;
  border-bottom: 1px solid #e4e7eb;
  padding: 11px 12px;
}

.soap-section-title {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
}

.soap-section-title > div {
  min-width: 0;
}

.soap-section-head h3 {
  color: #18181b;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.35;
}

.soap-section-head p {
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.soap-section-head .soap-section-alert {
  color: #b45309;
  font-weight: 850;
}

.soap-section-state {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
}

.soap-section-state strong {
  color: #71717a;
  font-size: 12px;
  font-weight: 750;
}

.soap-section-state em {
  font-style: normal;
}

.soap-section-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  border-bottom: 1px solid #edf1f5;
  background: #fbfcfd;
  padding: 9px 12px;
}

.soap-section-metrics span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #ffffff;
  color: #64748b;
  padding: 6px 5px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
}

.soap-section-metrics strong {
  color: #18181b;
  font-size: 12px;
  font-weight: 800;
}

.chart-quick-entry {
  border-bottom: 1px solid #edf1f5;
  background: #ffffff;
  padding: 12px;
}

.chart-quick-entry__label {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.chart-quick-entry__label > span {
  color: #111827;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.2;
}

.chart-quick-entry textarea {
  width: 100%;
  min-width: 0;
  min-height: 148px;
  resize: vertical;
  border: 1px solid #cbd5df;
  border-radius: 7px;
  background: #ffffff;
  color: #18181b;
  padding: 12px;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.55;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.chart-quick-entry textarea:focus {
  border-color: #14b8a6;
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.14);
}

.chart-assist-panel {
  display: block;
  min-width: 0;
  border-bottom: 1px solid #edf1f5;
  background: #fbfcfd;
}

.chart-assist-panel > summary {
  display: flex;
  min-width: 0;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 12px;
  list-style: none;
}

.chart-assist-panel > summary::-webkit-details-marker {
  display: none;
}

.chart-assist-panel > summary::after {
  flex: 0 0 auto;
  color: #64748b;
  content: "+";
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
}

.chart-assist-panel[open] > summary {
  border-bottom: 1px solid #edf1f5;
}

.chart-assist-panel[open] > summary::after {
  content: "-";
}

.chart-assist-panel > summary span,
.chart-assist-panel > summary strong {
  min-width: 0;
  overflow: hidden;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chart-assist-panel > summary span {
  color: #334155;
  font-size: 12px;
  font-weight: 900;
}

.chart-assist-panel > summary strong {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.chart-assist-panel__body {
  display: grid;
  min-width: 0;
  gap: 10px;
  padding: 10px 12px 12px;
}

.chart-assist-panel .subjective-inquiry-panel,
.chart-assist-panel .objective-emr-panel {
  border-top: 0;
  padding-top: 0;
}

.soap-clinical-structure {
  border-bottom: 1px solid #edf1f5;
  background: #ffffff;
  padding: 12px;
}

.subjective-compact-editor {
  display: grid;
  min-width: 0;
  gap: 10px;
}

.subjective-summary-card {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.subjective-summary-card > span {
  color: #334155;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.2;
}

.subjective-summary-card textarea {
  width: 100%;
  min-width: 0;
  min-height: 164px;
  resize: vertical;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  color: #18181b;
  padding: 12px;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.55;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.subjective-summary-card textarea:focus {
  border-color: #14b8a6;
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.14);
}

.subjective-inquiry-panel {
  display: grid;
  min-width: 0;
  gap: 5px;
  border-top: 1px solid #edf1f5;
  padding-top: 10px;
}

.subjective-inquiry-row {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
}

.subjective-inquiry-row button {
  display: grid;
  min-width: 0;
  grid-template-columns: 42px minmax(68px, 0.32fr) minmax(0, 1fr);
  align-items: center;
  gap: 7px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #ffffff;
  color: #18181b;
  padding: 7px 8px;
  text-align: left;
  transition: border-color 150ms ease, background 150ms ease;
}

.subjective-inquiry-row button:not(:disabled):hover {
  border-color: #14b8a6;
  background: #f7fcfa;
}

.subjective-inquiry-row span {
  color: #64748b;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.2;
}

.subjective-inquiry-row strong {
  color: #111827;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.25;
}

.subjective-inquiry-row em {
  overflow: hidden;
  color: #64748b;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subjective-inquiry-row small {
  min-width: 56px;
  border: 1px solid #d9dee3;
  border-radius: 999px;
  background: #ffffff;
  color: #64748b;
  padding: 4px 7px;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.2;
  text-align: center;
}

.subjective-inquiry-row[data-state=complete] button {
  border-color: #cdeee8;
  background: #f8fcfb;
}

.subjective-inquiry-row[data-state=complete] small {
  border-color: #b7e4d5;
  background: #ecfdf5;
  color: #047857;
}

.subjective-inquiry-row[data-state=available] button {
  border-color: #bfdbfe;
  background: #f8fbff;
}

.subjective-inquiry-row[data-state=available] small {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.subjective-inquiry-row[data-state=editing] button {
  border-color: #f1d99b;
  background: #fffdf5;
}

.subjective-inquiry-row[data-state=editing] small {
  border-color: #f1d99b;
  background: #fffbeb;
  color: #92400e;
}

.soap-clinical-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  gap: 8px;
}

.soap-clinical-item {
  display: grid;
  min-width: 0;
  min-height: 62px;
  align-content: start;
  gap: 5px;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #ffffff;
  color: #18181b;
  padding: 10px;
  text-align: left;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.soap-clinical-item:not(:disabled):hover {
  border-color: #14b8a6;
  background: #f7fcfa;
  box-shadow: inset 0 0 0 1px rgba(20, 184, 166, 0.12);
}

.soap-clinical-item span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.soap-clinical-item strong {
  display: -webkit-box;
  overflow: hidden;
  color: #111827;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.soap-clinical-item small {
  justify-self: start;
  border: 1px solid #e4e7eb;
  border-radius: 999px;
  background: #f8fafc;
  color: #64748b;
  padding: 2px 7px;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 1.3;
}

.soap-clinical-item--empty strong {
  color: #94a3b8;
}

.soap-clinical-item--available {
  border-color: #bfdbfe;
  background: #f8fbff;
}

.soap-clinical-item--available small {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.soap-clinical-item--editing {
  border-color: #f1d99b;
  background: #fffdf5;
}

.soap-clinical-item--editing small {
  border-color: #f1d99b;
  background: #fffbeb;
  color: #92400e;
}

.soap-clinical-item--complete {
  border-color: #cdeee8;
  background: #f5fbf8;
}

.soap-clinical-item--complete small {
  border-color: #b7ddd4;
  background: #e9f7f2;
  color: #17645d;
}

.assessment-review-panel {
  display: grid;
  min-width: 0;
  gap: 8px;
}

.assessment-review-panel__head,
.assessment-review-card header {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.assessment-review-panel__head > div,
.assessment-review-card header > div {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.assessment-review-panel__head span,
.assessment-review-card header span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.assessment-review-panel__head strong,
.assessment-review-card header strong {
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.assessment-review-panel__head em {
  flex: 0 0 auto;
  border: 1px solid #fde68a;
  border-radius: 999px;
  background: #fffbeb;
  color: #92400e;
  padding: 4px 8px;
  font-size: 10px;
  font-style: normal;
  font-weight: 900;
  line-height: 1.2;
}

.assessment-review-empty,
.assessment-review-card {
  display: grid;
  min-width: 0;
  gap: 8px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #ffffff;
  padding: 9px;
}

.assessment-review-empty {
  background: #f8fafc;
}

.assessment-review-empty strong {
  color: #334155;
  font-size: 13px;
  font-weight: 900;
}

.assessment-review-empty span {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.assessment-review-card {
  border-color: #fde68a;
  background: #fffdf7;
}

.assessment-review-card header em {
  font-style: normal;
}

.assessment-evidence-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
}

.assessment-evidence-grid section {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 5px;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #ffffff;
  padding: 8px;
}

.assessment-evidence-grid small,
.assessment-judgement-note > span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.assessment-evidence-grid span {
  display: -webkit-box;
  overflow: hidden;
  color: #334155;
  font-size: 12px;
  font-weight: 720;
  line-height: 1.4;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.assessment-review-flow {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.assessment-flow-step {
  display: grid;
  min-width: 0;
  gap: 6px;
  border: 1px solid #e4e7eb;
  border-left-width: 3px;
  border-radius: 7px;
  background: #ffffff;
  padding: 8px 9px;
}

.assessment-flow-step > div {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
}

.assessment-flow-step > div > span {
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
}

.assessment-flow-step small {
  color: #475569;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.assessment-flow-step strong {
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.assessment-flow-step p,
.assessment-flow-step li {
  color: #334155;
  font-size: 12px;
  font-weight: 720;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.assessment-flow-step p {
  margin: 0;
}

.assessment-flow-step ul {
  display: grid;
  min-width: 0;
  gap: 4px;
  margin: 0;
  padding: 0 0 0 16px;
}

.assessment-flow-step--candidate {
  border-left-color: #14b8a6;
  background: #f8fcfb;
}

.assessment-flow-step--supporting {
  border-left-color: #22c55e;
}

.assessment-flow-step--opposing {
  border-left-color: #f59e0b;
}

.assessment-flow-step--followup {
  border-left-color: #64748b;
}

.assessment-judgement-note {
  display: grid;
  min-width: 0;
  gap: 6px;
}

.assessment-judgement-note textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  color: #18181b;
  padding: 8px 9px;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.45;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.assessment-judgement-note textarea:focus {
  border-color: #d97706;
  box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.12);
}

.assessment-judgement-note--readonly p {
  margin: 0;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #f8fafc;
  color: #334155;
  padding: 8px 9px;
  font-size: 12px;
  font-weight: 720;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.assessment-review-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.objective-finding-panel {
  display: grid;
  gap: 8px;
  padding: 0;
}

.objective-priority-panel {
  display: grid;
  min-width: 0;
  gap: 7px;
  border: 1px solid #d7ede8;
  border-radius: 7px;
  background: #fbfefd;
  padding: 8px;
}

.objective-priority-panel > header {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.objective-priority-panel > header > div {
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: 7px;
}

.objective-priority-panel span {
  color: #0f766e;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.objective-priority-panel strong {
  min-width: 0;
  overflow: hidden;
  color: #334155;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.objective-priority-list {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.objective-priority-card {
  display: grid;
  min-width: 0;
  min-height: 66px;
  gap: 3px;
  align-content: start;
  border: 1px solid #d9dee3;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px;
  text-align: left;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.objective-priority-card > span,
.objective-priority-card > strong,
.objective-priority-card > em {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.objective-priority-card > span {
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  line-height: 1.2;
  white-space: nowrap;
}

.objective-priority-card > strong {
  color: #111827;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.25;
  white-space: nowrap;
}

.objective-priority-card > em {
  display: -webkit-box;
  color: #475569;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.objective-priority-card:not(:disabled):hover {
  border-color: #14b8a6;
  background: #f8fffd;
  box-shadow: 0 5px 14px rgba(15, 118, 110, 0.08);
}

.objective-priority-card--danger {
  border-color: #fecaca;
  background: #fffafa;
}

.objective-priority-card--danger > span {
  color: #b91c1c;
}

.objective-priority-card--warning {
  border-color: #fed7aa;
  background: #fffaf5;
}

.objective-priority-card--warning > span {
  color: #b45309;
}

.objective-priority-card--primary {
  border-color: #b8e4dc;
}

.objective-summary-card {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.objective-summary-card > span {
  color: #334155;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.2;
}

.objective-summary-card textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  color: #18181b;
  padding: 9px 10px;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.45;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.objective-summary-card textarea {
  min-height: 94px;
  padding: 12px;
  font-size: 13px;
  line-height: 1.55;
}

.objective-summary-card textarea:focus {
  border-color: #14b8a6;
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.14);
}

.objective-emr-panel {
  display: grid;
  min-width: 0;
  gap: 6px;
}

.objective-emr-panel__head {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #334155;
  padding: 2px 1px;
}

.objective-emr-panel__head span,
.objective-emr-panel__head strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.objective-emr-panel__head span {
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.objective-emr-panel__head strong {
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  line-height: 1.2;
}

.objective-emr-group {
  display: grid;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #ffffff;
}

.objective-emr-group > summary {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
  list-style: none;
  padding: 8px 9px;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.objective-emr-group > summary::-webkit-details-marker {
  display: none;
}

.objective-emr-group > summary::after {
  flex: 0 0 auto;
  color: #94a3b8;
  content: "+";
  font-size: 13px;
  font-weight: 850;
}

.objective-emr-group[open] > summary {
  border-bottom: 1px solid #edf1f5;
}

.objective-emr-group[open] > summary::after {
  content: "-";
}

.objective-emr-group > summary span,
.objective-emr-group > summary strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.objective-emr-group > summary strong {
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
}

.objective-emr-group__body {
  display: grid;
  min-width: 0;
}

.objective-emr-row {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(92px, 0.18fr) minmax(0, 1fr) minmax(160px, 0.28fr);
  align-items: stretch;
  gap: 6px;
  border-bottom: 1px solid #edf1f5;
  padding: 6px 8px;
}

.objective-emr-row:last-child {
  border-bottom: 0;
}

.objective-emr-row[data-state=complete] {
  background: #fbfefd;
}

.objective-emr-label {
  display: grid;
  min-width: 0;
  align-content: center;
  gap: 2px;
}

.objective-emr-label strong {
  color: #111827;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.25;
}

.objective-emr-label span {
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.25;
}

.objective-emr-fields,
.objective-assist-row > div {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(4, minmax(78px, 1fr));
  gap: 5px;
}

.objective-emr-field,
.objective-emr-note {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.objective-emr-field span,
.objective-emr-note span {
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  line-height: 1.15;
}

.objective-emr-field select,
.objective-emr-note textarea {
  width: 100%;
  min-width: 0;
  border: 1px solid #d9dee3;
  border-radius: 5px;
  background: #ffffff;
  color: #18181b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
}

.objective-emr-field select {
  min-height: 30px;
  padding: 0 7px;
}

.objective-emr-note textarea {
  min-height: 30px;
  max-height: 56px;
  resize: vertical;
  padding: 6px 7px;
}

.objective-emr-field select:focus,
.objective-emr-note textarea:focus {
  border-color: #14b8a6;
  box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.14);
}

.objective-point-chip {
  min-height: 25px;
  border: 1px solid #d9dee3;
  border-radius: 999px;
  background: #fbfcfd;
  color: #475569;
  padding: 0 9px;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.2;
  transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
}

.objective-point-chip:not(:disabled):hover {
  border-color: #14b8a6;
  background: #f0fdfa;
  color: #0f766e;
}

.objective-point-chip--complete {
  border-color: #0f766e;
  background: #ecfdf5;
  color: #0f4f49;
}

.objective-emr-group--assist {
  border-style: dashed;
}

.objective-assist-row {
  display: grid;
  min-width: 0;
  grid-template-columns: 62px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #edf1f5;
  padding: 8px 10px;
}

.objective-assist-row:last-child {
  border-bottom: 0;
}

.objective-assist-row > span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.treatment-plan-panel {
  display: grid;
  gap: 8px;
  padding: 0;
}

.treatment-plan-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.treatment-plan-panel__head > div {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.treatment-plan-panel__head span {
  color: #64748b;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.2;
}

.treatment-plan-panel__head strong {
  color: #18181b;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.25;
}

.treatment-plan-panel__head em {
  flex: 0 0 auto;
  border: 1px solid #d9dee3;
  border-radius: 999px;
  background: #ffffff;
  color: #475569;
  padding: 4px 8px;
  font-size: 10px;
  font-style: normal;
  font-weight: 900;
  line-height: 1.2;
}

.treatment-precheck-panel {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.treatment-precheck-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.treatment-precheck-panel__head span {
  color: #475569;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.treatment-precheck-panel__head strong {
  flex: 0 0 auto;
  border: 1px solid #d9dee3;
  border-radius: 999px;
  background: #ffffff;
  color: #334155;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.2;
}

.treatment-safety-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.treatment-safety-strip > button {
  display: grid;
  width: 100%;
  min-width: 0;
  gap: 3px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #f8fafc;
  padding: 7px;
  text-align: left;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.treatment-safety-strip > button:hover {
  border-color: #cbd5e1;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.treatment-safety-strip > button[data-state=complete] {
  border-color: #cdeee8;
  background: #f5fbf8;
}

.treatment-safety-strip > button[data-state=warning],
.treatment-safety-strip > button[data-state=empty] {
  border-color: #fde68a;
  background: #fffbeb;
}

.treatment-safety-strip span {
  color: #64748b;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.2;
}

.treatment-safety-strip strong {
  min-width: 0;
  overflow: hidden;
  color: #111827;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.treatment-safety-strip em {
  overflow: hidden;
  color: #64748b;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.treatment-plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 7px;
}

.treatment-plan-card {
  display: grid;
  min-width: 0;
  gap: 6px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px;
}

.treatment-plan-card--complete {
  border-color: #bfdbfe;
  background: #f8fbff;
}

.treatment-plan-card header {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.treatment-plan-card header > div {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.treatment-plan-card span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.treatment-plan-card strong {
  display: -webkit-box;
  overflow: hidden;
  color: #111827;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.35;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.treatment-plan-card--empty strong {
  color: #94a3b8;
}

.treatment-plan-card em {
  color: #64748b;
  font-size: 11px;
  font-style: normal;
  font-weight: 650;
  line-height: 1.35;
  display: none;
}

.treatment-plan-chip-row {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 5px;
}

.treatment-plan-chip {
  min-height: 24px;
  border: 1px solid #d9dee3;
  border-radius: 999px;
  background: #ffffff;
  color: #475569;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.2;
  transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
}

.treatment-plan-chip:not(:disabled):hover {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.treatment-plan-chip.is-selected {
  border-color: #1d4ed8;
  background: #dbeafe;
  color: #1e3a8a;
}

.treatment-plan-card textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  color: #18181b;
  padding: 8px 9px;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.45;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.treatment-plan-card textarea:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.treatment-plan-preview {
  display: grid;
  min-width: 0;
  gap: 3px;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #f8fafc;
  padding: 7px 8px;
}

.treatment-plan-preview p {
  display: -webkit-box;
  overflow: hidden;
  color: #334155;
  font-size: 12px;
  font-weight: 720;
  line-height: 1.4;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.treatment-plan-preview span {
  color: #64748b;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.2;
}

.treatment-plan-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.treatment-plan-actions--summary {
  border-top: 0;
  padding-top: 0;
}

.soap-candidate-lane {
  display: grid;
  gap: 8px;
  border-bottom: 1px solid #e4e7eb;
  background: #fffdf5;
  padding: 10px 12px;
}

.soap-candidate-lane--summary {
  background: #f8fafc;
}

.soap-candidate-lane-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.soap-candidate-lane-head > div {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.soap-candidate-lane-head strong {
  color: #92400e;
  font-size: 13px;
  font-weight: 900;
}

.soap-candidate-lane-head span,
.soap-candidate-note {
  color: #92400e;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.4;
}

.soap-pending-suggestion {
  display: grid;
  gap: 8px;
  border: 1px solid #fde68a;
  border-radius: 8px;
  background: #ffffff;
  padding: 9px;
}

.soap-pending-suggestion strong {
  color: #18181b;
  font-size: 12px;
  font-weight: 800;
}

.soap-pending-suggestion p {
  margin-top: 4px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.soap-pending-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.soap-sentence-list {
  display: grid;
  gap: 9px;
  padding: 12px;
}

.soap-sentence {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
}

.soap-sentence--dirty {
  border-color: #fde68a;
  background: #fffbeb;
}

.soap-sentence--excluded {
  border-color: #fecaca;
  background: #fef2f2;
}

.soap-sentence-footer {
  align-items: flex-start;
  margin-top: 8px;
}

.soap-sentence-footer > div:first-child {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 6px 10px;
}

.sentence-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.soap-add-row {
  display: grid;
  gap: 8px;
  border-top: 1px dashed #d4d4d8;
  padding: 12px;
}

.soap-add-row .button {
  justify-self: end;
}

.ai-assistant-panel {
  display: grid;
  gap: 14px;
  padding: 14px;
}

.ai-assistant-intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e4e7eb;
  padding-bottom: 12px;
}

.ai-assistant-intro h3 {
  margin-top: 3px;
  color: #18181b;
  font-size: 15px;
  font-weight: 850;
}

.ai-assistant-intro p:not(.panel-kicker) {
  margin-top: 4px;
  color: #52525b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.ai-assistant-intro-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.ai-assistant-flow {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.ai-assistant-flow--compact {
  grid-template-columns: 1fr;
}

.ai-assistant-flow > div {
  display: grid;
  gap: 4px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #eff6ff;
  padding: 9px;
}

.ai-assistant-flow span,
.ai-result-route span,
.ai-action-group > span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.ai-assistant-flow strong {
  color: #1e3a8a;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.35;
}

.ai-assistant-block {
  display: grid;
  gap: 10px;
}

.ai-quick-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.ai-quick-action {
  display: grid;
  min-width: 0;
  min-height: 46px;
  align-content: center;
  gap: 4px;
  border-color: var(--hs-btn-secondary-border, #c7d0da);
  border-radius: 6px;
  background: var(--hs-btn-secondary-bg, #ffffff);
  padding: 10px;
  text-align: left;
}

.ai-quick-action strong {
  color: var(--hs-btn-secondary-text, #334155);
  font-size: 13px;
  font-weight: 850;
  line-height: 1.35;
}

.ai-quick-action span {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.ai-quick-action:hover:not(:disabled) {
  background: var(--hs-btn-secondary-bg-hover, #f6f8fb);
}

.ai-request-field small {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.ai-request-actions {
  display: flex;
  justify-content: flex-end;
}

.ai-assistant-results {
  border-top: 1px solid #e4e7eb;
  padding-top: 12px;
}

.ai-result-card {
  display: grid;
  gap: 10px;
  border: 1px solid #d9dee3;
  border-radius: 8px;
  background: #ffffff;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.ai-result-card:hover {
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.ai-result-card--ai,
.ai-result-card--info {
  border-color: #bae6fd;
  background: #f0f9ff;
}

.ai-result-card--warning,
.ai-result-card--pending {
  border-color: #fde68a;
  background: #fffbeb;
}

.ai-result-card--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.ai-result-card--excluded {
  border-color: #d4d4d8;
  background: #f8fafc;
}

.ai-result-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.ai-result-badges {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.ai-result-head .ai-result-badges .status-chip {
  color: inherit;
  font-size: 11px;
  line-height: 1.25;
  white-space: nowrap;
}

.ai-result-head .ai-result-badges .status-chip--ai {
  color: #0e7490;
}

.ai-result-head span:first-child,
.ai-result-head p,
.ai-result-meta span {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.4;
}

.ai-result-head h4 {
  margin-top: 3px;
  color: #111827;
  font-size: 14px;
  font-weight: 850;
  line-height: 1.35;
}

.ai-result-head p {
  margin-top: 4px;
  overflow-wrap: anywhere;
}

.ai-result-summary {
  color: #334155;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
}

.ai-result-route {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccfbf1;
  border-radius: 7px;
  background: #f0fdfa;
  padding: 7px 9px;
}

.ai-result-route strong {
  color: #0f766e;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.35;
}

.ai-result-items {
  display: grid;
  gap: 8px;
}

.ai-result-items > div {
  border: 1px solid rgba(203, 213, 225, 0.8);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.72);
  padding: 9px;
}

.ai-result-items > div.ai-pattern-candidate {
  display: grid;
  gap: 8px;
  border-color: #99f6e4;
  background: #f8fffe;
}

.ai-result-items > div.ai-soap-suggestion {
  display: grid;
  gap: 8px;
  border-color: #c7d2fe;
  background: #ffffff;
}

.ai-pattern-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.ai-soap-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.ai-soap-title-row > div {
  display: grid;
  min-width: 0;
  gap: 5px;
}

.ai-soap-title-row .status-chip {
  flex: 0 0 auto;
}

.ai-soap-section {
  display: inline-flex;
  width: fit-content;
  min-height: 22px;
  align-items: center;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 0 7px;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
}

.ai-soap-text,
.ai-soap-target,
.ai-soap-reasons {
  display: grid;
  gap: 5px;
  border-left: 3px solid #c7d2fe;
  background: rgba(248, 250, 252, 0.78);
  padding: 8px 10px;
}

.ai-soap-target {
  border-left-color: #cbd5e1;
}

.ai-soap-reasons {
  border-left-color: #99f6e4;
}

.ai-soap-text small,
.ai-soap-target small,
.ai-soap-reasons small {
  color: #475569;
  font-size: 11px;
  font-weight: 900;
}

.ai-soap-text p,
.ai-soap-target p {
  margin-top: 0;
  color: #334155;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.ai-soap-reasons span {
  display: block;
  margin-top: 0;
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
}

.ai-soap-route-map {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  border: 1px solid #dbeafe;
  border-radius: 7px;
  background: #eff6ff;
  padding: 7px 8px;
}

.ai-soap-route-map span {
  margin-top: 0;
  color: #475569;
  font-size: 11px;
  font-weight: 900;
}

.ai-soap-route-map strong {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  border-radius: 6px;
  background: #ffffff;
  color: #1d4ed8;
  padding: 0 7px;
  font-size: 11px;
  font-weight: 900;
}

.ai-soap-route-map small {
  color: #0f766e;
  font-size: 11px;
  font-weight: 850;
}

.ai-pattern-title-row > div {
  min-width: 0;
}

.ai-pattern-title-row .status-chip {
  flex: 0 0 auto;
  margin-top: 0;
}

.ai-pattern-clinical-brief {
  display: grid;
  gap: 8px;
}

.ai-pattern-clinical-brief > div {
  display: grid;
  gap: 4px;
  border-left: 3px solid #0f766e;
  background: rgba(240, 253, 250, 0.82);
  padding: 8px 10px;
}

.ai-pattern-clinical-brief small {
  color: #115e59;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.ai-pattern-clinical-brief p {
  color: #1f2937;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.ai-pattern-review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.ai-pattern-review-block {
  display: grid;
  min-width: 0;
  gap: 5px;
  border-left: 3px solid #38bdf8;
  background: rgba(248, 250, 252, 0.84);
  padding: 8px 10px;
}

.ai-pattern-review-block--opposing {
  border-left-color: #f59e0b;
}

.ai-pattern-review-block--followup {
  border-left-color: #64748b;
}

.ai-pattern-review-block--judgement {
  border-left-color: #16a34a;
}

.ai-pattern-review-block small {
  color: #475569;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.3;
}

.ai-result-items .ai-pattern-review-block span {
  display: block;
  margin-top: 0;
  color: #334155;
  font-size: 12px;
  font-weight: 730;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.ai-pattern-review-block textarea {
  width: 100%;
  min-height: 58px;
  resize: vertical;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  background: #ffffff;
  color: #14532d;
  padding: 7px 8px;
  font-size: 12px;
  font-weight: 720;
  line-height: 1.45;
}

@media (max-width: 720px) {
  .ai-pattern-review-grid {
    grid-template-columns: 1fr;
  }
}
.ai-result-items strong {
  display: block;
  color: #18181b;
  font-size: 13px;
  font-weight: 850;
  line-height: 1.35;
}

.ai-result-items p {
  margin-top: 4px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.ai-result-items span {
  display: inline-flex;
  margin-top: 6px;
  color: #0e7490;
  font-size: 11px;
  font-weight: 850;
}

.ai-pattern-evidence,
.ai-pattern-questions {
  display: grid;
  gap: 5px;
  border-left: 3px solid #99f6e4;
  padding-left: 8px;
}

.ai-pattern-evidence--opposing {
  border-left-color: #fbbf24;
}

.ai-pattern-evidence small,
.ai-pattern-questions small {
  color: #475569;
  font-size: 11px;
  font-weight: 900;
}

.ai-pattern-evidence span,
.ai-pattern-questions span {
  display: block;
  margin-top: 0;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.ai-item-target {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.ai-item-target small {
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  border: 1px solid #ccfbf1;
  border-radius: 6px;
  background: #f0fdfa;
  color: #0f766e;
  padding: 0 7px;
  font-size: 11px;
  font-weight: 850;
  line-height: 1;
}

.ai-item-status-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.ai-item-status-row small {
  color: #475569;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.35;
}

.ai-item-status-row .status-chip {
  margin-top: 0;
  color: inherit;
}

.ai-action-panel,
.ai-item-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

.ai-action-group {
  display: flex;
  min-width: min(100%, 160px);
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.ai-action-group > span {
  flex: 1 0 100%;
  text-align: right;
}

.ai-evidence-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.ai-evidence-panel {
  display: grid;
  gap: 7px;
  border-left: 3px solid #cbd5e1;
  background: rgba(248, 250, 252, 0.72);
  padding: 8px 0 8px 10px;
}

.ai-evidence-panel--result {
  border-top: 1px solid rgba(203, 213, 225, 0.8);
  border-left-color: #93c5fd;
  margin-top: -2px;
  padding-top: 10px;
}

.ai-evidence-row {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.ai-evidence-row span {
  display: block;
  margin-top: 0;
  color: #64748b;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.3;
}

.ai-evidence-row strong {
  color: #111827;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.35;
}

.ai-evidence-row p,
.ai-evidence-row small {
  margin: 0;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.ai-evidence-row small {
  color: #64748b;
}

.ai-result-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.ai-result-meta strong {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 850;
}

.ai-result-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  border-top: 1px solid rgba(203, 213, 225, 0.8);
  padding-top: 10px;
}

.ai-result-actions > span {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.workspace-bottom-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 288px;
  z-index: 30;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid #d9dee3;
  background: rgba(255, 255, 255, 0.92);
  padding: 7px 22px calc(7px + env(safe-area-inset-bottom));
  box-shadow: 0 -4px 14px rgba(15, 23, 42, 0.05);
  backdrop-filter: blur(10px);
  animation: hs-panel-enter 220ms ease both;
}

.encounter-workspace.workspace-modal-open .workspace-bottom-bar {
  display: none;
}

.bottom-summary {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  color: #71717a;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
}

.bottom-summary strong {
  min-width: 0;
  color: #18181b;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bottom-summary__eyebrow,
.bottom-summary__meta,
.bottom-summary__review {
  flex: 0 0 auto;
}

.bottom-summary__eyebrow {
  color: #64748b;
  font-weight: 900;
}

.bottom-summary__review {
  min-height: 28px;
  border: 1px solid #facc15;
  border-radius: 999px;
  background: #fefce8;
  color: #b45309;
  padding: 0 9px;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.bottom-summary--mobile {
  display: none;
}

.bottom-actions {
  flex: 0 1 auto;
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.bottom-actions .button {
  min-height: 32px;
  min-width: 82px;
  padding-top: 0;
  padding-bottom: 0;
}

.bottom-save-state {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  gap: 6px;
  color: #52615d;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.bottom-save-state::before {
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #e4f5ef;
  color: #08745f;
  content: "✓";
  font-size: 11px;
  font-weight: 900;
}

.mobile-context-brief {
  display: none;
  width: min(100%, 1500px);
  margin: 0 auto 8px;
  overflow: hidden;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
}

.mobile-safety-alert {
  display: none;
}

.mobile-context-brief summary {
  display: grid;
  min-height: 42px;
  cursor: pointer;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  list-style: none;
  padding: 8px 10px;
}

.mobile-context-brief summary::-webkit-details-marker {
  display: none;
}

.mobile-context-brief[open] summary {
  border-bottom: 1px solid #edf1f5;
}

.mobile-context-brief__label,
.mobile-context-item span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
  white-space: nowrap;
}

.mobile-context-brief summary strong {
  min-width: 0;
  overflow: hidden;
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-context-brief summary em {
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  padding: 4px 7px;
  font-size: 11px;
  font-style: normal;
  font-weight: 900;
  line-height: 1;
}

.mobile-context-brief__body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  padding: 8px;
}

.mobile-context-item {
  display: grid;
  min-width: 0;
  min-height: 42px;
  appearance: none;
  cursor: pointer;
  gap: 3px;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  padding: 7px 8px;
  font: inherit;
  text-align: left;
}

.mobile-context-item--wide,
.mobile-context-safety {
  grid-column: 1/-1;
}

.mobile-context-item strong {
  display: -webkit-box;
  min-width: 0;
  overflow: hidden;
  color: #111827;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.3;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.mobile-context-safety {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.mobile-context-safety .safety-chip {
  min-height: 34px;
}

.mobile-context-item--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.mobile-context-item--primary {
  border-color: #99f6e4;
  background: #f0fdfa;
}

.mobile-context-item--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.mobile-context-item--pending {
  border-color: #fed7aa;
  background: #fff7ed;
}

.mobile-context-item--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.patient-context-strip {
  display: grid;
  width: min(100%, 1500px);
  margin: 0 auto 10px;
  align-items: stretch;
  gap: 8px;
  grid-template-columns: minmax(180px, 0.9fr) minmax(220px, 1.25fr) minmax(220px, 1.2fr) minmax(240px, 1.15fr) minmax(220px, 0.9fr);
}

.patient-context-strip--compact {
  align-items: center;
}

.soap-entry-status__label-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
}

.current-task-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #0f766e;
  color: #ffffff;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

.context-patient,
.context-chief,
.context-card,
.context-safety,
.context-actions {
  min-width: 0;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  padding: 8px 10px;
}

.context-patient,
.context-chief,
.context-card {
  display: grid;
  gap: 3px;
}

.context-card {
  min-height: 48px;
  appearance: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.context-card:hover {
  border-color: #94a3b8;
  background: #fbfcfd;
}

.context-patient span,
.context-chief span,
.context-card span,
.queue-metrics span,
.aux-card span,
.soap-focus-status > span:not(.status-chip) {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.context-patient strong,
.context-chief strong,
.context-card strong,
.queue-metrics strong,
.aux-card strong,
.soap-focus-status strong {
  min-width: 0;
  color: #111827;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.context-card small {
  min-width: 0;
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.context-patient small {
  color: #71717a;
  font-size: 12px;
  font-weight: 750;
}

.context-safety {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
}

.context-safety::-webkit-scrollbar {
  display: none;
}

.safety-chip {
  display: inline-grid;
  min-width: 0;
  min-height: 38px;
  align-content: center;
  gap: 2px;
  border: 1px solid #d4d4d8;
  border-radius: 7px;
  background: #f8fafc;
  padding: 5px 8px;
  text-align: left;
}

.safety-chip span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.safety-chip strong {
  color: #334155;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.safety-chip--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.safety-chip--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.safety-chip--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.context-safety--quiet,
.mobile-context-safety--quiet {
  background: #fbfcfd;
}

.context-safety--quiet .safety-chip,
.mobile-context-safety--quiet .safety-chip {
  border-color: #e4e7eb;
  background: #ffffff;
}

.context-safety--quiet .safety-chip--warning,
.mobile-context-safety--quiet .safety-chip--warning {
  border-color: #f1d99b;
  background: #fffdf5;
}

.context-safety--quiet .safety-chip--danger,
.mobile-context-safety--quiet .safety-chip--danger {
  border-color: #efc3c3;
  background: #fffafa;
}

.context-card--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.context-card--primary {
  border-color: #99f6e4;
  background: #f0fdfa;
}

.context-card--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.context-card--pending {
  border-color: #fed7aa;
  background: #fff7ed;
}

.context-card--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.context-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
}

.auxiliary-action-group .button {
  min-width: 104px;
}

.clinical-grid--focused {
  grid-template-columns: minmax(300px, 340px) minmax(620px, 1fr) minmax(280px, 320px);
  align-items: stretch;
  height: clamp(560px, 100dvh - var(--workspace-bottom-bar-clearance) - var(--workspace-focused-grid-offset), 800px);
  min-height: 0;
}

.clinical-grid--focused .clinical-panel--patient {
  order: 1;
}

.clinical-grid--focused .auxiliary-panel {
  order: 3;
}

.clinical-grid--focused .clinical-panel--soap {
  order: 2;
}

.clinical-grid--focused > .workspace-panel {
  display: flex;
  min-height: 0;
  max-height: 100%;
  flex-direction: column;
}

.clinical-grid--focused .auxiliary-panel {
  animation-delay: 45ms;
}

.clinical-grid--focused .clinical-panel--soap {
  animation-delay: 90ms;
}

.patient-summary-panel,
.clinical-panel--queue,
.focus-queue-panel,
.auxiliary-panel {
  overflow: hidden;
}

.patient-summary-panel__body {
  display: grid;
  flex: 1 1 auto;
  align-content: start;
  gap: 12px;
  min-height: 0;
  overflow: auto;
  padding: 12px 12px calc(12px + var(--workspace-panel-bottom-space));
  scroll-padding-bottom: var(--workspace-inner-scroll-clearance);
}

.conversation-panel__body {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  padding: 12px;
}

.conversation-patient-strip {
  min-width: 0;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
  transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.conversation-patient-strip:hover {
  border-color: #bfdbfe;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}

.conversation-patient-strip > div {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.conversation-patient-strip span,
.conversation-patient-strip em,
.conversation-turn__meta span {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.25;
}

.conversation-patient-strip strong {
  color: #111827;
  font-size: 13px;
  font-weight: 850;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.conversation-patient-strip em {
  font-style: normal;
}

.conversation-panel__count {
  justify-self: end;
  border: 1px solid #cbd5df;
  border-radius: 999px;
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  padding: 5px 8px;
  white-space: nowrap;
}

.conversation-thread {
  display: grid;
  min-height: 0;
  flex: 1 1 0;
  align-content: start;
  gap: 10px;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
  overscroll-behavior-y: contain;
  scroll-behavior: smooth;
  scrollbar-color: #94a3b8 transparent;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scroll-padding-bottom: var(--workspace-inner-scroll-clearance);
}

.conversation-thread:focus-visible {
  outline: 2px solid rgba(15, 118, 110, 0.4);
  outline-offset: 2px;
}

.conversation-thread[aria-busy=true] {
  padding-bottom: 18px;
  scroll-behavior: auto;
}

.conversation-thread::-webkit-scrollbar {
  width: 8px;
}

.conversation-thread::-webkit-scrollbar-thumb {
  border: 2px solid transparent;
  border-radius: 999px;
  background: #94a3b8;
  background-clip: padding-box;
}

.conversation-empty,
.conversation-loading {
  display: grid;
  gap: 4px;
  border: 1px dashed #cbd5df;
  border-radius: 8px;
  background: #ffffff;
  padding: 16px 10px;
  color: #64748b;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.4;
  text-align: center;
}

.conversation-empty--start {
  justify-items: center;
  padding: 18px 14px;
}

.conversation-empty--start strong {
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.35;
}

.conversation-empty--start span {
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.45;
}

.conversation-loading {
  position: relative;
  isolation: isolate;
  display: grid;
  width: min(92%, 360px);
  min-height: 56px;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  justify-self: start;
  box-sizing: border-box;
  flex: 0 0 auto;
  gap: 10px;
  overflow: visible;
  border-style: solid;
  border-color: #a5d8e5;
  background: linear-gradient(115deg, #ffffff 0%, #ecfeff 54%, #eff6ff 100%);
  padding: 11px 12px 13px;
  color: #155e75;
  scroll-margin-block: 10px;
  text-align: left;
  animation: hs-loading-breathe 1300ms ease-in-out infinite;
}

.conversation-loading__orb {
  position: relative;
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  box-sizing: border-box;
  flex: 0 0 30px;
  border: 1px solid rgba(14, 116, 144, 0.2);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.84);
}

.conversation-loading__orb::before {
  content: "";
  position: absolute;
  inset: 5px;
  box-sizing: border-box;
  border: 2px solid rgba(14, 116, 144, 0.2);
  border-top-color: #0e7490;
  border-right-color: #0e7490;
  border-radius: 50%;
  animation: hs-thinking-spin 760ms linear infinite;
}

.conversation-loading__orb::after {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #0e7490;
  box-shadow: 0 0 0 4px rgba(14, 116, 144, 0.1);
}

.conversation-loading__copy {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.conversation-loading__copy strong {
  color: #164e63;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.3;
}

.conversation-loading__copy small {
  overflow: hidden;
  color: #52717c;
  font-size: 10px;
  font-weight: 750;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-loading__dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding-right: 2px;
}

.conversation-loading__dots i {
  display: block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #0891b2;
  animation: hs-thinking-dot 1050ms ease-in-out infinite;
}

.conversation-loading__dots i:nth-child(2) {
  animation-delay: 140ms;
}

.conversation-loading__dots i:nth-child(3) {
  animation-delay: 280ms;
}

.conversation-loading__track {
  position: absolute;
  right: 1px;
  bottom: 1px;
  left: 1px;
  height: 3px;
  overflow: hidden;
  border-radius: 0 0 6px 6px;
  background: rgba(14, 116, 144, 0.08);
}

.conversation-loading__track i {
  display: block;
  width: 34%;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, #06b6d4, #2563eb, transparent);
  animation: hs-thinking-scan 1450ms ease-in-out infinite;
}

.conversation-turn {
  display: grid;
  width: min(92%, 360px);
  gap: 6px;
  border: 1px solid #d9dee3;
  border-radius: 8px;
  background: #ffffff;
  padding: 9px 10px;
  animation: hs-turn-enter 180ms ease both;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.conversation-turn:hover {
  box-shadow: 0 9px 22px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.conversation-turn--user {
  justify-self: end;
  border-color: #99f6e4;
  background: #ecfdf5;
}

.conversation-turn--assistant {
  justify-self: start;
  border-color: #bfdbfe;
  background: #eff6ff;
}

.conversation-turn--streaming {
  border-color: #67e8f9;
  background: #ecfeff;
  box-shadow: 0 0 0 1px rgba(14, 116, 144, 0.08), 0 10px 24px rgba(14, 116, 144, 0.08);
}

.conversation-turn--failed {
  border-color: #fde68a;
  background: #fffbeb;
}

.conversation-turn__meta {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.conversation-turn__meta strong {
  color: #334155;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.conversation-turn p {
  color: #1f2937;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.55;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.conversation-turn p strong {
  color: #0f172a;
  font-weight: 900;
}

.conversation-markdown-heading {
  display: block;
  margin-top: 6px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
}

.conversation-turn p code {
  border: 1px solid #dbeafe;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.72);
  color: #1d4ed8;
  font-family: inherit;
  font-size: 12px;
  font-weight: 850;
  padding: 1px 4px;
}

.conversation-stream-cursor {
  display: inline-block;
  width: 7px;
  height: 16px;
  margin-left: 2px;
  border-radius: 999px;
  background: #0e7490;
  vertical-align: -3px;
  animation: hs-cursor-blink 880ms steps(1, end) infinite;
}

.conversation-input {
  display: grid;
  gap: 8px;
}

.conversation-input--docked {
  position: relative;
  z-index: 1;
  min-width: 0;
  flex: 0 0 auto;
  margin-top: auto;
  border-top: 1px solid #e4e7eb;
  background: #ffffff;
  padding-top: 10px;
}

.conversation-input textarea {
  width: 100%;
  min-height: 88px;
  max-height: 180px;
  resize: vertical;
  border: 1px solid #cbd5df;
  border-radius: 6px;
  background: #ffffff;
  color: #18181b;
  font-size: 14px;
  line-height: 1.5;
  outline: none;
  padding: 9px 10px;
  transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
}

.conversation-input textarea:focus {
  border-color: #0f766e;
  background: #fbfffe;
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
}

.conversation-panel--empty .conversation-thread {
  min-height: 0;
  flex: 1 1 0;
}

.conversation-panel--empty .conversation-input textarea {
  min-height: 128px;
}

.conversation-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.live-ai-recommendations {
  display: grid;
  gap: 9px;
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 10px;
}

.live-ai-recommendations__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.live-ai-recommendations__head span {
  color: #0f766e;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.live-ai-recommendations__head strong {
  min-width: 34px;
  border: 1px solid #bae6fd;
  border-radius: 999px;
  background: #ecfeff;
  color: #0e7490;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
  padding: 5px 8px;
  text-align: center;
}

.ai-result-card--compact {
  gap: 8px;
  padding: 10px;
  animation: hs-turn-enter 180ms ease both;
}

.ai-result-card--compact .ai-result-head {
  align-items: flex-start;
}

.ai-result-card--compact h4 {
  font-size: 13px;
}

.ai-result-card--compact .ai-result-summary {
  display: -webkit-box;
  overflow: hidden;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.live-ai-items {
  display: grid;
  gap: 6px;
}

.live-ai-item {
  display: grid;
  gap: 3px;
  border: 1px solid #dbeafe;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.72);
  padding: 7px;
}

.live-ai-item strong {
  color: #111827;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.25;
}

.live-ai-item p {
  display: -webkit-box;
  overflow: hidden;
  color: #334155;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.live-ai-item span {
  color: #0e7490;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.patient-summary-primary {
  display: grid;
  gap: 5px;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  background: #f0f9ff;
  padding: 12px;
}

.patient-summary-primary span,
.patient-visit-grid span,
.support-queue-card span {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.25;
}

.patient-summary-primary strong {
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.patient-summary-facts {
  display: grid;
  gap: 8px;
}

.patient-summary-facts .patient-fact {
  padding: 10px;
}

.patient-summary-facts .patient-fact dd {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.patient-visit-grid {
  display: grid;
  gap: 8px;
}

.patient-visit-grid > div {
  display: grid;
  min-width: 0;
  gap: 3px;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #f8fafc;
  padding: 9px 10px;
}

.patient-visit-grid strong {
  min-width: 0;
  color: #111827;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.support-queue-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 10px 12px;
}

.support-queue-card {
  display: grid;
  min-width: 0;
  gap: 4px;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 9px;
  text-align: left;
}

.support-queue-card:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}

.support-queue-card--accent {
  border-color: #bae6fd;
  background: #f0f9ff;
}

.support-queue-card strong {
  color: #111827;
  font-size: 18px;
  font-weight: 850;
  line-height: 1.1;
}

.support-queue-card p {
  display: -webkit-box;
  min-width: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.45;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.queue-metrics {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 12px;
}

.queue-metrics > div {
  display: grid;
  min-width: 0;
  gap: 3px;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #f8fafc;
  padding: 8px;
}

.queue-list {
  display: grid;
  flex: 1 1 auto;
  gap: 10px;
  min-height: 0;
  overflow: auto;
  padding: 12px;
}

.checklist-summary-card {
  display: grid;
  gap: 12px;
  flex: 1 1 auto;
  align-content: start;
  padding: 12px;
}

.checklist-summary-card__top {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #d9dee3;
  border-radius: 8px;
  background: #ffffff;
  padding: 12px;
}

.checklist-summary-card__top > div {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.checklist-summary-card__top span {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.checklist-summary-card__top strong {
  color: #111827;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
}

.checklist-summary-card__top p {
  color: #52525b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.checklist-summary-card__top em {
  flex: 0 0 auto;
  font-style: normal;
}

.checklist-summary-progress {
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #e4e7eb;
}

.checklist-summary-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #0f766e;
}

.checklist-summary-card .queue-metrics {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 0;
}

.soap-panel-header {
  flex: 0 0 auto;
}

.workspace-compact-status {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: repeat(4, minmax(124px, 1fr));
  gap: 8px;
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 10px 14px;
}

.compact-status-card {
  display: grid;
  min-width: 0;
  min-height: 54px;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 8px;
  border: 1px solid #d9dee3;
  border-radius: 7px;
  background: #ffffff;
  cursor: pointer;
  font: inherit;
  padding: 8px 10px;
  text-align: left;
}

.compact-status-card:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}

.compact-status-card span,
.compact-status-card strong {
  min-width: 0;
}

.compact-status-card span {
  color: #64748b;
  font-size: 11px;
  font-weight: 750;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.compact-status-card strong {
  color: #111827;
  font-size: 18px;
  font-weight: 850;
  line-height: 1;
  white-space: nowrap;
}

.compact-status-card em {
  grid-column: 2;
  grid-row: 1/span 2;
  justify-self: end;
  max-width: 88px;
  overflow: hidden;
  border-radius: 999px;
  padding: 4px 7px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
  line-height: 1.15;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-status-card--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.compact-status-card--primary {
  border-color: #99f6e4;
  background: #f0fdfa;
}

.compact-status-card--info,
.compact-status-card--ai {
  border-color: #bae6fd;
  background: #f0f9ff;
}

.compact-status-card--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.compact-status-card--pending {
  border-color: #fed7aa;
  background: #fff7ed;
}

.compact-status-card--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.soap-entry-prepare-strip {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #d9dee3;
  background: #fbfcfd;
  padding: 10px 12px;
}

.soap-entry-status {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: 5px;
}

.soap-entry-status__label-row {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.soap-entry-status strong,
.soap-entry-status p {
  min-width: 0;
  overflow: hidden;
}

.soap-entry-status strong {
  color: #111827;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.soap-entry-status p {
  display: -webkit-box;
  color: #64748b;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.soap-entry-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.soap-entry-metrics > div {
  display: grid;
  min-width: 0;
  gap: 2px;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #f8fafc;
  padding: 7px 8px;
}

.soap-entry-metrics span,
.candidate-queue-card span,
.candidate-queue-actions span {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.25;
}

.soap-entry-metrics strong,
.candidate-queue-card strong,
.candidate-queue-actions strong {
  min-width: 0;
  color: #111827;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.soap-entry-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
}

.soap-entry-actions .button {
  min-width: 88px;
}

.candidate-queue-stack {
  display: grid;
  flex: 1 1 auto;
  align-content: start;
  gap: 10px;
  min-height: 0;
  overflow: auto;
  padding: 12px;
}

.candidate-queue-card,
.candidate-queue-actions {
  display: grid;
  min-width: 0;
  gap: 8px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
}

.candidate-queue-card {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
}

.candidate-queue-card > div,
.candidate-queue-actions > div:first-child {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.candidate-queue-card p,
.candidate-queue-actions p {
  display: -webkit-box;
  min-width: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 650;
  line-height: 1.45;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.candidate-queue-card--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.candidate-queue-card--ai,
.candidate-queue-card--info {
  border-color: #bae6fd;
  background: #f0f9ff;
}

.candidate-queue-card--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.candidate-queue-card--pending {
  border-color: #fed7aa;
  background: #fff7ed;
}

.candidate-queue-card--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.candidate-queue-actions {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.candidate-queue-actions > div:last-child {
  display: grid;
  grid-template-columns: 1fr;
  gap: 7px;
}

.candidate-queue-actions .button,
.candidate-queue-card .button {
  min-width: 0;
  width: 100%;
}

.soap-queue-overview {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 10px 12px;
}

.soap-queue-overview > div,
.soap-queue-status-grid > div {
  display: grid;
  min-width: 0;
  gap: 3px;
  border: 1px solid #e4e7eb;
  border-radius: 7px;
  background: #ffffff;
  padding: 8px;
}

.soap-queue-overview span,
.soap-queue-status-grid span,
.candidate-queue-group summary span,
.candidate-queue-row__meta span,
.candidate-queue-row__head span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.soap-queue-overview strong,
.soap-queue-status-grid strong,
.candidate-queue-group summary strong,
.candidate-queue-row__head strong {
  display: -webkit-box;
  min-width: 0;
  overflow: hidden;
  color: #111827;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.3;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.soap-queue-overview p,
.soap-queue-status-grid p {
  min-width: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-queue-role-bar {
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #cffafe;
  background: #ecfeff;
  padding: 9px 10px;
}

.candidate-queue-role-bar > div {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.candidate-queue-role-bar span:not(.status-chip) {
  color: #0e7490;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.candidate-queue-role-bar strong {
  color: #111827;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-queue-role-bar small {
  color: #155e75;
  font-size: 11px;
  font-weight: 750;
  line-height: 1.35;
}

.candidate-queue-role-bar p {
  display: none;
  min-width: 0;
  color: #155e75;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.candidate-queue-role-bar .status-chip {
  justify-self: end;
}

.candidate-batch-applied {
  animation: hs-candidate-batch-complete 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.candidate-queue-next {
  display: grid;
  gap: 7px;
  border: 1px dashed #94a3b8;
  border-radius: 6px;
  background: #f8fafc;
  padding: 9px;
}

.candidate-queue-next span {
  color: #475569;
  font-size: 11px;
  font-weight: 750;
  line-height: 1.4;
}

.candidate-queue-next .button,
.candidate-batch-complete .button {
  width: 100%;
}

@keyframes hs-candidate-batch-complete {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .candidate-batch-applied {
    animation: none;
  }
}
.clinical-prescription-card {
  display: grid;
  gap: 12px;
  width: 100%;
}

.clinical-prescription-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.clinical-prescription-card__head > div {
  display: grid;
  gap: 3px;
}

.clinical-prescription-card__head span,
.clinical-prescription-card__grid small {
  color: var(--hs-text-muted, #667085);
  font-size: 12px;
}

.clinical-prescription-card__head em {
  border-radius: 999px;
  background: #fff7e6;
  color: #9a6700;
  font-size: 11px;
  font-style: normal;
  padding: 4px 8px;
}

.clinical-prescription-card__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.clinical-prescription-card__grid > div,
.clinical-prescription-card__safety {
  border: 1px solid var(--hs-border, #e4e7ec);
  border-radius: 10px;
  padding: 10px;
}

.clinical-prescription-card__grid p {
  margin: 4px 0 0;
}

.clinical-prescription-card__safety {
  display: grid;
  gap: 4px;
  border-color: #fedf89;
  background: #fffaeb;
}

.clinical-prescription-card__safety span {
  font-size: 12px;
}

.mcp-telemetry-panel {
  border: 1px solid var(--hs-border, #e4e7ec);
  border-radius: 8px;
  background: var(--hs-surface, #ffffff);
}

.mcp-telemetry-panel > summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  padding: 9px 11px;
  color: var(--hs-text, #344054);
  font-size: 12px;
}

.mcp-telemetry-panel__body {
  display: grid;
  gap: 8px;
  border-top: 1px solid var(--hs-border, #e4e7ec);
  padding: 10px 11px;
}

.mcp-telemetry-panel--drawer {
  margin: 0 0 10px;
}

.mcp-telemetry-panel__body p,
.mcp-telemetry-panel__body small {
  margin: 0;
  color: var(--hs-text-muted, #667085);
  font-size: 11px;
  line-height: 1.45;
}

.mcp-telemetry-panel__totals {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.mcp-telemetry-panel__totals span,
.mcp-telemetry-tool {
  border: 1px solid var(--hs-border, #e4e7ec);
  border-radius: 6px;
  padding: 7px 8px;
  font-size: 11px;
}

.mcp-telemetry-panel__totals strong {
  margin-right: 3px;
  color: var(--hs-accent, #17645d);
}

.mcp-telemetry-tool {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.mcp-telemetry-tool span {
  color: var(--hs-text-muted, #667085);
}

.clinical-prescription-card__evidence,
.clinical-prescription-card__expansion,
.clinical-prescription-card__trace {
  border: 1px solid #b8ded7;
  border-radius: 10px;
  background: #f3faf8;
  padding: 10px;
}

.clinical-prescription-card__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.clinical-prescription-card__section-head > span {
  color: var(--hs-text-muted, #667085);
  font-size: 11px;
}

.clinical-prescription-card__expansion {
  display: grid;
  gap: 8px;
  border-color: #bfdbfe;
  background: #f8fbff;
}

.clinical-prescription-card__expansion > summary {
  cursor: pointer;
  color: #1e3a5f;
  font-size: 12px;
  font-weight: 800;
}

.clinical-prescription-card__expansion p,
.clinical-prescription-card__expansion small {
  margin: 0;
  color: var(--hs-text-muted, #667085);
  font-size: 11px;
  line-height: 1.45;
}

.clinical-expansion-terms {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.clinical-expansion-terms span {
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #ffffff;
  padding: 4px 7px;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 750;
}

.clinical-prescription-card__evidence {
  display: grid;
  gap: 7px;
}

.clinical-evidence-split {
  display: grid;
  grid-template-columns: minmax(150px, 0.85fr) minmax(0, 1.6fr);
  gap: 10px;
  min-width: 0;
}

.clinical-evidence-split__sources {
  display: grid;
  align-content: start;
  gap: 5px;
  min-width: 0;
}

.clinical-evidence-split__sources button {
  display: grid;
  gap: 3px;
  width: 100%;
  min-width: 0;
  border: 1px solid #d7ebe7;
  border-radius: 7px;
  background: #ffffff;
  padding: 8px;
  color: #344054;
  text-align: left;
  cursor: pointer;
}

.clinical-evidence-split__sources button:hover,
.clinical-evidence-split__sources button.is-selected {
  border-color: #0f766e;
  background: #ecfdf8;
}

.clinical-evidence-split__sources button:focus-visible {
  outline: 3px solid rgba(15, 118, 110, 0.24);
  outline-offset: 1px;
}

.clinical-evidence-split__sources span {
  overflow-wrap: anywhere;
  font-size: 12px;
  font-weight: 800;
}

.clinical-evidence-split__sources small {
  overflow-wrap: anywhere;
}

.clinical-evidence-split__preview {
  display: grid;
  align-content: start;
  gap: 7px;
  min-width: 0;
  border: 1px solid #99d8cd !important;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px !important;
}

.clinical-evidence-split__preview > span {
  color: #0f766e;
  font-size: 11px;
  font-weight: 850;
}

.clinical-evidence-split__preview > strong,
.clinical-evidence-split__preview > small {
  overflow-wrap: anywhere;
}

.clinical-evidence-split__preview mark {
  border-radius: 5px;
  background: #fff3bf;
  padding: 8px;
  color: #25313d;
  font-size: 12px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.clinical-prescription-card__evidence article,
.clinical-prescription-card__trace > div {
  display: grid;
  gap: 2px;
  border-top: 1px solid #d7ebe7;
  padding-top: 7px;
}

.clinical-prescription-card__evidence p,
.clinical-prescription-card__evidence small,
.clinical-prescription-card__trace p,
.clinical-prescription-card__trace span,
.clinical-prescription-card__trace small {
  margin: 0;
  color: var(--hs-text-muted, #667085);
  font-size: 11px;
  line-height: 1.45;
}

.clinical-prescription-card__trace > summary {
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

@media (max-width: 900px) {
  .clinical-prescription-card__grid {
    grid-template-columns: 1fr;
  }
  .clinical-prescription-card__head {
    flex-direction: column;
  }
  .mcp-telemetry-panel__totals {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .mcp-telemetry-tool {
    display: grid;
  }
  .clinical-evidence-split {
    grid-template-columns: 1fr;
  }
}
.candidate-queue-stack--soap {
  gap: 8px;
}

.candidate-queue-group {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
}

.candidate-queue-group--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.candidate-queue-group--pending {
  border-color: #fed7aa;
  background: #fff7ed;
}

.candidate-queue-group--neutral {
  border-color: #d9dee3;
  background: #f8fafc;
}

.candidate-queue-group--success {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.candidate-queue-group--danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.candidate-queue-group > summary {
  display: flex;
  min-width: 0;
  cursor: pointer;
  list-style: none;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px;
}

.candidate-queue-group > summary::-webkit-details-marker {
  display: none;
}

.candidate-queue-group > summary::after {
  content: "+";
  display: inline-flex;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5df;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.78);
  color: #334155;
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
}

.candidate-queue-group[open] > summary::after {
  content: "-";
}

.candidate-queue-group summary > div {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.candidate-queue-group summary p {
  color: #64748b;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.35;
}

.candidate-queue-group summary em {
  max-width: 120px;
  flex: 0 0 auto;
  overflow: hidden;
  color: #475569;
  font-size: 11px;
  font-style: normal;
  font-weight: 850;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-queue-preview,
.soap-queue-drawer-groups {
  display: grid;
  gap: 8px;
  padding: 0 10px 10px;
}

.candidate-queue-row {
  display: grid;
  min-width: 0;
  gap: 10px;
  border: 1px solid rgba(203, 213, 225, 0.9);
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.86);
  padding: 12px;
  cursor: default;
  transition: border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
}

.candidate-queue-row__detail {
  display: grid;
  width: 100%;
  min-width: 0;
  cursor: pointer;
  gap: 10px;
  border: 0;
  background: transparent;
  color: inherit;
  padding: 0;
  font: inherit;
  text-align: left;
}

.candidate-queue-row__detail:focus-visible {
  border-radius: 3px;
  outline: 2px solid var(--clinic-primary);
  outline-offset: 3px;
}

.candidate-queue-row:hover {
  border-color: rgba(15, 118, 110, 0.36);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.candidate-queue-row--warning {
  border-color: #fde68a;
}

.candidate-queue-row--pending {
  border-color: #fed7aa;
}

.candidate-queue-row--neutral {
  border-color: #d9dee3;
  background: #f8fafc;
}

.candidate-queue-row--success {
  border-color: #bbf7d0;
}

.candidate-queue-row--danger {
  border-color: #fecaca;
}

.candidate-queue-row__head {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.candidate-queue-row__head > div {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.candidate-queue-row__head em {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 11px;
  font-style: normal;
  font-weight: 800;
  line-height: 1.3;
  white-space: nowrap;
}

.candidate-queue-row__head--clinical span {
  color: #0f766e;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
}

.candidate-queue-row__head--clinical strong {
  display: block;
  color: #111827;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-queue-row > p {
  color: #334155;
  font-size: 13px;
  font-weight: 750;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.candidate-queue-row__clinical {
  display: grid;
  gap: 7px;
}

.candidate-queue-row__clinical--hint {
  gap: 5px;
}

.candidate-queue-row__clinical > div {
  display: grid;
  gap: 3px;
  border-left: 3px solid #0f766e;
  background: rgba(240, 253, 250, 0.8);
  padding: 7px 9px;
}

.candidate-queue-row__clinical--hint > div {
  grid-template-columns: 90px minmax(0, 1fr);
  align-items: start;
  gap: 8px;
  padding: 6px 8px;
}

.candidate-queue-row__clinical span {
  color: #115e59;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
  white-space: nowrap;
}

.candidate-queue-row__clinical p {
  color: #1f2937;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.candidate-queue-row__clinical--hint p {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.candidate-queue-row__reason {
  border-left: 3px solid #99f6e4;
  background: #f0fdfa;
  padding: 7px 9px;
  color: #115e59;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.45;
}

.candidate-queue-row__meta {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.candidate-queue-row__meta span {
  min-height: 22px;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  background: #eff6ff;
  padding: 3px 7px;
  color: #1d4ed8;
  line-height: 1.2;
}

.candidate-queue-row__actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
  padding-top: 2px;
}

.candidate-queue-row__actions .button {
  min-width: 0;
  width: 100%;
  min-height: 32px;
  padding-right: 6px;
  padding-left: 6px;
  line-height: 1.15;
  white-space: normal;
}

.candidate-queue-actions--compact > div:last-child {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.soap-queue-drawer {
  display: grid;
  gap: 10px;
}

.soap-queue-status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
  gap: 7px;
}

.soap-queue-status-grid > div {
  background: #f8fafc;
}

.soap-queue-drawer-groups {
  padding: 0;
}

.soap-queue-drawer-groups .candidate-queue-group {
  background: #ffffff;
}

.soap-queue-drawer-groups .candidate-queue-group > .state-panel {
  margin: 0 10px 10px;
}

.ai-assistant-disclosure {
  overflow: hidden;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
}

.ai-assistant-disclosure > summary {
  display: flex;
  cursor: pointer;
  list-style: none;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 11px 12px;
}

.ai-assistant-disclosure > summary::-webkit-details-marker {
  display: none;
}

.ai-assistant-disclosure > summary strong {
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.3;
}

.ai-assistant-disclosure > summary span {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.3;
}

.ai-assistant-disclosure__body {
  display: grid;
  gap: 10px;
  border-top: 1px solid #e4e7eb;
  padding: 12px;
}

.soap-focus-status {
  display: grid;
  flex: 0 0 auto;
  gap: 8px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 10px 14px;
}

.soap-focus-status--compact {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
}

.soap-focus-status--compact .status-chip {
  min-height: 24px;
}

.soap-focus-status > div,
.soap-focus-status > .status-chip {
  display: grid;
  min-width: 0;
  min-height: 44px;
  align-content: center;
  gap: 3px;
  border-radius: 7px;
  text-align: left;
}

.soap-focus-status > div {
  border: 1px solid #e4e7eb;
  background: #ffffff;
  padding: 7px 9px;
}

.soap-focus-status > .status-chip {
  justify-content: start;
  padding: 7px 9px;
}

.soap-focus-status--compact > .status-chip {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}

.assistant-summary-strip {
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.8fr) auto;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #dbeafe;
  background: #f8fafc;
  padding: 10px 14px;
}

.assistant-summary-main,
.assistant-summary-metrics > div {
  min-width: 0;
}

.assistant-summary-main span,
.assistant-summary-metrics span {
  display: block;
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.3;
}

.assistant-summary-main strong,
.assistant-summary-metrics strong {
  display: block;
  margin-top: 3px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.assistant-summary-main p {
  margin-top: 4px;
  color: #475569;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.assistant-summary-metrics {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
}

.assistant-summary-metrics > div {
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #ffffff;
  padding: 7px 8px;
}

.soap-panel .message-stack {
  flex: 0 0 auto;
}

.soap-panel .soap-editor {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding-bottom: calc(14px + var(--workspace-panel-bottom-space));
  scroll-padding-bottom: var(--workspace-inner-scroll-clearance);
  overscroll-behavior: contain;
}

.auxiliary-stack {
  display: grid;
  gap: 10px;
  overflow: auto;
  padding: 12px;
}

.aux-card {
  display: grid;
  min-width: 0;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 11px;
}

.aux-card--compact {
  min-height: 52px;
  gap: 8px;
  padding: 9px 10px;
}

.aux-card--compact > div {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.aux-card--compact strong {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aux-card--compact .button {
  min-width: 48px;
  padding-right: 9px;
  padding-left: 9px;
}

.aux-card p {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.workspace-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 48;
  background: rgba(15, 23, 42, 0.22);
}

.workspace-drawer-backdrop--assistant {
  background: rgba(15, 23, 42, 0.1);
}

.workspace-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 52;
  display: flex;
  width: min(100vw, var(--workspace-drawer-width, 520px));
  flex-direction: column;
  border-left: 1px solid #d9dee3;
  background: #ffffff;
  box-shadow: -16px 0 36px rgba(15, 23, 42, 0.18);
}

.workspace-drawer--checklist {
  width: min(100vw, var(--workspace-drawer-width, 640px));
}

.workspace-drawer--assistant {
  width: min(100vw, var(--workspace-drawer-width, 680px));
}

.workspace-drawer--safety {
  width: min(100vw, var(--workspace-drawer-width, 420px));
}

.drawer-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 14px 16px;
}

.drawer-header h2 {
  margin-top: 3px;
  color: #111827;
  font-size: 18px;
  font-weight: 850;
  line-height: 1.3;
}

.drawer-header-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.drawer-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.drawer-body .ai-assistant-panel {
  border-top: 0;
}

.patient-identity--drawer,
.patient-fact-list--drawer {
  padding: 14px 16px 0;
}

.patient-identity--drawer {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.patient-fact-list--drawer {
  padding-bottom: 14px;
}

.workspace-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(15, 23, 42, 0.34);
  animation: hs-modal-fade 160ms ease both;
}

.clinical-candidate-modal-backdrop {
  z-index: 84;
  background: rgba(15, 23, 42, 0.58);
}

.workspace-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 74;
  display: grid;
  width: min(100vw - 32px, 720px);
  max-height: min(84vh, 760px);
  transform: translate(-50%, -50%);
  grid-template-rows: auto minmax(0, 1fr) auto;
  border: 1px solid #d9dee3;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.24);
  overflow: hidden;
  animation: hs-modal-pop 210ms ease both;
}

.workspace-modal--evidence {
  width: min(100vw - 32px, 640px);
}

.workspace-modal--final {
  width: min(100vw - 32px, 860px);
}

.workspace-modal--patient-summary {
  width: min(100vw - 40px, 1040px);
  max-height: min(88vh, 820px);
}

.workspace-modal--candidate-queue {
  width: min(100vw - 40px, 920px);
  max-height: min(88vh, 820px);
}

.workspace-modal--clinical-candidate {
  z-index: 88;
  width: min(100vw - 48px, 900px);
  max-height: min(90vh, 840px);
  border-color: #cbd5e1;
  border-radius: 8px;
  box-shadow: 0 32px 90px rgba(15, 23, 42, 0.42);
}

.clinical-candidate-modal__header {
  align-items: flex-start;
  border-bottom-color: #cbd5e1;
  background: #ffffff;
  padding: 18px 20px;
}

.clinical-candidate-modal__title {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.clinical-candidate-modal__header p:not(.panel-kicker) {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.4;
}

.clinical-candidate-modal__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.clinical-candidate-modal__target {
  overflow-wrap: anywhere;
}

.clinical-candidate-modal__body {
  display: grid;
  gap: 12px;
  background: #f1f5f9;
  padding: 16px;
}

.clinical-candidate-judgement-grid,
.clinical-candidate-primary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;
}

.clinical-candidate-structure-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.clinical-candidate-section {
  display: grid;
  align-content: start;
  gap: 10px;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  padding: 12px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.clinical-candidate-section--cue {
  border-color: #99f6e4;
  background: #f8fffd;
}

.clinical-candidate-section--reason {
  border-color: #cbd5e1;
  background: #ffffff;
}

.clinical-candidate-section--connection {
  border-color: #fde68a;
  background: #fffdf5;
}

.clinical-candidate-section--reflection {
  border-color: #bfdbfe;
  background: #f8fbff;
}

.clinical-candidate-summary {
  margin: 0;
  color: #0f172a;
  font-size: 13px;
  font-weight: 760;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.clinical-candidate-list {
  display: grid;
  gap: 6px;
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
}

.clinical-candidate-detail-list {
  display: grid;
  gap: 7px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.clinical-candidate-detail-list li {
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #0f766e;
  border-radius: 6px;
  background: #ffffff;
  padding: 8px 9px;
  color: #1e293b;
  font-size: 13px;
  font-weight: 720;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.clinical-candidate-detail-list--caution li {
  border-color: #fde68a;
  border-left-color: #b45309;
  background: #fffbeb;
}

.clinical-candidate-detail-list--check li {
  border-color: #bfdbfe;
  border-left-color: #1d4ed8;
  background: #eff6ff;
}

.clinical-candidate-detail-list--reason li {
  border-color: #99f6e4;
  border-left-color: #0f766e;
  background: #ffffff;
}

.clinical-candidate-section--cue .clinical-candidate-detail-list li {
  border-left-color: #0f766e;
}

.clinical-candidate-section--connection .clinical-candidate-detail-list li {
  border-left-color: #b45309;
}

.clinical-candidate-evidence-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.clinical-candidate-evidence-grid article {
  display: grid;
  align-content: start;
  gap: 6px;
  min-width: 0;
  border: 1px solid #dbeafe;
  border-radius: 7px;
  background: #ffffff;
  padding: 10px;
}

.clinical-candidate-evidence-grid span {
  color: #0f766e;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
}

.clinical-candidate-evidence-grid p {
  margin: 0;
  color: #334155;
  font-size: 12px;
  font-weight: 720;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.clinical-candidate-section--reflection textarea {
  width: 100%;
  min-height: 118px;
  resize: vertical;
  border: 1px solid #93c5fd;
  border-radius: 7px;
  background: #ffffff;
  padding: 10px 12px;
  color: #0f172a;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.55;
  box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.08);
}

.clinical-candidate-section--reflection textarea:focus {
  border-color: #2563eb;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16);
}

.clinical-candidate-modal__footer {
  display: flex;
  justify-content: flex-end;
  border-top-color: #cbd5e1;
  background: #ffffff;
  padding: 14px 20px;
}

.workspace-modal__header,
.workspace-modal__footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: #fbfcfd;
  padding: 14px 16px;
}

.workspace-modal__header {
  border-bottom: 1px solid #e4e7eb;
}

.workspace-modal__header h2 {
  margin-top: 3px;
  color: #111827;
  font-size: 18px;
  font-weight: 850;
  line-height: 1.3;
}

.workspace-modal__body {
  min-height: 0;
  overflow: auto;
  padding: 16px;
}

.workspace-modal__footer {
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #e4e7eb;
}

.candidate-queue-modal__body,
.candidate-queue-modal-group {
  display: grid;
  gap: 10px;
}

.candidate-queue-modal-group {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #fbfcfd;
  padding: 10px;
}

.candidate-queue-modal-group__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 2px 2px;
}

.candidate-queue-modal-group__head span {
  color: #475569;
  font-size: 12px;
  font-weight: 850;
}

.candidate-queue-modal-group__head strong {
  color: #111827;
  font-size: 13px;
  font-weight: 900;
}

.workspace-modal__footer--final {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
}

.patient-summary-modal__header p:not(.panel-kicker) {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}

.patient-summary-modal__body {
  display: grid;
  gap: 14px;
  background: #f8fafc;
}

.patient-summary-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  background: #ffffff;
  padding: 14px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.patient-summary-hero span,
.patient-reference-metric__head span,
.patient-reference-metric small {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.3;
}

.patient-summary-hero strong {
  display: block;
  margin-top: 4px;
  color: #111827;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.patient-summary-hero p {
  margin-top: 6px;
  color: #475569;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}

.patient-summary-hero__badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.patient-summary-modal-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.85fr);
  gap: 14px;
}

.patient-summary-modal-card,
.patient-reference-overview {
  display: grid;
  gap: 12px;
  border: 1px solid #d9dee3;
  border-radius: 10px;
  background: #ffffff;
  padding: 14px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
}

.patient-modal-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.patient-modal-safety {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.patient-reference-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.patient-reference-metric {
  display: grid;
  gap: 7px;
  border: 1px solid #dbeafe;
  border-radius: 9px;
  background: #f8fafc;
  padding: 11px;
}

.patient-reference-metric--success {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.patient-reference-metric--attention,
.patient-reference-metric--warning {
  border-color: #fde68a;
  background: #fffbeb;
}

.patient-reference-metric__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.patient-reference-metric__head em {
  border-radius: 999px;
  background: #ffffff;
  color: #0f766e;
  font-size: 11px;
  font-style: normal;
  font-weight: 900;
  line-height: 1;
  padding: 5px 8px;
}

.patient-reference-metric--warning .patient-reference-metric__head em {
  color: #b45309;
}

.patient-reference-metric strong {
  color: #111827;
  font-size: 21px;
  font-weight: 950;
  line-height: 1.1;
}

.patient-reference-metric p {
  min-height: 34px;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.42;
}

.patient-reference-chart {
  display: grid;
  gap: 5px;
}

.patient-reference-chart__track {
  position: relative;
  height: 18px;
  border-radius: 999px;
  background: linear-gradient(90deg, #fee2e2 0%, #fef3c7 22%, #dcfce7 50%, #fef3c7 78%, #fee2e2 100%);
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.32);
}

.patient-reference-chart__range {
  position: absolute;
  top: 4px;
  bottom: 4px;
  border-radius: 999px;
  background: rgba(20, 184, 166, 0.26);
  box-shadow: inset 0 0 0 1px rgba(13, 148, 136, 0.24);
}

.patient-reference-chart__mean {
  position: absolute;
  top: 2px;
  bottom: 2px;
  width: 2px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.42);
  transform: translateX(-50%);
}

.patient-reference-chart__patient {
  position: absolute;
  top: 50%;
  width: 18px;
  height: 18px;
  border: 2px solid #ffffff;
  border-radius: 999px;
  background: #0f766e;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.24);
  transform: translate(-50%, -50%);
}

.patient-reference-chart__legend {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  color: #64748b;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.2;
}

.patient-reference-chart__legend strong {
  color: #0f766e;
  font-size: 10px;
  font-weight: 950;
}

.patient-reference-chart__legend span:last-child {
  text-align: right;
}

.patient-summary-standard-tools {
  margin-top: 2px;
}

.evidence-modal-meta {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.9fr) minmax(0, 0.9fr);
  gap: 8px;
}

.evidence-modal-meta div,
.final-modal-readiness {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #f8fafc;
  padding: 10px;
}

.evidence-modal-meta div {
  display: grid;
  gap: 4px;
  align-content: start;
}

.evidence-modal-meta span {
  color: #64748b;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.3;
}

.evidence-modal-meta strong {
  color: #111827;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.evidence-debug-detail {
  margin-top: 10px;
}

.evidence-modal-context {
  border-left: 3px solid #0f766e;
  margin: 12px 0 0;
  background: #f0fdfa;
  color: #115e59;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.5;
  padding: 9px 10px;
}

.evidence-modal-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.evidence-modal-list .ai-evidence-row {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
}

.evidence-debug-line {
  color: #7c3aed !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.final-modal-readiness {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
}

.final-modal-readiness__main {
  display: grid;
  min-width: 0;
  gap: 7px;
}

.final-modal-readiness__main strong {
  color: #111827;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.35;
}

.final-modal-readiness p {
  margin: 0;
  color: #475569;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.45;
}

.final-modal-readiness__metrics {
  display: grid;
  width: min(280px, 38%);
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.final-modal-readiness__metrics span {
  display: grid;
  min-width: 0;
  align-content: center;
  gap: 2px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 8px 6px;
  text-align: center;
}

.final-modal-readiness__metrics strong {
  color: #0f172a;
  font-size: 17px;
  font-weight: 950;
  line-height: 1;
}

.final-modal-readiness__metrics em {
  color: #64748b;
  font-size: 11px;
  font-style: normal;
  font-weight: 850;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.final-section-review {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.final-section-card {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 7px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
}

.final-section-card--blocking {
  border-color: #fecaca;
  background: #fff7f7;
}

.final-section-card--confirmable {
  border-color: #fde68a;
  background: #fffbeb;
}

.final-section-card--complete {
  border-color: #bbf7d0;
  background: #f7fef9;
}

.final-section-card__head {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.final-section-card__head strong {
  min-width: 0;
  color: #111827;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.final-section-card__head span {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 11px;
  font-weight: 850;
  line-height: 1.2;
}

.final-section-card__state {
  width: fit-content;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  padding: 3px 7px;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.2;
}

.final-section-card--blocking .final-section-card__state {
  background: #fee2e2;
  color: #991b1b;
}

.final-section-card--confirmable .final-section-card__state {
  background: #fef3c7;
  color: #92400e;
}

.final-section-card--complete .final-section-card__state {
  background: #dcfce7;
  color: #166534;
}

.final-section-card p {
  color: #334155;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.final-section-card ul {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.final-section-card li {
  position: relative;
  color: #475569;
  font-size: 11px;
  font-weight: 750;
  line-height: 1.35;
  overflow-wrap: anywhere;
  padding-left: 10px;
}

.final-section-card li::before {
  position: absolute;
  top: 0;
  left: 0;
  color: #94a3b8;
  content: "•";
}

.final-modal-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.final-gate-column--confirmed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.final-gate-details {
  display: grid;
  gap: 8px;
}

.final-gate-details summary {
  cursor: pointer;
  color: #166534;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.35;
}

.final-gate-item--confirmed {
  border-color: #bbf7d0;
}

.checklist-drawer-state,
.drawer-footer {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.checklist-drawer-state {
  border-bottom: 1px solid #e4e7eb;
  background: #fbfcfd;
  padding: 12px 14px;
}

.checklist-drawer-state > div,
.drawer-footer > div:first-child {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.checklist-drawer-state span,
.drawer-footer span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.3;
}

.checklist-drawer-state strong,
.drawer-footer strong {
  color: #111827;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.3;
}

.checklist-drawer-state p {
  color: #52525b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.drawer-footer {
  flex: 0 0 auto;
  border-top: 1px solid #d9dee3;
  background: #ffffff;
  padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
  box-shadow: 0 -8px 18px rgba(15, 23, 42, 0.06);
}

.drawer-footer-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.reference-comparison-panel {
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(34, 67, 95, 0.14);
  border-radius: 8px;
  background: #f8fbfc;
}

.reference-comparison-panel--drawer {
  margin-top: 12px;
}

.reference-summary {
  color: #183244;
  font-size: 14px;
  line-height: 1.35;
}

.reference-comparison-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.reference-comparison-card {
  display: grid;
  gap: 4px;
  min-width: 0;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(34, 67, 95, 0.12);
  background: #ffffff;
}

.reference-comparison-card span,
.standard-tool-card span {
  color: #637083;
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reference-comparison-card strong,
.standard-tool-card strong {
  color: #132838;
  font-size: 16px;
  line-height: 1.2;
}

.reference-comparison-card p,
.standard-tool-card p {
  margin: 0;
  color: #465363;
  font-size: 12px;
  line-height: 1.35;
}

.reference-comparison-card small {
  color: #6c7886;
  font-size: 11px;
  font-weight: 750;
  line-height: 1.35;
}

.standard-tool-card small {
  color: #64748b;
  font-size: 11px;
  font-weight: 750;
  line-height: 1.35;
}

.reference-comparison-card--warning {
  border-color: rgba(196, 91, 24, 0.35);
  background: #fff8f3;
}

.reference-comparison-card--attention {
  border-color: rgba(191, 146, 30, 0.32);
  background: #fffaf0;
}

.reference-comparison-card--success {
  border-color: rgba(37, 132, 96, 0.26);
  background: #f5fbf8;
}

.standard-tool-mini-list,
.standard-tool-list {
  display: grid;
  gap: 8px;
}

.standard-tool-mini-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.standard-tool-card {
  display: grid;
  gap: 10px;
  min-width: 0;
  min-height: 48px;
  padding: 8px 10px;
  border: 1px solid rgba(15, 118, 110, 0.18);
  border-radius: 8px;
  background: #ffffff;
  text-align: left;
}

.standard-tool-card > * {
  min-width: 0;
}

.standard-tool-card > div {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

button.standard-tool-card {
  cursor: pointer;
}

button.standard-tool-card:disabled {
  cursor: default;
  opacity: 0.68;
}

.standard-tool-card--active {
  border-color: rgba(37, 132, 96, 0.32);
  background: #f8fcfa;
}

.standard-tool-list .standard-tool-card {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 10px;
}

.standard-tool-card span {
  flex: 0 0 auto;
  max-width: 100px;
  border-radius: 999px;
  background: #eefcf6;
  color: #0f766e;
  padding: 4px 7px;
  font-size: 11px;
  line-height: 1;
}

.standard-tool-card strong {
  flex: 0 1 auto;
  min-width: 96px;
  max-width: 220px;
  overflow: hidden;
  color: #132838;
  font-size: 14px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.standard-tool-card p,
.standard-tool-card small {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.standard-tool-card p {
  flex: 0 1 auto;
  max-width: 260px;
  color: #465363;
  font-size: 12px;
  font-weight: 800;
}

.standard-tool-card small {
  flex: 1 1 auto;
  color: #64748b;
  font-size: 11px;
}

.standard-tool-card .button {
  min-height: 34px;
  padding: 7px 10px;
  white-space: nowrap;
}

@media (min-width: 1180px) {
  .encounter-workspace.workspace-drawer-open--patient {
    --workspace-drawer-width: 480px;
  }
  .encounter-workspace.workspace-drawer-open--checklist {
    --workspace-drawer-width: 520px;
  }
  .encounter-workspace.workspace-drawer-open--assistant {
    --workspace-drawer-width: 640px;
  }
  .encounter-workspace.workspace-drawer-open--safety {
    --workspace-drawer-width: 420px;
  }
  .encounter-workspace.workspace-drawer-open .workspace-main {
    padding-right: calc(var(--workspace-drawer-width) + 14px);
  }
  .encounter-workspace.workspace-drawer-open .workspace-bottom-bar {
    right: var(--workspace-drawer-width);
  }
  .encounter-workspace.workspace-drawer-open .patient-context-strip {
    grid-template-columns: minmax(170px, 0.75fr) minmax(250px, 1fr);
  }
  .encounter-workspace.workspace-drawer-open .context-safety {
    grid-column: auto;
  }
  .encounter-workspace.workspace-drawer-open .clinical-grid--focused {
    grid-template-columns: minmax(240px, 300px) minmax(560px, 1fr);
  }
  .encounter-workspace.workspace-drawer-open .clinical-grid--focused .auxiliary-panel {
    display: none;
  }
}
@media (max-width: 1280px) {
  .clinical-grid {
    grid-template-columns: 1fr;
  }
  .patient-context-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .context-safety {
    grid-column: 1/-1;
  }
  .clinical-grid--focused {
    height: auto;
    max-height: none;
    min-height: 0;
  }
  .clinical-grid--focused .clinical-panel--soap {
    order: 2;
  }
  .clinical-grid--focused .patient-summary-panel {
    order: 1;
  }
  .clinical-grid--focused .auxiliary-panel,
  .clinical-grid--focused .focus-queue-panel {
    order: 3;
  }
  .patient-panel {
    position: static;
  }
}
@media (max-width: 1024px) {
  .workspace-bottom-bar {
    left: 0;
  }
}
@media (max-width: 900px) {
  .encounter-workspace {
    --workspace-bottom-bar-height: 92px;
    --workspace-bottom-bar-gap: 20px;
    --workspace-bottom-bar-clearance: calc(var(--workspace-bottom-bar-height) + var(--workspace-bottom-bar-gap));
    --workspace-panel-bottom-space: 22px;
  }
  .encounter-workspace.workspace-overlay-open .workspace-bottom-bar {
    display: none;
  }
  .workspace-header {
    min-height: 40px;
    align-items: center;
    flex-direction: row;
    gap: 8px;
    padding-top: 5px;
    padding-bottom: 5px;
  }
  .title-row--compact {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    align-items: baseline;
  }
  .title-row--compact h1,
  .title-row--compact .identity-muted {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .title-row--compact h1 {
    flex: 0 1 auto;
    max-width: min(62vw, 220px);
    font-size: 17px;
  }
  .title-row--compact .identity-muted {
    flex: 0 1 auto;
    max-width: 54%;
    font-size: 11px;
    line-height: 1.3;
  }
  .workspace-status,
  .bottom-actions,
  .bottom-summary {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  .workspace-status {
    display: none;
  }
  .workspace-main {
    padding: 8px 10px calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
    scroll-padding-bottom: calc(var(--workspace-inner-scroll-clearance) + env(safe-area-inset-bottom));
  }
  .workspace-header,
  .workspace-bottom-bar {
    padding-right: 10px;
    padding-left: 10px;
  }
  .workspace-bottom-bar {
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    gap: 7px;
    padding-top: 7px;
    padding-bottom: calc(7px + env(safe-area-inset-bottom));
  }
  .mobile-panel-tabs {
    position: sticky;
    top: 0;
    z-index: 6;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px;
    margin-bottom: 6px;
    border: 1px solid #d9dee3;
    border-radius: 8px;
    background: rgba(241, 245, 249, 0.96);
    padding: 4px;
    backdrop-filter: blur(10px);
  }
  .mobile-panel-tabs button {
    position: relative;
    display: grid;
    min-height: 44px;
    height: auto;
    align-content: center;
    justify-items: center;
    gap: 1px;
    border-color: transparent;
    border-radius: 6px;
    padding: 4px 2px;
    background: transparent;
    line-height: 1.1;
  }
  .mobile-panel-tabs button.is-active {
    border-color: #99f6e4;
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  }
  .mobile-tab-label {
    max-width: 100%;
    overflow: hidden;
    color: inherit;
    font-size: 12px;
    font-weight: 900;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mobile-panel-tabs button small {
    max-width: 100%;
    overflow: hidden;
    color: #64748b;
    font-size: 10px;
    font-weight: 850;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mobile-panel-tabs button.is-active small {
    color: #0f766e;
  }
  .mobile-panel-tabs .tab-count {
    position: absolute;
    top: 3px;
    right: 3px;
    min-width: 16px;
    height: 16px;
    font-size: 9px;
  }
  .clinical-grid {
    display: block;
    width: 100%;
  }
  .patient-context-strip {
    display: none;
  }
  .mobile-context-brief {
    display: block;
    margin-bottom: 6px;
    border-radius: 6px;
  }
  .mobile-context-brief summary {
    min-height: 34px;
    gap: 6px;
    padding: 5px 8px;
  }
  .mobile-context-brief__body {
    gap: 5px;
    padding: 6px;
  }
  .mobile-context-item {
    min-height: 38px;
    padding: 6px 7px;
  }
  .mobile-context-brief__label,
  .mobile-context-item span {
    font-size: 10px;
  }
  .mobile-context-brief summary strong {
    font-size: 12px;
  }
  .mobile-context-brief summary em {
    padding: 3px 6px;
    font-size: 10px;
  }
  .mobile-context-item strong {
    font-size: 11px;
  }
  .safety-chip {
    min-width: 0;
    min-height: 34px;
    padding: 5px 7px;
  }
  .safety-chip span {
    font-size: 10px;
  }
  .safety-chip strong {
    display: -webkit-box;
    overflow: hidden;
    font-size: 11px;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }
  .context-actions {
    display: none;
  }
  .context-actions .button {
    min-width: 0;
    width: 100%;
  }
  .clinical-grid--focused > .clinical-panel {
    display: none;
  }
  .clinical-grid--focused > .clinical-panel.is-mobile-active {
    display: flex;
    min-height: 0;
    max-height: calc(100dvh - var(--workspace-bottom-bar-clearance) - 214px);
    flex-direction: column;
    /* 패널 내부 상태 변경 때 전체 화면 진입 효과가 반복되지 않도록 고정한다. */
    animation: none;
  }
  .clinical-grid--focused > .clinical-panel--soap.is-mobile-active {
    max-height: calc(100dvh - var(--workspace-bottom-bar-clearance) - 214px);
  }
  .patient-summary-panel__body,
  .conversation-panel__body {
    gap: 10px;
    padding: 10px;
  }
  .conversation-thread {
    min-height: 0;
  }
  .conversation-patient-strip {
    display: none;
  }
  .conversation-turn {
    width: min(94%, 360px);
  }
  .patient-summary-facts {
    gap: 7px;
  }
  .patient-summary-facts .patient-fact dd {
    -webkit-line-clamp: 2;
  }
  .support-queue-summary {
    grid-template-columns: 1fr;
    padding: 8px;
  }
  .soap-panel-header {
    display: none;
  }
  .soap-panel-header p:not(.panel-kicker) {
    display: none;
  }
  .soap-focus-status {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 5px;
    padding: 8px;
  }
  .workspace-compact-status {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    padding: 8px;
  }
  .compact-status-card {
    min-height: 50px;
    padding: 7px 8px;
  }
  .compact-status-card strong {
    font-size: 16px;
  }
  .compact-status-card em {
    max-width: 74px;
    padding: 3px 6px;
    font-size: 10px;
  }
  .soap-entry-prepare-strip {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding: 8px;
  }
  .soap-entry-status {
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .soap-entry-status .status-chip {
    grid-column: 2;
    grid-row: 1/span 2;
  }
  .soap-entry-status strong,
  .soap-entry-status p {
    grid-column: 1;
  }
  .soap-entry-metrics {
    display: none;
  }
  .soap-entry-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .soap-entry-actions .button {
    min-width: 0;
    width: 100%;
  }
  .soap-focus-status > div,
  .soap-focus-status > .status-chip {
    min-height: 40px;
    padding: 5px;
  }
  .soap-focus-status > span:not(.status-chip) {
    font-size: 10px;
  }
  .soap-focus-status strong {
    font-size: 11px;
  }
  .clinical-panel--soap .soap-focus-status {
    display: none;
  }
  .assistant-summary-strip {
    display: none;
  }
  .assistant-summary-strip .button {
    width: 100%;
  }
  .assistant-summary-metrics {
    grid-template-columns: 1fr;
  }
  .clinical-panel--soap .soap-editor {
    gap: 8px;
  }
  .clinical-panel--soap .soap-section-head {
    padding: 8px 10px;
  }
  .clinical-panel--soap .chart-quick-entry {
    padding: 10px;
  }
  .clinical-panel--soap .chart-quick-entry textarea {
    min-height: 132px;
  }
  .clinical-panel--soap .chart-assist-panel > summary {
    padding: 8px 10px;
  }
  .clinical-panel--soap .chart-assist-panel__body {
    padding: 8px 10px 10px;
  }
  .clinical-panel--soap .soap-clinical-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    padding: 0;
  }
  .clinical-panel--soap .soap-clinical-item {
    min-height: 46px;
    padding: 7px 8px;
  }
  .clinical-panel--soap .soap-clinical-item strong {
    font-size: 11px;
    -webkit-line-clamp: 1;
  }
  .clinical-panel--soap .objective-finding-panel {
    padding: 0;
  }
  .clinical-panel--soap .objective-priority-list {
    grid-template-columns: 1fr;
  }
  .clinical-panel--soap .objective-priority-card {
    min-height: 54px;
    padding: 7px 8px;
  }
  .clinical-panel--soap .assessment-review-panel {
    padding: 0;
  }
  .clinical-panel--soap .assessment-review-panel__head,
  .clinical-panel--soap .assessment-review-card header {
    align-items: stretch;
    flex-direction: column;
  }
  .clinical-panel--soap .assessment-evidence-grid {
    grid-template-columns: 1fr;
  }
  .clinical-panel--soap .assessment-review-actions {
    display: grid;
    grid-template-columns: 1fr;
  }
  .clinical-panel--soap .subjective-inquiry-row {
    grid-template-columns: minmax(0, 1fr);
  }
  .clinical-panel--soap .subjective-inquiry-row button {
    grid-template-columns: 40px minmax(62px, 0.34fr) minmax(0, 1fr);
  }
  .clinical-panel--soap .subjective-inquiry-row small {
    justify-self: start;
  }
  .clinical-panel--soap .objective-emr-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  .clinical-panel--soap .objective-emr-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .clinical-panel--soap .objective-assist-row {
    grid-template-columns: 56px minmax(0, 1fr);
  }
  .clinical-panel--soap .treatment-plan-panel {
    padding: 0;
  }
  .clinical-panel--soap .treatment-plan-panel__head {
    align-items: flex-start;
  }
  .clinical-panel--soap .treatment-safety-strip {
    grid-template-columns: 1fr;
  }
  .clinical-panel--soap .treatment-plan-grid {
    grid-template-columns: 1fr;
    gap: 7px;
  }
  .clinical-panel--soap .treatment-plan-card {
    padding: 9px;
  }
  .clinical-panel--soap .treatment-plan-actions {
    display: grid;
    grid-template-columns: 1fr;
  }
  .clinical-panel--soap .soap-section-head p,
  .clinical-panel--soap .soap-section-state,
  .clinical-panel--soap .soap-section-metrics,
  .clinical-panel--soap .soap-candidate-lane--summary,
  .clinical-panel--soap .state-panel--compact {
    display: none;
  }
  .clinical-panel--soap .soap-sentence-list {
    padding: 10px;
  }
  .clinical-panel--soap .soap-add-row {
    border-top: 0;
    padding: 10px;
  }
  .clinical-panel--soap .soap-add-row .button {
    width: 100%;
    justify-self: stretch;
  }
  .panel-header,
  .subsection-header,
  .candidate-actions,
  .checklist-summary-card__top,
  .checklist-soap-link-head,
  .checklist-card-footer,
  .soap-sentence-footer,
  .final-readiness-head,
  .ai-assistant-intro,
  .ai-assistant-intro-actions,
  .ai-result-head,
  .ai-pattern-title-row {
    align-items: stretch;
    flex-direction: column;
  }
  .conversation-panel__header,
  .drawer-header,
  .clinical-candidate-modal__header,
  .workspace-modal__header {
    align-items: center;
    flex-direction: row;
  }
  .panel-header {
    min-height: 46px;
    gap: 8px;
    padding: 8px 10px;
  }
  .panel-header h2 {
    margin-top: 0;
    font-size: 15px;
  }
  .conversation-panel__header .button {
    min-height: 40px;
    flex: 0 0 auto;
  }
  .conversation-actions .button {
    min-width: 72px;
    min-height: 44px;
  }
  .native-select {
    display: block;
    max-width: none;
  }
  .disease-tabs {
    display: none;
  }
  .checklist-start-row {
    align-items: stretch;
    flex-direction: column;
  }
  .checklist-start-row .button,
  .workflow-actions .button,
  .workflow-step .button,
  .bottom-actions .button {
    width: 100%;
  }
  .checklist-actions,
  .workflow-actions,
  .final-counts,
  .soap-hero-card,
  .soap-state-dashboard,
  .queue-metrics,
  .soap-section-metrics {
    grid-template-columns: 1fr;
  }
  .soap-hero-card {
    display: grid;
  }
  .soap-hero-metrics {
    min-width: 0;
    width: 100%;
  }
  .checklist-actions {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .workflow-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .final-counts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .soap-section-head,
  .soap-candidate-lane-head {
    align-items: stretch;
    flex-direction: column;
  }
  .soap-section-state {
    justify-items: start;
  }
  .ai-quick-grid {
    grid-template-columns: 1fr;
  }
  .ai-assistant-flow {
    grid-template-columns: 1fr;
  }
  .ai-request-actions,
  .ai-item-actions,
  .ai-result-actions,
  .ai-action-panel,
  .ai-action-group,
  .checklist-soap-actions,
  .soap-hero-bridge {
    justify-content: stretch;
  }
  .ai-request-actions .button,
  .ai-item-actions .button,
  .ai-result-actions .button,
  .ai-action-group .button,
  .checklist-soap-actions .button,
  .soap-hero-bridge .button {
    width: 100%;
  }
  .checklist-soap-link-head strong {
    text-align: left;
  }
  .ai-action-group > span {
    text-align: left;
  }
  .ai-result-badges {
    justify-content: flex-start;
  }
  .bottom-actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
    gap: 7px;
  }
  .bottom-actions .button {
    min-height: 44px;
    min-width: 0;
    width: 100%;
    padding-right: 5px;
    padding-left: 5px;
    font-size: 12px;
    line-height: 1.1;
    white-space: normal;
  }
  .bottom-summary {
    width: 100%;
    min-width: 0;
    flex: 0 1 auto;
    flex-wrap: nowrap;
    gap: 6px;
    font-size: 11px;
    line-height: 1.25;
  }
  .bottom-summary--desktop {
    display: none;
  }
  .bottom-summary--mobile {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .bottom-summary strong {
    min-width: 0;
    max-width: none;
    font-size: 12px;
  }
  .bottom-summary__meta {
    min-width: 0;
    overflow: hidden;
    color: #64748b;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sentence-actions {
    justify-content: flex-start;
  }
  .workspace-drawer {
    top: auto;
    bottom: 0;
    left: 0;
    width: 100%;
    max-height: min(86vh, 760px);
    border-top: 1px solid #d9dee3;
    border-left: 0;
    border-radius: 10px 10px 0 0;
    box-shadow: 0 -16px 36px rgba(15, 23, 42, 0.2);
    animation: hs-mobile-sheet-enter 180ms ease both;
  }
  .workspace-drawer--checklist {
    top: 0;
    max-height: none;
    height: 100dvh;
    border-radius: 0;
    animation: hs-mobile-screen-enter 180ms ease both;
  }
  .workspace-drawer--assistant {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    height: 100dvh;
    max-height: none;
    border-top: 0;
    border-radius: 0;
    animation: hs-mobile-screen-enter 180ms ease both;
  }
  .drawer-header {
    gap: 8px;
    padding: 9px 10px;
  }
  .drawer-header h2 {
    margin-top: 1px;
    font-size: 15px;
  }
  .drawer-header-actions {
    flex-wrap: nowrap;
    gap: 6px;
  }
  .drawer-header-actions .button {
    min-height: 44px;
  }
  .workspace-drawer [data-testid=soap-candidate-apply] {
    min-height: 44px;
  }
  .patient-identity--drawer {
    grid-template-columns: 1fr;
  }
  .workspace-modal {
    top: auto;
    bottom: 0;
    left: 0;
    width: 100%;
    max-height: min(88vh, 760px);
    transform: none;
    border-radius: 10px 10px 0 0;
    animation: hs-mobile-sheet-enter 180ms ease both;
  }
  .evidence-modal-meta,
  .clinical-candidate-evidence-grid,
  .clinical-candidate-judgement-grid,
  .clinical-candidate-primary-grid,
  .clinical-candidate-structure-grid,
  .final-modal-grid,
  .final-section-review,
  .patient-summary-hero,
  .patient-summary-modal-grid,
  .patient-modal-facts,
  .patient-modal-safety,
  .patient-reference-grid,
  .workspace-modal__footer--final {
    grid-template-columns: 1fr;
  }
  .clinical-candidate-modal__footer {
    display: grid;
    grid-template-columns: 1fr;
  }
  .patient-summary-hero__badges {
    justify-content: flex-start;
  }
  .final-modal-readiness {
    display: grid;
  }
  .final-modal-readiness__metrics {
    width: 100%;
  }
  .drawer-footer,
  .drawer-footer-actions {
    display: grid;
    grid-template-columns: 1fr;
  }
  .drawer-footer {
    gap: 8px;
    padding: 9px 10px calc(9px + env(safe-area-inset-bottom));
  }
  .candidate-queue-stack {
    max-height: 100%;
    padding: 10px;
  }
  .soap-section-tabs {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 5px;
    padding: 6px;
  }
  .soap-section-tab {
    min-height: 42px;
    padding: 5px 6px;
    text-align: center;
  }
  .soap-section-tab span {
    font-size: 12px;
  }
  .soap-section-tab strong {
    font-size: 10px;
  }
  .soap-queue-overview {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    padding: 8px;
  }
  .soap-queue-overview > div,
  .soap-queue-status-grid > div {
    padding: 7px;
  }
  .soap-queue-status-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .candidate-queue-group summary em {
    max-width: 92px;
  }
  .candidate-queue-row__head {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  }
  .candidate-queue-row__head em {
    white-space: normal;
  }
  .candidate-queue-row__actions {
    grid-template-columns: minmax(0, 1fr);
  }
  .candidate-queue-card,
  .candidate-queue-actions {
    grid-template-columns: minmax(0, 1fr);
  }
  .workspace-modal__footer .button,
  .drawer-footer-actions .button {
    width: 100%;
  }
}
/* Functional motion: communicate clinical workflow changes without decorative movement. */
@keyframes hs-entry-reveal {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hs-drawer-enter {
  from {
    opacity: 0;
    transform: translateX(18px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
@keyframes hs-content-reveal {
  from {
    opacity: 0.72;
    transform: translateX(5px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
@keyframes hs-candidate-arrive {
  0% {
    border-color: var(--rams-rule);
    box-shadow: inset 0 0 0 0 rgba(23, 100, 93, 0);
  }
  45% {
    border-color: var(--rams-accent);
    box-shadow: inset 0 0 0 2px rgba(23, 100, 93, 0.14);
  }
  100% {
    border-color: var(--rams-rule);
    box-shadow: inset 0 0 0 0 rgba(23, 100, 93, 0);
  }
}
@keyframes hs-chart-update {
  0% {
    background: #ffffff;
    box-shadow: inset 0 0 0 0 rgba(23, 100, 93, 0);
  }
  35% {
    background: #edf5f1;
    box-shadow: inset 4px 0 0 var(--rams-accent);
  }
  100% {
    background: #ffffff;
    box-shadow: inset 0 0 0 0 rgba(23, 100, 93, 0);
  }
}
@keyframes hs-feedback-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hs-skeleton-shimmer {
  from {
    background-position: 120% 0;
  }
  to {
    background-position: -120% 0;
  }
}
@keyframes hs-saving-spin {
  to {
    transform: rotate(360deg);
  }
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  clip-path: inset(50%);
}

.workspace-entry-skeleton {
  display: grid;
  gap: 10px;
  width: 100%;
  min-height: min(68vh, 620px);
}

.workspace-entry-skeleton__context,
.workspace-entry-skeleton__grid > div {
  border: 1px solid var(--rams-rule, #d0d1ca);
  border-radius: 4px;
  background: var(--rams-paper, #fbfbf8);
}

.workspace-entry-skeleton__context {
  display: grid;
  min-height: 72px;
  grid-template-columns: 1fr 1.45fr 0.8fr 0.8fr;
  gap: 1px;
  padding: 14px;
}

.workspace-entry-skeleton__grid {
  display: grid;
  min-height: 480px;
  grid-template-columns: minmax(260px, 0.72fr) minmax(480px, 1.48fr) minmax(210px, 0.64fr);
  gap: 10px;
}

.workspace-entry-skeleton__grid > div {
  display: grid;
  align-content: start;
  gap: 14px;
  padding: 18px 14px;
}

.workspace-entry-skeleton span,
.workspace-entry-skeleton strong {
  display: block;
  min-height: 14px;
  border-radius: 2px;
  background: linear-gradient(90deg, #e8e9e3 25%, #f7f7f3 45%, #e8e9e3 65%);
  background-size: 220% 100%;
  animation: hs-skeleton-shimmer 1200ms linear infinite;
}

.workspace-entry-skeleton strong {
  width: 42%;
  min-height: 20px;
}

.workspace-entry-skeleton__grid span:nth-of-type(1) {
  width: 86%;
  min-height: 120px;
}

.workspace-entry-skeleton__grid span:nth-of-type(2) {
  width: 72%;
}

.workspace-entry-skeleton__grid span:nth-of-type(3) {
  width: 54%;
}

.encounter-workspace.is-entry-revealed .patient-context-strip--task-focused,
.encounter-workspace.is-entry-revealed .workspace-panel {
  animation: hs-entry-reveal 220ms ease-out both;
}

.encounter-workspace.is-entry-revealed .clinical-grid > .workspace-panel:nth-child(1) {
  animation-delay: 35ms;
}

.encounter-workspace.is-entry-revealed .clinical-grid > .workspace-panel:nth-child(2) {
  animation-delay: 70ms;
}

.encounter-workspace.is-entry-revealed .clinical-grid > .workspace-panel:nth-child(3) {
  animation-delay: 105ms;
}

.workspace-drawer-backdrop {
  animation: hs-modal-fade 160ms ease-out both;
}

.workspace-drawer {
  animation: hs-drawer-enter 210ms ease-out both;
}

.clinical-panel--soap .soap-section {
  animation: hs-content-reveal 150ms ease-out both;
}

.clinical-panel--soap .soap-section.is-recently-updated .chart-quick-entry {
  animation: hs-chart-update 1500ms ease-out both;
}

.clinical-panel--queue.has-fresh-candidate {
  animation: hs-candidate-arrive 900ms ease-out 1;
}

.workspace-action-feedback {
  position: fixed;
  right: 18px;
  bottom: calc(var(--workspace-bottom-bar-height) + 18px + env(safe-area-inset-bottom));
  z-index: 46;
  display: inline-flex;
  max-width: min(420px, 100vw - 36px);
  min-height: 42px;
  align-items: center;
  gap: 9px;
  border: 1px solid var(--rams-rule-strong, #aeb1aa);
  border-radius: 4px;
  background: var(--rams-paper-strong, #ffffff);
  color: var(--rams-ink, #1b1b18);
  padding: 9px 12px;
  box-shadow: 0 12px 30px rgba(27, 27, 24, 0.14);
  pointer-events: none;
  animation: hs-feedback-enter 180ms ease-out both;
}

.workspace-action-feedback > span {
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--rams-accent-soft, #e8f0ed);
  color: var(--rams-accent, #17645d);
  font-size: 12px;
  font-weight: 800;
}

.workspace-action-feedback strong {
  font-size: 13px;
  font-weight: 680;
  line-height: 1.35;
}

.workspace-action-feedback--info > span {
  background: #eaf1f5;
  color: #315d73;
}

.conversation-input__hint {
  color: var(--rams-muted, #6d706a);
  font-size: 11px;
  font-weight: 560;
  line-height: 1.45;
}

.candidate-empty-card .button {
  margin-top: 4px;
  justify-self: start;
}

.workspace-bottom-bar {
  transition: border-color 160ms ease, background 160ms ease;
}

.workspace-bottom-bar--dirty {
  border-top-color: #c58b33;
}

.workspace-bottom-bar--ready {
  border-top-color: var(--rams-accent, #17645d);
}

.workspace-bottom-bar--saving .bottom-temp-save::before {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  border-radius: 50%;
  content: "";
  animation: hs-saving-spin 700ms linear infinite;
}

@media (max-width: 900px) {
  .workspace-entry-skeleton {
    min-height: 540px;
  }
  .workspace-entry-skeleton__context {
    min-height: 56px;
    grid-template-columns: 1fr 1.4fr;
  }
  .workspace-entry-skeleton__context span:nth-child(n+3) {
    display: none;
  }
  .workspace-entry-skeleton__grid {
    min-height: 420px;
    grid-template-columns: minmax(0, 1fr);
  }
  .workspace-entry-skeleton__grid > div:not(:nth-child(2)) {
    display: none;
  }
  .workspace-action-feedback {
    right: 10px;
    bottom: calc(var(--workspace-bottom-bar-height) + 10px + env(safe-area-inset-bottom));
    left: 10px;
    max-width: none;
  }
  .workspace-drawer {
    animation-name: hs-mobile-sheet-enter;
    animation-duration: 180ms;
  }
}
@media (prefers-reduced-motion: reduce) {
  .encounter-workspace *,
  .encounter-workspace *::before,
  .encounter-workspace *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 1ms !important;
  }
  .workspace-panel:hover,
  .button:not(:disabled):hover,
  .conversation-turn:hover,
  .patient-fact:hover,
  .soap-section:hover,
  .ai-result-card:hover {
    transform: none;
  }
}
/* Clinic task-focused usability refinement — 2026-07-10 */
.encounter-workspace {
  --workspace-focused-grid-offset: 132px;
  --clinic-bg: #f2f6f4;
  --clinic-surface: #ffffff;
  --clinic-surface-soft: #f7faf8;
  --clinic-surface-muted: #eef3f1;
  --clinic-ink: #10211f;
  --clinic-body: #334155;
  --clinic-muted: #64748b;
  --clinic-border: #dbe4e1;
  --clinic-border-strong: #c7d3cf;
  --clinic-primary: #0f766e;
  --clinic-primary-soft: #e8f7f4;
  background: var(--clinic-bg);
  color: var(--clinic-ink);
}

.workspace-header {
  min-height: 60px;
  border-bottom-color: var(--clinic-border);
  background: rgba(255, 255, 255, 0.96);
  padding: 9px 16px;
  backdrop-filter: blur(12px);
}

.workspace-header-actions {
  display: flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
}

.workspace-header-action {
  min-width: 0;
  min-height: 38px;
  border-radius: 8px;
  padding-right: 12px;
  padding-left: 12px;
}

.title-row h1 {
  color: var(--clinic-ink);
  font-weight: 800;
}

.identity-muted {
  color: var(--clinic-muted);
  font-weight: 650;
}

.workspace-main {
  padding: 14px 16px calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
}

.workspace-panel {
  border-color: var(--clinic-border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.035), 0 1px 2px rgba(15, 23, 42, 0.025);
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.workspace-panel:hover {
  border-color: var(--clinic-border-strong);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.045);
  transform: none;
}

.panel-header {
  min-height: 54px;
  align-items: center;
  border-bottom-color: var(--clinic-border);
  background: #fbfdfc;
  padding: 10px 12px;
}

.panel-header h2 {
  margin-top: 0;
  color: var(--clinic-ink);
  font-weight: 750;
}

.soap-section-tabs {
  border-color: var(--clinic-border);
  background: var(--clinic-surface-soft);
}

.soap-section-tab {
  border-color: var(--clinic-border);
  border-radius: 8px;
  background: #ffffff;
}

.soap-section-tab span {
  color: var(--clinic-ink);
  font-weight: 750;
}

.soap-section-tab strong {
  font-weight: 650;
}

.soap-section-tab.is-active {
  border-color: var(--clinic-primary);
  background: var(--clinic-primary-soft);
}

.workspace-bottom-bar {
  border-top-color: var(--clinic-border);
  background: rgba(255, 255, 255, 0.96);
  padding-top: 9px;
  padding-bottom: calc(9px + env(safe-area-inset-bottom));
  box-shadow: 0 -8px 26px rgba(15, 23, 42, 0.055);
}

.bottom-summary {
  color: var(--clinic-muted);
  font-weight: 650;
}

.bottom-summary strong {
  color: var(--clinic-ink);
  font-weight: 750;
}

.bottom-actions .button {
  min-height: 38px;
  border-radius: 8px;
}

@media (min-width: 1024px) {
  .workspace-bottom-bar {
    left: 80px;
  }
}
@media (max-width: 1023px) {
  .workspace-bottom-bar {
    left: 0;
  }
}
@media (min-width: 901px) {
  .patient-context-strip--task-focused {
    overflow: hidden;
    align-items: stretch;
    gap: 0;
    border: 1px solid var(--clinic-border);
    border-radius: 10px;
    background: #ffffff;
    grid-template-columns: minmax(0, 1.55fr) minmax(380px, 1fr);
    box-shadow: 0 5px 18px rgba(15, 23, 42, 0.025);
  }
  .patient-context-strip--task-focused > .context-card,
  .patient-context-strip--task-focused > .context-safety {
    min-height: 68px;
    grid-column: auto;
    border: 0;
    border-right: 1px solid #e5ece9;
    border-radius: 0;
    background: #ffffff;
    padding: 10px 13px;
  }
  .patient-context-strip--task-focused .context-card--summary {
    display: grid;
    grid-template-columns: minmax(150px, 0.75fr) minmax(230px, 1.25fr) auto;
    align-items: stretch;
    gap: 0;
    padding: 0;
  }
  .context-card__summary-item {
    display: grid;
    min-width: 0;
    align-content: center;
    gap: 4px;
    padding: 10px 13px;
  }
  .context-card__summary-item + .context-card__summary-item {
    border-left: 1px solid #e5ece9;
  }
  .context-card__summary-action {
    display: inline-flex;
    min-width: 94px;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border-left: 1px solid #e5ece9;
    color: var(--clinic-primary);
    padding: 0 12px;
    font-size: 11px;
    font-weight: 750;
    white-space: nowrap;
  }
  .context-card__summary-action b {
    font-size: 17px;
    font-weight: 500;
    line-height: 1;
  }
  .patient-context-strip--task-focused .context-card:hover {
    border-color: transparent;
    background: var(--clinic-surface-soft);
    box-shadow: inset 0 -2px 0 rgba(15, 118, 110, 0.35);
  }
  .patient-context-strip--task-focused .context-card span,
  .patient-context-strip--task-focused .safety-chip span {
    color: var(--clinic-muted);
    font-weight: 700;
  }
  .patient-context-strip--task-focused .context-card strong {
    display: -webkit-box;
    overflow: hidden;
    color: var(--clinic-ink);
    font-size: 13px;
    line-height: 1.4;
    font-weight: 750;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  .patient-context-strip--task-focused .context-card .context-card__summary-action {
    color: var(--clinic-primary);
    font-weight: 750;
  }
  .patient-context-strip--task-focused .context-safety {
    gap: 6px;
    overflow: hidden;
  }
  .patient-context-strip--task-focused .safety-chip {
    min-height: 44px;
    border-color: #e2e8e5;
    background: var(--clinic-surface-soft);
  }
  .patient-context-strip--task-focused .safety-chip strong {
    color: var(--clinic-body);
    font-weight: 700;
  }
  .patient-context-strip--task-focused .safety-chip--warning {
    border-color: #f1d99b;
    background: #fffdf5;
  }
  .patient-context-strip--task-focused .safety-chip--danger {
    border-color: #efc3c3;
    background: #fffafa;
  }
  .clinical-grid--focused.clinical-grid--task-focused {
    height: clamp(560px, 100dvh - var(--workspace-bottom-bar-clearance) - var(--workspace-focused-grid-offset), 820px);
    max-height: none;
    gap: 12px;
    grid-template-columns: minmax(300px, 0.72fr) minmax(0, 1.28fr);
    grid-template-rows: minmax(360px, 1fr) minmax(188px, 0.45fr);
  }
  .clinical-grid--focused.clinical-grid--task-focused.clinical-grid--assistant-empty {
    grid-template-rows: minmax(420px, 1fr) 116px;
  }
  .clinical-grid--task-focused > .clinical-panel--patient {
    grid-column: 1;
    grid-row: 1/3;
  }
  .clinical-grid--task-focused > .clinical-panel--soap {
    grid-column: 2;
    grid-row: 1;
  }
  .clinical-grid--task-focused > .auxiliary-panel {
    display: flex;
    min-height: 0;
    max-height: none;
    grid-column: 2;
    grid-row: 2;
    border-color: #c9ded9;
    box-shadow: 0 8px 24px rgba(15, 118, 110, 0.055);
  }
  .clinical-grid--task-focused > .auxiliary-panel .panel-header {
    background: #f1f8f6;
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .panel-header {
    min-height: 46px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .panel-header p {
    display: none;
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .candidate-queue-stack {
    align-content: center;
    padding: 8px 10px 10px;
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .candidate-empty-card {
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    justify-items: start;
    gap: 5px 10px;
    border: 0;
    background: transparent;
    padding: 0;
    text-align: left;
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .candidate-empty-card span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .clinical-grid--task-focused .conversation-patient-strip {
    display: none;
  }
}
@media (min-width: 1201px) {
  .clinical-grid--focused.clinical-grid--task-focused {
    grid-template-columns: minmax(320px, 0.7fr) minmax(0, 1.3fr) minmax(270px, 300px);
    grid-template-rows: minmax(0, 1fr);
  }
  .clinical-grid--task-focused > .clinical-panel--patient,
  .clinical-grid--task-focused > .clinical-panel--soap,
  .clinical-grid--task-focused > .auxiliary-panel {
    height: auto;
    grid-row: 1;
  }
  .clinical-grid--task-focused > .clinical-panel--patient {
    grid-column: 1;
  }
  .clinical-grid--task-focused > .clinical-panel--soap {
    grid-column: 2;
  }
  .clinical-grid--task-focused > .auxiliary-panel {
    max-height: 100%;
    grid-column: 3;
  }
  .clinical-grid--focused.clinical-grid--task-focused.clinical-grid--assistant-empty {
    grid-template-columns: minmax(320px, 0.7fr) minmax(0, 1.3fr) minmax(205px, 224px);
    grid-template-rows: minmax(0, 1fr);
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .panel-header p {
    display: block;
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .candidate-empty-card {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
  .clinical-grid--task-focused > .auxiliary-panel--empty .candidate-empty-card span {
    display: none;
  }
}
@media (min-width: 901px) and (max-width: 1100px) {
  .workspace-header-action {
    padding-right: 10px;
    padding-left: 10px;
  }
  .patient-context-strip--task-focused {
    grid-template-columns: minmax(160px, 1fr) minmax(180px, 1.1fr) minmax(220px, 1fr) minmax(175px, 0.8fr);
  }
}
@media (max-width: 900px) {
  .encounter-workspace {
    --workspace-focused-grid-offset: 0px;
    background: var(--clinic-bg);
  }
  .workspace-header {
    min-height: 42px;
    padding: 6px 10px;
  }
  .workspace-header-actions {
    display: none;
  }
  .workspace-patient-number {
    display: none;
  }
  .workspace-main {
    padding: 8px 10px calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
  }
  .mobile-context-brief,
  .mobile-panel-tabs,
  .workspace-panel {
    border-color: var(--clinic-border);
  }
  .mobile-panel-tabs {
    background: rgba(238, 243, 241, 0.96);
  }
  .mobile-panel-tabs button.is-active {
    border-color: #b9ddd6;
    background: #ffffff;
    color: var(--clinic-primary);
  }
  .mobile-context-brief summary,
  .mobile-context-item,
  .mobile-context-safety .safety-chip,
  .conversation-panel__header .button {
    min-height: 44px;
  }
  .mobile-safety-alert {
    display: grid;
    width: 100%;
    min-height: 44px;
    cursor: pointer;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    margin: 0 0 6px;
    border: 1px solid #efb8b8;
    border-radius: 8px;
    background: #fff8f8;
    color: #7f1d1d;
    padding: 8px 10px;
    font: inherit;
    text-align: left;
  }
  .mobile-safety-alert span,
  .mobile-safety-alert strong,
  .mobile-safety-alert em {
    min-width: 0;
    line-height: 1.25;
  }
  .mobile-safety-alert span {
    font-size: 12px;
    font-weight: 800;
  }
  .mobile-safety-alert strong {
    overflow: hidden;
    font-size: 13px;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mobile-safety-alert em {
    border-radius: 999px;
    background: #fee2e2;
    padding: 4px 7px;
    font-size: 12px;
    font-style: normal;
    font-weight: 800;
  }
  .title-row--compact .identity-muted,
  .mobile-context-brief__label,
  .mobile-context-brief summary em {
    font-size: 12px;
  }
  .workspace-panel {
    border-radius: 10px;
  }
  .bottom-actions .button {
    min-height: 44px;
  }
  .bottom-save-state {
    min-height: 44px;
    padding-right: 2px;
    padding-left: 2px;
  }
}
/* Professional chart authoring: keep the clinical note primary while showing detailed inputs by default. */
.clinical-panel--soap .soap-editor {
  align-content: start;
  gap: 12px;
  padding: 0;
}

.clinical-panel--soap .soap-section-tabs {
  min-height: 50px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  border-top: 0;
  border-bottom: 1px solid var(--clinic-border);
  background: #ffffff;
  padding: 0 12px;
}

.clinical-panel--soap .soap-section-tab {
  min-height: 50px;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 4px 10px;
  box-shadow: none;
}

.clinical-panel--soap .soap-section-tab:hover {
  border-color: transparent;
  background: var(--clinic-surface-soft);
}

.clinical-panel--soap .soap-section-tab.is-active {
  border-color: transparent;
  background: #f7fbf9;
  box-shadow: inset 0 -2px 0 var(--clinic-primary);
}

.clinical-panel--soap .soap-section-tab span {
  font-size: 13px;
  font-weight: 750;
}

.clinical-panel--soap .soap-section-tab strong {
  font-size: 12px;
  font-weight: 650;
}

.clinical-panel--soap .soap-section {
  border: 0;
  border-radius: 0;
  background: #ffffff;
}

.clinical-panel--soap .chart-quick-entry {
  border-bottom: 0;
  padding: 18px 20px 16px;
}

.clinical-panel--soap .chart-quick-entry__label {
  gap: 0;
}

.clinical-panel--soap .chart-quick-entry textarea {
  min-height: clamp(104px, 14vh, 132px);
  border-color: #c9d5d1;
  border-radius: 8px;
  padding: 15px 16px;
  font-size: 14px;
  font-weight: 520;
  line-height: 1.7;
  transition: min-height 180ms ease, border-color 150ms ease, box-shadow 150ms ease;
}

.clinical-panel--soap .chart-quick-entry[data-soap-section=S] textarea {
  min-height: clamp(116px, 16vh, 144px);
}

.clinical-panel--soap .chart-quick-entry textarea:focus {
  min-height: clamp(148px, 21vh, 188px);
}

.clinical-panel--soap .chart-quick-entry[data-soap-section=S] textarea:focus {
  min-height: clamp(164px, 23vh, 204px);
}

.clinical-panel--soap .chart-assist-panel {
  overflow: hidden;
  margin: 0 20px 18px;
  border: 1px solid #e1e8e5;
  border-radius: 8px;
  background: #f8faf9;
}

.clinical-panel--soap .chart-assist-panel > summary {
  min-height: 46px;
  padding: 10px 13px;
}

.clinical-panel--soap .chart-assist-panel > summary span {
  color: var(--clinic-ink);
  font-size: 13px;
  font-weight: 700;
}

.clinical-panel--soap .chart-assist-panel > summary strong {
  font-size: 11px;
  font-weight: 600;
}

.clinical-panel--soap .chart-assist-panel__body {
  gap: 12px;
  border-top: 1px solid #e5ebe8;
  padding: 14px;
}

.clinical-panel--soap .objective-priority-panel {
  gap: 8px;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0 0 4px;
}

.clinical-panel--soap .objective-priority-panel > header {
  min-height: 24px;
}

.clinical-panel--soap .objective-priority-panel > header > span {
  color: #7c2d12;
  font-size: 12px;
  font-weight: 700;
}

.clinical-panel--soap .objective-priority-list {
  grid-template-columns: 1fr;
  gap: 6px;
}

.clinical-panel--soap .objective-priority-card {
  min-height: 0;
  grid-template-columns: minmax(120px, 0.35fr) minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
}

.clinical-panel--soap .objective-priority-card > strong {
  font-size: 12px;
  font-weight: 700;
}

.clinical-panel--soap .objective-priority-card > em {
  display: block;
  font-size: 11px;
  font-weight: 550;
  line-height: 1.4;
  -webkit-line-clamp: unset;
}

.clinical-panel--soap .objective-emr-panel {
  gap: 0;
}

.clinical-panel--soap .objective-emr-group {
  overflow: visible;
  border: 0;
  border-top: 1px solid #e4ebe8;
  border-radius: 0;
  background: transparent;
}

.clinical-panel--soap .objective-emr-group > summary {
  min-height: 44px;
  background: transparent;
  padding: 11px 0;
  font-size: 13px;
  font-weight: 700;
}

.clinical-panel--soap .objective-emr-group > summary strong {
  font-size: 11px;
  font-weight: 600;
}

.clinical-panel--soap .objective-emr-row {
  grid-template-columns: 104px minmax(0, 1fr);
  align-items: start;
  gap: 10px 16px;
  padding: 12px 0;
}

.clinical-panel--soap .objective-emr-label {
  min-height: 38px;
  align-content: center;
}

.clinical-panel--soap .objective-emr-label strong {
  font-size: 13px;
  font-weight: 700;
}

.clinical-panel--soap .objective-emr-fields {
  grid-template-columns: repeat(4, minmax(82px, 1fr));
  gap: 8px;
}

.clinical-panel--soap .objective-emr-field span {
  font-size: 11px;
  font-weight: 650;
}

.clinical-panel--soap .objective-emr-field select {
  min-height: 38px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.clinical-panel--soap .objective-emr-note-details {
  min-width: 0;
  grid-column: 2;
}

.clinical-panel--soap .objective-emr-note-details > summary {
  width: max-content;
  cursor: pointer;
  color: var(--clinic-muted);
  font-size: 11px;
  font-weight: 650;
  list-style: none;
}

.clinical-panel--soap .objective-emr-note-details > summary::-webkit-details-marker {
  display: none;
}

.clinical-panel--soap .objective-emr-note-details > summary::before {
  margin-right: 4px;
  content: "+";
}

.clinical-panel--soap .objective-emr-note-details[open] > summary::before {
  content: "−";
}

.clinical-panel--soap .objective-emr-note-details textarea {
  width: 100%;
  min-height: 68px;
  margin-top: 8px;
  resize: vertical;
  border: 1px solid #d5dfdb;
  border-radius: 6px;
  background: #ffffff;
  color: var(--clinic-ink);
  padding: 9px 10px;
  font-size: 12px;
  line-height: 1.5;
}

.clinical-panel--soap .treatment-precheck-panel__head > span,
.clinical-panel--soap .assessment-review-panel__head > div > span {
  font-weight: 700;
}

.workspace-bottom-bar .bottom-summary {
  gap: 0;
}

.workspace-bottom-bar .bottom-summary strong {
  font-size: 13px;
}

@media (max-width: 900px) {
  .clinical-panel--soap .soap-section-tabs {
    min-height: 46px;
    padding: 0 4px;
  }
  .clinical-panel--soap .soap-section-tab {
    min-height: 46px;
    padding: 4px;
    text-align: center;
  }
  .clinical-panel--soap .soap-section-tab span {
    font-size: 12px;
  }
  .clinical-panel--soap .chart-quick-entry {
    padding: 14px 12px;
  }
  .clinical-panel--soap .chart-quick-entry textarea {
    min-height: 112px;
    padding: 13px 14px;
    font-size: 16px;
    line-height: 1.65;
  }
  .clinical-panel--soap .chart-quick-entry[data-soap-section=S] textarea {
    min-height: 124px;
  }
  .clinical-panel--soap .chart-quick-entry textarea:focus {
    min-height: 148px;
  }
  .clinical-panel--soap .chart-quick-entry[data-soap-section=S] textarea:focus {
    min-height: 160px;
  }
  .clinical-panel--soap .chart-assist-panel {
    margin: 0 12px 14px;
  }
  .clinical-panel--soap .chart-assist-panel__body {
    padding: 12px;
  }
  .clinical-panel--soap .objective-priority-card {
    grid-template-columns: 1fr;
    gap: 3px;
  }
  .clinical-panel--soap .objective-emr-row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px 0;
  }
  .clinical-panel--soap .objective-emr-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .clinical-panel--soap .objective-emr-field select {
    min-height: 44px;
    font-size: 16px;
  }
  .clinical-panel--soap .objective-emr-note-details {
    grid-column: 1;
  }
}
/* Quiet functionalism: a restrained, legible clinical instrument rather than a card gallery. */
.encounter-workspace {
  --rams-canvas: #efefeb;
  --rams-paper: #fbfbf8;
  --rams-paper-strong: #ffffff;
  --rams-ink: #1b1b18;
  --rams-body: #3f413d;
  --rams-muted: #6d706a;
  --rams-rule: #d0d1ca;
  --rams-rule-strong: #aeb1aa;
  --rams-accent: #17645d;
  --rams-accent-soft: #e8f0ed;
  --clinic-bg: var(--rams-canvas);
  --clinic-surface: var(--rams-paper-strong);
  --clinic-surface-soft: var(--rams-paper);
  --clinic-surface-muted: #e7e8e2;
  --clinic-ink: var(--rams-ink);
  --clinic-body: var(--rams-body);
  --clinic-muted: var(--rams-muted);
  --clinic-border: var(--rams-rule);
  --clinic-border-strong: var(--rams-rule-strong);
  --clinic-primary: var(--rams-accent);
  --clinic-primary-dark: #104c46;
  --clinic-primary-soft: var(--rams-accent-soft);
  --hs-btn-primary-bg: var(--rams-accent);
  --hs-btn-primary-bg-hover: #104f49;
  --hs-btn-secondary-border: var(--rams-rule-strong);
  --hs-btn-secondary-bg: var(--rams-paper-strong);
  --hs-btn-secondary-bg-hover: #f1f2ed;
  --hs-btn-secondary-text: var(--rams-ink);
  --hs-btn-ghost-text: var(--rams-body);
  --hs-btn-ghost-bg-hover: #e4e5df;
  background: var(--rams-canvas);
  color: var(--rams-ink);
  font-family: "Pretendard", "Noto Sans KR", "Apple SD Gothic Neo", system-ui, sans-serif;
  font-variant-numeric: tabular-nums;
}

.encounter-workspace .workspace-panel,
.encounter-workspace .workspace-bottom-bar {
  animation: none;
}

.encounter-workspace .button {
  border-radius: 3px;
  box-shadow: none;
  font-weight: 680;
  letter-spacing: -0.01em;
}

.encounter-workspace .button:not(:disabled):hover,
.encounter-workspace .button:not(:disabled):active {
  box-shadow: none;
  transform: none;
}

.encounter-workspace button:focus-visible,
.encounter-workspace textarea:focus-visible,
.encounter-workspace select:focus-visible,
.encounter-workspace [tabindex]:focus-visible {
  outline: 2px solid var(--rams-ink);
  outline-offset: 2px;
}

.workspace-header {
  min-height: 62px;
  border-bottom: 1px solid var(--rams-rule-strong);
  background: var(--rams-paper);
  padding: 0 18px;
  backdrop-filter: none;
}

.title-row h1 {
  color: var(--rams-ink);
  font-size: 20px;
  font-weight: 720;
  letter-spacing: -0.035em;
}

.workspace-patient-number {
  border-color: var(--rams-rule);
  border-radius: 2px;
  background: transparent;
  color: var(--rams-muted);
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.035em;
}

.identity-muted {
  color: var(--rams-muted);
  font-weight: 580;
}

.workspace-header-action {
  border-radius: 3px;
}

.workspace-main {
  padding: 12px 14px calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
}

.patient-context-strip--task-focused {
  box-shadow: none;
}

.workspace-panel {
  border-color: var(--rams-rule);
  border-radius: 4px;
  background: var(--rams-paper-strong);
  box-shadow: none;
  transition: border-color 120ms ease;
}

.workspace-panel:hover {
  border-color: var(--rams-rule-strong);
  box-shadow: none;
  transform: none;
}

.clinical-panel--soap {
  border-color: var(--rams-rule-strong);
  box-shadow: inset 0 2px 0 var(--rams-accent);
}

.panel-header {
  display: grid;
  min-height: 56px;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  border-bottom-color: var(--rams-rule);
  background: var(--rams-paper);
  padding: 0 12px;
}

.panel-header > div {
  min-width: 0;
}

.panel-header h2 {
  margin-top: 0;
  color: var(--rams-ink);
  font-size: 15px;
  font-weight: 720;
  letter-spacing: -0.025em;
}

.panel-header p:not(.panel-kicker) {
  margin-top: 2px;
  color: var(--rams-muted);
  font-size: 11px;
  font-weight: 560;
}

.conversation-panel__body {
  gap: 9px;
  background: var(--rams-paper-strong);
  padding: 12px;
}

.conversation-input--docked {
  border-top-color: var(--rams-rule);
  background: var(--rams-paper-strong);
}

.conversation-thread {
  gap: 8px;
  border-color: var(--rams-rule);
  border-radius: 3px;
  background: #f5f5f1;
}

.conversation-empty,
.conversation-loading {
  border-color: var(--rams-rule-strong);
  border-radius: 2px;
  background: var(--rams-paper);
  color: var(--rams-muted);
}

.conversation-turn {
  border-color: var(--rams-rule);
  border-radius: 3px;
  box-shadow: none;
  animation: none;
}

.conversation-turn:hover {
  box-shadow: none;
  transform: none;
}

.conversation-turn--user {
  border-color: #b7cac4;
  background: #edf3f0;
}

.conversation-turn--user.conversation-turn--user-entering {
  transform-origin: right bottom;
  animation: hs-user-turn-enter 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: opacity, transform, box-shadow;
}

.conversation-turn--assistant {
  border-color: var(--rams-rule);
  background: var(--rams-paper-strong);
}

.conversation-input textarea,
.clinical-panel--soap .chart-quick-entry textarea,
.clinical-panel--soap .objective-emr-note-details textarea {
  border-color: var(--rams-rule-strong);
  border-radius: 3px;
  background: var(--rams-paper-strong);
  color: var(--rams-ink);
  box-shadow: none;
}

.conversation-input textarea:focus,
.clinical-panel--soap .chart-quick-entry textarea:focus,
.clinical-panel--soap .objective-emr-note-details textarea:focus {
  border-color: var(--rams-accent);
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(23, 100, 93, 0.1);
}

.clinical-panel--soap .soap-section-tabs {
  border-bottom-color: var(--rams-rule);
  background: var(--rams-paper);
}

.clinical-panel--soap .soap-section-tab {
  position: relative;
  color: var(--rams-muted);
}

.clinical-panel--soap .soap-section-tab + .soap-section-tab {
  border-left: 1px solid #e3e3dd;
}

.clinical-panel--soap .soap-section-tab:hover {
  background: #f0f1ec;
}

.clinical-panel--soap .soap-section-tab.is-active {
  background: var(--rams-paper-strong);
  box-shadow: inset 0 -3px 0 var(--rams-accent);
}

.clinical-panel--soap .soap-section-tab.is-active span,
.clinical-panel--soap .soap-section-tab.is-active strong {
  color: var(--rams-ink);
}

.clinical-panel--soap .chart-quick-entry {
  background: var(--rams-paper-strong);
}

.clinical-panel--soap .chart-assist-panel {
  border-color: var(--rams-rule);
  border-radius: 3px;
  background: #f4f4f0;
}

.clinical-panel--soap .chart-assist-panel > summary,
.clinical-panel--soap .chart-assist-panel[open] > summary,
.clinical-panel--soap .chart-assist-panel__body {
  border-color: var(--rams-rule);
}

.candidate-queue-stack {
  background: var(--rams-paper);
}

.candidate-queue-row {
  border-color: var(--rams-rule);
  border-radius: 3px;
  background: var(--rams-paper-strong);
  box-shadow: none;
}

.candidate-queue-row:hover {
  border-color: var(--rams-rule-strong);
  box-shadow: none;
  transform: none;
}

.candidate-queue-row__clinical > div {
  border-left-width: 2px;
  border-left-color: var(--rams-accent);
  background: #eef3f0;
}

.candidate-queue-row__head--clinical span,
.candidate-queue-row__clinical span {
  color: var(--rams-accent);
}

.candidate-empty-card {
  border-color: var(--rams-rule);
  border-radius: 3px;
  background: transparent;
}

.clinical-grid--task-focused > .auxiliary-panel {
  border-color: var(--rams-rule);
  box-shadow: none;
}

.clinical-grid--task-focused > .auxiliary-panel .panel-header {
  background: var(--rams-paper);
}

.clinical-grid--task-focused > .auxiliary-panel--empty .panel-header p {
  display: none;
}

.workspace-bottom-bar {
  border-top: 1px solid var(--rams-rule-strong);
  background: var(--rams-paper);
  box-shadow: none;
  backdrop-filter: none;
}

.bottom-summary {
  color: var(--rams-muted);
  font-weight: 580;
}

.bottom-summary strong {
  color: var(--rams-ink);
  font-weight: 680;
}

.bottom-save-state::before {
  border-radius: 2px;
  background: var(--rams-accent-soft);
  color: var(--rams-accent);
}

.workspace-drawer,
.workspace-modal {
  border-color: var(--rams-rule-strong);
  border-radius: 4px;
  background: var(--rams-paper-strong);
  box-shadow: 0 18px 48px rgba(27, 27, 24, 0.16);
}

.drawer-header,
.workspace-modal__header,
.workspace-modal__footer {
  border-color: var(--rams-rule);
  background: var(--rams-paper);
}

@media (min-width: 901px) {
  .patient-context-strip--task-focused {
    border-color: var(--rams-rule);
    border-radius: 4px;
    background: var(--rams-paper);
  }
  .patient-context-strip--task-focused > .context-card,
  .patient-context-strip--task-focused > .context-safety {
    border-right-color: var(--rams-rule);
    background: var(--rams-paper);
  }
  .patient-context-strip--task-focused .context-card:hover {
    background: #f0f1ec;
    box-shadow: none;
  }
  .patient-context-strip--task-focused .safety-chip {
    border-radius: 3px;
  }
  .clinical-grid--focused.clinical-grid--task-focused {
    gap: 10px;
  }
}
@media (min-width: 1201px) {
  .clinical-grid--focused.clinical-grid--task-focused {
    grid-template-columns: minmax(280px, 0.72fr) minmax(520px, 1.48fr) minmax(238px, 0.64fr);
  }
  .clinical-grid--focused.clinical-grid--task-focused.clinical-grid--assistant-empty {
    grid-template-columns: minmax(280px, 0.72fr) minmax(560px, 1.62fr) minmax(184px, 0.46fr);
  }
}
@media (max-width: 900px) {
  .encounter-workspace {
    --workspace-bottom-bar-height: 92px;
    --workspace-bottom-bar-gap: 18px;
  }
  .workspace-header {
    min-height: 42px;
    border-bottom-color: var(--rams-rule-strong);
    padding: 5px 10px;
  }
  .title-row--compact h1 {
    font-size: 17px;
    font-weight: 700;
  }
  .workspace-main {
    padding: 8px 10px calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
  }
  .mobile-context-brief {
    border-color: var(--rams-rule);
    border-radius: 3px;
    background: var(--rams-paper);
  }
  .mobile-context-brief summary,
  .mobile-context-brief[open] summary {
    border-color: var(--rams-rule);
    background: var(--rams-paper);
  }
  .mobile-safety-alert {
    border-radius: 3px;
  }
  .mobile-panel-tabs {
    gap: 0;
    border-color: var(--rams-rule-strong);
    border-radius: 3px;
    background: var(--rams-paper);
    padding: 3px;
    backdrop-filter: none;
  }
  .mobile-panel-tabs button {
    border: 0;
    border-radius: 1px;
  }
  .mobile-panel-tabs button + button {
    border-left: 1px solid var(--rams-rule);
  }
  .mobile-panel-tabs button.is-active {
    border-color: transparent;
    background: var(--rams-paper-strong);
    box-shadow: inset 0 -3px 0 var(--rams-accent);
  }
  .mobile-panel-tabs button.is-active small {
    color: var(--rams-accent);
  }
  .workspace-panel {
    border-radius: 3px;
  }
  .panel-header {
    min-height: 48px;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    padding: 0 10px;
  }
  .clinical-panel--soap .soap-section-tabs {
    background: var(--rams-paper);
  }
  .clinical-panel--soap .chart-quick-entry {
    padding: 14px 12px;
  }
  .clinical-panel--soap .chart-assist-panel {
    border-radius: 3px;
  }
  .workspace-bottom-bar {
    border-top-color: var(--rams-rule-strong);
    background: var(--rams-paper);
  }
  .workspace-drawer,
  .workspace-modal {
    border-radius: 0;
    box-shadow: none;
  }
}
.safety-detail-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--rams-canvas, #f4f4f0);
  padding: 14px;
}

.safety-detail-summary,
.safety-detail-section {
  border: 1px solid var(--rams-rule, #d6d6cf);
  border-radius: 4px;
  background: var(--rams-paper-strong, #ffffff);
  padding: 14px;
}

.safety-detail-summary {
  border-left: 3px solid var(--rams-rule-strong, #b7b7ae);
}

.safety-detail-summary--danger {
  border-left-color: #b42318;
  background: #fff9f8;
}

.safety-detail-summary--warning {
  border-left-color: #a15c00;
  background: #fffbf2;
}

.safety-detail-summary--success {
  border-left-color: var(--rams-accent, #17645d);
}

.safety-detail-summary > span {
  display: block;
  color: var(--rams-muted, #686862);
  font-size: 11px;
  font-weight: 650;
}

.safety-detail-summary > strong {
  display: block;
  margin-top: 5px;
  color: var(--rams-ink, #1b1b18);
  font-size: 19px;
  font-weight: 720;
}

.safety-detail-summary > p {
  margin: 7px 0 0;
  color: var(--rams-body, #3f3f3a);
  font-size: 13px;
  line-height: 1.5;
}

.safety-detail-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.safety-detail-section__head h3 {
  margin: 0;
  color: var(--rams-ink, #1b1b18);
  font-size: 14px;
  font-weight: 700;
}

.safety-detail-section__head span {
  color: var(--rams-muted, #686862);
  font-size: 12px;
  font-weight: 650;
}

.safety-detail-record {
  margin: 0;
  color: var(--rams-ink, #1b1b18);
  font-size: 14px;
  font-weight: 620;
  line-height: 1.6;
  white-space: pre-wrap;
}

.safety-caution-list {
  display: grid;
  gap: 8px;
}

.safety-caution-item {
  border-left: 3px solid var(--rams-rule-strong, #b7b7ae);
  background: #f5f5f1;
  padding: 10px 11px;
}

.safety-caution-item.is-unresolved {
  border-left-color: #b42318;
  background: #fff8f7;
}

.safety-caution-item.is-resolved {
  border-left-color: var(--rams-accent, #17645d);
}

.safety-caution-item > div {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.safety-caution-item strong {
  color: var(--rams-ink, #1b1b18);
  font-size: 13px;
  line-height: 1.45;
}

.safety-caution-item span {
  flex: 0 0 auto;
  color: var(--rams-muted, #686862);
  font-size: 11px;
  font-weight: 700;
}

.safety-caution-item p {
  margin: 6px 0 0;
  color: var(--rams-body, #3f3f3a);
  font-size: 12px;
  line-height: 1.45;
}

.safety-detail-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-top: auto;
  border-top: 1px solid var(--rams-rule, #d6d6cf);
  padding-top: 14px;
}

.candidate-queue-row.is-applying {
  border-color: var(--rams-accent, #17645d);
  background: #f2f7f5;
  pointer-events: none;
}

.button.is-applying {
  position: relative;
  padding-left: 28px;
}

.button.is-applying::before {
  position: absolute;
  left: 10px;
  width: 11px;
  height: 11px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  content: "";
  animation: hs-saving-spin 650ms linear infinite;
}

@media (max-width: 900px) {
  .workspace-drawer--safety {
    max-height: min(72vh, 620px);
    border-radius: 8px 8px 0 0;
  }
  .safety-detail-body {
    padding: 12px;
  }
  .safety-detail-actions .button {
    flex: 1 1 auto;
  }
}
/* Desktop clinical workbench: one persistent instrument surface, not a page of cards. */
.workspace-product-label {
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  border-right: 1px solid var(--rams-rule-strong, #aeb1aa);
  color: var(--rams-accent, #17645d);
  padding-right: 11px;
  font-size: 11px;
  font-weight: 760;
  letter-spacing: 0.06em;
  white-space: nowrap;
}

.workspace-session-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--rams-muted, #6d706a);
  font-size: 11px;
  font-weight: 650;
}

.workspace-session-state {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  gap: 7px;
  border: 1px solid var(--rams-rule, #d0d1ca);
  background: var(--rams-paper-strong, #ffffff);
  padding: 0 9px;
  color: var(--rams-body, #3f413d);
}

.workspace-session-state i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--rams-accent, #17645d);
  box-shadow: 0 0 0 3px rgba(23, 100, 93, 0.1);
}

.workspace-session-state.is-complete i {
  background: #6d706a;
  box-shadow: none;
}

.workspace-session-room {
  padding: 0 2px;
  white-space: nowrap;
}

.clinical-workbench,
.workbench-stage {
  min-width: 0;
  min-height: 0;
}

.bottom-shortcut-hint {
  flex: 0 0 auto;
  margin-left: 4px;
  border-left: 1px solid var(--rams-rule, #d0d1ca);
  color: var(--rams-muted, #6d706a);
  padding-left: 10px;
  font-size: 10px;
  font-weight: 560;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

@media (min-width: 901px) {
  .workspace-main {
    padding: 10px 12px calc(var(--workspace-bottom-bar-clearance) + env(safe-area-inset-bottom));
  }
  .clinical-workbench {
    display: block;
    width: 100%;
    height: clamp(620px, 100dvh - var(--workspace-bottom-bar-clearance) - 76px, 880px);
    margin: 0 auto;
    overflow: hidden;
    border: 1px solid var(--rams-rule-strong, #aeb1aa);
    background: var(--rams-paper, #fbfbf8);
  }
  .workbench-stage {
    display: grid;
    height: 100%;
    overflow: hidden;
    background: var(--rams-paper-strong, #ffffff);
    grid-template-rows: auto minmax(0, 1fr);
  }
  .workbench-stage > .patient-context-strip--task-focused {
    width: 100%;
    margin: 0;
    border: 0;
    border-bottom: 1px solid var(--rams-rule-strong, #aeb1aa);
    border-radius: 0;
  }
  .workbench-stage > .patient-context-strip--task-focused > .context-card,
  .workbench-stage > .patient-context-strip--task-focused > .context-safety {
    min-height: 68px;
    border-bottom: 0;
  }
  .workbench-stage .context-safety {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .workbench-stage .context-checklist-chip {
    cursor: pointer;
    font: inherit;
  }
  .workbench-stage > .clinical-grid--focused.clinical-grid--task-focused {
    width: 100%;
    height: auto;
    min-height: 0;
    margin: 0;
    gap: 0;
  }
  .workbench-stage > .clinical-grid--focused > .workspace-panel {
    border-top: 0;
    border-bottom: 0;
    border-radius: 0;
  }
  .workbench-stage > .clinical-grid--task-focused > .clinical-panel--patient,
  .workbench-stage > .clinical-grid--task-focused > .clinical-panel--soap {
    border-right: 0;
  }
  .workbench-stage .panel-header {
    background: #f5f5f1;
  }
  .workbench-stage .clinical-panel--soap {
    box-shadow: inset 0 2px 0 var(--rams-accent, #17645d);
  }
  .workbench-stage .clinical-grid--task-focused > .auxiliary-panel {
    box-shadow: none;
  }
}
@media (min-width: 901px) and (max-width: 1200px) {
  .clinical-workbench {
    height: auto;
    min-height: 690px;
  }
  .workbench-stage {
    overflow: visible;
  }
  .workbench-stage > .clinical-grid--focused.clinical-grid--task-focused {
    min-height: 610px;
  }
}
@media (max-width: 900px) {
  .workspace-product-label,
  .bottom-shortcut-hint {
    display: none;
  }
  .clinical-workbench,
  .workbench-stage {
    display: block;
    overflow: visible;
    border: 0;
    background: transparent;
  }
}
/* Clinic focus mode owns the full viewport; navigation is the explicit board-back control. */
.workspace-back-nav {
  display: inline-flex;
  min-height: 34px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  gap: 6px;
  border: 0;
  border-right: 1px solid var(--rams-rule-strong, #aeb1aa);
  background: transparent;
  color: var(--rams-body, #3f413d);
  padding: 0 12px 0 0;
  font: inherit;
}

.workspace-back-nav:hover {
  color: var(--rams-accent, #17645d);
}

.workspace-back-nav > span {
  font-size: 18px;
  font-weight: 520;
  line-height: 1;
}

.workspace-back-nav > strong {
  font-size: 12px;
  font-weight: 680;
  white-space: nowrap;
}

@media (min-width: 901px) {
  .workspace-bottom-bar {
    left: 0;
  }
}
@media (max-width: 900px) {
  .workspace-back-nav {
    width: 36px;
    min-height: 34px;
    justify-content: center;
    border-right: 0;
    padding: 0;
  }
  .workspace-back-nav > strong {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }
}
/* Usability pass: one clinical flow, recoverable work, and task-focused density. */
.workspace-recovery-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 10px;
  border: 1px solid #e0b85c;
  border-radius: 8px;
  background: #fff9e8;
  padding: 10px 12px;
}

.workspace-recovery-banner > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workspace-recovery-banner span {
  color: var(--rams-muted, #696c66);
  font-size: 12px;
}

.workspace-flow-guide {
  display: flex;
  min-height: 38px;
  align-items: center;
  gap: 10px;
  border-top: 1px solid var(--rams-rule, #d9dbd5);
  border-bottom: 1px solid var(--rams-rule, #d9dbd5);
  background: #f8f8f5;
  padding: 5px 12px;
}

.context-card--summary {
  cursor: default;
}

@media (min-width: 901px) {
  .workbench-stage {
    grid-template-rows: auto 38px minmax(0, 1fr);
  }
  .workbench-stage > .patient-context-strip--task-focused {
    grid-row: 1;
  }
  .workbench-stage > .workspace-flow-guide {
    grid-row: 2;
  }
  .workbench-stage > .clinical-grid--task-focused {
    grid-row: 3;
  }
  .workbench-stage .context-card--summary {
    grid-template-columns: minmax(150px, 0.75fr) minmax(230px, 1.25fr);
  }
  .workbench-stage .context-card--summary:hover {
    background: var(--rams-paper, #fbfbf8);
    box-shadow: none;
  }
  .workbench-stage .context-safety {
    grid-template-columns: minmax(0, 1fr);
  }
}
.workspace-flow-guide > div {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  color: var(--rams-muted, #696c66);
}

.workspace-flow-guide span {
  font-size: 12px;
  font-weight: 700;
}

.workspace-flow-guide small {
  font-size: 10px;
}

.workspace-flow-guide > div.is-active {
  color: #8b5a05;
}

.workspace-flow-guide > div.is-complete {
  color: var(--rams-accent, #17645d);
}

.workspace-flow-guide i {
  color: #a4a7a0;
  font-style: normal;
}

.clinical-grid--chart-focus {
  grid-template-columns: minmax(230px, 0.7fr) minmax(520px, 1.85fr) minmax(260px, 0.75fr);
}

@media (min-width: 1201px) {
  .clinical-grid--focused.clinical-grid--task-focused.clinical-grid--chart-focus {
    grid-template-columns: minmax(240px, 0.58fr) minmax(600px, 1.78fr) minmax(220px, 0.54fr);
  }
}
@media (min-width: 901px) and (max-width: 1200px) {
  .clinical-grid--focused.clinical-grid--task-focused.clinical-grid--chart-focus {
    grid-template-columns: minmax(220px, 0.56fr) minmax(520px, 1.44fr);
  }
}
.soap-panel-header {
  align-items: center;
}

.subjective-density-toggle {
  width: 100%;
  margin-top: 4px;
}

.chart-quality-message {
  align-items: flex-start;
}

.chart-quality-link {
  min-height: 30px;
  cursor: pointer;
  border: 0;
  border-bottom: 1px solid currentColor;
  background: transparent;
  color: inherit;
  padding: 2px 0;
  font: inherit;
  text-align: left;
}

.safety-overview-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.safety-overview-list .safety-chip {
  min-height: 58px;
  justify-content: space-between;
  text-align: left;
}

.candidate-action-history {
  margin-bottom: 12px;
  border: 1px solid var(--rams-rule, #d9dbd5);
  border-radius: 8px;
  background: #fafaf7;
  padding: 10px 12px;
}

.candidate-action-history > div,
.candidate-action-history li {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.candidate-action-history ol {
  display: grid;
  gap: 6px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

.candidate-action-history span,
.candidate-action-history small {
  font-size: 11px;
}

.workspace-action-feedback {
  gap: 10px;
}

.workspace-action-feedback .button {
  border-color: currentColor;
  color: inherit;
}

button.final-section-card,
button.final-gate-item {
  width: 100%;
  cursor: pointer;
  appearance: none;
  font: inherit;
  text-align: left;
}

button.final-section-card:hover,
button.final-gate-item:hover {
  border-color: var(--rams-accent, #17645d);
  box-shadow: 0 0 0 2px rgba(23, 100, 93, 0.09);
}

.final-section-card > em,
.final-gate-item > em {
  display: block;
  margin-top: 8px;
  color: var(--rams-accent, #17645d);
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
}

.workspace-modal:focus,
.workspace-drawer:focus {
  outline: none;
}

.workspace-modal :focus-visible,
.workspace-drawer :focus-visible,
.encounter-workspace :focus-visible {
  outline: 2px solid var(--rams-accent, #17645d);
  outline-offset: 2px;
}

@media (max-width: 1200px) {
  .clinical-grid--chart-focus {
    grid-template-columns: minmax(220px, 0.75fr) minmax(480px, 1.6fr);
  }
  .clinical-grid--chart-focus .auxiliary-panel {
    grid-column: 1/-1;
  }
}
@media (max-width: 900px) {
  .workspace-recovery-banner {
    align-items: stretch;
    flex-direction: column;
  }
  .workspace-recovery-banner > div {
    justify-content: space-between;
  }
  .workspace-flow-guide {
    overflow-x: auto;
    gap: 7px;
    padding-right: 8px;
    padding-left: 8px;
    scrollbar-width: none;
  }
  .workspace-flow-guide > div {
    flex: 0 0 auto;
  }
  .workspace-flow-guide small {
    display: none;
  }
  .clinical-grid--chart-focus {
    display: block;
  }
  .encounter-workspace button,
  .encounter-workspace [role=button],
  .encounter-workspace input:not([type=checkbox]):not([type=radio]),
  .encounter-workspace select,
  .encounter-workspace summary {
    min-height: 44px;
  }
  .workspace-back-nav {
    width: 44px;
    min-height: 44px;
  }
  .mobile-panel-tabs button {
    min-height: 52px;
  }
  .subjective-inquiry-row button {
    min-height: 52px;
  }
  .conversation-panel__body {
    padding: 10px;
  }
  .clinical-grid--focused > .conversation-panel.is-mobile-active {
    height: clamp(300px, 100dvh - var(--workspace-bottom-bar-clearance) - 220px, 460px);
  }
  .conversation-input__hint {
    display: none;
  }
  .conversation-input textarea,
  .conversation-panel--empty .conversation-input textarea {
    height: 72px;
    min-height: 72px;
    max-height: 120px;
  }
  .safety-overview-list {
    grid-template-columns: 1fr;
  }
}`],
})
export class PortalHanscribeEncounterWorkspaceComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() context: any;
    @ViewChild('bottomBar') private bottomBarRef?: ElementRef;
    @ViewChild('conversationInputArea') private conversationInputRef?: ElementRef;
    @ViewChild('conversationThread') private conversationThreadRef?: ElementRef;

    public componentId = 'encounter.workspace';
    public loading = true;
    public saving = false;
    public mobilePanel = 'patient';
    public startingChecklist = false;
    public savingChecklistItemCode = '';
    public error = '';
    public requestId = '';
    public debugDetailsOpen = false;
    public checklistMessage = '';
    public soapMessage = '';
    public savingChecklistAll = false;
    public soapSaving = false;
    public soapCandidateAppending = false;
    public soapCreating = false;
    public soapValidating = false;
    public soapReadying = false;
    public soapValidation: WorkspaceMap = {};
    public summaryGenerating = false;
    public summaryCandidateRun: WorkspaceMap | null = null;
    public summaryCandidateDraft: SummaryCandidateDraft = {
        overview: '',
        key_points: [],
        limitations: [],
        source_refs: [],
        dirty: false,
    };
    public summaryCandidateDecision = '';
    public summaryCandidateMessage = '';
    public summaryCandidateError = '';
    public soapGenerating = false;
    public soapEntryPreparing = false;
    public soapEntryPrepareRun: WorkspaceMap = {};
    public soapGenerationRun: WorkspaceMap | null = null;
    public soapGenerationError = '';
    public finalChart: WorkspaceMap = {};
    public finalApproving = false;
    public finalMessage = '';
    public finalError = '';
    public finalGateConfirmed = false;
    public finalWarningsAcknowledged = false;

    public encounterId = '';
    public data: WorkspaceMap | null = null;
    public encounter: WorkspaceMap | null = null;
    public patient: WorkspaceMap | null = null;
    public precheck: WorkspaceMap | null = null;
    public summary: WorkspaceMap = {};
    public checklist: WorkspaceMap = {};
    public standardTools: WorkspaceMap = {};
    public referenceComparison: WorkspaceMap = {};
    public biometric: WorkspaceMap = {};
    public emr: WorkspaceMap = {};
    public emrExporting = false;
    public emrMessage = '';
    public soap: WorkspaceMap = {};
    public auth: WorkspaceMap = {};
    public requestContext: WorkspaceMap = {};
    public llmSettings: WorkspaceMap = {};
    public mcpHarness: WorkspaceMap = {};
    public mcpGenerating = false;
    public mcpMessage = '';
    public mcpError = '';
    public workspaceActionFeedback = '';
    public workspaceActionFeedbackTone = 'success';
    public candidatePulseActive = false;
    public recentAppliedSoapSection = '';
    public entryRevealActive = false;
    public workspaceDensityMode: 'focused' | 'all' = 'focused';
    public workspaceLayoutMode: 'balanced' | 'chart' = 'balanced';
    public showAllSubjectiveInquiry = false;
    public activeSubjectiveInquiryCode = '';
    public autosaveState: 'idle' | 'pending' | 'saving' | 'saved' | 'failed' = 'idle';
    // 자동 서버 저장은 진료자가 직접 켠 현재 작업에서만 동작한다.
    // 사용 전 변경 내용은 복구용 브라우저 저장소에만 보관한다.
    public autosaveEnabled = false;
    public lastAutosavedAt = '';
    public soapDraftRevision = '';
    public recoveryAvailable = false;
    public recoverySnapshot: WorkspaceRecoverySnapshot | null = null;
    public retryActionLabel = '';
    public candidateActionHistory: WorkspaceActionHistoryItem[] = [];
    public candidateUndoItem: WorkspaceActionHistoryItem | null = null;
    public interactionLatencyMs = 0;
    private bottomBarResizeObserver: any = null;
    private bottomBarFrame = 0;
    private bottomBarWindowResizeHandler: any = null;
    private workspaceFeedbackTimer: any = null;
    private candidatePulseTimer: any = null;
    private recentAppliedTimer: any = null;
    private entryRevealTimer: any = null;
    private autosaveTimer: any = null;
    private recoveryTimer: any = null;
    private candidateUndoTimer: any = null;
    private overlayTriggerElement: HTMLElement | null = null;
    private overlayTriggerLocator: { testId: string; index: number } | null = null;
    private allowNextUnload = false;
    private beforeUnloadHandler = (event: BeforeUnloadEvent) => {
        if (this.allowNextUnload || !this.hasUnsavedWorkspaceChanges) return;
        event.preventDefault();
        event.returnValue = '';
    };
    private workspaceNavigationClickHandler = (event: MouseEvent) => {
        if (!event || event.defaultPrevented || !this.hasUnsavedWorkspaceChanges) return;
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const target = event.target as HTMLElement;
        const anchor = target && target.closest ? target.closest('a[href]') as HTMLAnchorElement : null;
        if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
        const destination = anchor.href || '';
        if (!destination || destination === window.location.href || destination.startsWith('javascript:')) return;
        if (!this.confirmWorkspaceNavigation()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }
        this.allowNextUnloadOnce();
    };
    private workspaceKeydownHandler = (event: KeyboardEvent) => {
        if (!event) return;
        const key = String(event.key || '');
        if (key === 'Escape' && this.hasOpenWorkspaceOverlay) {
            event.preventDefault();
            void this.closeTopWorkspaceOverlay();
            return;
        }
        if (key === 'Tab' && this.hasOpenWorkspaceOverlay) {
            this.trapOverlayFocus(event);
            return;
        }
        if (event.altKey && !event.ctrlKey && !event.metaKey) {
            const sectionIndex = ['1', '2', '3', '4'].indexOf(key);
            if (sectionIndex >= 0) {
                event.preventDefault();
                void this.setActiveSoapSection(this.soapSectionOrder[sectionIndex]);
                return;
            }
            if (key.toLowerCase() === 'd') {
                event.preventDefault();
                void this.focusConversationInput();
                return;
            }
            if (key.toLowerCase() === 'r') {
                event.preventDefault();
                void this.openWorkspaceDrawer('assistant');
                return;
            }
        }
        const tab = (event.target as HTMLElement) && (event.target as HTMLElement).closest
            ? (event.target as HTMLElement).closest('[role="tab"]') as HTMLElement
            : null;
        if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(key) >= 0) {
            const tablist = tab.closest('[role="tablist"]');
            const tabs = tablist ? Array.from(tablist.querySelectorAll('[role="tab"]')) as HTMLElement[] : [];
            if (tabs.length) {
                event.preventDefault();
                const current = Math.max(0, tabs.indexOf(tab));
                const targetIndex = key === 'Home' ? 0
                    : key === 'End' ? tabs.length - 1
                        : key === 'ArrowLeft' ? (current - 1 + tabs.length) % tabs.length
                            : (current + 1) % tabs.length;
                tabs[targetIndex].focus();
                tabs[targetIndex].click();
                return;
            }
        }
        if (!(event.ctrlKey || event.metaKey) || key.toLowerCase() !== 's') return;
        event.preventDefault();
        if (this.busy || this.clinicalReadOnly) return;
        if (this.checklistDirtyCount > 0 && (this.activeWorkspaceDrawer === 'checklist' || !this.hasSoapDirty)) {
            void this.saveDirtyChecklistItems();
            return;
        }
        if (this.hasSoapDirty) void this.saveSoapDraft();
    };
    private soapCandidateQueueStatusDefs: Array<{ code: SoapCandidateQueueStatus; label: string; description: string; tone: string }> = [
        { code: 'new_candidate_ready', label: '진료 참고 내용', description: '새로 살펴볼 내용', tone: 'warning' },
        { code: 'previous_version', label: '이전 문장', description: '이전 문장', tone: 'neutral' },
        { code: 'pending', label: '진료 참고 내용', description: '한의사 판단 전', tone: 'warning' },
        { code: 'stale', label: '진료 참고 내용', description: '최신 진료 내용 확인', tone: 'pending' },
        { code: 'applied', label: '차트 반영 완료', description: '반영됨', tone: 'success' },
        { code: 'deferred', label: '나중에 보기', description: '나중에 보기', tone: 'neutral' },
        { code: 'excluded', label: '사용 안 함', description: '사용 안 함', tone: 'danger' },
    ];

    public selectedChecklistCode = 'low_back_pain';
    public checklistDrafts: Record<string, ChecklistDraft> = {};
    public checklistSoapCandidateQueue: Record<string, boolean> = {};
    private checklistResolvedStatuses = ['confirmed', 'not_applicable', 'excluded', 'action_completed'];
    private soapCandidateQueueRevision = 0;
    private soapCandidateQueueBuiltRevision = -1;
    private soapCandidateQueueGroupCache: SoapCandidateQueueGroup[] = [];
    private readonly coreSoapCandidateLimit = 3;
    private coreSoapCandidateBatchInitialized = false;
    private coreSoapCandidateVisibleKeys: string[] = [];
    public coreCandidateBatchAppliedCount = 0;
    private readonly actionableSoapCandidateStatuses: SoapCandidateQueueStatus[] = ['new_candidate_ready', 'pending', 'stale'];
    private readonly candidateDecisionTimeoutMs = 8000;
    public checklistStateOptions = [
        { code: 'confirmed', label: '확인' },
        { code: 'not_applicable', label: '해당 없음' },
        { code: 'excluded', label: '사용 안 함' },
        { code: 'deferred', label: '나중에 보기' },
        { code: 'action_completed', label: '조치 완료' },
    ];

    public soapSectionOrder = ['S', 'O', 'A', 'P'];
    public activeSoapSection = 'S';
    public soapSectionAssistPanelOpenState: WorkspaceMap = {
        S: true,
        O: true,
        A: true,
        P: true,
    };
    public soapSectionLabels: WorkspaceMap = {
        S: '문진 기록',
        O: '진찰/소견',
        A: '변증/진단',
        P: '치료/처방',
    };
    public soapSectionPlaceholders: WorkspaceMap = {
        S: '주호소, 양상, 한열, 수면, 식욕, 대소변, 과거력·복약·알레르기',
        O: '설진, 맥진, 복진, 압통/ROM, 기타 소견',
        A: '의심 변증/진단, 근거, 감별, 추가 확인, 한의사 판단',
        P: '치료 방향, 한약·제제, 생활지도, 추적 계획',
    };
    public soapClinicalStructure: Record<string, SoapClinicalStructureItem[]> = {
        S: [
            { code: 'chief_complaint', label: '주호소', hint: '주 증상과 시작 시점을 확인', keys: ['chief_complaint'], labels: ['주호소'], keywords: ['주호소', '호소', '통증', '불편'] },
            { code: 'pattern', label: '양상', hint: '악화/완화, 빈도, 강도, 방사 여부', keys: ['symptom_pattern', 'symptom_detail', 'memo'], labels: ['양상', '증상 상세', '핵심 메모', '메모'], keywords: ['양상', '악화', '완화', '강도', '방사'] },
            { code: 'cold_heat', label: '한열', hint: '오한, 열감, 냉증, 상열감', keys: ['cold_heat', 'thermal'], labels: ['한열', '오한', '열감', '냉증'], keywords: ['한열', '오한', '열감', '냉증', '상열'] },
            { code: 'sleep', label: '수면', hint: '입면, 중도각성, 수면 질', keys: ['sleep'], labels: ['수면'], keywords: ['수면', '불면', '입면', '각성'] },
            { code: 'appetite', label: '식욕', hint: '식욕, 소화, 더부룩함', keys: ['appetite', 'digestion'], labels: ['식욕', '소화'], keywords: ['식욕', '소화', '더부룩', '속쓰림'] },
            { code: 'bowel_urine', label: '대소변', hint: '대변, 소변, 배뇨/배변 변화', keys: ['bowel_urine', 'stool', 'urine'], labels: ['대소변', '대변', '소변'], keywords: ['대소변', '대변', '소변', '배뇨', '배변'] },
            { code: 'past_history', label: '과거력 요약', hint: '과거력, 수술력, 복약 이력', keys: ['past_history'], labels: ['과거력'], keywords: ['과거력', '수술력', '진단력', '기왕력'] },
            { code: 'medication', label: '복약', hint: '현재 복약, 건강기능식품, 최근 중단 약', keys: ['medication', 'medicine'], labels: ['복약', '복용약'], keywords: ['복약', '복용약', '약물', '약'] },
            { code: 'allergy', label: '알레르기', hint: '약물/음식 알레르기, 이상 반응', keys: ['allergy'], labels: ['알레르기'], keywords: ['알레르기', '이상 반응', '부작용'] },
        ],
        O: [
            { code: 'tongue', label: '설진', hint: '설질, 설태, 색, 건조감', keys: ['tongue'], labels: ['설진', '혀'], keywords: ['설진', '설질', '설태', '혀'] },
            { code: 'pulse', label: '맥진', hint: '부침, 지삭, 허실, 긴활삽', keys: ['pulse'], labels: ['맥진', '맥상'], keywords: ['맥진', '맥상', '부맥', '침맥', '현맥'] },
            { code: 'abdomen', label: '복진', hint: '복부 긴장, 압통, 냉감', keys: ['abdomen'], labels: ['복진', '복부'], keywords: ['복진', '복부', '복직근', '냉감'] },
            { code: 'tender_rom', label: '압통/가동범위', hint: '압통점, ROM 제한, 자세/동작', keys: ['tenderness_rom', 'rom'], labels: ['압통', '가동범위', 'ROM'], keywords: ['압통', '가동범위', 'ROM', '제한', '운동범위'] },
            { code: 'other', label: '기타 소견', hint: '신경학적 소견, 부종, 피부, 특이사항', keys: ['objective_other'], labels: ['기타 소견', '특이 소견'], keywords: ['소견', '부종', '피부', '신경학적'] },
        ],
        A: [
            { code: 'pattern_candidates', label: '의심 소견', hint: '의심 변증/진단과 우선순위', dynamic: 'pattern_candidates', keywords: ['변증', '의심', '진단'] },
            { code: 'supporting_evidence', label: '지지 근거', hint: '지지 소견', dynamic: 'pattern_support', keywords: ['근거', '소견', '지지'] },
            { code: 'opposing_evidence', label: '반대/불확실', hint: '맞지 않는 소견, 근거 부족, 감별 필요 항목', dynamic: 'pattern_opposing', keywords: ['반대', '불일치', '감별'] },
            { code: 'followup', label: '추가 확인', hint: '추가 문진/진찰 항목', dynamic: 'followup_questions', keywords: ['추가 확인', '추가 문진', '확인'] },
            { code: 'doctor_judgement', label: '판단 메모', hint: '한의사 최종 판단', keywords: ['판단', '진단', '변증', '평가'] },
        ],
        P: [
            { code: 'acupuncture', label: '침구', hint: '부위, 술기, 빈도', keys: ['acupuncture_plan'], labels: ['침구 계획', '침구'], keywords: ['침', '뜸', '부항', '침구', '혈위'] },
            { code: 'herbal', label: '한약/제제', hint: '복약, 알레르기, 금기', keys: ['herbal_plan'], labels: ['한약', '제제'], keywords: ['한약', '제제', '처방', '복용'] },
            { code: 'lifestyle', label: '생활지도', hint: '운동, 자세, 식이, 수면', keys: ['lifestyle_guidance'], labels: ['생활지도'], keywords: ['생활지도', '운동', '자세', '식이', '수면'] },
            { code: 'followup_observation', label: '추적 관찰', hint: '재진, 경과 지표', keys: ['followup_plan'], labels: ['추적 관찰', '재진'], keywords: ['추적', '재진', '경과', '관찰'] },
        ],
    };
    public soapDrafts: Record<string, SoapSentenceDraft[]> = {
        S: [],
        O: [],
        A: [],
        P: [],
    };
    public soapEditorItemsBySection: Record<string, SoapSentenceDraft[]> = {
        S: [],
        O: [],
        A: [],
        P: [],
    };
    public soapSectionCompactTextBySection: WorkspaceMap = {
        S: '',
        O: '',
        A: '',
        P: '',
    };
    public soapSectionCountLabelBySection: WorkspaceMap = {
        S: '입력 전',
        O: '입력 전',
        A: '입력 전',
        P: '입력 전',
    };
    public soapSectionStateLabelBySection: WorkspaceMap = {
        S: '시작 전',
        O: '시작 전',
        A: '시작 전',
        P: '시작 전',
    };
    public soapSectionStateClassBySection: WorkspaceMap = {
        S: 'status-chip status-chip--neutral',
        O: 'status-chip status-chip--neutral',
        A: 'status-chip status-chip--neutral',
        P: 'status-chip status-chip--neutral',
    };
    public soapSectionAssistTextBySection: WorkspaceMap = {
        S: '',
        O: '',
        A: '',
        P: '',
    };
    public objectiveFindingPreviewTextValue = '';
    public objectiveFindingPriorityItems: ObjectiveFindingPriorityPoint[] = [];
    public objectiveFindingEmrGroupItems: WorkspaceMap[] = [];
    public objectiveFindingAssistGroupItems: WorkspaceMap[] = [];
    public objectiveFindingOpenGroupState: WorkspaceMap = {};
    public soapNewText: Record<string, string> = {
        S: '',
        O: '',
        A: '',
        P: '',
    };
    public objectiveFindingItems: ObjectiveFindingInput[] = [
        {
            code: 'tongue',
            label: '설진',
            hint: '설질, 설태, 색, 습윤 정도',
            placeholder: '예: 설질 담홍, 설태 백니, 약간 건조',
            chips: [],
            fields: [
                { code: 'body', label: '설질', placeholder: '선택', options: ['담홍설', '담백설', '홍설', '암홍설', '자암설', '치흔설'] },
                { code: 'coating', label: '설태', placeholder: '선택', options: ['박백태', '백니태', '황태', '후니태', '무태', '건조태'] },
                { code: 'color', label: '설색', placeholder: '선택', options: ['담홍', '담백', '홍', '암홍', '자암', '창백'] },
                { code: 'moisture', label: '윤조', placeholder: '선택', options: ['정상', '건조', '습윤', '진액 부족', '끈적함'] },
            ],
        },
        {
            code: 'pulse',
            label: '맥진',
            hint: '부침, 지삭, 허실, 긴활삽',
            placeholder: '예: 맥 현세, 좌관 긴장',
            chips: [],
            fields: [
                { code: 'depth', label: '부침', placeholder: '선택', options: ['부맥', '침맥', '중맥', '부침 불명'] },
                { code: 'rate', label: '지삭', placeholder: '선택', options: ['평맥', '삭맥', '지맥', '완맥'] },
                { code: 'strength', label: '허실', placeholder: '선택', options: ['중등도', '허맥', '실맥', '무력'] },
                { code: 'texture', label: '맥상', placeholder: '선택', options: ['현맥', '긴맥', '활맥', '삽맥', '세맥'] },
            ],
        },
        {
            code: 'abdomen',
            label: '복진',
            hint: '복부 긴장, 압통, 냉감, 저항',
            placeholder: '예: 하복부 냉감, 복직근 긴장',
            chips: [],
            fields: [
                { code: 'tenderness', label: '압통', placeholder: '선택', options: ['없음', '상복부', '하복부', '제하부', '협늑부', '회맹부'] },
                { code: 'tension', label: '긴장', placeholder: '선택', options: ['없음', '복직근 긴장', '상복부 긴장', '하복부 긴장', '팽만'] },
                { code: 'coldness', label: '냉감', placeholder: '선택', options: ['없음', '상복부', '하복부', '사지 동반', '전반'] },
                { code: 'resistance', label: '저항', placeholder: '선택', options: ['없음', '심하부 저항', '소복부 저항', '긴장성 저항'] },
            ],
        },
        {
            code: 'tender_rom',
            label: '압통/가동범위',
            hint: '압통점, ROM, 방사통, 신경학적 확인',
            placeholder: '예: L4-5 주변 압통, 요추 굴곡 제한',
            chips: [],
            fields: [
                { code: 'tender_point', label: '압통점', placeholder: '선택', options: ['없음', '경항부', '견배부', '요천추부', '둔부', '슬관절'] },
                { code: 'rom', label: 'ROM', placeholder: '선택', options: ['제한 없음', '굴곡 제한', '신전 제한', '회전 제한', '측굴 제한'] },
                { code: 'radiating', label: '방사통', placeholder: '선택', options: ['없음', '상지', '하지', '둔부', '흉복부'] },
                { code: 'neuro', label: '신경학적', placeholder: '선택', options: ['정상', '감각 저하', '근력 저하', '반사 저하', '추가 확인'] },
            ],
        },
        {
            code: 'other',
            label: '기타 소견',
            hint: '부종, 피부, 체표 온도, 특이사항',
            placeholder: '예: 하지 감각 저하 호소 없음, 피부 발적 없음',
            chips: [],
            fields: [
                { code: 'edema', label: '부종', placeholder: '선택', options: ['없음', '경도', '중등도', '말초', '국소'] },
                { code: 'skin', label: '피부', placeholder: '선택', options: ['정상', '발적', '열감', '창백', '자반'] },
                { code: 'temperature', label: '체표온도', placeholder: '선택', options: ['정상', '냉감', '열감', '좌우차', '사지 냉감'] },
                { code: 'specific', label: '특이', placeholder: '선택', options: ['특이 없음', '추가 확인', '검사 참고', '사진 참고'] },
            ],
        },
    ];
    public objectiveFindingDrafts: Record<string, ObjectiveFindingDraft> = {};
    public objectiveFindingManualText = '';
    public savingQuantitativeObservations = false;
    public quantitativeObservationMessage = '';
    public quantitativeObservationDraft: WorkspaceMap = {
        body_site_code: 'lumbar_spine',
        rom_flexion: '',
        rom_extension: '',
        rom_rotation_left: '',
        rom_rotation_right: '',
        pulse_rate: '',
        tongue_coating_ratio: '',
        tongue_moisture_index: '',
    };
	    public treatmentPlanItems: TreatmentPlanInput[] = [
	        {
	            code: 'acupuncture',
	            label: '침구',
	            hint: '부위, 자극, 빈도',
	            placeholder: '예: 요부/둔부 압통점 중심, 반응 확인 후 조절',
	            chips: ['부위 재확인', '침 자극 검토', '뜸/부항 검토', '빈도 계획'],
	        },
	        {
	            code: 'herbal',
	            label: '한약/제제',
	            hint: '복약, 알레르기, 금기',
	            placeholder: '예: 복약/알레르기 확인 후 필요성 검토',
	            chips: ['복약력 재확인', '알레르기 재확인', '금기 확인', '제제 필요성 검토'],
	        },
	        {
	            code: 'lifestyle',
	            label: '생활지도',
	            hint: '운동, 자세, 수면, 식이',
	            placeholder: '예: 통증 악화 동작 제한, 수면/자세 조절 지도 계획',
	            chips: ['자세 지도 계획', '운동 조절 검토', '수면 확인', '식이 확인'],
	        },
	        {
	            code: 'followup',
	            label: '추적 관찰',
	            hint: '재진 시점, 경과 지표',
	            placeholder: '예: 1주 내 통증 NRS, ROM, 야간통 변화 재평가 계획',
	            chips: ['재진 시점 계획', 'NRS 추적', 'ROM 추적', '반응 확인'],
	        },
	    ];
    public treatmentPlanDrafts: Record<string, TreatmentPlanDraft> = {};
    public soapPanelMode = 'soap';
    public conversationInput = '';
    public conversationError = '';
    public conversationSending = false;
    public conversationStreaming = false;
    public conversationTurns: ConversationTurn[] = [];
    private readonly conversationRequestTimeoutMs = 12000;
    public aiAssistantInput = '';
    public aiAssistantMessage = '';
    public aiAssistantGenerating = false;
    public standardToolStartingCode = '';
    public aiAssistantResults: AiAssistantResult[] = [];
    public evidencePanelsOpen: Record<string, boolean> = {};
    public activeWorkspaceDrawer = '';
    public activeSafetyDetailCode = '';
    public applyingSoapCandidateKey = '';
    public evidenceModalOpen = false;
    public patientSummaryModalOpen = false;
    public candidateQueueModalOpen = false;
    public clinicalCandidateModalOpen = false;
    public finalSaveModalOpen = false;
    public activeClinicalCandidateRow: SoapCandidateQueueRow | null = null;
    public activeClinicalCandidateEditText = '';
    public activeEvidenceTitle = '';
    public activeEvidenceRows: EvidenceSourceItem[] = [];
    public activeEvidenceSourceRefs: string[] = [];
    public activeEvidenceDebugRefs: string[] = [];
    public activeEvidenceReflectionLabel = '';
    public activeEvidenceTargetLabel = '';
    public activeEvidenceContextLabel = '';
    public activeEvidenceAnatomicWarning = '';
	    public aiAssistantQuickActions: AiAssistantQuickAction[] = [
	        {
	            code: 'followup_questions',
	            label: '추가 문진',
	            description: '확인 메모, 문진',
	        },
	        {
	            code: 'red_flags',
	            label: '진료 전 주의',
	            description: '주의 항목, 필수 확인',
	        },
	        {
	            code: 'pattern_candidates',
	            label: '의심 변증/진단',
            description: '변증/진단',
	        },
	        {
	            code: 'soap_improvement',
	            label: '진찰 소견 보완',
	            description: '진찰 소견',
	        },
	        {
	            code: 'standard_tools',
	            label: '표준 평가',
	            description: '표준도구 결과',
	        },
	        {
	            code: 'reference_compare',
	            label: '건강인 비교',
	            description: '참조군 대비 위치',
	        },
	        {
	            code: 'missing_review',
	            label: '진료 전 확인',
	            description: '진료 전 확인',
	        },
	        {
	            code: 'precheck_summary',
	            label: '예진 요약',
	            description: '주호소, 주의 정보',
	        },
	    ];

    private readonly referenceMetricDisplayNames: WorkspaceMap = {
        functional_health_score: '기능 건강 점수',
        digestive_comfort_score: '소화기 편안함 점수',
        pain_nrs: '통증 NRS',
    };
    private contextLoadStarted = false;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        this.resolveContext();
        this.loadWorkspacePreferences();
        this.attachWorkspaceGuards();
        this.contextLoadStarted = true;
        await Promise.all([
            this.service.init(),
            this.loadContext(),
        ]);
        this.loadWorkspaceRecovery();
    }

    public async ngOnChanges(changes: SimpleChanges) {
        if (!changes.context || !this.contextLoadStarted) return;
        const previousId = this.encounterId;
        this.resolveContext();
        if (this.encounterId && this.encounterId !== previousId) {
            this.loadWorkspacePreferences();
            await this.loadContext();
            this.loadWorkspaceRecovery();
        }
    }

    public ngAfterViewInit() {
        this.attachBottomBarObserver();
        this.scheduleBottomBarClearance();
        this.scrollConversationThread(true);
    }

    public ngOnDestroy() {
        this.detachWorkspaceGuards();
        [this.workspaceFeedbackTimer, this.candidatePulseTimer, this.recentAppliedTimer, this.entryRevealTimer, this.autosaveTimer, this.recoveryTimer, this.candidateUndoTimer]
            .forEach((timer) => {
                if (timer) clearTimeout(timer);
            });
        if (this.bottomBarResizeObserver) {
            this.bottomBarResizeObserver.disconnect();
            this.bottomBarResizeObserver = null;
        }
        if (this.bottomBarFrame && typeof window !== 'undefined') {
            window.cancelAnimationFrame(this.bottomBarFrame);
            this.bottomBarFrame = 0;
        }
        if (this.bottomBarWindowResizeHandler && typeof window !== 'undefined') {
            window.removeEventListener('resize', this.bottomBarWindowResizeHandler);
            this.bottomBarWindowResizeHandler = null;
        }
    }

    private attachWorkspaceGuards() {
        if (typeof window === 'undefined') return;
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
        window.addEventListener('keydown', this.workspaceKeydownHandler);
        document.addEventListener('click', this.workspaceNavigationClickHandler, true);
    }

    private detachWorkspaceGuards() {
        if (typeof window === 'undefined') return;
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        window.removeEventListener('keydown', this.workspaceKeydownHandler);
        document.removeEventListener('click', this.workspaceNavigationClickHandler, true);
    }

    public get hasOpenWorkspaceOverlay() {
        return !!(this.activeWorkspaceDrawer || this.evidenceModalOpen || this.finalSaveModalOpen
            || this.patientSummaryModalOpen || this.candidateQueueModalOpen || this.clinicalCandidateModalOpen);
    }

    private captureOverlayTrigger(explicitTrigger?: HTMLElement | null) {
        if (typeof document === 'undefined') return;
        const active = explicitTrigger || document.activeElement as HTMLElement;
        if (active && active !== document.body && (explicitTrigger || !active.closest('.workspace-modal, .workspace-drawer'))) {
            this.overlayTriggerElement = active;
            const testId = String(active.getAttribute('data-testid') || '');
            const matches = testId
                ? Array.from(document.querySelectorAll(`[data-testid="${testId}"]`))
                : [];
            this.overlayTriggerLocator = testId
                ? { testId, index: Math.max(0, matches.indexOf(active)) }
                : null;
        }
    }

    private focusActiveOverlay(testId?: string) {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        window.requestAnimationFrame(() => {
            const selector = testId ? `[data-testid="${testId}"]` : '.workspace-modal, .workspace-drawer';
            const overlay = document.querySelector(selector) as HTMLElement;
            if (!overlay) return;
            const focusable = this.overlayFocusableElements(overlay);
            (focusable[0] || overlay).focus();
        });
    }

    private restoreOverlayTrigger() {
        if (typeof window === 'undefined') return;
        const trigger = this.overlayTriggerElement;
        const locator = this.overlayTriggerLocator;
        this.overlayTriggerElement = null;
        this.overlayTriggerLocator = null;
        window.requestAnimationFrame(() => {
            if (trigger && document.contains(trigger)) {
                trigger.focus();
                return;
            }
            if (!locator || !locator.testId) return;
            const matches = Array.from(document.querySelectorAll(
                `[data-testid="${locator.testId}"]`
            )) as HTMLElement[];
            const visibleMatches = matches.filter((element) => element.offsetParent !== null);
            const restored = visibleMatches[Math.min(locator.index, Math.max(0, visibleMatches.length - 1))];
            if (restored) {
                restored.focus();
                return;
            }
            const responsiveFallback = Array.from(document.querySelectorAll(
                '[data-testid="mobile-tab-assistant"]'
            )).find((element) => (element as HTMLElement).offsetParent !== null) as HTMLElement;
            if (responsiveFallback) responsiveFallback.focus();
        });
    }

    private overlayFocusableElements(overlay: HTMLElement) {
        return Array.from(overlay.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter((element: Element) => !(element as HTMLElement).hidden && (element as HTMLElement).offsetParent !== null) as HTMLElement[];
    }

    private trapOverlayFocus(event: KeyboardEvent) {
        if (typeof document === 'undefined') return;
        const overlay = document.querySelector('.workspace-modal, .workspace-drawer') as HTMLElement;
        if (!overlay) return;
        const focusable = this.overlayFocusableElements(overlay);
        if (!focusable.length) {
            event.preventDefault();
            overlay.focus();
            return;
        }
        const current = document.activeElement as HTMLElement;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (current === first || !overlay.contains(current))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (current === last || !overlay.contains(current))) {
            event.preventDefault();
            first.focus();
        }
    }

    private async closeTopWorkspaceOverlay() {
        if (this.clinicalCandidateModalOpen) return this.closeClinicalCandidateModal();
        if (this.candidateQueueModalOpen) return this.closeCandidateQueueModal();
        if (this.evidenceModalOpen) return this.closeEvidenceModal();
        if (this.patientSummaryModalOpen) return this.closePatientSummaryModal();
        if (this.finalSaveModalOpen) return this.closeFinalSaveModal();
        if (this.activeWorkspaceDrawer) return this.closeWorkspaceDrawer();
    }

    private attachBottomBarObserver() {
        if (typeof window === 'undefined') return;
        const element = this.bottomBarRef && this.bottomBarRef.nativeElement ? this.bottomBarRef.nativeElement : null;
        if (!element || this.bottomBarResizeObserver) return;

        const ResizeObserverCtor = (window as any).ResizeObserver;
        if (ResizeObserverCtor) {
            this.bottomBarResizeObserver = new ResizeObserverCtor(() => this.scheduleBottomBarClearance());
            this.bottomBarResizeObserver.observe(element);
        }
        if (!this.bottomBarWindowResizeHandler) {
            this.bottomBarWindowResizeHandler = () => this.scheduleBottomBarClearance();
            window.addEventListener('resize', this.bottomBarWindowResizeHandler);
        }
    }

    private scheduleBottomBarClearance() {
        if (typeof window === 'undefined') return;
        if (this.bottomBarFrame) window.cancelAnimationFrame(this.bottomBarFrame);
        this.bottomBarFrame = window.requestAnimationFrame(() => {
            this.bottomBarFrame = 0;
            this.updateBottomBarClearance();
        });
    }

    private updateBottomBarClearance() {
        const element = this.bottomBarRef && this.bottomBarRef.nativeElement ? this.bottomBarRef.nativeElement : null;
        if (!element) return;
        const workspace = element.closest ? element.closest('.encounter-workspace') : null;
        if (!workspace) return;
        const measuredHeight = Math.ceil(element.getBoundingClientRect().height || 0);
        if (measuredHeight <= 0) return;
        const isCompact = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 780px)').matches;
        const clearanceGap = isCompact ? 20 : 32;
        workspace.style.setProperty('--workspace-bottom-bar-height', `${measuredHeight}px`);
        workspace.style.setProperty('--workspace-bottom-bar-gap', `${clearanceGap}px`);
        workspace.style.setProperty('--workspace-bottom-bar-clearance', `${measuredHeight + clearanceGap}px`);
    }

    public async setMobilePanel(panel: string) {
        if (['patient', 'checklist', 'soap', 'assistant'].indexOf(panel) === -1) return;
        if (panel === 'assistant') {
            await this.openWorkspaceDrawer('assistant');
            return;
        }
        if (panel === 'checklist') {
            await this.openWorkspaceDrawer('checklist');
            return;
        }
        this.mobilePanel = panel;
        if (panel === 'soap') this.soapPanelMode = 'soap';
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public async setSoapPanelMode(mode: string) {
        if (['soap', 'assistant'].indexOf(mode) === -1) return;
        if (mode === 'assistant') {
            await this.openWorkspaceDrawer('assistant');
            return;
        }
        this.soapPanelMode = mode;
        if (this.mobilePanel === 'soap' || this.mobilePanel === 'assistant') {
            this.mobilePanel = 'soap';
        }
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public async setActiveSoapSection(section: string) {
        const normalized = this.normalizedSoapSection(section);
        if (this.soapSectionOrder.indexOf(normalized) < 0 || this.activeSoapSection === normalized) return;
        const startedAt = typeof performance !== 'undefined' ? performance.now() : 0;
        this.activeSoapSection = normalized;
        this.reflectActiveSoapTab(normalized);
        this.saveWorkspacePreferences();
        await this.service.render();
        if (startedAt) this.interactionLatencyMs = Math.round(performance.now() - startedAt);
        this.scheduleBottomBarClearance();
        this.scrollSoapEditorToTop();
    }

    private reflectActiveSoapTab(section: string) {
        if (typeof document === 'undefined') return;
        this.soapSectionOrder.forEach((code) => {
            const tab = document.querySelector(`[data-testid="soap-section-tab-${code}"]`) as HTMLElement;
            if (!tab) return;
            const active = code === section;
            tab.setAttribute('aria-selected', active ? 'true' : 'false');
            tab.setAttribute('tabindex', active ? '0' : '-1');
            tab.classList.toggle('is-active', active);
        });
    }

    public async toggleWorkspaceLayout() {
        this.workspaceLayoutMode = this.workspaceLayoutMode === 'chart' ? 'balanced' : 'chart';
        this.saveWorkspacePreferences();
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public get workspaceLayoutToggleLabel() {
        return this.workspaceLayoutMode === 'chart' ? '균형 보기' : '차트 집중';
    }

    public async toggleSubjectiveInquiryDensity() {
        this.showAllSubjectiveInquiry = !this.showAllSubjectiveInquiry;
        this.workspaceDensityMode = this.showAllSubjectiveInquiry ? 'all' : 'focused';
        this.saveWorkspacePreferences();
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public get subjectiveInquiryDensityLabel() {
        return this.showAllSubjectiveInquiry ? '핵심 항목만 보기' : '전체 항목 보기';
    }

    private scrollSoapEditorToTop() {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        window.requestAnimationFrame(() => {
            const editor = document.querySelector('.clinical-panel--soap .soap-editor') as HTMLElement;
            if (editor) editor.scrollTop = 0;
        });
    }

    private focusSoapSectionAfterCandidateAction(section: any) {
        const normalized = this.normalizedSoapSection(section);
        this.activeSoapSection = normalized;
        this.soapSectionAssistPanelOpenState = {
            ...this.soapSectionAssistPanelOpenState,
            [normalized]: true,
        };
        this.soapPanelMode = 'soap';
        this.mobilePanel = 'soap';
        this.activeWorkspaceDrawer = '';
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        this.evidenceModalOpen = false;
    }

    private announceCandidateApplied(section: any) {
        const normalized = this.normalizedSoapSection(section);
        this.recentAppliedSoapSection = normalized;
        this.showWorkspaceFeedback(`${this.soapSectionDisplayName(normalized)} 차트에 반영했습니다.`, 'success');
        if (this.recentAppliedTimer) clearTimeout(this.recentAppliedTimer);
        this.recentAppliedTimer = setTimeout(() => {
            this.recentAppliedSoapSection = '';
            void this.service.render();
        }, 1800);
        if (typeof window === 'undefined') return;
        window.requestAnimationFrame(() => {
            const target = document.querySelector(`.soap-section[data-soap-section="${normalized}"]`) as HTMLElement;
            if (!target) return;
            target.scrollIntoView({
                behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
                block: 'nearest',
            });
        });
    }

    private triggerCandidatePulse(count: number = 1) {
        if (count <= 0) return;
        this.candidatePulseActive = true;
        this.showWorkspaceFeedback(`새 진료 참고 내용 ${count}건이 준비되었습니다.`, 'info');
        if (this.candidatePulseTimer) clearTimeout(this.candidatePulseTimer);
        this.candidatePulseTimer = setTimeout(() => {
            this.candidatePulseActive = false;
            void this.service.render();
        }, 1800);
    }

    private triggerEntryReveal() {
        this.entryRevealActive = true;
        if (this.entryRevealTimer) clearTimeout(this.entryRevealTimer);
        this.entryRevealTimer = setTimeout(() => {
            this.entryRevealActive = false;
            void this.service.render();
        }, 620);
    }

    private showWorkspaceFeedback(message: string, tone: string = 'success') {
        this.workspaceActionFeedback = message;
        this.workspaceActionFeedbackTone = tone;
        if (this.workspaceFeedbackTimer) clearTimeout(this.workspaceFeedbackTimer);
        this.workspaceFeedbackTimer = setTimeout(() => {
            this.workspaceActionFeedback = '';
            void this.service.render();
        }, 2400);
    }

    public async focusConversationInput() {
        this.activeWorkspaceDrawer = '';
        this.activeSafetyDetailCode = '';
        this.soapPanelMode = 'soap';
        this.mobilePanel = 'patient';
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        await this.service.render();
        if (typeof window === 'undefined') return;
        window.requestAnimationFrame(() => {
            const element = this.conversationInputRef && this.conversationInputRef.nativeElement
                ? this.conversationInputRef.nativeElement as HTMLElement
                : null;
            if (!element) return;
            element.focus();
            element.scrollIntoView({
                behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
                block: 'center',
            });
        });
    }

    public soapSectionTabClass(section: string) {
        const normalized = this.normalizedSoapSection(section);
        const active = this.activeSoapSection === normalized ? ' is-active' : '';
        const attention = this.soapSectionUnreviewedAiCount(normalized) > 0 || this.soapSectionPendingSuggestionCount(normalized) > 0 ? ' has-attention' : '';
        return `soap-section-tab${active}${attention}`;
    }

    public async openWorkspaceDrawer(drawer: string) {
        if (['patient', 'checklist', 'assistant'].indexOf(drawer) === -1) return;
        this.captureOverlayTrigger();
        this.evidenceModalOpen = false;
        this.finalSaveModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        this.activeSafetyDetailCode = '';
        this.activeWorkspaceDrawer = drawer;
        if (drawer === 'assistant') this.soapPanelMode = 'assistant';
        if (drawer !== 'assistant' && this.soapPanelMode === 'assistant') this.soapPanelMode = 'soap';
        if (drawer === 'checklist' && this.mobilePanel === 'checklist') this.mobilePanel = 'soap';
        await this.service.render();
        this.focusActiveOverlay(`workspace-drawer-${drawer}`);
        this.scheduleBottomBarClearance();
    }

    public async closeWorkspaceDrawer() {
        const closedDrawer = this.activeWorkspaceDrawer;
        this.activeWorkspaceDrawer = '';
        if (closedDrawer === 'safety') this.activeSafetyDetailCode = '';
        if (this.soapPanelMode === 'assistant') this.soapPanelMode = 'soap';
        if (['checklist', 'assistant'].indexOf(closedDrawer) >= 0 && this.mobilePanel !== 'patient') {
            this.mobilePanel = 'soap';
        }
        await this.service.render();
        this.restoreOverlayTrigger();
        this.scheduleBottomBarClearance();
    }

    public workspaceDrawerIsOpen(drawer: string) {
        return this.activeWorkspaceDrawer === drawer;
    }

    public async openSafetyDetail(chip: WorkspaceMap) {
        const code = String(chip && chip.code || '').trim();
        if (['overview', 'allergy', 'medication', 'clinical_caution'].indexOf(code) < 0) return;
        this.captureOverlayTrigger();
        this.evidenceModalOpen = false;
        this.finalSaveModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        this.activeSafetyDetailCode = code;
        this.activeWorkspaceDrawer = 'safety';
        await this.service.render();
        this.focusActiveOverlay('workspace-drawer-safety');
        this.scheduleBottomBarClearance();
    }

    public async openChecklistFromSafetyDetail() {
        this.activeSafetyDetailCode = '';
        await this.openWorkspaceDrawer('checklist');
    }

    public async runSoapEntryPrimaryAction() {
        const action = this.soapEntryPrimaryActionCode;
        if (!this.canRunSoapEntryPrimaryAction) return;
        if (action === 'prepare') {
            await this.prepareSoapCandidate(false);
            return;
        }
        if (action === 'manual') {
            await this.createManualSoapDraft();
            return;
        }
        if (action === 'save') {
            await this.saveSoapDraft();
            return;
        }
        if (action === 'review') {
            await this.reviewSoapCandidateQueue();
        }
    }

    public async runSoapEntrySecondaryAction() {
        if (!this.canRunSoapEntrySecondaryAction) return;
        const action = this.soapEntryPrimaryActionCode;
        if (action === 'prepare') {
            await this.createManualSoapDraft();
            return;
        }
        if (action === 'manual' || action === 'wait' || action === 'none') {
            await this.loadContext(false);
            return;
        }
        if (action === 'save') {
            await this.validateSoapDraft(false);
            return;
        }
        if (action === 'review') {
            if (this.assistantUnappliedCandidateCount > 0 || this.soapPendingSuggestionCount > 0) {
                await this.openWorkspaceDrawer('assistant');
            } else {
                await this.createManualSoapDraft();
            }
        }
    }

    public async reviewSoapCandidateQueue() {
        if (this.soapCandidateQueueWorkCount > 0 || this.soapPendingSuggestionCount > 0) {
            await this.openCandidateQueueModal();
            return;
        }
        this.activeWorkspaceDrawer = '';
        this.soapPanelMode = 'soap';
        this.mobilePanel = 'soap';
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public async openEvidenceModalForResult(result: AiAssistantResult) {
        if (!result) return;
        this.captureOverlayTrigger();
        this.activeEvidenceTitle = result.title || this.clinicalRecordDisplayText(result.type) || '근거';
        this.activeEvidenceRows = this.evidenceItemsForResult(result);
        this.activeEvidenceSourceRefs = this.evidenceSourceRefsForResult(result);
        this.activeEvidenceDebugRefs = this.evidenceDebugRefsForResult(result);
        this.activeEvidenceReflectionLabel = this.evidenceReflectionLabelForResult(result);
        this.activeEvidenceTargetLabel = this.aiAssistantResultDestinationLabel(result);
        this.activeEvidenceContextLabel = result.summary || result.requestText || '';
        this.activeEvidenceAnatomicWarning = '';
        this.activeWorkspaceDrawer = '';
        this.soapPanelMode = 'soap';
        this.evidenceModalOpen = true;
        this.finalSaveModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        await this.service.render();
        this.focusActiveOverlay('evidence-modal');
    }

    public async openEvidenceModalForItem(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!item) return;
        this.captureOverlayTrigger();
        this.activeEvidenceTitle = item.title || (result && result.title) || '근거';
        this.activeEvidenceRows = this.evidenceItemsForItem(item);
        this.activeEvidenceSourceRefs = this.evidenceSourceRefsForItem(item);
        this.activeEvidenceDebugRefs = this.evidenceDebugRefsForItem(item);
        this.activeEvidenceReflectionLabel = this.evidenceReflectionLabelForItem(item);
        this.activeEvidenceTargetLabel = this.aiItemSoapTargetLabel(result, item);
        this.activeEvidenceContextLabel = item.description || item.evidence_summary || '';
        this.activeEvidenceAnatomicWarning = '';
        this.activeWorkspaceDrawer = '';
        this.soapPanelMode = 'soap';
        this.evidenceModalOpen = true;
        this.finalSaveModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        await this.service.render();
        this.focusActiveOverlay('evidence-modal');
    }

    public async openEvidenceModalForSoapSentence(item: SoapSentenceDraft) {
        if (!item) return;
        this.captureOverlayTrigger();
        const section = this.normalizedSoapSection(item.soap_section);
        this.activeEvidenceTitle = `${this.soapSectionDisplayName(section)} 문장 근거`;
        this.activeEvidenceRows = this.evidenceItemsForSoapSentence(item);
        this.activeEvidenceSourceRefs = this.evidenceSourceRefsForSoapSentence(item);
        this.activeEvidenceDebugRefs = this.evidenceDebugRefsForSoapSentence(item);
        this.activeEvidenceReflectionLabel = this.evidenceReflectionLabelForSoapSentence(item);
        this.activeEvidenceTargetLabel = this.soapSectionChipLabel(section);
        this.activeEvidenceContextLabel = this.truncateText(item.text || '', 140);
        this.activeEvidenceAnatomicWarning = '';
        this.activeWorkspaceDrawer = '';
        this.soapPanelMode = 'soap';
        this.evidenceModalOpen = true;
        this.finalSaveModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        await this.service.render();
        this.focusActiveOverlay('evidence-modal');
    }

    public async closeEvidenceModal() {
        this.evidenceModalOpen = false;
        this.activeEvidenceAnatomicWarning = '';
        await this.service.render();
        this.restoreOverlayTrigger();
    }

    public async openPatientSummaryModal() {
        this.captureOverlayTrigger();
        this.activeWorkspaceDrawer = '';
        this.evidenceModalOpen = false;
        this.finalSaveModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        this.patientSummaryModalOpen = true;
        await this.service.render();
        this.focusActiveOverlay('patient-summary-modal');
    }

    public async closePatientSummaryModal() {
        this.patientSummaryModalOpen = false;
        await this.service.render();
        this.restoreOverlayTrigger();
    }

    public async openFinalSaveModal() {
        this.captureOverlayTrigger();
        this.finalSaveModalOpen = true;
        this.evidenceModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        if (this.activeWorkspaceDrawer === 'assistant') this.soapPanelMode = 'soap';
        if (this.activeWorkspaceDrawer) this.activeWorkspaceDrawer = '';
        await this.service.render();
        this.focusActiveOverlay('final-save-modal');
    }

    public async openCandidateQueueModal() {
        this.captureOverlayTrigger();
        this.activeWorkspaceDrawer = '';
        this.soapPanelMode = 'soap';
        this.evidenceModalOpen = false;
        this.finalSaveModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.clinicalCandidateModalOpen = false;
        this.candidateQueueModalOpen = true;
        await this.service.render();
        this.focusActiveOverlay('candidate-queue-modal');
    }

    public async closeCandidateQueueModal() {
        this.candidateQueueModalOpen = false;
        await this.service.render();
        this.restoreOverlayTrigger();
    }

    public async openClinicalCandidateModal(row: SoapCandidateQueueRow, event?: Event) {
        if (!row) return;
        const trigger = event && event.currentTarget ? event.currentTarget as HTMLElement : null;
        if (trigger) trigger.focus();
        this.captureOverlayTrigger(trigger);
        this.activeWorkspaceDrawer = '';
        this.soapPanelMode = 'soap';
        this.evidenceModalOpen = false;
        this.finalSaveModalOpen = false;
        this.patientSummaryModalOpen = false;
        this.candidateQueueModalOpen = false;
        this.activeClinicalCandidateRow = row;
        this.activeClinicalCandidateEditText = this.candidateClinicalChartSentenceFull(row);
        this.clinicalCandidateModalOpen = true;
        await this.service.render();
        this.focusActiveOverlay('clinical-candidate-modal');
    }

    public async closeClinicalCandidateModal() {
        this.clinicalCandidateModalOpen = false;
        this.activeClinicalCandidateRow = null;
        this.activeClinicalCandidateEditText = '';
        await this.service.render();
        this.restoreOverlayTrigger();
    }

    public async setActiveClinicalCandidateEditText(value: string) {
        this.activeClinicalCandidateEditText = String(value || '');
        await this.service.render();
    }

    public get canApplyActiveClinicalCandidate() {
        return !!this.activeClinicalCandidateRow
            && this.canEditApplySoapCandidateQueueRow(this.activeClinicalCandidateRow)
            && this.activeClinicalCandidateEditText.trim() !== ''
            && !this.soapCandidateTextHasAnatomicConflict(
                this.activeClinicalCandidateRow,
                this.activeClinicalCandidateEditText
            );
    }

    public get activeClinicalCandidateAnatomicWarning() {
        if (
            !this.activeClinicalCandidateRow
            || !this.activeClinicalCandidateEditText.trim()
            || !this.soapCandidateTextHasAnatomicConflict(
                this.activeClinicalCandidateRow,
                this.activeClinicalCandidateEditText
            )
        ) return '';
        return '근거와 다른 신체 부위가 섞여 있습니다. 근거에 맞게 문장을 나누거나 수정해야 반영할 수 있습니다.';
    }

    public get canExcludeActiveClinicalCandidate() {
        return !!this.activeClinicalCandidateRow && this.canExcludeSoapCandidateQueueRow(this.activeClinicalCandidateRow);
    }

    public async applyActiveClinicalCandidate(edit: boolean) {
        const row = this.activeClinicalCandidateRow;
        if (!row || !this.canApplyActiveClinicalCandidate) return;
        const text = this.activeClinicalCandidateEditText.trim();
        await this.runSoapCandidateApply(row, async () => {
            if (this.isMcpCandidateRow(row)) {
                await this.applyMcpCandidateRow(row, text);
                return;
            }
            if (row.sentence) {
                const sentence = { ...row.sentence, text, dirty: edit || row.sentence.dirty || text !== row.sentence.text };
                await this.applySoapQueueSentence(sentence, edit || text !== row.sentence.text);
                return;
            }
            if (row.kind === 'soap_supplement' && row.item) {
                const item = { ...row.item, soap_sentence_text: text, description: text };
                await this.applySoapSupplementSuggestion(item, edit ? 'modify' : 'add');
                return;
            }
            if (row.kind === 'ai_item' && row.result && row.item) {
                const item = { ...row.item, soap_sentence_text: text, description: text };
                await this.applyAiItemQueueRowToChart(row.result, item);
                return;
            }
            await this.applySoapCandidateQueueRowAction(row, edit);
        });
        if (!this.error) {
            this.clinicalCandidateModalOpen = false;
            this.activeClinicalCandidateRow = null;
            this.activeClinicalCandidateEditText = '';
        }
    }

    public async excludeActiveClinicalCandidate() {
        const row = this.activeClinicalCandidateRow;
        if (!row || !this.canExcludeActiveClinicalCandidate) return;
        await this.excludeSoapCandidateQueueRow(row);
        this.clinicalCandidateModalOpen = false;
        this.activeClinicalCandidateRow = null;
        this.activeClinicalCandidateEditText = '';
    }

	    public async openAttentionCompactDetail() {
        if (this.headerBlockingCount > 0 || this.primaryFinalSaveWarningItems.length > 0) {
	            await this.openFinalSaveModal();
	            return;
	        }
	        if (this.hasUnresolvedRedFlags || this.checklistRiskOpenCount > 0) {
	            await this.openWorkspaceDrawer('checklist');
	            return;
	        }
	        await this.openWorkspaceDrawer('patient');
	    }

    public async closeFinalSaveModal() {
        this.finalSaveModalOpen = false;
        await this.service.render();
        this.restoreOverlayTrigger();
    }

    public async confirmFinalSaveModal() {
        this.finalError = '';
        if (this.finalSaveBlockingItems.length) {
            this.finalMessage = '';
            this.finalError = '더 작성할 내용을 보완한 뒤 다시 점검하세요.';
            await this.service.render();
            return;
        }
        if (this.finalGateConfirmableItems.length) {
            this.finalGateConfirmed = true;
            this.finalWarningsAcknowledged = true;
            this.finalMessage = '확인했습니다. 지금 최종 저장할 수 있습니다.';
        } else {
            this.finalWarningsAcknowledged = true;
            this.finalMessage = '최종 점검이 완료되었습니다. 지금 최종 저장할 수 있습니다.';
        }
        await this.service.render();
    }

    public async executeFinalSaveFromModal() {
        if (!this.finalSaveModalOpen) this.finalSaveModalOpen = true;
        await this.approveFinalChart();
    }

    public get busy() {
        return this.loading || this.saving || this.startingChecklist || this.savingChecklistAll || !!this.savingChecklistItemCode || this.summaryGenerating || this.soapGenerating || this.soapEntryPreparing || this.soapSaving || this.soapCandidateAppending || this.soapCreating || this.soapValidating || this.soapReadying || this.finalApproving || this.aiAssistantGenerating || !!this.standardToolStartingCode;
    }

    public get hasEncounter() {
        return !!this.encounter;
    }

    public get empty() {
        return !this.loading && !this.error && !this.encounter;
    }

    public get workspaceContext() {
        return {
            encounter_id: this.encounterId,
            encounter: this.encounter,
            patient: this.patient,
            precheck: this.precheck,
            summary: this.summary,
            checklist: this.checklist,
            soap: this.soap,
            final_chart: this.finalChart,
        };
    }

    public get summaryFacts() {
        return this.asArray(this.summary.facts);
    }

    public get encounterFacts() {
        return this.asArray(this.summary.encounter_facts);
    }

    public get summarySections() {
        return this.asArray(this.summary.sections);
    }

    public get patientDisplayName() {
        return this.clinicalPersonName(this.patient && this.patient.display_name, '환자 미확인');
    }

    public get patientNumber() {
        return this.visibleClinicalIdentifier(this.patient && this.patient.patient_no, '환자번호 미표시');
    }

    public get patientBirthSex() {
        const birth = this.displayValue(this.patient && this.patient.birth_date, '생년월일 없음');
        const sex = this.sexLabel(this.patient && this.patient.sex_code);
        return `${birth} / ${sex}`;
    }

    public get encounterNumber() {
        return this.visibleClinicalIdentifier(this.encounter && this.encounter.encounter_no, '방문번호 미표시');
    }

    public get scheduledAt() {
        return this.displayValue(this.encounter && this.encounter.scheduled_at, '예약일시 없음');
    }

    public get assignedPerson() {
        return this.clinicalPersonName(this.encounter && this.encounter.assigned_doctor_name, '담당자 없음');
    }

    public get roomCode() {
        return this.displayValue(this.encounter && this.encounter.room_code, '진료실 미정');
    }

    public get canViewDebugDetails() {
        return this.queryValue('debug') === '1' || this.isDevMode();
    }

	    public get finalChartSummaryText() {
	        if (this.finalApprovedAt) return `최종 저장 ${this.finalApprovedAt}`;
	        if (this.readOnlyMode) return '최종 저장됨';
	        if (this.finalGateBlockingItems.length) return `더 작성할 내용 ${this.finalGateBlockingItems.length}건`;
	        if (this.finalGateConfirmableItems.length) return `확인만 하면 되는 내용 ${this.finalGateConfirmableItems.length}건`;
	        return '최종 저장 전';
	    }

	    public get finalReadinessDescription() {
	        if (this.readOnlyMode) return '최종 저장됨';
	        if (this.finalGateBlockingItems.length) return '더 작성할 내용';
	        if (this.finalGateConfirmableItems.length) return '확인만 하면 되는 내용';
	        return '최종 점검';
	    }

    public get serverAutoChartingState() {
        const contextState = this.data && (this.data.auto_charting || this.data.charting_state);
        const soapState = this.soap && (this.soap.auto_charting_state || this.soap.charting_state);
        if (contextState && typeof contextState === 'object') return contextState;
        if (soapState && typeof soapState === 'object') return soapState;
        return {};
    }

    public get autoChartingStateCode() {
        if (this.readOnlyMode) return 'final_saved';
        if (this.soapGenerating || this.soapCreating) return 'candidate_preparing';
        if (this.hasSoapDirty || this.soapDirtySentenceCount > 0) return 'needs_temp_save';
        const code = String(this.serverAutoChartingState.code || '').trim();
        if (code) return code;
        if (!this.hasSoapDraft) return 'no_candidate';
        if (this.soapNeedsRegeneration) return 'needs_regeneration';
        if (this.canApproveFinal) return 'ready_for_final_save';
        if (this.candidateNewReviewCount > 0) return 'pending_apply';
        return 'applied';
    }

    public get autoChartingStateLabel() {
        return this.statusLabel(this.autoChartingStateCode);
    }

    public get autoChartingStateDescription() {
        const descriptions: WorkspaceMap = {
            no_candidate: '작성 전',
            candidate_preparing: '진료 내용 정리 중',
            candidate_ready: '참고 내용 확인',
                new_candidate_ready: `진료 참고 내용 ${this.soapNewCandidateReadyCount || 0}건`,
            previous_version: `이전 문장 ${this.soapPreviousVersionCount || 0}`,
                pending_apply: `진료 참고 내용 ${this.candidateNewReviewCount || 0}건`,
            needs_regeneration: this.soapRegenerationText,
            applied: this.candidateChartAppliedCount > 0 ? `차트 반영 완료 ${this.candidateChartAppliedCount}문장` : '차트 반영 완료',
            deferred: '나중에 볼 항목',
            excluded: '사용 안 함',
            needs_temp_save: `저장 필요 ${this.soapDirtySentenceCount}`,
            ready_for_final_save: '최종 저장 가능',
            final_saved: this.finalChartSummaryText,
        };
        return descriptions[this.autoChartingStateCode] || this.statusLabel(this.autoChartingStateCode);
    }

    public get autoChartingStateClass() {
        const state = this.serverAutoChartingState;
        const tones: WorkspaceMap = {
            no_candidate: 'neutral',
            candidate_preparing: 'ai',
            candidate_ready: 'ai',
            new_candidate_ready: 'warning',
            previous_version: 'neutral',
            pending_apply: 'warning',
            needs_regeneration: 'pending',
            applied: 'success',
            deferred: 'pending',
            excluded: 'danger',
            needs_temp_save: 'warning',
            ready_for_final_save: 'primary',
            final_saved: 'success',
        };
        const tone = state && state.code === this.autoChartingStateCode && state.tone
            ? state.tone
            : (tones[this.autoChartingStateCode] || 'neutral');
        return `status-chip status-chip--${tone}`;
    }

    public get soapEntryPrepareState() {
        if (this.soapEntryPreparing) {
            return {
                status: 'candidate_preparing',
                state: 'preparing',
                manual_available: true,
                doctor_action_required: 'wait',
            };
        }
        const run = this.soapEntryPrepareRun && Object.keys(this.soapEntryPrepareRun).length ? this.soapEntryPrepareRun : null;
        if (run && (run.status || run.state)) return run;
        const contextState = this.data && this.data.soap_entry_prepare ? this.data.soap_entry_prepare : null;
        if (contextState && (contextState.status || contextState.state)) return contextState;
        const soapState = this.soap && this.soap.entry_prepare ? this.soap.entry_prepare : null;
        if (soapState && (soapState.status || soapState.state)) return soapState;
        return {};
    }

    public get soapEntryPrepareAttemptKey() {
        const state = this.soapEntryPrepareState;
        const idempotency = state && state.idempotency ? state.idempotency : {};
        return idempotency.key || `${this.encounterId || 'encounter'}:${state.status || state.state || 'entry'}`;
    }

    public get canPrepareSoapEntryCandidate() {
        const state = this.soapEntryPrepareState;
        return !this.clinicalReadOnly
            && this.canGenerateClinicalAi
            && !this.soapEntryPreparing
            && !this.soapGenerating
            && (
                state.can_generate === true
                || state.doctor_action_required === 'prepare_candidate'
                || state.status === 'no_candidate'
                || (
                    this.serverAutoChartingState
                    && this.serverAutoChartingState.can_auto_prepare === true
                    && state.doctor_action_required !== 'regenerate_or_edit'
                )
            );
    }

    public get soapEntryPrepareStatusCode() {
        const state = this.soapEntryPrepareState;
        const status = String(state.status || '');
        const rawState = String(state.state || '');
        if (this.soapEntryPreparing || this.soapGenerating || this.soapCreating) return 'candidate_preparing';
        if (this.soapNeedsRegeneration || status === 'needs_regeneration' || rawState === 'needs_regeneration') return 'needs_regeneration';
        if (this.soapNewCandidateReadyCount > 0) return 'new_candidate_ready';
        if (this.candidateNewReviewCount > 0 || status === 'pending_apply') return 'pending_apply';
        if (status === 'no_candidate' || rawState === 'no_candidate') return 'no_candidate';
        if (['insufficient_input', 'snapshot_failed'].indexOf(status) >= 0 || rawState === 'manual_only') return 'insufficient_input';
        if (['config_missing', 'policy_blocked', 'previous_generation_failed', 'provider_failed', 'failed'].indexOf(status) >= 0 || rawState === 'failed') return 'manual_available';
        if (status === 'candidate_ready' || state.existing_candidate === true || this.hasSoapDraft) return 'candidate_ready';
        if (this.canPrepareSoapEntryCandidate) return 'manual_available';
        if (state.manual_available === true) return 'manual_available';
        return 'manual_available';
    }

    public get soapEntryPrepareStatusLabel() {
        const labels: WorkspaceMap = {
            candidate_preparing: '진료 내용 정리 중',
            new_candidate_ready: '진료 참고 내용',
            candidate_ready: '참고 내용 확인',
            pending_apply: '진료 참고 내용',
            needs_regeneration: '최신 진료 내용 확인',
            no_candidate: '작성 전',
            insufficient_input: '예진/문진을 확인해 주세요',
            manual_available: '직접 작성 가능',
        };
        return labels[this.soapEntryPrepareStatusCode] || '직접 작성 가능';
    }

    public get soapEntryPrepareTone() {
        const tones: WorkspaceMap = {
            candidate_preparing: 'ai',
            new_candidate_ready: 'warning',
            candidate_ready: 'success',
            pending_apply: 'warning',
            needs_regeneration: 'pending',
            no_candidate: 'neutral',
            insufficient_input: 'pending',
            manual_available: 'neutral',
        };
        return tones[this.soapEntryPrepareStatusCode] || 'neutral';
    }

    public get soapEntryPrepareStateClass() {
        return `status-chip status-chip--${this.soapEntryPrepareTone}`;
    }

    public get soapEntryPrepareTitle() {
        const pending = this.candidateNewReviewCount;
        const titles: WorkspaceMap = {
            candidate_preparing: '진료 내용 정리 중',
            new_candidate_ready: `진료 참고 내용 ${this.soapNewCandidateReadyCount}건`,
            candidate_ready: '참고 내용 확인',
            pending_apply: `진료 참고 내용 ${pending}건`,
            needs_regeneration: '최신 진료 내용 확인',
            no_candidate: '준비 가능',
            insufficient_input: '예진·요약을 확인해 주세요',
            manual_available: '직접 작성 가능',
        };
        return titles[this.soapEntryPrepareStatusCode] || '직접 작성 가능';
    }

    public get soapEntryPrepareDescription() {
        const descriptions: WorkspaceMap = {
            candidate_preparing: '진료 내용 정리 중',
            new_candidate_ready: '진료 참고 내용',
            candidate_ready: '참고 내용 확인',
            pending_apply: '진료 참고 내용',
            needs_regeneration: '최신 진료 내용 확인',
            no_candidate: '준비 가능',
            insufficient_input: '입력 부족',
            manual_available: '직접 작성 가능',
        };
        return descriptions[this.soapEntryPrepareStatusCode] || '직접 작성 가능';
    }

    public get soapEntryPrepareInputLabel() {
        const readiness = this.soapEntryPrepareState && this.soapEntryPrepareState.readiness ? this.soapEntryPrepareState.readiness : {};
        if (this.soapEntryPrepareStatusCode === 'needs_regeneration') return '최신 진료 내용';
        if (readiness.minimum_input_ready === true || readiness.ready === true) return '입력 충분';
        if (this.soapEntryPrepareStatusCode === 'insufficient_input') return '입력 부족';
        return '입력 확인';
    }

    public get soapEntryPrepareQueueMeta() {
        const parts = [
            `확인 전 ${this.soapUnreviewedAiCount}`,
            `차트 보완 ${this.soapPendingSuggestionCount}`,
            `진료 전 확인 ${this.checklistSoapQueueRequiredCount}`,
            `저장 ${this.soapDirtySentenceCount}`,
        ];
        return parts.join(' · ');
    }

    public get soapCandidateQueueItems(): SoapCandidateQueueItem[] {
        const items: SoapCandidateQueueItem[] = [
            {
                label: '참고 내용 정리',
                value: this.soapEntryPrepareStatusLabel,
                meta: this.soapEntryPrepareInputLabel,
                tone: this.soapEntryPrepareTone,
            },
            {
                label: '진료 전 확인 반영',
                value: this.checklistSoapQueueValue,
                meta: this.checklistSoapQueueMeta,
                tone: this.checklistSoapQueueTone,
            },
            {
            label: '진료 참고 내용',
            value: this.candidateNewReviewCount > 0 ? `${this.candidateNewReviewCount}건` : '없음',
                meta: this.candidateNewReviewCount > 0 ? '차트에 넣기 전' : '새로 볼 내용 없음',
                tone: this.candidateNewReviewCount > 0 ? 'warning' : 'neutral',
            },
            {
                label: '편집 상태',
                value: this.soapDirtySentenceCount > 0 ? `${this.soapDirtySentenceCount}건` : `${this.soapActiveSentenceCount}문장`,
                meta: this.soapDirtySentenceCount > 0 ? '저장 필요' : '저장됨',
                tone: this.soapDirtySentenceCount > 0 ? 'warning' : 'neutral',
            },
        ];
        return items;
    }

    public soapCandidateQueueItemClass(item: SoapCandidateQueueItem) {
        return `candidate-queue-card candidate-queue-card--${item && item.tone ? item.tone : 'neutral'}`;
    }

    public get soapCandidateQueueGroups(): SoapCandidateQueueGroup[] {
        if (this.soapCandidateQueueBuiltRevision === this.soapCandidateQueueRevision) return this.soapCandidateQueueGroupCache;
        const rows = this.soapCandidateQueueRows;
        this.soapCandidateQueueGroupCache = this.soapCandidateQueueStatusDefs.map((definition) => {
            const groupRows = this.sortSoapCandidateRowsByClinicalPriority(rows.filter((row) => row.status === definition.code));
            return {
                code: definition.code,
                label: definition.label,
                description: definition.description,
                count: groupRows.length,
                tone: definition.tone,
                open: ['new_candidate_ready', 'pending', 'stale'].indexOf(definition.code) >= 0 && groupRows.length > 0,
                latestLabel: groupRows.length ? (groupRows[0].updated || '최근') : '없음',
                rows: groupRows,
            };
        });
        this.soapCandidateQueueBuiltRevision = this.soapCandidateQueueRevision;
        return this.soapCandidateQueueGroupCache;
    }

    private markSoapCandidateQueueDirty() {
        this.soapCandidateQueueRevision += 1;
    }

    private prependAiAssistantResult(result: AiAssistantResult, limit: number = 0) {
        const rows = [result].concat(this.aiAssistantResults);
        this.aiAssistantResults = limit > 0 ? rows.slice(0, limit) : rows;
        this.resetCoreSoapCandidateBatch();
        this.markSoapCandidateQueueDirty();
        this.triggerCandidatePulse(this.asArray(result && result.items).length || 1);
    }

    private prependConversationClinicalResult(result: AiAssistantResult, limit: number = 0) {
        const rows = [result].concat(this.aiAssistantResults.filter((existing) => {
            if (!this.isConversationClinicalResult(existing)) return true;
            return this.conversationResultHasOpenItems(existing);
        }));
        this.aiAssistantResults = limit > 0 ? rows.slice(0, limit) : rows;
        this.resetCoreSoapCandidateBatch();
        this.markSoapCandidateQueueDirty();
        this.triggerCandidatePulse(this.asArray(result && result.items).length || 1);
    }

    private resetCoreSoapCandidateBatch() {
        this.coreSoapCandidateBatchInitialized = false;
        this.coreSoapCandidateVisibleKeys = [];
    }

    private conversationResultHasOpenItems(result: AiAssistantResult) {
        return this.asArray(result && result.items).some((item) => {
            const status = String((item && item.decision_status) || 'pending_doctor_review');
            return ['ignored', 'excluded', 'deferred', 'added', 'modified', 'soap_candidate_sent', 'referenced', 'edit_staged'].indexOf(status) === -1;
        });
    }

    public get mainSoapCandidateQueueGroups(): SoapCandidateQueueGroup[] {
        const rows = this.coreSoapCandidateQueueRows;
        if (!rows.length) return [];
        return [{
            code: rows[0].status,
            label: '우선 확인',
            description: '우선순위',
            count: rows.length,
            tone: rows[0].tone || 'warning',
            open: true,
            latestLabel: rows[0].updated || '최근',
            rows,
        }];
    }

    public get candidateQueueModalGroups(): SoapCandidateQueueGroup[] {
        return this.actionableSoapCandidateQueueGroups;
    }

    public get actionableSoapCandidateQueueGroups(): SoapCandidateQueueGroup[] {
        return this.soapCandidateQueueGroups.filter((group) => this.actionableSoapCandidateStatuses.indexOf(group.code) >= 0 && group.count > 0);
    }

    public get coreSoapCandidateQueueRows(): SoapCandidateQueueRow[] {
        const rows = this.sortSoapCandidateRowsByClinicalPriority(
            this.soapCandidateQueueRows.filter((row) => this.isActionableSoapCandidateRow(row))
        );
        if (!this.coreSoapCandidateBatchInitialized && rows.length > 0) {
            this.coreSoapCandidateVisibleKeys = rows
                .slice(0, this.coreSoapCandidateLimit)
                .map((row) => this.soapCandidateQueueStableKey(row));
            this.coreSoapCandidateBatchInitialized = true;
        }
        const rowByKey: Record<string, SoapCandidateQueueRow> = {};
        rows.forEach((row) => {
            rowByKey[this.soapCandidateQueueStableKey(row)] = row;
        });
        return this.coreSoapCandidateVisibleKeys
            .map((key) => rowByKey[key])
            .filter((row) => !!row)
            .slice(0, this.coreSoapCandidateLimit);
    }

    public get hiddenSoapCandidateQueueCount() {
        return Math.max(0, this.soapCandidateQueueWorkCount - this.coreSoapCandidateQueueRows.length);
    }

    public get coreCandidateQueueFocusLabel() {
        const total = this.soapCandidateQueueWorkCount;
        const visible = this.coreSoapCandidateQueueRows.length;
        if (total > visible) return `미검토 전체 ${total}건 · 현재 ${visible}건`;
        if (total > 0) return `미검토 전체 ${total}건`;
        return '새로 참고할 내용 없음';
    }

    public get coreCandidateQueueProgressLabel() {
        const remaining = this.soapCandidateQueueWorkCount;
        if (this.coreCandidateBatchAppliedCount > 0) {
            return `이번 진료 ${this.coreCandidateBatchAppliedCount}건 반영 완료 · ${remaining}건 남음`;
        }
        return `${remaining}건 중 우선순위가 높은 내용부터 표시합니다.`;
    }

    public get nextCoreCandidateLabel() {
        return `다음 추천 보기 · ${this.hiddenSoapCandidateQueueCount}건 남음`;
    }

    public async showNextCoreSoapCandidates() {
        if (this.clinicalReadOnly || this.hiddenSoapCandidateQueueCount <= 0) return;
        const rows = this.sortSoapCandidateRowsByClinicalPriority(
            this.soapCandidateQueueRows.filter((row) => this.isActionableSoapCandidateRow(row))
        );
        const rowByKey: Record<string, SoapCandidateQueueRow> = {};
        rows.forEach((row) => {
            rowByKey[this.soapCandidateQueueStableKey(row)] = row;
        });
        const visibleKeys = this.coreSoapCandidateVisibleKeys.filter((key) => !!rowByKey[key]);
        const nextKeys = rows
            .map((row) => this.soapCandidateQueueStableKey(row))
            .filter((key) => visibleKeys.indexOf(key) === -1);
        const nextVisibleKeys = visibleKeys.concat(nextKeys).slice(0, this.coreSoapCandidateLimit);
        const addedCount = Math.max(0, nextVisibleKeys.length - visibleKeys.length);
        this.coreSoapCandidateVisibleKeys = nextVisibleKeys;
        this.coreSoapCandidateBatchInitialized = true;
        this.markSoapCandidateQueueDirty();
        if (addedCount > 0) this.showWorkspaceFeedback(`다음 추천 ${addedCount}건을 표시했습니다.`, 'info');
        await this.service.render();
    }

    public get candidateQueuePanelSummary() {
        return this.candidateNewReviewCount > 0 ? `진료 참고 내용 ${this.candidateNewReviewCount}건` : '새로 참고할 내용 없음';
    }

    public get soapCandidateQueueWorkCount() {
        return this.soapCandidateQueueGroups
            .filter((group) => ['new_candidate_ready', 'pending', 'stale'].indexOf(group.code) >= 0)
            .reduce((total, group) => total + group.count, 0);
    }

    public get soapCandidateQueueDoneCount() {
        const group = this.soapCandidateQueueGroups.find((item) => item.code === 'applied');
        return group ? group.count : 0;
    }

    public get soapCandidateQueueHeldCount() {
        return this.soapCandidateQueueGroups
            .filter((group) => ['deferred', 'excluded'].indexOf(group.code) >= 0)
            .reduce((total, group) => total + group.count, 0);
    }

    public get soapCandidateQueueFocusLabel() {
        const fresh = this.soapCandidateQueueGroups.find((item) => item.code === 'new_candidate_ready');
        const stale = this.soapCandidateQueueGroups.find((item) => item.code === 'stale');
        const pending = this.soapCandidateQueueGroups.find((item) => item.code === 'pending');
        if (fresh && fresh.count > 0) return `진료 참고 내용 ${fresh.count}건`;
        if (stale && stale.count > 0) return `진료 참고 내용 ${stale.count}건`;
        if (pending && pending.count > 0) return `진료 참고 내용 ${pending.count}건`;
        return '새로 참고할 내용 없음';
    }

    public get soapCandidateQueueLatestLabel() {
        const rows = this.soapCandidateQueueRows;
        if (!rows.length) return '새로 참고할 내용 없음';
        return rows[0].updated || '최근';
    }

    public soapCandidateQueueGroupClass(group: { tone?: string }) {
        const tone = group && group.tone ? group.tone : 'neutral';
        return `candidate-queue-group candidate-queue-group--${tone}`;
    }

    public soapCandidateQueueRowClass(row: SoapCandidateQueueRow) {
        const tone = row && row.tone ? row.tone : 'neutral';
        const applying = this.isApplyingSoapCandidateQueueRow(row) ? ' is-applying' : '';
        return `candidate-queue-row candidate-queue-row--${tone}${applying}`;
    }

    public soapCandidateQueuePreviewRows(group: SoapCandidateQueueGroup) {
        return group && Array.isArray(group.rows) ? group.rows.slice(0, 3) : [];
    }

    public soapCandidateQueueOverflowCount(group: SoapCandidateQueueGroup) {
        if (!group || !Array.isArray(group.rows)) return 0;
        return Math.max(0, group.rows.length - 3);
    }

    private isActionableSoapCandidateRow(row: SoapCandidateQueueRow) {
        return !!row && this.actionableSoapCandidateStatuses.indexOf(row.status) >= 0;
    }

    private sortSoapCandidateRowsByClinicalPriority(rows: SoapCandidateQueueRow[]) {
        return (Array.isArray(rows) ? rows.slice() : []).sort((left, right) => {
            const leftRank = this.candidateClinicalPriorityRank(left);
            const rightRank = this.candidateClinicalPriorityRank(right);
            if (leftRank !== rightRank) return leftRank - rightRank;

            const leftStatus = this.candidateStatusPriority(left);
            const rightStatus = this.candidateStatusPriority(right);
            if (leftStatus !== rightStatus) return leftStatus - rightStatus;

            const leftEvidence = this.soapCandidateQueueEvidenceCount(left);
            const rightEvidence = this.soapCandidateQueueEvidenceCount(right);
            if (leftEvidence !== rightEvidence) return rightEvidence - leftEvidence;

            return (right && right.sortKey ? right.sortKey : 0) - (left && left.sortKey ? left.sortKey : 0);
        });
    }

    private candidateStatusPriority(row: SoapCandidateQueueRow) {
        if (!row) return 99;
        if (row.status === 'new_candidate_ready') return 1;
        if (row.status === 'stale') return 2;
        if (row.status === 'pending') return 3;
        return 9;
    }

    private candidateClinicalPriorityRank(row: SoapCandidateQueueRow) {
        if (!row) return 99;
        const text = this.candidateClinicalPriorityText(row);
        const section = this.normalizedSoapSection(row.section || 'S');
        const safetyKeywords = [
            '안전', '위험', '주의', '레드플래그', '금기', '알레르기', '복약', '약물', '임신', '항응고',
            '감염', '발열', '야간통', '외상', '마비', '근력', '감각', '저림', '대소변', '배뇨', '방광', '응급',
        ];
        const patternKeywords = [
            '변증', '진단', '병증', '감별', '한열', '허실', '어혈', '혈어', '한습', '담음', '담습',
            '기체', '비위', '간신', '습열', '체질', '패턴',
        ];
        const treatmentKeywords = [
            '치료', '처방', '치료/처방', '치료 방향', '침', '뜸', '부항', '약침', '한약', '복약', '시술', '계획',
        ];

        if (this.candidateTextHasAny(text, safetyKeywords)) return 1;
        if (this.conversationQueueRowIsDirectChartCandidate(row)) {
            if (section === 'O') return 2;
            if (section === 'S' || section === 'A') return 3;
            if (section === 'P') return 4;
        }
        if (section === 'A' || row.kind === 'pattern_candidate' || this.candidateTextHasAny(text, patternKeywords)) return 4;
        if (section === 'P' || this.candidateTextHasAny(text, treatmentKeywords)) return 5;
        return 6;
    }

    private conversationQueueRowIsDirectChartCandidate(row: SoapCandidateQueueRow) {
        return !!(row
            && row.kind === 'ai_item'
            && row.result
            && this.isConversationClinicalResult(row.result)
            && row.item
            && row.item.kind === 'conversation_chart_candidate');
    }

    private candidateClinicalPriorityText(row: SoapCandidateQueueRow) {
        const parts = [
            row.title,
            row.text,
            row.target,
            row.source,
            row.meta,
            row.section,
        ];
        if (row.item) {
            parts.push(
                row.item.kind,
                row.item.title,
                row.item.description,
                row.item.reason,
                row.item.evidence_summary,
                row.item.answer_target,
                row.item.display_name,
                this.asArray(row.item.suggestion_reasons).join(' '),
                this.asArray(row.item.source_labels).join(' '),
                this.asArray(row.item.supporting_evidence).join(' '),
                this.asArray(row.item.opposing_evidence).join(' ')
            );
        }
        if (row.sentence) {
            const meta = row.sentence.sentence_meta && typeof row.sentence.sentence_meta === 'object' ? row.sentence.sentence_meta : {};
            const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
            parts.push(
                row.sentence.text,
                row.sentence.author_type,
                row.sentence.candidate_source,
                meta.source,
                meta.pattern_code,
                meta.display_name,
                payload.pattern_code,
                payload.display_name,
                this.asArray(payload.supporting_evidence).join(' '),
                this.asArray(payload.opposing_evidence).join(' ')
            );
        }
        return this.normalizeLookupLabel(parts.filter((value) => value !== undefined && value !== null).join(' '));
    }

    public candidateClinicalPriorityLabel(row: SoapCandidateQueueRow) {
        const rank = this.candidateClinicalPriorityRank(row);
        if (rank === 1) return '안전 확인';
        if (rank === 2) return '변증/진단';
        if (rank === 3) return '치료 전 확인';
        return '차트 반영';
    }

    public soapCandidateQueueEvidenceCount(row: SoapCandidateQueueRow) {
        if (!row) return 0;
        if (row.sentence) return this.evidenceCountForSoapSentence(row.sentence);
        if (row.item) return this.evidenceCountForItem(row.item);
        if (row.result) return this.evidenceCountForResult(row.result);
        return 0;
    }

    public candidateClinicalEvidenceSummaryLabel(row: SoapCandidateQueueRow) {
        const count = this.soapCandidateQueueEvidenceCount(row);
        const counterCount = this.clinicalCandidateOpposingItems(row).length;
        const followupCount = this.clinicalCandidateConfirmationItems(row).length;
        const parts = [
            count > 0 ? `단서 ${count}` : '단서 확인',
            counterCount > 0 ? `불확실 ${counterCount}` : '',
            followupCount > 0 ? `확인 ${followupCount}` : '',
        ].filter((value) => value !== '');
        return parts.join(' · ');
    }

    public clinicalCandidateTypeLabel(row: SoapCandidateQueueRow) {
        if (!row) return '진료 참고 내용';
        const section = this.normalizedSoapSection(row.section || 'S');
        const text = this.candidateClinicalPriorityText(row);
        if (this.candidateClinicalPriorityRank(row) === 1 || this.candidateTextHasAny(text, ['확인', '레드플래그', '복약', '알레르기', '금기', '저림', '감각', '근력', '대소변'])) {
            return '추가 확인 포인트';
        }
        if (this.candidateClinicalIsPattern(row) || section === 'A') return '변증/진단 가능성';
        if (section === 'P') return '치료 방향 참고';
        return '증상 해석';
    }

    public clinicalCandidateStatusLabel(row: SoapCandidateQueueRow) {
        if (!row) return '한의사 판단 전';
        if (this.soapCandidateQueueRowIsUpdated(row) && ['new_candidate_ready', 'pending', 'stale'].indexOf(row.status) >= 0) return '최신 내용 확인';
        const labels: Record<string, string> = {
            new_candidate_ready: '한의사 판단 전',
            previous_version: '이전 문장',
            pending: '한의사 판단 전',
            stale: '최신 내용 확인',
            applied: '차트 반영 완료',
            deferred: '나중에 보기',
            excluded: '사용 안 함',
        };
        return labels[row.status] || this.statusLabel(row.status);
    }

    private soapCandidateQueueRowMeta(row: SoapCandidateQueueRow) {
        return row && row.sentence && row.sentence.sentence_meta && typeof row.sentence.sentence_meta === 'object'
            ? row.sentence.sentence_meta
            : {};
    }

    private soapCandidateQueueRowIsUpdated(row: SoapCandidateQueueRow) {
        const meta = this.soapCandidateQueueRowMeta(row);
        return meta.updated_candidate === true
            || meta.candidate_merge_policy === 'semantic_update'
            || Number(meta.merged_candidate_count || 0) > 0;
    }

    public clinicalCandidateRecommendationReason(row: SoapCandidateQueueRow) {
        if (!row) return '판단 이유를 더 확인해 주세요';
        const hintText = this.candidateClinicalHintText(row);
        const core = this.cleanCandidateClinicalPreview(this.candidateClinicalCoreEvidence(row), 140);
        const parts = [
            hintText ? hintText : '',
            core && core !== hintText && core !== '근거를 더 확인해 주세요' ? core : '',
        ];
        return this.joinSentenceParts(parts) || '환자 단서와 차트 반영 위치를 함께 확인합니다.';
    }

    public clinicalCandidateReasonItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        if (row && row.item) {
            this.asArray(row.item.suggestion_reasons).forEach((value) => {
                const text = this.clinicalCandidateMeaningfulText(value, 120);
                if (text) rows.push(text);
            });
            const reason = this.clinicalCandidateMeaningfulText(row.item.reason || row.item.description || row.item.evidence_summary || '', 140);
            if (reason) rows.push(reason);
        }
        if (row && row.result && row.result.summary) {
            const summary = this.clinicalCandidateMeaningfulText(row.result.summary, 140);
            if (summary) rows.push(summary);
        }
        if (!rows.length) rows.push(this.candidateClinicalReason(row));
        return this.dedupeStrings(rows).slice(0, 6);
    }

    public clinicalCandidateSupportingItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        const payload = this.clinicalCandidateStructuredPayload(row);
        this.asArray(payload.supporting_evidence).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
        if (row && row.item) {
            this.asArray(row.item.supporting_evidence).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
            const evidence = this.clinicalRecordDisplayText(row.item.evidence_summary || row.item.reason || '');
            if (evidence) rows.push(evidence);
        }
        if (row && row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) {
            const payload = this.patternPayloadFromSoapSentence(row.sentence);
            this.asArray(payload.supporting_evidence).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
            if (payload.evidence_summary) rows.push(this.clinicalRecordDisplayText(payload.evidence_summary));
        }
        if (!rows.length && row) rows.push(this.candidateClinicalCoreEvidence(row));
        return this.dedupeStrings(rows.filter((value) => value !== '')).slice(0, 6);
    }

    public clinicalCandidateOpposingItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        const payload = this.clinicalCandidateStructuredPayload(row);
        this.asArray(payload.opposing_evidence).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
        if (row && row.item) {
            this.asArray(row.item.opposing_evidence).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
        }
        if (row && row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) {
            const payload = this.patternPayloadFromSoapSentence(row.sentence);
            this.asArray(payload.opposing_evidence).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
        }
        return this.dedupeStrings(rows.filter((value) => value !== '')).slice(0, 6);
    }

    public clinicalCandidateConfirmationItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        const payload = this.clinicalCandidateStructuredPayload(row);
        this.asArray(payload.followup_questions).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
        if (row && row.item) {
            this.asArray(row.item.followup_questions).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
        }
        if (row && row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) {
            const payload = this.patternPayloadFromSoapSentence(row.sentence);
            this.asArray(payload.followup_questions).forEach((value) => rows.push(this.clinicalRecordDisplayText(value)));
        }
        if (!rows.length && row && this.clinicalCandidateTypeLabel(row) === '추가 확인 포인트') {
            rows.push(this.candidateClinicalTitle(row));
        }
        return this.dedupeStrings(rows.filter((value) => value !== '')).slice(0, 6);
    }

    public clinicalCandidateUncertaintyItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        this.clinicalCandidateOpposingItems(row).forEach((value) => {
            const text = this.cleanCandidateClinicalPreview(value, 110);
            if (text) rows.push(`불확실: ${text}`);
        });
        this.clinicalCandidateConfirmationItems(row).forEach((value) => {
            const text = this.cleanCandidateClinicalPreview(value, 110);
            if (text) rows.push(`추가 확인: ${text}`);
        });
        if (!rows.length && row && row.status === 'stale') rows.push('최신 진료 내용 반영 여부 확인');
        return this.dedupeStrings(rows).slice(0, 8);
    }

    public clinicalCandidateAdditionalCheckItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        this.clinicalCandidateConfirmationItems(row).forEach((value) => {
            const text = this.cleanCandidateClinicalPreview(value, 120);
            if (text) rows.push(text);
        });
        this.clinicalCandidateOpposingItems(row).forEach((value) => {
            const text = this.cleanCandidateClinicalPreview(value, 120);
            if (text) rows.push(`반대/불확실 근거: ${text}`);
        });
        if (row && row.status === 'stale') rows.push('최신 대화·진찰 내용과 일치하는지 확인');
        if (!rows.length && row && this.clinicalCandidateTypeLabel(row) === '치료 방향 참고') rows.push('복약·알레르기·시술 금기 확인');
        if (!rows.length) rows.push('추가 문진·진찰 확인');
        return this.dedupeStrings(rows).slice(0, 4);
    }

    public clinicalCandidatePatientCueItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        const payload = this.clinicalCandidateStructuredPayload(row);
        this.asArray(payload.patient_cues).forEach((value) => rows.push(this.clinicalCandidateMeaningfulText(value, 130)));
        if (row && row.item) {
            this.asArray((row.item as any).patient_cues).forEach((value) => rows.push(this.clinicalCandidateMeaningfulText(value, 130)));
            this.asArray(row.item.supporting_evidence).forEach((value) => rows.push(this.clinicalCandidateMeaningfulText(value, 130)));
            const evidence = this.clinicalCandidateMeaningfulText(row.item.clinical_evidence || row.item.evidence_summary || '', 130);
            if (evidence) rows.push(evidence);
            this.asArray(row.item.source_refs).forEach((ref) => {
                const value = ref && typeof ref === 'object' ? (ref.value || ref.text || ref.display_text || ref.note) : ref;
                const text = this.clinicalCandidateMeaningfulText(value, 130);
                if (text) rows.push(text);
            });
        }
        if (row && row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) {
            this.asArray(payload.supporting_evidence).forEach((value) => rows.push(this.clinicalCandidateMeaningfulText(value, 130)));
            if (payload.evidence_summary) rows.push(this.clinicalCandidateMeaningfulText(payload.evidence_summary, 130));
        }
        const conversation = this.conversationCandidateSourcePayload(row);
        if (conversation.source_excerpt) rows.push(this.clinicalCandidateMeaningfulText(conversation.source_excerpt, 130));
        if (!rows.length && row) rows.push(this.clinicalCandidateMeaningfulText(this.candidateClinicalCoreEvidence(row), 130));
        return this.dedupeStrings(rows.filter((value) => value !== '')).slice(0, 4);
    }

    public clinicalCandidateConnectionItems(row: SoapCandidateQueueRow) {
        const rows: string[] = [];
        const payload = this.clinicalCandidateStructuredPayload(row);
        this.asArray(payload.connection_reasons).forEach((value) => rows.push(this.clinicalCandidateMeaningfulText(value, 150)));
        if (row && row.item) {
            this.asArray((row.item as any).connection_reasons).forEach((value) => rows.push(this.clinicalCandidateMeaningfulText(value, 150)));
            const reason = this.clinicalCandidateMeaningfulText(row.item.reason || row.item.description || '', 150);
            if (reason) rows.push(reason);
        }
        const core = this.clinicalCandidateMeaningfulText(this.candidateClinicalCoreEvidence(row), 120);
        const name = this.cleanCandidateClinicalPreview(this.candidateClinicalHintText(row), 72);
        const type = this.clinicalCandidateTypeLabel(row);
        if (this.candidateClinicalIsPattern(row) || this.normalizedSoapSection(row && row.section) === 'A') {
            if (name && core) rows.push(`${name}: ${core} 단서와 병증/변증 가능성을 함께 감별합니다.`);
            else if (name) rows.push(`${name}: 지지 근거와 반대 근거를 대조해 감별합니다.`);
        } else if (type === '치료 방향 참고') {
            rows.push(core ? `${core} 단서를 치료 방향, 반응 관찰, 금기 확인과 연결합니다.` : '치료 반응과 금기 확인 후 계획에 반영합니다.');
        } else if (type === '추가 확인 포인트') {
            rows.push(core ? `${core} 때문에 추가 문진 또는 진찰 확인이 필요합니다.` : '차트 반영 전 추가 문진 또는 진찰 확인이 필요합니다.');
        } else if (core) {
            rows.push(`${core} 단서를 문진·진찰 기록과 연결해 확인합니다.`);
        }
        return this.dedupeStrings(rows.filter((value) => value !== '')).slice(0, 4);
    }

    public clinicalCandidateFollowupItems(row: SoapCandidateQueueRow) {
        const rows = this.clinicalCandidateConfirmationItems(row)
            .map((value) => this.cleanCandidateClinicalPreview(value, 130))
            .filter((value) => value !== '');
        if (!rows.length && row && row.status === 'stale') rows.push('최신 대화/진찰 내용이 이전 정리 내용과 맞는지 재확인');
        if (!rows.length && row && this.clinicalCandidateTypeLabel(row) === '치료 방향 참고') rows.push('시행 전 복약, 알레르기, 시술 금기, 치료 반응 기준 확인');
        if (!rows.length) rows.push('핵심 증상 양상, 악화/완화 요인, 진찰 소견을 추가 확인');
        return this.dedupeStrings(rows).slice(0, 5);
    }

    public clinicalCandidateCounterEvidenceItems(row: SoapCandidateQueueRow) {
        const rows = this.clinicalCandidateOpposingItems(row)
            .map((value) => this.cleanCandidateClinicalPreview(value, 130))
            .filter((value) => value !== '');
        if (row && row.status === 'stale') rows.push('후속 입력으로 참고 내용이 갱신되어 이전 판단과 차이가 날 수 있습니다.');
        if (!rows.length) rows.push('반대 근거 또는 감별 소견 확인');
        return this.dedupeStrings(rows).slice(0, 5);
    }

    private clinicalCandidateStructuredPayload(row: SoapCandidateQueueRow): WorkspaceMap {
        if (!row) return {};
        if (row.item && (row.item as any).source_payload && typeof (row.item as any).source_payload === 'object') return (row.item as any).source_payload;
        if (row.sentence) {
            const meta = row.sentence.sentence_meta && typeof row.sentence.sentence_meta === 'object' ? row.sentence.sentence_meta : {};
            if (meta.source_payload && typeof meta.source_payload === 'object') return meta.source_payload;
        }
        return {};
    }

    private clinicalCandidateMeaningfulText(value: any, limit: number = 120) {
        const text = this.cleanCandidateClinicalPreview(value, limit);
        if (!text || this.clinicalCandidateTextIsSourceOnly(text)) return '';
        return text;
    }

    private clinicalCandidateTextIsSourceOnly(value: any) {
        const normalized = this.normalizeLookupLabel(value);
        if (!normalized || normalized === '-') return true;
        const sourceOnly = [
            '진료대화',
            '진료대화기반',
            '대화기반',
            '환자요약',
            '예진',
            '예진요약',
            '체크',
            '체크리스트',
            '한의차트',
            '차트반영',
            '진료대화임상단서',
            '임상단서',
            '진료지원',
            'aiassistant',
            'patterncandidate',
            'conversationchat',
        ];
        if (sourceOnly.indexOf(normalized) >= 0) return true;
        return ['진료대화', '대화기반', '환자요약', '예진', '체크리스트', '진료지원']
            .some((token) => normalized.indexOf(token) === 0 && normalized.length <= token.length + 6);
    }

    private clinicalCandidateSourceLabels(row: SoapCandidateQueueRow) {
        const labels: string[] = [];
        if (!row) return labels;
        if (row.source) labels.push(this.clinicalSourceDisplayText(row.source));
        if (row.result) this.asArray(row.result.sources).forEach((value) => labels.push(this.clinicalSourceDisplayText(value)));
        if (row.item) {
            this.asArray(row.item.source_labels).forEach((value) => labels.push(this.clinicalSourceDisplayText(value)));
            if (row.item.source) labels.push(this.clinicalSourceDisplayText(row.item.source));
            this.asArray(row.item.source_refs).forEach((ref) => labels.push(this.clinicalSourceDisplayText(this.evidenceRefLabel(ref))));
        }
        if (row.sentence) {
            this.asArray(row.sentence.source_refs).forEach((ref) => labels.push(this.clinicalSourceDisplayText(this.evidenceRefLabel(ref))));
            if (row.sentence.candidate_source) labels.push(this.clinicalSourceDisplayText(row.sentence.candidate_source));
        }
        return this.dedupeStrings(labels.filter((value) => value !== '' && value !== '-')).slice(0, 4);
    }

    private conversationCandidateSourcePayload(row: SoapCandidateQueueRow): WorkspaceMap {
        if (!row) return {};
        const item = row.item as any;
        if (item && (item.clinical_summary || item.clinical_evidence || item.source_excerpt)) {
            return {
                clinical_summary: item.clinical_summary || '',
                clinical_evidence: item.clinical_evidence || item.evidence_summary || '',
                chart_sentence: item.soap_sentence_text || '',
                source_excerpt: item.source_excerpt || item.evidence_summary || '',
                request_text: row.result && row.result.requestText ? row.result.requestText : '',
                input_type: 'conversation_chat',
            };
        }
        const meta = row.sentence && row.sentence.sentence_meta && typeof row.sentence.sentence_meta === 'object'
            ? row.sentence.sentence_meta
            : {};
        const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
        const source = String(row.source || meta.source || row.sentence?.author_type || row.sentence?.candidate_source || '').trim();
        if (payload.input_type === 'conversation_chat' || source === 'conversation_chat' || source.indexOf('대화') >= 0) return payload;
        return {};
    }

    private conversationCandidateClinicalSummary(row: SoapCandidateQueueRow) {
        if (!row) return '';
        const payload = this.conversationCandidateSourcePayload(row);
        const summary = this.clinicalRecordDisplayText(payload.clinical_summary || '');
        const sourceText = this.dedupeStrings([
            this.clinicalRecordDisplayText(payload.request_text || ''),
            this.clinicalRecordDisplayText(payload.source_excerpt || ''),
            this.clinicalRecordDisplayText(payload.clinical_evidence || ''),
            this.clinicalRecordDisplayText(payload.chart_sentence || ''),
            this.clinicalRecordDisplayText(row.text || row.title || ''),
        ]).find((value) => value !== '');
        if (sourceText) {
            const derived = this.conversationClinicalSummaryForSection(row.section, sourceText);
            const summaryScopes = this.clinicalAnatomicScopesFromText(summary);
            const sourceScopes = this.clinicalAnatomicScopesFromText(sourceText);
            const scopesConflict = summaryScopes.length > 0
                && sourceScopes.length > 0
                && !summaryScopes.some((scope) => sourceScopes.indexOf(scope) >= 0);
            if (
                derived
                && !this.conversationClinicalSummaryIsGeneric(derived)
                && (this.conversationClinicalSummaryIsGeneric(summary) || !summary || scopesConflict)
            ) return derived;
        }
        if (summary) return this.truncateText(summary, 72);
        const source = String(row.source || row.sentence?.author_type || row.sentence?.candidate_source || '').trim();
        if (source !== 'conversation_chat' && source.indexOf('대화') < 0) return '';
        return summary || this.conversationClinicalSummaryForSection(row.section, row.text || row.title || '');
    }

    private conversationClinicalSummaryIsGeneric(value: any) {
        const normalized = this.normalizeLookupLabel(this.clinicalRecordDisplayText(value || ''));
        return [
            '통증',
            '증상',
            '소견',
            '문진단서',
            '진찰소견',
            '임상단서',
            '추가확인',
            '통증감별',
            '통증병증감별',
        ].indexOf(normalized) >= 0;
    }

    private conversationCandidateEvidenceSummary(row: SoapCandidateQueueRow) {
        if (!row) return '';
        const payload = this.conversationCandidateSourcePayload(row);
        const evidence = this.clinicalRecordDisplayText(payload.clinical_evidence || payload.source_excerpt || '');
        if (evidence) return this.truncateText(evidence, 86);
        const source = String(row.source || row.sentence?.author_type || row.sentence?.candidate_source || '').trim();
        if (source !== 'conversation_chat' && source.indexOf('대화') < 0) return '';
        return this.conversationClinicalEvidenceSummary(row.section, row.text || row.title || '');
    }

    private clinicalSourceDisplayText(value: any) {
        const text = this.clinicalRecordDisplayText(value);
        const normalized = this.normalizeLookupLabel(text);
        if (!normalized || normalized === '-') return '';
        if (normalized.indexOf('precheck') >= 0 || normalized.indexOf('예진') >= 0) return '예진';
        if (normalized.indexOf('conversation') >= 0 || normalized.indexOf('대화') >= 0) return '진료 대화';
        if (normalized.indexOf('checklist') >= 0 || normalized.indexOf('체크') >= 0) return '진료 전 확인';
        if (normalized.indexOf('objective') >= 0 || normalized.indexOf('진찰') >= 0 || normalized.indexOf('소견') >= 0) return '진찰/소견';
        if (normalized.indexOf('summary') >= 0 || normalized.indexOf('요약') >= 0) return '환자 요약';
        if (normalized.indexOf('pattern') >= 0 || normalized.indexOf('변증') >= 0) return '변증/진단 참고';
        if (normalized.indexOf('ai') >= 0 || normalized.indexOf('assistant') >= 0 || normalized.indexOf('추천') >= 0) return '진료지원';
        return this.truncateText(text, 28);
    }

    public candidateClinicalCue(row: SoapCandidateQueueRow) {
        if (!row) return '진료지원';
        const text = this.normalizeLookupLabel(`${row.title} ${row.text} ${row.source}`);
        if (this.candidateTextHasAny(text, ['설진', '설질', '설태'])) return '설진 확인';
        if (this.candidateTextHasAny(text, ['맥진', '맥상'])) return '맥진 확인';
        if (this.candidateTextHasAny(text, ['복진', '복부', '복직근'])) return '복진 확인';
        if (row.section === 'O') return '진찰 소견 확인';
        if (row.section === 'A') return '변증 검토';
        if (row.section === 'P') return '치료 방향';
        return '문진 보완';
    }

    public candidateClinicalTitle(row: SoapCandidateQueueRow) {
        if (!row) return '살펴볼 내용';
        if (row.item && this.isPatternCandidateResult(row.result)) {
            const name = this.displayValue(row.item.display_name || row.item.title, '');
            if (name) return name;
        }
        if (row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) {
            const payload = this.patternPayloadFromSoapSentence(row.sentence);
            const name = this.displayValue(payload.display_name || row.title, '');
            if (name) return name;
        }
        const conversationSummary = this.conversationCandidateClinicalSummary(row);
        if (conversationSummary) return conversationSummary;
        const text = this.normalizeLookupLabel(`${row.title} ${row.text}`);
        if (this.candidateTextHasAny(text, ['삽맥'])) return '어혈 가능성 감별';
        if (this.candidateTextHasAny(text, ['세맥'])) return '허증 가능성 감별';
        if (this.candidateTextHasAny(text, ['삭맥'])) return '열상 여부 감별';
        if (this.candidateTextHasAny(text, ['긴맥', '현맥'])) return '통증 긴장 양상 확인';
        if (this.candidateTextHasAny(text, ['어혈', '혈어'])) return '어혈 병증 감별';
        if (this.candidateTextHasAny(text, ['한습', '냉감', '찬'])) return '한습 양상 감별';
        if (this.candidateTextHasAny(text, ['담습', '담음', '묵직'])) return '담습 양상 감별';
        if (this.candidateTextHasAny(text, ['열상', '열감', '상열감', '발열', '홍설', '설홍', '건조'])) return '열상 여부 확인';
        if (this.candidateTextHasAny(text, ['허리', '요통', '요부'])) return '요통 병증 확인';
        if (this.candidateTextHasAny(text, ['두통', '머리'])) return '두통 양상 확인';
        if (this.candidateTextHasAny(text, ['소화', '식욕', '비위'])) return '비위 상태 확인';
        if (this.candidateTextHasAny(text, ['수면', '불면'])) return '수면 상태 확인';
        if (this.candidateTextHasAny(text, ['저림', '감각', '근력', '하지 저림', '하지 감각', '하지 통증', '하지 방사', '다리', '방사통'])) return '신경학적 동반 소견 확인';
        if (this.candidateTextHasAny(text, ['설진', '설질', '설태'])) return '설진 소견 확인';
        if (this.candidateTextHasAny(text, ['맥진', '맥상'])) return '맥진 소견 확인';
        if (this.candidateTextHasAny(text, ['복진', '복부', '복직근'])) return '복진 소견 확인';
        if (row.section === 'A') return '변증/진단 가능성';
        if (row.section === 'P') return '치료 계획 참고';
        if (row.section === 'O') return '진찰 소견 보완';
        return '추가 문진 포인트';
    }

    public candidateClinicalFieldLabel(row: SoapCandidateQueueRow) {
        const section = row ? this.normalizedSoapSection(row.section) : 'S';
        if (section === 'A') return '의심 변증/진단';
        if (section === 'O') return '진찰/소견 보완';
        if (section === 'P') return '치료/처방 참고';
        return '문진 보완';
    }

    public candidateClinicalIsPattern(row: SoapCandidateQueueRow) {
        if (!row) return false;
        if (row.item && this.isPatternCandidateResult(row.result)) return true;
        if (row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) return true;
        const text = this.normalizeLookupLabel(`${row.title || ''} ${row.text || ''} ${row.source || ''}`);
        return this.normalizedSoapSection(row.section) === 'A' && this.candidateTextHasAny(text, ['변증', '패턴', '한습', '기체', '혈어', '담습', '허증']);
    }

    public candidateClinicalSubjectLabel(row: SoapCandidateQueueRow) {
        return this.clinicalCandidateTypeLabel(row);
    }

    public candidateClinicalDecisionLabel(row: SoapCandidateQueueRow) {
        const type = this.clinicalCandidateTypeLabel(row);
        if (type === '변증/진단 가능성' || type === '증상 해석') return '의심되는 내용';
        return '확인할 내용';
    }

    public candidateClinicalHintLabel(row: SoapCandidateQueueRow) {
        const type = this.clinicalCandidateTypeLabel(row);
        if (type === '변증/진단 가능성') return '변증/진단 가능성';
        if (type === '치료 방향 참고') return '치료 방향';
        if (type === '추가 확인 포인트') return '추가 확인 포인트';
        return '의심 증상 조합';
    }

    public candidateClinicalHintText(row: SoapCandidateQueueRow) {
        const conversationSummary = this.conversationCandidateClinicalSummary(row);
        if (conversationSummary) return conversationSummary;
        const label = this.candidateClinicalHintLabel(row);
        if (label === '변증/진단 가능성') return this.candidateClinicalPatternHint(row);
        if (label === '치료 방향') return this.candidateClinicalTreatmentHint(row);
        if (label === '추가 확인 포인트') return this.candidateClinicalConfirmationHint(row);
        return this.candidateClinicalSymptomHint(row);
    }

    public candidateClinicalEvidenceLabel(row: SoapCandidateQueueRow) {
        return '핵심 근거';
    }

    public candidateClinicalSentenceLabel(row: SoapCandidateQueueRow) {
        return '반영할 기록';
    }

    public candidateClinicalBriefEvidence(row: SoapCandidateQueueRow) {
        if (!row) return '상세에서 근거 확인';
        const conversationEvidence = this.conversationCandidateEvidenceSummary(row);
        if (conversationEvidence) return conversationEvidence;
        const chartText = this.cleanCandidateClinicalPreview(this.candidateClinicalChartSentence(row), 48);
        let text = this.cleanCandidateClinicalPreview(this.candidateClinicalCoreEvidence(row), 48);
        if (!text || text === chartText) {
            const reason = this.clinicalCandidateReasonItems(row).find((item) => !!item);
            text = this.cleanCandidateClinicalPreview(reason || '', 48);
        }
        return text || '상세에서 근거 확인';
    }

    private candidateClinicalPatternHint(row: SoapCandidateQueueRow) {
        const text = this.candidateClinicalPriorityText(row);
        const extracted = this.extractClinicalValueAfterLabel(text, ['의심 소견', '의심 후보', '변증', '진단']);
        let name = '';
        if (row && row.item) name = this.displayValue(row.item.display_name || row.item.title, '');
        if (!name && row && row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) {
            const payload = this.patternPayloadFromSoapSentence(row.sentence);
            name = this.displayValue(payload.display_name || payload.pattern_code, '');
        }
        if (!name) name = extracted;
        if (!name) name = this.candidateClinicalTitle(row);
        name = this.cleanCandidateClinicalPreview(name, 54);
        if (!name || name === '변증 검토 후보' || name === '변증 검토 제안') return '변증/진단 감별';
        if (name.indexOf('가능') >= 0 || name.indexOf('감별') >= 0 || name.indexOf('확인') >= 0) return name;
        return `${name} 가능성`;
    }

    private candidateClinicalTreatmentHint(row: SoapCandidateQueueRow) {
        const text = this.candidateClinicalPriorityText(row);
        if (this.candidateTextHasAny(text, ['뜸', '온열', '냉감', '한습'])) return '온열·한습 반응을 보고 치료 조정';
        if (this.candidateTextHasAny(text, ['부항', '어혈', '삽맥', '혈어'])) return '어혈·압통 양상에 맞춰 부항/침 검토';
        if (this.candidateTextHasAny(text, ['약침', '침', '자침'])) return '통증 부위와 반응 기준으로 침구 방향 검토';
        if (this.candidateTextHasAny(text, ['한약', '처방', '복약'])) return '변증 확인 후 처방·복약 방향 검토';
        if (this.candidateTextHasAny(text, ['운동', '생활', '관리', '추적'])) return '생활관리와 치료 반응 추적';
        const title = this.cleanCandidateClinicalPreview(this.candidateClinicalTitle(row), 54);
        if (title && title !== '치료 계획 후보' && title !== '치료 계획 제안') return title;
        return '치료 반응과 금기 확인 후 계획 조정';
    }

    private candidateClinicalConfirmationHint(row: SoapCandidateQueueRow) {
        const followup = this.clinicalCandidateConfirmationItems(row).find((item) => !!item);
        if (followup) return this.cleanCandidateClinicalPreview(followup, 62);
        const text = this.candidateClinicalPriorityText(row);
        if (this.candidateTextHasAny(text, ['레드플래그', '발열', '야간통', '외상'])) return '진료 전 주의 동반 여부 확인';
        if (this.candidateTextHasAny(text, ['복약', '약물', '알레르기', '금기'])) return '복약·알레르기·시술 금기 확인';
        if (this.candidateTextHasAny(text, ['저림', '감각', '근력', '대소변', '배뇨'])) return '신경학적 동반 소견 확인';
        return this.cleanCandidateClinicalPreview(this.candidateClinicalTitle(row), 62) || '추가 문진·진찰 확인';
    }

    private candidateClinicalSymptomHint(row: SoapCandidateQueueRow) {
        const text = this.candidateClinicalPriorityText(row);
        const definitions = [
            { label: '요통', keywords: ['요통', '허리', '요부'] },
            { label: '하지 저림/감각', keywords: ['저림', '감각', '하지 저림', '하지 감각', '하지 통증', '하지 방사', '다리', '방사통', '근력'] },
            { label: '냉감', keywords: ['냉감', '찬', '한습'] },
            { label: '열감', keywords: ['열감', '상열감', '발열', '열상'] },
            { label: '수면 저하', keywords: ['수면', '불면'] },
            { label: '소화 저하', keywords: ['소화', '식욕', '비위'] },
            { label: '설진 소견', keywords: ['설진', '설질', '설태'] },
            { label: '맥상 소견', keywords: ['맥진', '맥상', '삽맥', '세맥', '삭맥', '긴맥', '현맥'] },
            { label: '복부 긴장', keywords: ['복진', '복부', '복직근'] },
        ];
        const labels = definitions
            .filter((definition) => this.candidateTextHasAny(text, definition.keywords))
            .map((definition) => definition.label);
        const deduped = this.dedupeStrings(labels);
        if (deduped.length >= 2) return deduped.slice(0, 3).join(' + ');
        if (deduped.length === 1) {
            const title = this.cleanCandidateClinicalPreview(this.candidateClinicalTitle(row), 46);
            if (title && title.indexOf('후보') < 0 && title.indexOf('제안') < 0) return `${deduped[0]} · ${title}`;
            return `${deduped[0]} 양상 확인`;
        }
        return this.cleanCandidateClinicalPreview(this.candidateClinicalTitle(row), 58) || '증상 조합 확인';
    }

    private extractClinicalValueAfterLabel(text: string, labels: string[]) {
        const source = this.clinicalRecordDisplayText(text || '');
        for (const label of labels) {
            const pattern = new RegExp(`${label}\\s*[:：]\\s*([^,.;/·]+)`);
            const match = source.match(pattern);
            if (match && match[1]) return this.cleanCandidateClinicalPreview(match[1], 54);
        }
        return '';
    }

    private cleanCandidateClinicalPreview(value: any, limit: number = 90) {
        let text = this.clinicalRecordDisplayText(value || '');
        text = text
            .replace(/^기록\s*후보\s*[:：]\s*/g, '')
            .replace(/^문장\s*후보\s*[:：]\s*/g, '')
            .replace(/^추천\s*이유\s*[:：]\s*/g, '')
            .replace(/신규\s*검토\s*후보/g, '')
            .replace(/검토\s*후보/g, '')
            .replace(/참고\s*후보/g, '')
            .replace(/후보/g, '')
            .replace(/제안/g, '')
            .replace(/\s*한의사\s*판단\s*필요\.?$/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        return text ? this.truncateText(text, limit) : '';
    }

    public candidateClinicalDisplayText(row: SoapCandidateQueueRow) {
        const conversationSummary = this.conversationCandidateClinicalSummary(row);
        if (conversationSummary) return conversationSummary;
        const text = String((row && (row.text || row.title)) || '').trim();
        if (!text) return '살펴볼 내용';
        const normalized = this.normalizeLookupLabel(text);
        const has = (keywords: string[]) => this.candidateTextHasAny(normalized, keywords);
        if (has(['삭맥', '세맥'])) return '열상·허증 감별';
        if (has(['삽맥'])) return '어혈성 통증 감별';
        if (has(['긴맥', '현맥'])) return '기체·한성 통증 감별';
        if (has(['뜸', '부항'])) return '온열·압통 확인';
        if (has(['수면', '불면'])) return '수면 관리 확인';
        if (has(['설질', '설태', '설진'])) return '한열·습담·어혈 감별';
        if (has(['복진', '복부', '복직근'])) return '복부 긴장·압통 확인';
        if (has(['저림', '감각', '근력', '하지 저림', '하지 감각', '하지 통증', '하지 방사', '다리', '방사통'])) return '신경학적 소견 확인';
        if (has(['요통', '허리', '요부'])) return '요통 감별';
        if (has(['두통', '머리'])) return '두통 감별';
        return this.truncateText(this.clinicalRecordDisplayText(text), 120);
    }

    public candidateClinicalCoreEvidence(row: SoapCandidateQueueRow) {
        if (!row) return '근거를 더 확인해 주세요';
        if (row.item && this.isPatternCandidateResult(row.result)) {
            return this.patternCandidateKeyEvidenceText(row.item, 2);
        }
        if (row.sentence && this.soapSentenceIsPatternCandidate(row.sentence)) {
            const payload = this.patternPayloadFromSoapSentence(row.sentence);
            const rows = this.patternCandidateTextRows(payload.supporting_evidence, 2);
            if (rows.length) return rows.join(' / ');
            if (payload.evidence_summary) return this.truncateText(this.clinicalRecordDisplayText(payload.evidence_summary), 90);
        }
        if (row.item) {
            const reasons = this.dedupeStrings(this.asArray(row.item.suggestion_reasons)
                .concat(this.asArray(row.item.source_labels))
                .map((value) => this.clinicalRecordDisplayText(value))
                .filter((value) => value !== ''));
            if (reasons.length) return this.truncateText(reasons.slice(0, 2).join(' / '), 100);
            const itemText = this.clinicalRecordDisplayText(row.item.reason || row.item.description || row.item.source || '');
            if (itemText) return this.truncateText(itemText, 100);
        }
        if (row.sentence) {
            const rows = this.evidenceItemsForSoapSentence(row.sentence)
                .map((item) => this.clinicalRecordDisplayText(item.value || item.label))
                .filter((value) => value !== '');
            if (rows.length) return this.truncateText(this.dedupeStrings(rows).slice(0, 2).join(' / '), 100);
        }
        return this.candidateClinicalDisplayText(row);
    }

    private candidateClinicalChartSentenceText(row: SoapCandidateQueueRow, limit: number) {
        if (!row) return '살펴볼 내용';
        const section = this.normalizedSoapSection(row.section || 'S');
        const chartText = (value: any) => {
            const text = this.cleanAppliedChartText(section, this.clinicalRecordDisplayText(value));
            return limit > 0 ? this.truncateText(text, limit) : text;
        };
        const conversationPayload = this.conversationCandidateSourcePayload(row);
        if (conversationPayload && conversationPayload.chart_sentence) return chartText(conversationPayload.chart_sentence);
        if (row.item && this.isPatternCandidateResult(row.result)) return chartText(this.patternCandidateChartSentence(row.item));
        if (row.item && row.item.soap_sentence_text) return chartText(row.item.soap_sentence_text);
        if (row.sentence && row.sentence.text) return chartText(row.sentence.text);
        if (row.text) return chartText(row.text);
        return chartText(row.title);
    }

    public candidateClinicalChartSentence(row: SoapCandidateQueueRow) {
        return this.candidateClinicalChartSentenceText(row, 130);
    }

    public candidateClinicalChartSentenceFull(row: SoapCandidateQueueRow) {
        return this.candidateClinicalChartSentenceText(row, 0);
    }

    public candidateClinicalTargetTabLabel(row: SoapCandidateQueueRow) {
        const section = row ? this.normalizedSoapSection(row.section) : 'S';
        return `${this.soapSectionDisplayName(section)} 탭`;
    }

    public candidateClinicalReflectionTarget(row: SoapCandidateQueueRow) {
        return this.candidateClinicalTargetTabLabel(row);
    }

    public candidateClinicalReason(row: SoapCandidateQueueRow) {
        if (!row) return '';
        if (row.section === 'A') return '감별 소견 확인';
        if (row.section === 'P') return '치료 방향 검토';
        if (row.section === 'O') return '진찰 확인';
        return '문진 확인';
    }

    private candidateTextHasAny(text: string, keywords: string[]) {
        return keywords.some((keyword) => text.indexOf(this.normalizeLookupLabel(keyword)) >= 0);
    }

    private soapCandidateSourceAnatomicScopes(row: SoapCandidateQueueRow) {
        if (!row) return [];
        const payload = this.conversationCandidateSourcePayload(row);
        const requestScopes = this.clinicalAnatomicScopesFromText(payload.request_text || '');
        if (requestScopes.length) return requestScopes;
        return this.clinicalAnatomicScopesFromText([
            payload.source_excerpt || '',
            payload.clinical_evidence || '',
        ]);
    }

    private soapCandidateTextHasAnatomicConflict(row: SoapCandidateQueueRow, text: string) {
        const chartScopes = this.clinicalAnatomicScopesFromText(text);
        const sourceScopes = this.soapCandidateSourceAnatomicScopes(row);
        if (this.anatomicScopesNeedSeparation(chartScopes)) return true;
        if (!chartScopes.length || !sourceScopes.length) return false;
        if (!chartScopes.some((scope) => sourceScopes.indexOf(scope) >= 0)) return true;
        return chartScopes.some((scope) => sourceScopes.indexOf(scope) < 0)
            && this.anatomicScopesNeedSeparation(chartScopes.concat(sourceScopes));
    }

    private canApplySoapCandidateQueueRowBase(row: SoapCandidateQueueRow) {
        if (this.clinicalReadOnly || !row) return false;
        if (['new_candidate_ready', 'pending', 'stale'].indexOf(row.status) === -1) return false;
        if (row.sentence) return row.sentence.status !== 'excluded' && this.soapIsAiCandidate(row.sentence);
        if (row.kind === 'soap_supplement' && row.item) return !row.item.applied_to_soap && ['deferred', 'excluded', 'ignored'].indexOf(String(row.item.decision_status || '')) === -1;
        if (row.kind === 'ai_item' && row.result && row.item) return this.canApplyAiItemQueueRow(row.result, row.item);
        return false;
    }

    public canApplySoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        return this.canApplySoapCandidateQueueRowBase(row)
            && !this.soapCandidateTextHasAnatomicConflict(row, this.candidateClinicalChartSentenceFull(row));
    }

    public canEditApplySoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        return this.canApplySoapCandidateQueueRowBase(row);
    }

    public canDeferSoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        if (this.clinicalReadOnly || !row) return false;
        if (['new_candidate_ready', 'pending', 'stale'].indexOf(row.status) === -1) return false;
        if (row.sentence) return row.sentence.status !== 'excluded' && this.soapIsAiCandidate(row.sentence);
        if (row.item) return ['deferred', 'excluded', 'ignored', 'added', 'modified'].indexOf(String(row.item.decision_status || '')) === -1;
        return false;
    }

    public soapCandidateQueueApplyLabel(row: SoapCandidateQueueRow) {
        if (this.isApplyingSoapCandidateQueueRow(row)) return '반영 중';
        if (row && row.status === 'new_candidate_ready') return '차트에 반영';
        if (row && row.status === 'previous_version') return '유지 중';
        return '차트에 반영';
    }

    public soapCandidateQueueEditApplyLabel(row: SoapCandidateQueueRow) {
        if (row && row.status === 'new_candidate_ready') return '수정 후 차트에 반영';
        return '수정 후 차트에 반영';
    }

    public soapCandidateQueueExcludeLabel(row: SoapCandidateQueueRow) {
        if (row && row.status === 'previous_version') return '이전 문장 사용 안 함';
        return '사용 안 함';
    }

    public canExcludeSoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        if (this.clinicalReadOnly || !row) return false;
        if (row.status === 'excluded' || row.status === 'applied') return false;
        if (row.sentence) return row.sentence.status !== 'excluded';
        if (row.item) return ['excluded', 'ignored', 'added', 'modified'].indexOf(String(row.item.decision_status || '')) === -1;
        return false;
    }

    public async applySoapCandidateQueueRow(row: SoapCandidateQueueRow, edit: boolean = false) {
        if (!this.canApplySoapCandidateQueueRow(row) || this.isApplyingSoapCandidateQueueRow(row)) return;
        if (this.isMcpCandidateRow(row)) {
            await this.openClinicalCandidateModal(row);
            return;
        }
        await this.runSoapCandidateApply(row, async () => {
            await this.applySoapCandidateQueueRowAction(row, edit);
        });
    }

    private async applySoapCandidateQueueRowAction(row: SoapCandidateQueueRow, edit: boolean = false) {
        const targetSection = this.normalizedSoapSection(row.section);
        if (row.sentence) {
            await this.applySoapQueueSentence(row.sentence, edit);
            this.focusSoapSectionAfterCandidateAction(targetSection);
            await this.service.render();
            this.scheduleBottomBarClearance();
            return;
        }
        if (row.kind === 'soap_supplement' && row.item) {
            await this.applySoapSupplementSuggestion(row.item, edit ? 'modify' : 'add');
            this.focusSoapSectionAfterCandidateAction(targetSection);
            await this.service.render();
            this.scheduleBottomBarClearance();
            return;
        }
        if (row.kind === 'ai_item' && row.result && row.item) {
            await this.applyAiItemQueueRowToChart(row.result, row.item);
            this.focusSoapSectionAfterCandidateAction(targetSection);
            await this.service.render();
            this.scheduleBottomBarClearance();
        }
    }

    public soapCandidateQueueStableKey(row: SoapCandidateQueueRow) {
        if (!row) return '';
        const item = row.item || {} as AiAssistantResultItem;
        const sentence = row.sentence || {} as SoapSentenceDraft;
        const result = row.result || {} as AiAssistantResult;
        const identity = String(
            sentence.id
            || item.conversation_signal_key
            || (item as WorkspaceMap).client_candidate_id
            || item.pattern_code
            || `${result.id || result.type || 'candidate'}::${item.title || row.title || row.id}`
        );
        return `${row.kind || 'candidate'}::${this.normalizedSoapSection(row.section)}::${identity}`;
    }

    public isApplyingSoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        const key = this.soapCandidateQueueStableKey(row);
        return !!key && key === this.applyingSoapCandidateKey;
    }

    private async runSoapCandidateApply(row: SoapCandidateQueueRow, action: () => Promise<void>) {
        const key = this.soapCandidateQueueStableKey(row);
        if (!key || this.applyingSoapCandidateKey) return;
        this.applyingSoapCandidateKey = key;
        this.error = '';
        this.showWorkspaceFeedback('차트에 반영 중입니다.', 'info');
        await this.service.render();
        try {
            await action();
            if (!this.error) this.recordCandidateApply(row);
        } finally {
            if (this.applyingSoapCandidateKey === key) this.applyingSoapCandidateKey = '';
            this.markSoapCandidateQueueDirty();
            await this.service.render();
        }
    }

    private recordCandidateApply(row: SoapCandidateQueueRow) {
        const section = this.normalizedSoapSection(row.section || (row.sentence && row.sentence.soap_section) || 'S');
        const rowKey = this.soapCandidateQueueStableKey(row);
        this.coreSoapCandidateVisibleKeys = this.coreSoapCandidateVisibleKeys.filter((key) => key !== rowKey);
        this.coreSoapCandidateBatchInitialized = true;
        this.coreCandidateBatchAppliedCount += 1;
        const item: WorkspaceActionHistoryItem = {
            id: `apply-${Date.now()}-${this.soapCandidateQueueStableKey(row)}`,
            label: `${this.soapSectionDisplayName(section)}에 반영`,
            section,
            at: this.koreaTimeLabel(),
            undoable: true,
            row,
        };
        this.candidateActionHistory = [item]
            .concat(this.candidateActionHistory.map((history) => ({ ...history, undoable: false })))
            .slice(0, 8);
        this.candidateUndoItem = item;
        if (this.candidateUndoTimer) clearTimeout(this.candidateUndoTimer);
        this.candidateUndoTimer = setTimeout(() => {
            if (this.candidateUndoItem && this.candidateUndoItem.id === item.id) {
                this.candidateUndoItem = null;
                void this.service.render();
            }
        }, 12000);
    }

    public async undoLastCandidateApply() {
        const history = this.candidateUndoItem;
        const row = history && history.row;
        if (!history || !history.undoable || !row || this.busy || this.clinicalReadOnly) return;
        const sentence = row.sentence;
        const item = row.item;
        const candidate = sentence
            ? this.soapCandidatePayloadFromSentence(sentence)
            : {
                ...(item || {}),
                soap_section: history.section,
                text: row.text,
                soap_sentence_text: row.text,
            };
        this.showWorkspaceFeedback('반영 내용을 되돌리는 중입니다.', 'info');
        const restored = await this.applySoapCandidateDecision({
            action: 'restore',
            section: history.section,
            sentence_id: sentence && sentence.id ? sentence.id : '',
            target_sentence_id: item && item.target_sentence_id ? item.target_sentence_id : '',
            text: row.text,
            source_refs: sentence ? sentence.source_refs : (item && item.source_refs) || [],
            candidate,
            reason: 'doctor_undo',
        });
        if (!restored) return;
        const restoredKey = this.soapCandidateQueueStableKey(row);
        this.coreSoapCandidateVisibleKeys = [restoredKey]
            .concat(this.coreSoapCandidateVisibleKeys.filter((key) => key !== restoredKey))
            .slice(0, this.coreSoapCandidateLimit);
        this.coreSoapCandidateBatchInitialized = true;
        this.coreCandidateBatchAppliedCount = Math.max(0, this.coreCandidateBatchAppliedCount - 1);
        this.markSoapCandidateQueueDirty();
        history.undoable = false;
        this.candidateUndoItem = null;
        this.candidateActionHistory = [{
            id: `undo-${Date.now()}`,
            label: `${this.soapSectionDisplayName(history.section)} 반영 취소`,
            section: history.section,
            at: this.koreaTimeLabel(),
            undoable: false,
        }].concat(this.candidateActionHistory.map((entry) => ({ ...entry, undoable: false }))).slice(0, 8);
        this.showWorkspaceFeedback('차트 반영을 취소하고 추천으로 되돌렸습니다.', 'success');
        await this.service.render();
    }

    public async deferSoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        if (!this.canDeferSoapCandidateQueueRow(row)) return;
        if (row.sentence) {
            await this.deferSoapQueueSentence(row.sentence);
            return;
        }
        if (row.kind === 'soap_supplement' && row.item) {
            await this.deferSoapSupplementSuggestion(row.item);
            return;
        }
        if (row.kind === 'ai_item' && row.result && this.isPatternCandidateResult(row.result) && row.item) {
            await this.deferPatternCandidate(row.item);
            return;
        }
        if (row.item) {
            await this.deferAiAssistantItem(row.item);
        }
    }

    public async excludeSoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        if (!this.canExcludeSoapCandidateQueueRow(row)) return;
        if (row.sentence) {
            await this.excludeSoapSentence(row.sentence);
            return;
        }
        if (row.kind === 'soap_supplement' && row.item) {
            await this.ignoreSoapSupplementSuggestion(row.item);
            return;
        }
        if (row.kind === 'ai_item' && row.result && this.isPatternCandidateResult(row.result) && row.item) {
            await this.excludePatternCandidate(row.item);
            return;
        }
        if (row.item) {
            await this.ignoreAiAssistantItem(row.item);
        }
    }

    public async openEvidenceModalForSoapCandidateQueueRow(row: SoapCandidateQueueRow) {
        if (!row) return;
        if (row.sentence) {
            await this.openEvidenceModalForSoapSentence(row.sentence);
            this.activeEvidenceTitle = this.candidateClinicalTitle(row);
            this.activeEvidenceContextLabel = this.candidateClinicalReason(row);
        } else if (row.result && row.item) {
            await this.openEvidenceModalForItem(row.result, row.item);
            this.activeEvidenceTitle = this.candidateClinicalTitle(row);
            this.activeEvidenceContextLabel = this.candidateClinicalReason(row);
        } else if (row.result) {
            await this.openEvidenceModalForResult(row.result);
            this.activeEvidenceTitle = this.candidateClinicalTitle(row);
            this.activeEvidenceContextLabel = this.candidateClinicalReason(row);
        }
        this.activeEvidenceAnatomicWarning = this.soapCandidateTextHasAnatomicConflict(
            row,
            this.candidateClinicalChartSentenceFull(row)
        )
            ? '추천 근거와 반영 문장에 서로 다른 신체 부위가 섞여 있습니다. 반영 전에 문장을 수정하세요.'
            : '';
        await this.service.render();
    }

    public stopCandidateRowEvent(event: Event) {
        if (!event) return;
        event.preventDefault();
        event.stopPropagation();
    }

    public get soapEntryPrimaryActionCode() {
        const status = this.soapEntryPrepareStatusCode;
        if (this.readOnlyMode || this.clinicalReadOnly) return 'none';
        if (status === 'candidate_preparing') return 'wait';
        if (this.soapDirtySentenceCount > 0) return 'save';
        if (this.canPrepareSoapEntryCandidate) return 'prepare';
        if (status === 'insufficient_input' || status === 'manual_available') return 'manual';
        if (status === 'candidate_ready' || status === 'pending_apply') return 'review';
        return this.hasSoapDraft ? 'review' : 'manual';
    }

    public get soapEntryPrimaryActionLabel() {
        const labels: WorkspaceMap = {
            wait: '진료 내용 정리 중',
            prepare: this.soapNeedsRegeneration ? '최신 내용 확인' : '참고 내용 찾기',
            manual: '직접 작성',
            save: '저장',
            review: '내용 보기',
            none: '확인',
        };
        return labels[this.soapEntryPrimaryActionCode] || '확인';
    }

    public get canRunSoapEntryPrimaryAction() {
        const action = this.soapEntryPrimaryActionCode;
        if (['none', 'wait'].indexOf(action) >= 0) return false;
        if (action === 'review') return !this.busy;
        return !this.busy && !this.clinicalReadOnly;
    }

    public get soapEntrySecondaryActionLabel() {
        const action = this.soapEntryPrimaryActionCode;
        if (action === 'prepare') return '직접 작성';
        if (action === 'manual') return '다시 확인';
        if (action === 'save') return '저장';
        if (action === 'review') return this.assistantUnappliedCandidateCount > 0 ? '내용 보기' : '직접 작성';
        return '다시 확인';
    }

    public get canRunSoapEntrySecondaryAction() {
        return !this.busy && !this.readOnlyMode;
    }

    public get patientClinicalItems(): PatientClinicalItem[] {
        const allergy = this.summaryLookup(['allergy'], ['알레르기'], '-');
        return [
            {
                label: '주호소',
                value: this.patientSummaryText(this.summaryLookup(['chief_complaint'], ['주호소'], '-'), 96),
                emphasis: true,
            },
            {
                label: '과거력',
                value: this.patientSummaryText(this.summaryLookup(['past_history'], ['과거력'], '-'), 88),
            },
            {
                label: '복약',
                value: this.patientSummaryText(this.summaryLookup(['medication', 'medicine'], ['복약', '복용약'], '-'), 88),
            },
            {
                label: '알레르기',
                value: this.patientSummaryText(allergy, 88),
                emphasis: allergy !== '-' && allergy !== '없음',
            },
            {
                label: '핵심 메모',
                value: this.patientSummaryText(this.summaryLookup(['memo', 'symptom_detail'], ['핵심 메모', '메모', '증상 상세'], '-'), 96),
            },
        ];
    }

    public get patientCoreItems(): PatientClinicalItem[] {
        return this.patientClinicalItems.slice(0, 3);
    }

    public get patientSecondaryItems(): PatientClinicalItem[] {
        const secondary = this.patientClinicalItems.slice(1);
        const priority = secondary.filter((item) => ['과거력', '복약'].indexOf(item.label) >= 0);
        const allergy = secondary.find((item) => item.label === '알레르기' && item.emphasis);
        const items = allergy ? priority.concat([allergy]) : priority;
        return items.slice(0, 3);
    }

    public get patientChiefComplaint() {
        return this.patientSummaryText(this.summaryLookup(['chief_complaint'], ['주호소'], '-'), 96);
    }

    public get hasPrecheckSubmitted() {
        return !!(this.precheck && this.precheck.submitted);
    }

    public get hasPatientChiefComplaint() {
        return this.patientChiefComplaint !== '-';
    }

    public get patientChiefComplaintDisplay() {
        return this.hasPatientChiefComplaint ? this.patientChiefComplaint : '주호소 입력 전';
    }

    public get patientStartSummary() {
        if (this.hasPatientChiefComplaint) return this.patientChiefComplaint;
        if (!this.hasPrecheckSubmitted) return '주호소부터 입력';
        return '주호소 입력 전';
    }

    public get patientPrecheckCoreText() {
        const detail = this.summaryLookup(
            ['symptom_detail', 'memo', 'onset', 'course', 'pain_pattern'],
            ['증상 상세', '핵심 메모', '메모', '발생 시점', '경과', '양상'],
            ''
        );
        const fallback = this.patientChiefComplaint !== '-' ? this.patientChiefComplaint : '';
        const text = this.patientSummaryText(detail || fallback, 74);
        if (text && text !== '-') return text;
        return this.hasPrecheckSubmitted ? '예진 제출됨' : '현장 문진으로 시작';
    }

    public get patientPrecheckCoreDisplayText() {
        const text = this.patientPrecheckCoreText;
        if (!this.hasPrecheckSubmitted) {
            if (text === '현장 문진으로 시작') return text;
            return `현장 문진 · ${text}`;
        }
        return text;
    }

    public get conversationEmptyTitle() {
        if (!this.hasPrecheckSubmitted && !this.hasPatientChiefComplaint) return '문진을 바로 시작';
        return '첫 문진을 입력하세요';
    }

    public get conversationEmptyDescription() {
        if (!this.hasPrecheckSubmitted && !this.hasPatientChiefComplaint) return '환자가 말한 주호소와 시작 시점부터 적으면 됩니다.';
        if (!this.hasPrecheckSubmitted) return '지금 확인한 내용을 이어서 입력할 수 있습니다.';
        return '추가 문진이나 한의사 메모를 입력할 수 있습니다.';
    }

    public get conversationInputPlaceholder() {
        if (!this.hasPrecheckSubmitted) return '예: 허리 통증 3일 전 시작, 굽힐 때 악화, 휴식 시 완화, 수면 불편';
        return '추가 문진, 환자 답변, 한의사 메모를 입력';
    }

    public get patientMedicationText() {
        return this.patientSummaryText(this.summaryLookup(['medication', 'medicine'], ['복약', '복용약'], '-'), 88);
    }

    public get patientAllergyText() {
        return this.patientSummaryText(this.summaryLookup(['allergy'], ['알레르기'], '-'), 88);
    }

    public get patientSafetyActiveCount() {
        return [
            this.patientAllergyText,
            this.patientMedicationText,
        ].filter((value) => this.hasMeaningfulSafetyValue(value)).length;
    }

    public get patientSafetyChips() {
        const chips = [
            {
                code: 'allergy',
                label: '알레르기',
                value: this.safetyPresenceLabel(this.patientAllergyText),
                detail: this.patientAllergyText,
                tone: this.safetyPresenceTone(this.patientAllergyText, 'danger'),
            },
            {
                code: 'medication',
                label: '복약',
                value: this.safetyPresenceLabel(this.patientMedicationText),
                detail: this.patientMedicationText,
                tone: this.safetyPresenceTone(this.patientMedicationText, 'warning'),
            },
            {
                code: 'clinical_caution',
                label: '진료 전 주의',
                value: this.redFlagCompactLabel,
                detail: this.redFlagSummaryLabel,
                tone: this.hasUnresolvedRedFlags ? 'danger' : (this.redFlagItems.length ? 'success' : 'neutral'),
            },
            {
                code: 'reference',
                label: '참조비교',
                value: this.referenceComparisonCompactLabel,
                detail: this.referenceComparisonSummary,
                tone: this.referenceComparisonTone,
            },
        ];
        return chips;
    }

    public get patientPrioritySafetyChips() {
        return this.patientSafetyChips.filter((chip) => chip.label !== '참조비교').slice(0, 3);
    }

    public get patientSafetyOverviewChip() {
        const needsInput = this.patientPrioritySafetyChips.filter((chip) => String(chip.value || '') === '입력 전').length;
        const risks = (this.hasMeaningfulSafetyValue(this.patientAllergyText) ? 1 : 0)
            + (this.hasMeaningfulSafetyValue(this.patientMedicationText) ? 1 : 0)
            + (this.hasUnresolvedRedFlags ? 1 : 0);
        return {
            code: 'overview',
            label: '안전 정보',
            value: risks > 0 ? `주의 ${risks}건` : (needsInput > 0 ? `미확인 ${needsInput}건` : '확인 완료'),
            detail: this.patientPrioritySafetyChips.map((chip) => `${chip.label} ${chip.value}`).join(' · '),
            tone: risks > 0 ? 'danger' : (needsInput > 0 || this.checklistPendingCount > 0 ? 'warning' : 'success'),
        };
    }

    public get clinicalCautionSafetyChip() {
        return this.patientSafetyChips.find((chip) => chip.code === 'clinical_caution') || {
            code: 'clinical_caution',
            label: '진료 전 주의',
            value: this.redFlagCompactLabel,
            detail: this.redFlagSummaryLabel,
            tone: this.hasUnresolvedRedFlags ? 'danger' : 'neutral',
        };
    }

    public get activeSafetyDetailChip(): WorkspaceMap | null {
        if (!this.activeSafetyDetailCode) return null;
        if (this.activeSafetyDetailCode === 'overview') return this.patientSafetyOverviewChip;
        return this.patientPrioritySafetyChips.find((chip) => chip.code === this.activeSafetyDetailCode) || null;
    }

    public get activeSafetyDetailTone() {
        return String((this.activeSafetyDetailChip && this.activeSafetyDetailChip.tone) || 'neutral');
    }

    public get activeSafetyDetailNeedsInput() {
        const chip = this.activeSafetyDetailChip;
        return !!chip && String(chip.value || '') === '입력 전';
    }

    public get activeSafetyDetailRecord() {
        const chip = this.activeSafetyDetailChip;
        if (!chip) return '확인된 정보가 없습니다.';
        const value = String(chip.value || '');
        const detail = String(chip.detail || '').trim();
        if (value === '입력 전') return `${chip.label} 여부를 아직 확인하지 않았습니다.`;
        if (value === '없음') return `현재 기록상 확인된 ${chip.label} 정보가 없습니다.`;
        return detail && detail !== '-' ? detail : `${chip.label} 정보가 기록되어 있습니다.`;
    }

    public get activeSafetyDetailGuidance() {
        const chip = this.activeSafetyDetailChip;
        if (!chip) return '진료 전에 확인해 주세요.';
        if (chip.code === 'allergy') {
            if (chip.value === '있음') return '원인 물질과 나타난 반응을 진료 전에 확인하세요.';
            if (chip.value === '입력 전') return '환자에게 알레르기 여부를 확인해 주세요.';
            return '현재 기록상 알레르기 없음으로 확인되어 있습니다.';
        }
        if (chip.code === 'medication') {
            if (chip.value === '있음') return '현재 복용약과 치료·처방의 상호 영향을 확인하세요.';
            if (chip.value === '입력 전') return '환자에게 현재 복용약을 확인해 주세요.';
            return '현재 기록상 복용약 없음으로 확인되어 있습니다.';
        }
        if (this.hasUnresolvedRedFlags) return '확인이 필요한 항목부터 살펴보세요.';
        if (this.redFlagItems.length) return '현재 주의 항목은 확인 완료 상태입니다.';
        return '현재 기록된 진료 전 주의 항목이 없습니다.';
    }

    public get focusedSafetyCautionItems() {
        const unresolved = this.redFlagItems.filter((item) => !this.isRiskResolved(item));
        return (unresolved.length ? unresolved : this.redFlagItems).slice(0, 4);
    }

    public get candidateNewReviewCount() {
        return Math.min(99, this.soapCandidateQueueWorkCount);
    }

    public get candidateChartAppliedCount() {
        return Math.min(99, this.soapActiveSentenceCount || 0);
    }

    public get candidateFinalCheckCount() {
        return this.readOnlyMode ? 0 : Math.min(99, this.finalSaveBlockingItems.length + this.primaryFinalSaveWarningItems.length);
    }

    public get candidateUnifiedStatusCards(): CandidateUnifiedStatusCard[] {
        const finalCheckTone = this.finalSaveBlockingItems.length > 0
            ? 'warning'
            : (this.primaryFinalSaveWarningItems.length > 0 ? 'warning' : 'success');
        return [
            {
                code: 'new_review',
                label: '진료 참고',
                value: this.candidateNewReviewCount > 0 ? `${this.candidateNewReviewCount}건` : '문진 후 정리',
                meta: this.candidateNewReviewCount > 0 ? '차트에 넣기 전' : '문진을 입력하면 정리',
                count: this.candidateNewReviewCount,
                tone: this.candidateNewReviewCount > 0 ? 'warning' : 'neutral',
            },
            {
                code: 'chart_applied',
                label: '차트 반영 완료',
                value: this.candidateChartAppliedCount > 0 ? `${this.candidateChartAppliedCount}문장` : '작성 시작',
                meta: this.soapCandidateQueueDoneCount > 0 ? `참고 내용 반영 ${this.soapCandidateQueueDoneCount}건` : (this.candidateChartAppliedCount > 0 ? '차트에 들어간 문장' : '문진 탭에서 바로 작성'),
                count: this.candidateChartAppliedCount,
                tone: this.candidateChartAppliedCount > 0 ? 'success' : 'neutral',
            },
            {
                code: 'final_check',
                label: '최종 점검',
                value: this.candidateFinalCheckCount > 0 ? `${this.candidateFinalCheckCount}건` : '필요 시',
                meta: this.finalSaveBlockingItems.length > 0 ? `더 작성 ${this.finalSaveBlockingItems.length}건` : (this.primaryFinalSaveWarningItems.length > 0 ? '확인 후 차트 저장' : '차트 작성 후 점검'),
                count: this.candidateFinalCheckCount,
                tone: finalCheckTone,
            },
        ];
    }

    public get candidateUnifiedPrimaryCard() {
        const cards = this.candidateUnifiedStatusCards;
        const review = cards[0];
        const applied = cards[1];
        const finalCheck = cards[2];
        if (review.count > 0) return review;
        if (finalCheck.count > 0) return finalCheck;
        if (applied.count > 0) return applied;
        return review;
    }

    public get candidateUnifiedPrimaryLabel() {
        const card = this.candidateUnifiedPrimaryCard;
        if (card.code === 'new_review' && card.count <= 0) return '문진 입력 후 정리';
        return `${card.label} ${card.value}`;
    }

    public get candidateUnifiedPrimaryTone() {
        return this.candidateUnifiedPrimaryCard.tone || 'neutral';
    }

    public get candidateUnifiedSummaryLabel() {
        return this.candidateUnifiedStatusCards.map((card) => `${card.label} ${card.value}`).join(' · ');
    }

    public get candidateUnifiedCompactSummaryLabel() {
        const cards = this.candidateUnifiedStatusCards;
        const suggestion = cards[0].count > 0 ? cards[0].value : '문진 후';
        return `참고 ${suggestion} · 반영 ${cards[1].value} · 확인 ${cards[2].value}`;
    }

    public get redFlagCompactLabel() {
        if (this.hasUnresolvedRedFlags) return `${this.unresolvedRedFlagCount}건`;
        if (this.redFlagItems.length) return '완료';
        return '진료 중';
    }

    private hasMeaningfulSafetyValue(value: string) {
        const normalized = String(value || '').trim().toLowerCase();
        return normalized !== ''
            && normalized !== '-'
            && normalized !== '없음'
            && normalized !== '무'
            && normalized !== '해당 없음'
            && normalized !== '미확인'
            && normalized !== '미기록'
            && normalized !== '입력 전'
            && normalized !== 'unknown'
            && normalized !== 'none'
            && normalized.indexOf('없음') < 0
            && normalized.indexOf('없습니다') < 0
            && normalized.indexOf('없다고') < 0
            && normalized.indexOf('부인') < 0;
    }

    private isSafetyValueMissing(value: string) {
        const normalized = String(value || '').trim().toLowerCase();
        return !normalized
            || normalized === '-'
            || normalized === '미확인'
            || normalized === '미기록'
            || normalized === '입력 전'
            || normalized === 'unknown'
            || normalized === 'none';
    }

    private safetyPresenceLabel(value: string) {
        if (this.hasMeaningfulSafetyValue(value)) return '있음';
        const normalized = String(value || '').trim();
        if (this.isSafetyValueMissing(normalized)) return '입력 전';
        return '없음';
    }

    private safetyPresenceTone(value: string, presentTone: string) {
        if (this.hasMeaningfulSafetyValue(value)) return presentTone || 'warning';
        if (this.isSafetyValueMissing(value)) return 'neutral';
        return 'success';
    }

    public safetyChipClass(chip: WorkspaceMap) {
        const tone = chip && chip.tone ? chip.tone : 'neutral';
        return `safety-chip safety-chip--${tone}`;
    }

    public soapSectionDisplayName(section: any) {
        const normalized = this.normalizedSoapSection(String(section || 'S'));
        return this.soapSectionLabels[normalized] || '한의 차트';
    }

    public soapSectionChipLabel(section: any) {
        const normalized = this.normalizedSoapSection(String(section || 'S'));
        return this.soapSectionDisplayName(normalized);
    }

    public soapSectionPlaceholder(section: any) {
        const normalized = this.normalizedSoapSection(String(section || 'S'));
        if (normalized === 'S' && !this.hasPrecheckSubmitted) return '예: 허리 통증 3일 전 시작, 굽힐 때 악화, 휴식 시 완화, 한열·수면·소화 확인';
        return this.soapSectionPlaceholders[normalized] || `${this.soapSectionDisplayName(normalized)} 문장 추가`;
    }

    public clinicalRecordDisplayText(value: any) {
        const text = this.displayValue(value, '');
        if (!text) return '';
        const sectionName = (section: string) => `${this.soapSectionDisplayName(section)} 기록`;
        return text
            .replace(/\b(?:QA|DEMO)-HS-[A-Za-z0-9_-]+\b/gi, '환자번호 미표시')
            .replace(/\b(?:QA|DEMO)-ENC-[A-Za-z0-9_-]+\b/gi, '방문번호 미표시')
            .replace(/\breviewops\b/gi, '')
            .replace(/\bpatient_id\b/gi, '환자 정보')
            .replace(/\bencounter_id\b/gi, '방문 정보')
            .replace(/SOAP\s+([SOAP])\/([SOAP])/g, (_match, first, second) => `${sectionName(first)}/${sectionName(second)}`)
            .replace(/SOAP\s+([SOAP])-([SOAP])/g, (_match, first, second) => `${sectionName(first)}/${sectionName(second)}`)
            .replace(/SOAP\s+([SOAP])/g, (_match, section) => sectionName(section))
            .replace(/\bS\/O\/A\/P\b/g, '문진/진찰/변증/치료')
            .replace(/\bA\/P\b/g, '변증/진단·치료/처방')
            .replace(/\bO\/A\b/g, '진찰/변증')
            .replace(/\bO-P\b/g, '진찰/치료')
            .replace(/\bS 문장/g, '문진 문장')
            .replace(/\bO 문장/g, '진찰/소견 문장')
            .replace(/\bA 문장/g, '변증/진단 문장')
            .replace(/\bP 문장/g, '치료/처방 문장')
            .replace(/S\/O\/A\/P/g, '문진/진찰/변증/치료')
            .replace(/SOAP/g, '한의 차트')
            .replace(/진료기록/g, '한의 차트')
            .replace(/진료 차트/g, '한의 진료 차트')
            .replace(/레드\s*플래그/g, '진료 전 주의')
            .replace(/레드플래그/g, '진료 전 주의')
            .replace(/진료지원\s*제안/g, '진료 참고 내용')
            .replace(/한의\s*차트\s*후보/g, '한의 차트에 넣을 내용')
            .replace(/차트\s*문장/g, '기록 문장')
            .replace(/차트\s*제안/g, '차트에 넣을 내용')
            .replace(/의심\s*후보/g, '의심 소견')
            .replace(/신규\s*검토\s*후보/g, '진료 참고 내용')
            .replace(/저장\s*전\s*확인\s*항목/g, '최종 저장 전 점검')
            .replace(/저장\s*전\s*확인/g, '최종 저장 전 점검')
            .replace(/보완\s*필요/g, '더 작성할 내용')
            .replace(/체크\s*메모/g, '확인 메모')
            .replace(/체크리스트/g, '진료 전 확인')
            .replace(/Subjective/g, '문진')
            .replace(/Objective/g, '진찰/소견')
            .replace(/Assessment/g, '변증/진단')
            .replace(/Plan/g, '치료/처방');
    }

    public standardToolCardClass(tool: WorkspaceMap) {
        const active = this.standardToolSessionFor(tool) ? 'active' : 'recommended';
        return `standard-tool-card standard-tool-card--${active}`;
    }

    public standardToolEyebrow(tool: WorkspaceMap) {
        const session = this.standardToolSessionFor(tool);
        if (session && session.status === 'completed') return '평가 완료';
        if (session) return '평가 진행 중';
        return '추가 평가 권장';
    }

    public standardToolSessionFor(tool: WorkspaceMap) {
        const code = tool && tool.tool_code ? String(tool.tool_code) : '';
        if (!code) return null;
        return this.standardToolSessions.find((item) => item && item.tool_code === code) || null;
    }

    public standardToolPreviewFor(tool: WorkspaceMap) {
        const code = tool && tool.tool_code ? String(tool.tool_code) : '';
        if (!code) return null;
        return this.standardToolPreviews.find((item) => item && item.tool_code === code) || null;
    }

    public standardToolActionLabel(tool: WorkspaceMap) {
        const code = tool && tool.tool_code ? String(tool.tool_code) : '';
        if (this.standardToolStartingCode === code) return '평가 여는 중';
        const session = this.standardToolSessionFor(tool);
        if (session && session.status === 'completed') return '결과 갱신';
        if (session) return '평가 이어가기';
        return '평가하기';
    }

    public standardToolActionHint(tool: WorkspaceMap) {
        const session = this.standardToolSessionFor(tool);
        const score = this.standardToolScoreForCode(tool && tool.tool_code ? String(tool.tool_code) : '');
        if (score) return '점수 산출됨';
        if (session) return '점수 갱신';
        return tool && tool.recommendation_reason ? String(tool.recommendation_reason) : '평가 필요';
    }

    public standardToolDisplayName(tool: WorkspaceMap) {
        if (!tool) return '평가도구';
        return this.displayValue(tool.tool_name || tool.metric_name || tool.tool_code, '평가도구');
    }

    public standardToolResultSummary(tool: WorkspaceMap) {
        const code = tool && tool.tool_code ? String(tool.tool_code) : '';
        const score = this.standardToolScoreForCode(code);
        const preview = this.standardToolPreviewFor(tool);
        const previewScore = preview && preview.score ? preview.score : {};
        const value = score && score.metric_value !== undefined ? score.metric_value : previewScore.metric_value;
        if (value !== undefined && value !== null && value !== '') {
            const metric = score && score.metric_code ? score.metric_code : (tool && tool.metric_code ? tool.metric_code : previewScore.metric_code);
            const unit = score && score.unit ? score.unit : (tool && tool.unit ? tool.unit : previewScore.unit || '');
            return `${this.referenceMetricDisplayName(metric)} ${this.formatMetricValue(value, unit)}`;
        }
        return tool && tool.description ? String(tool.description) : '점수 미산출';
    }

    public standardToolScoreForCode(toolCode: string) {
        if (!toolCode) return null;
        return this.standardToolScores.find((item) => item && item.tool_code === toolCode) || null;
    }

    public referenceComparisonItemClass(item: WorkspaceMap) {
        const tone = item && item.severity === 'warning' ? 'warning' : (item && item.severity === 'attention' ? 'attention' : 'success');
        return `reference-comparison-card reference-comparison-card--${tone}`;
    }

    public referenceComparisonValueLabel(item: WorkspaceMap) {
        if (!item) return '-';
        return this.formatMetricValue(item.patient_value, item.unit);
    }

    public referenceMetricLabel(item: WorkspaceMap) {
        if (!item) return '참조 지표';
        return this.referenceMetricDisplayName(item.metric_code || item.metric_name || '');
    }

    public referenceComparisonInterpretationLabel(item: WorkspaceMap) {
        return this.displayValue(item && item.interpretation_label, '건강인 참조 비교');
    }

    public referenceComparisonEvidenceLabel(item: WorkspaceMap) {
        if (!item) return '';
        const mean = item.reference_mean !== undefined && item.reference_mean !== null ? this.formatMetricValue(item.reference_mean, item.unit) : '';
        const p20 = item.reference_p20 !== undefined && item.reference_p20 !== null ? this.formatMetricValue(item.reference_p20, item.unit) : '';
        const p80 = item.reference_p80 !== undefined && item.reference_p80 !== null ? this.formatMetricValue(item.reference_p80, item.unit) : '';
        const lowerIsBurden = item && item.comparison_meta && item.comparison_meta.higher_is_better === false;
        const pieces = [];
        if (mean) pieces.push(`평균 ${mean}`);
        if (lowerIsBurden && p80) {
            pieces.push(`증상 상위 20% 기준 ${p80}`);
        } else if (p20) {
            pieces.push(`하위 20% 기준 ${p20}`);
        }
        return pieces.join(' · ');
    }

    public patientReferenceMetricClass(item: WorkspaceMap) {
        return `patient-reference-metric patient-reference-metric--${this.patientReferenceTone(item)}`;
    }

    public patientReferenceTone(item: WorkspaceMap) {
        const severity = String((item && item.severity) || '');
        const percentile = String((item && item.percentile_label) || '');
        if (severity === 'warning' || percentile === 'lower_20' || percentile === 'upper_20_burden') return 'warning';
        if (severity === 'attention') return 'attention';
        return 'success';
    }

    public patientReferenceToneLabel(item: WorkspaceMap) {
        const tone = this.patientReferenceTone(item);
        if (tone === 'warning') return '주의';
        if (tone === 'attention') return '확인';
        return '양호';
    }

    public referenceComparisonPosition(item: WorkspaceMap) {
        return this.referenceComparisonScalePosition(item, item && item.patient_value, 50);
    }

    public referenceComparisonMeanPosition(item: WorkspaceMap) {
        return this.referenceComparisonScalePosition(item, item && item.reference_mean, 50);
    }

    public referenceComparisonP20Position(item: WorkspaceMap) {
        return this.referenceComparisonScalePosition(item, item && item.reference_p20, 20);
    }

    public referenceComparisonP80Position(item: WorkspaceMap) {
        return this.referenceComparisonScalePosition(item, item && item.reference_p80, 80);
    }

    public referenceComparisonReferenceBandWidth(item: WorkspaceMap) {
        return Math.max(6, this.referenceComparisonP80Position(item) - this.referenceComparisonP20Position(item));
    }

    private referenceComparisonScalePosition(item: WorkspaceMap, target: any, fallback: number) {
        if (!item) return fallback;
        const value = Number(target);
        const candidates = [item.reference_p20, item.reference_mean, item.reference_p80, item.patient_value]
            .map((entry) => Number(entry))
            .filter((entry) => !Number.isNaN(entry));
        if (Number.isNaN(value) || candidates.length < 2) return fallback;
        const min = Math.min(...candidates);
        const max = Math.max(...candidates);
        if (max === min) return fallback;
        return Math.max(0, Math.min(100, Math.round(((value - min) / (max - min)) * 100)));
    }

    public referenceComparisonDeltaLabel(item: WorkspaceMap) {
        if (!item) return '';
        const value = Number(item.patient_value);
        const mean = Number(item.reference_mean);
        if (Number.isNaN(value) || Number.isNaN(mean)) return this.referenceComparisonInterpretationLabel(item);
        const diff = Math.round((value - mean) * 10) / 10;
        const normalizedDiff = Object.is(diff, -0) ? 0 : diff;
        const higherIsBetter = !(item && item.comparison_meta && item.comparison_meta.higher_is_better === false);
        const better = normalizedDiff === 0 ? '평균 수준' : ((higherIsBetter && normalizedDiff > 0) || (!higherIsBetter && normalizedDiff < 0) ? '평균보다 좋음' : '평균보다 불리');
        const signed = normalizedDiff > 0 ? `+${this.formatMetricValue(normalizedDiff, item.unit)}` : this.formatMetricValue(normalizedDiff, item.unit);
        return `평균 대비 ${signed} · ${better}`;
    }

    private referenceMetricDisplayName(metricCode: string) {
        const key = String(metricCode || '');
        return this.displayValue(this.referenceMetricDisplayNames[key] || metricCode, '참조 지표');
    }

    private formatMetricValue(value: any, unit: any = '') {
        const display = this.displayValue(value, '-');
        const normalizedUnit = this.displayValue(unit, '');
        if (!normalizedUnit) return display;
        return `${display}${normalizedUnit}`;
    }

    public get hasSummaryCandidate() {
        return !!(
            this.summaryCandidateRun
            && this.summaryCandidateRun.status === 'candidate_ready'
            && this.summaryCandidateRun.candidate
            && this.summaryCandidateDraft.overview
        );
    }

    public get summaryCandidateStatus() {
        if (this.summaryGenerating) return 'generating';
        if (this.summaryCandidateDecision) return this.summaryCandidateDecision;
        if (this.summaryCandidateRun && this.summaryCandidateRun.status) return this.summaryCandidateRun.status;
        return '';
    }

    public get summaryCandidateSourceRefCount() {
        return Array.isArray(this.summaryCandidateDraft.source_refs) ? this.summaryCandidateDraft.source_refs.length : 0;
    }

    public get checklistItems() {
        return this.asArray(this.checklist.items);
    }

    public checklistStatusResolved(status: string) {
        return this.checklistResolvedStatuses.indexOf(String(status || 'unchecked')) >= 0;
    }

    public get checklistSoapQueueItems() {
        return this.asArray(this.checklist.soap_candidate_queue);
    }

    public get checklistSoapQueueSummary() {
        return this.checklist && this.checklist.soap_candidate_queue_summary ? this.checklist.soap_candidate_queue_summary : {};
    }

    public get checklistSoapQueueCounts() {
        const summary = this.checklistSoapQueueSummary;
        return summary && summary.counts ? summary.counts : {};
    }

    public get checklistSoapQueueRequiredCount() {
        const counts = this.checklistSoapQueueCounts;
        if (counts && counts.queued !== undefined) return Number(counts.queued || 0);
        return this.checklistSoapQueueItems.filter((item) => item && item.queue_required === true).length;
    }

    public get checklistSoapQueueExcludedCount() {
        const counts = this.checklistSoapQueueCounts;
        if (counts && counts.excluded !== undefined) return Number(counts.excluded || 0);
        return this.checklistSoapQueueItems.filter((item) => item && ['excluded', 'not_applicable'].indexOf(String(item.status || '')) >= 0).length;
    }

    public get checklistSoapQueueDeferredCount() {
        const counts = this.checklistSoapQueueCounts;
        if (counts && counts.deferred !== undefined) return Number(counts.deferred || 0);
        return this.checklistSoapQueueItems.filter((item) => item && String(item.status || '') === 'deferred').length;
    }

    public get checklistSoapQueueActionCompletedCount() {
        const counts = this.checklistSoapQueueCounts;
        if (counts && counts.action_completed !== undefined) return Number(counts.action_completed || 0);
        return this.checklistSoapQueueItems.filter((item) => item && String(item.status || '') === 'action_completed').length;
    }

    public get checklistSoapQueueValue() {
        if (this.checklistSoapQueueRequiredCount > 0) return `${this.checklistSoapQueueRequiredCount}건`;
        if (this.checklistSoapQueueExcludedCount > 0) return `사용 안 함 ${this.checklistSoapQueueExcludedCount}`;
        return '대기 없음';
    }

    public get checklistSoapQueueMeta() {
        const parts = [];
        if (this.checklistSoapQueueDeferredCount) parts.push(`나중에 보기 ${this.checklistSoapQueueDeferredCount}`);
        if (this.checklistSoapQueueActionCompletedCount) parts.push(`조치 ${this.checklistSoapQueueActionCompletedCount}`);
        if (this.checklistSoapQueueExcludedCount) parts.push(`사용 안 함 ${this.checklistSoapQueueExcludedCount}`);
        if (!parts.length && this.checklistSoapQueueRequiredCount > 0) parts.push('진료 전 확인 변경 반영 필요');
        return parts.length ? parts.join(' · ') : '진료 전 확인 변경 없음';
    }

    public get checklistSoapQueueTone() {
        if (this.checklistSoapQueueRequiredCount > 0) return this.soapNeedsRegeneration ? 'pending' : 'warning';
        if (this.checklistSoapQueueExcludedCount > 0) return 'neutral';
        return 'success';
    }

    public get checklistDefinitions() {
        return this.asArray(this.checklist.available_checklists);
    }

    public get standardToolRecommendations() {
        return this.asArray(this.standardTools.recommended_tools);
    }

    public get standardToolSessions() {
        return this.asArray(this.standardTools.sessions);
    }

    public get standardToolScores() {
        return this.asArray(this.standardTools.scores);
    }

    public get standardToolPreviews() {
        return this.asArray(this.standardTools.previews);
    }

    public get standardToolWarnings() {
        return this.asArray(this.standardTools.warnings);
    }

    public get standardToolCounts() {
        return this.standardTools && this.standardTools.counts ? this.standardTools.counts : {};
    }

    public get standardToolRecommendedCount() {
        return Number(this.standardToolCounts.recommended || this.standardToolRecommendations.length || 0);
    }

    public get standardToolCompletedCount() {
        return Number(this.standardToolCounts.scores || this.standardToolSessions.filter((item) => item.status === 'completed').length || 0);
    }

    public get standardToolPendingCount() {
        return Math.max(this.standardToolRecommendedCount - this.standardToolCompletedCount, 0);
    }

    public get standardToolAttentionCount() {
        return this.standardToolWarnings.length + this.referenceComparisonAttentionItems.length;
    }

    public get referenceComparisonItems() {
        return this.asArray(this.referenceComparison.items);
    }

    public get referenceComparisonSummary() {
        const item = this.referenceComparisonAttentionItems[0] || this.referenceComparisonItems[0];
        if (item) {
            return `${this.referenceMetricLabel(item)} ${this.referenceComparisonValueLabel(item)}: ${this.referenceComparisonInterpretationLabel(item)}`;
        }
        return '비교 결과 없음';
    }

    public get referenceComparisonDatasetLabel() {
        const dataset = this.referenceComparison.dataset || {};
        return dataset.population_label || dataset.dataset_name || '성인 건강인 참조군';
    }

    public get referenceComparisonDatasetMetaLabel() {
        const dataset = this.referenceComparison.dataset || {};
        return dataset.source_label || '건강인 참조 데이터셋';
    }

    public get referenceComparisonAttentionItems() {
        return this.referenceComparisonItems.filter((item) => {
            const percentile = String(item.percentile_label || '');
            const severity = String(item.severity || '');
            return percentile === 'lower_20' || percentile === 'upper_20_burden' || severity === 'warning';
        });
    }

    public get referenceComparisonCompactLabel() {
        if (this.referenceComparisonAttentionItems.length) return `확인 ${this.referenceComparisonAttentionItems.length}`;
        if (this.referenceComparisonItems.length) return `${this.referenceComparisonItems.length}건`;
        return '필요 시';
    }

    public get referenceComparisonTone() {
        if (this.referenceComparisonAttentionItems.length) return 'warning';
        if (this.referenceComparisonItems.length) return 'success';
        return 'neutral';
    }

    public get activeChecklistSessionId() {
        return this.checklist.session_id || (this.checklist.session && this.checklist.session.id) || '';
    }

    public get selectedChecklist() {
        return this.checklist.selected_checklist || {};
    }

    public get supportItems() {
        return this.asArray(this.checklist.support_items);
    }

    public get redFlagSummary() {
        const fromChecklist = this.checklist && this.checklist.red_flags ? this.checklist.red_flags : {};
        const fromFinal = this.finalChart && this.finalChart.red_flags ? this.finalChart.red_flags : {};
        return Object.keys(fromChecklist || {}).length ? fromChecklist : (fromFinal || {});
    }

    public get redFlagItems() {
        return this.asArray(this.redFlagSummary && this.redFlagSummary.items);
    }

    public get unresolvedRedFlagItems() {
        const explicit = this.asArray(this.redFlagSummary && this.redFlagSummary.unresolved);
        if (explicit.length) return explicit;
        return this.redFlagItems.filter((item) => !this.isRiskResolved(item));
    }

    public get unresolvedRedFlagCount() {
        const count = Number(this.redFlagSummary && this.redFlagSummary.unresolved_count);
        if (count > 0) return count;
        return this.unresolvedRedFlagItems.length;
    }

    public get hasUnresolvedRedFlags() {
        return this.unresolvedRedFlagCount > 0;
    }

    public get headerAttentionCount() {
        const riskCount = Math.max(this.unresolvedRedFlagCount, this.checklistRiskOpenCount);
        return Math.min(99, this.patientSafetyActiveCount + riskCount + this.standardToolAttentionCount + this.primaryFinalSaveWarningItems.length);
    }

    public get headerAttentionLabel() {
        return `확인할 내용 ${this.headerAttentionCount}`;
    }

    public get headerAttentionClass() {
        const tone = this.headerAttentionCount > 0 ? 'warning' : 'neutral';
        return `status-badge status-badge--${tone}`;
    }

    public get headerBlockingCount() {
        return Math.min(99, this.finalSaveBlockingItems.length);
    }

    public get headerBlockingLabel() {
        return `필수 확인 ${this.headerBlockingCount}`;
    }

    public get headerBlockingClass() {
        const tone = this.headerBlockingCount > 0 ? 'danger' : 'success';
        return `status-badge status-badge--${tone}`;
    }

    public get finalGateBlockingItems() {
        const items = this.asArray(this.finalChart && this.finalChart.blocking_items);
        if (items.length) return items;
        return this.finalBlockingItems.map((item) => ({
            ...item,
            severity: 'blocking',
            category: item.category || 'final_save',
            proceed_mode: 'resolve_before_save',
            action_label: '해소 필요',
        }));
    }

    public get finalGateConfirmableItems() {
        return this.asArray(this.finalChart && this.finalChart.confirmable_items);
    }

    public get finalGateMissingConfirmations() {
        const missing = this.asArray(this.finalChart && this.finalChart.missing_confirmations);
        return missing.length ? missing : this.finalGateConfirmableItems.filter((item) => item && item.confirmed !== true);
    }

    public get finalGateNeedsConfirmation() {
        return !this.readOnlyMode && !this.finalGateBlockingItems.length && this.finalGateConfirmableItems.length > 0;
    }

    public get finalGateConfirmationSatisfied() {
        return !this.finalGateNeedsConfirmation || this.finalGateConfirmed;
    }

    public get finalGateReadyText() {
        if (this.readOnlyMode) return '최종 저장됨';
        if (this.finalGateBlockingItems.length) return '더 작성할 내용';
        if (this.finalGateNeedsConfirmation) return this.finalGateConfirmed ? '확인 완료' : '확인만 하면 되는 내용';
        return '저장 가능';
    }

    public get finalGateConfirmText() {
        return this.finalGateConfirmed ? '확인만 하면 되는 내용을 확인했습니다.' : '위 내용을 확인하고 최종 저장을 진행합니다.';
    }

    public get finalGateIssueCount() {
        return this.finalGateBlockingItems.length + this.finalGateConfirmableItems.length;
    }

    public get finalGateCompactLabel() {
        if (this.readOnlyMode) return '최종 저장됨';
        if (this.finalGateBlockingItems.length) return `더 작성 ${this.finalGateBlockingItems.length}건`;
        if (this.finalGateConfirmableItems.length) return `확인만 ${this.finalGateConfirmableItems.length}건`;
        return '저장 가능';
    }

	    public get finalGateCompactClass() {
	        const tone = this.finalGateBlockingItems.length ? 'warning' : (this.finalGateConfirmableItems.length ? 'warning' : 'success');
	        return `status-chip status-chip--${tone}`;
	    }

	    public compactStatusCardClass(tone: string) {
	        return `compact-status-card compact-status-card--${tone || 'neutral'}`;
	    }

	    public get soapCompactValue() {
	        return String(this.soapActiveSentenceCount || 0);
	    }

	    public get soapCompactLabel() {
	        if (this.readOnlyMode) return '최종';
	        if (this.soapGenerating) return '생성';
	        if (this.soapDirtySentenceCount > 0) return `저장 ${this.soapDirtySentenceCount}`;
	        if (this.soapUnreviewedAiCount > 0) return `참고 ${this.soapUnreviewedAiCount}`;
	        if (this.soapPendingSuggestionCount > 0) return `보완 ${this.soapPendingSuggestionCount}`;
	        if (this.hasSoapDraft) return this.soapBusinessStatusLabel;
	        return '작성 전';
	    }

	    public get soapCompactTone() {
	        if (this.readOnlyMode || this.soapReadyForApproval) return 'success';
	        if (!this.hasSoapDraft) return 'neutral';
	        if (this.soapDirtySentenceCount > 0 || this.soapGenerating) return 'warning';
	        if (this.soapValidationIssueCount > 0 || this.finalGateBlockingItems.length > 0) return 'pending';
	        return 'primary';
	    }

	    public get aiCompactValue() {
	        return String(this.assistantUnappliedCandidateCount + this.checklistSoapQueueRequiredCount || 0);
	    }

	    public get aiCompactLabel() {
	        if (this.aiAssistantGenerating) return '준비';
	        if (this.assistantUnappliedCandidateCount > 0) return '참고';
	        if (this.checklistSoapQueueRequiredCount > 0) return '확인';
	        if (this.assistantLatestResult) return '최근';
	        return '대기';
	    }

	    public get aiCompactTone() {
	        if (this.aiAssistantGenerating) return 'ai';
	        if (this.assistantUnappliedCandidateCount > 0 || this.checklistSoapQueueRequiredCount > 0) return 'warning';
	        if (this.assistantLatestResult) return 'info';
	        return 'neutral';
	    }

    public get finalCompactIssueCount() {
	        return Math.min(99, this.finalSaveBlockingItems.length + this.primaryFinalSaveWarningItems.length);
	    }

	    public get finalCompactValue() {
	        return String(this.readOnlyMode ? 0 : this.finalCompactIssueCount);
	    }

	    public get finalCompactLabel() {
	        if (this.readOnlyMode) return '완료';
	        if (this.finalSaveBlockingItems.length > 0) return '필수';
	        if (this.primaryFinalSaveWarningItems.length > 0) return '확인';
	        if (this.canApproveFinal) return '가능';
	        if (!this.soapDraftId) return '작성';
	        return '대기';
	    }

	    public get finalCompactTone() {
	        if (this.readOnlyMode || this.canApproveFinal) return 'success';
	        if (this.finalSaveBlockingItems.length > 0) return 'danger';
	        if (this.primaryFinalSaveWarningItems.length > 0) return 'warning';
	        return 'neutral';
	    }

	    public get attentionCompactCount() {
	        return Math.min(99, this.headerBlockingCount + this.headerAttentionCount);
	    }

	    public get attentionCompactValue() {
	        return String(this.attentionCompactCount);
	    }

	    public get attentionCompactLabel() {
	        if (this.headerBlockingCount > 0) return '필수';
	        if (this.hasUnresolvedRedFlags) return '주의';
	        if (this.headerAttentionCount > 0) return '확인';
	        return '없음';
	    }

	    public get attentionCompactTone() {
	        if (this.headerBlockingCount > 0) return 'danger';
	        if (this.headerAttentionCount > 0) return 'warning';
	        return 'success';
	    }

	    public get patientSummaryCompactStatus() {
	        if (this.hasUnresolvedRedFlags) return `주의 ${this.unresolvedRedFlagCount}`;
	        if (this.patientSafetyActiveCount > 0) return `안전 ${this.patientSafetyActiveCount}`;
	        if (this.patientChiefComplaint !== '-') return '요약 있음';
	        return '요약 대기';
	    }

	    public get assistantCompactStatusLabel() {
	        if (this.aiAssistantGenerating) return '진료 참고 정리';
	        if (this.assistantUnappliedCandidateCount > 0) return `참고 ${this.assistantUnappliedCandidateCount}`;
	        if (this.assistantLatestResult) return '최근 참고';
	        return '대기';
	    }

	    public get finalSaveCompactStatusLabel() {
	        if (this.readOnlyMode) return '최종 저장';
	        if (this.finalSaveBlockingItems.length > 0) return `정리 ${this.finalSaveBlockingItems.length}`;
	        if (this.primaryFinalSaveWarningItems.length > 0) return `확인 ${this.primaryFinalSaveWarningItems.length}`;
	        if (this.canApproveFinal) return '저장 가능';
	        return '검토 전';
	    }

	    public get bottomBarStatusLabel() {
	        if (this.readOnlyMode) return '최종 저장됨';
	        if (!this.hasEncounter) return '방문 선택 필요';
	        if (this.soapSaving) return '저장 중';
	        if (this.soapValidating) return '검토 중';
	        if (this.soapReadying) return '검토 중';
	        if (this.soapGenerating || this.soapCreating) return this.statusLabel('candidate_preparing');
	        if (this.soapDirtySentenceCount > 0) return `저장 대기 ${this.soapDirtySentenceCount}문장`;
	        if (!this.hasSoapDraft && !this.hasPrecheckSubmitted) return '문진부터 작성';
	        if (!this.hasSoapDraft && this.candidateNewReviewCount > 0) return `진료 참고 내용 ${this.candidateNewReviewCount}건`;
	        if (!this.hasSoapDraft) return '차트 작성 시작';
	        if (this.finalSaveBlockingItems.length > 0) return `보완 필요 ${this.finalSaveBlockingItems.length}건`;
	        if (this.primaryFinalSaveWarningItems.length > 0) return `확인 필요 ${this.primaryFinalSaveWarningItems.length}건`;
	        if (this.candidateNewReviewCount > 0) return `진료 참고 내용 ${this.candidateNewReviewCount}건`;
	        if (this.canApproveFinal || this.soapReadyForApproval) return this.statusLabel('ready_for_final_save');
	        if (this.candidateChartAppliedCount > 0) return '차트 반영 완료';
	        return this.candidateUnifiedPrimaryLabel;
	    }

	    public get bottomBarSentenceLabel() {
	        if (this.readOnlyMode) return this.finalChartSummaryText;
	        if (this.soapValidating || this.soapReadying) return '최종 저장 전 점검 중입니다';
	        if (this.hasSoapDirty) return `미저장 ${this.soapDirtySentenceCount}문장 · 저장 후 점검`;
	        if (!this.hasSoapDraft && !this.hasPrecheckSubmitted) return '환자 답변을 입력하면 차트에 넣을 내용을 확인할 수 있습니다';
	        if (!this.hasSoapDraft && this.candidateNewReviewCount > 0) return '참고 내용을 확인하거나 문진 탭에서 직접 작성';
	        if (!this.hasSoapDraft) return '문진 탭에서 작성 시작';
	        if (this.finalSaveBlockingItems.length > 0) return '최종 저장 전에 보완해 주세요';
	        if (this.primaryFinalSaveWarningItems.length > 0) return '최종 저장 전에 확인해 주세요';
	        if (this.canApproveFinal || this.soapReadyForApproval) return '점검 완료 · 최종 저장 가능';
	        return this.candidateUnifiedCompactSummaryLabel;
	    }

	    public get bottomBarMobileStatusLabel() {
	        if (this.readOnlyMode) return '저장됨';
	        if (!this.hasEncounter) return '방문 필요';
	        if (this.soapSaving) return '저장 중';
	        if (this.soapValidating || this.soapReadying) return '점검 중';
	        if (this.soapGenerating || this.soapCreating) return '정리 중';
	        if (this.soapDirtySentenceCount > 0) return `미저장 ${this.soapDirtySentenceCount}`;
	        if (!this.hasSoapDraft && !this.hasPrecheckSubmitted) return '문진 입력';
	        if (!this.hasSoapDraft && this.candidateNewReviewCount > 0) return `참고 ${this.candidateNewReviewCount}`;
	        if (!this.hasSoapDraft) return '차트 시작';
	        if (this.finalSaveBlockingItems.length > 0) return `보완 ${this.finalSaveBlockingItems.length}`;
	        if (this.primaryFinalSaveWarningItems.length > 0) return `확인 ${this.primaryFinalSaveWarningItems.length}`;
	        if (this.candidateNewReviewCount > 0) return `참고 ${this.candidateNewReviewCount}`;
	        if (this.canApproveFinal || this.soapReadyForApproval) return '저장 가능';
	        if (this.candidateChartAppliedCount > 0) return `차트 ${this.candidateChartAppliedCount}`;
	        return '진료 중';
	    }

	    public get bottomBarMobileMetaLabel() {
	        if (this.readOnlyMode) return this.finalChartSummaryText ? '최종 기록' : '';
	        if (this.soapSaving) return '임시 저장';
	        if (this.soapValidating || this.soapReadying) return '최종 점검';
	        if (this.hasSoapDirty) return '저장 후 점검';
	        if (!this.hasSoapDraft && !this.hasPrecheckSubmitted) return '차트 전';
	        if (!this.hasSoapDraft) return '문진 탭';
	        if (this.finalSaveBlockingItems.length > 0 || this.primaryFinalSaveWarningItems.length > 0) return '점검 내용';
	        if (this.candidateNewReviewCount > 0) return '참고 확인';
	        if (this.canApproveFinal || this.soapReadyForApproval) return '최종 저장';
	        return `${this.soapActiveSentenceCount || 0}문장`;
	    }

	    public get bottomCheckCanOpenResult() {
	        return !this.readOnlyMode && !this.hasSoapDirty && (
	            this.finalSaveBlockingItems.length > 0
	            || this.primaryFinalSaveWarningItems.length > 0
	            || this.canApproveFinal
	            || this.soapReadyForApproval
	            || !!this.finalMessage
	            || !!this.finalError
	        );
	    }

	    public get bottomSaveButtonLabel() {
	        if (this.soapSaving) return '저장 중';
	        if (this.hasSoapDirty) return '임시 저장';
	        if (this.hasSoapDraft) return '임시 저장됨';
	        return '임시 저장';
	    }

    public get bottomSaveStateLabel() {
        return this.hasSoapDraft ? '임시 저장 완료' : '차트 작성 전';
    }

    public get hasUnsavedWorkspaceChanges() {
        return this.hasSoapDirty || this.checklistDirtyCount > 0;
    }

    public get workspaceSaveStateCode() {
        if (this.soapSaving || this.savingChecklistAll || !!this.savingChecklistItemCode) return 'saving';
        if (this.hasUnsavedWorkspaceChanges) return 'dirty';
        if (this.readOnlyMode) return 'final';
        if (this.soapReadyForApproval || this.canApproveFinal) return 'ready';
        if (this.hasSoapDraft) return 'saved';
        return 'empty';
    }

    public get workspaceBottomBarClass() {
        return `workspace-bottom-bar workspace-bottom-bar--${this.workspaceSaveStateCode}`;
    }

	    public get bottomSaveDisabled() {
	        return this.clinicalReadOnly || this.busy || !this.hasSoapDraft || !this.hasSoapDirty;
	    }

	    public get bottomSaveButtonTitle() {
	        if (this.clinicalReadOnly) return '차트를 수정할 수 없는 상태입니다.';
	        if (!this.hasSoapDraft) return '저장할 한의 차트가 없습니다.';
	        if (!this.hasSoapDirty) return '저장할 변경사항이 없습니다.';
	        if (this.busy) return '현재 작업이 끝난 뒤 저장할 수 있습니다.';
	        return '미저장 변경사항을 저장합니다. Ctrl/Cmd+S';
	    }

	    public get bottomCheckButtonLabel() {
	        if (this.soapValidating || this.soapReadying) return '검토 중';
	        if (this.bottomCheckCanOpenResult) return '점검 갱신';
	        return '진료기록 점검';
	    }

	    public get bottomCheckDisabled() {
	        return this.clinicalReadOnly || this.busy || !this.hasSoapDraft || this.hasSoapDirty;
	    }

	    public get bottomCheckButtonTitle() {
	        if (this.clinicalReadOnly) return '최종 점검을 진행할 수 없는 상태입니다.';
	        if (!this.hasSoapDraft) return '먼저 한의 차트를 작성하세요.';
	        if (this.hasSoapDirty) return '변경사항을 저장한 뒤 최종 점검을 진행하세요.';
	        if (this.busy) return '현재 작업이 끝난 뒤 최종 점검할 수 있습니다.';
	        return '최종 저장 전에 작성 보완과 확인 항목을 점검합니다.';
	    }

	    public get bottomFinalSaveDisabled() {
	        return this.busy || (!this.canApproveFinal && !this.bottomCheckCanOpenResult);
	    }

	    public get finalSaveSubmitDisabled() {
	        return this.busy || !this.canApproveFinal || this.finalSaveWarningItems.length > 0;
	    }

	    public get bottomFinalSaveButtonLabel() {
	        if (this.finalApproving) return '저장 중';
	        if (this.canApproveFinal) return '최종 저장';
	        if (this.bottomCheckCanOpenResult) return '점검 내용';
	        return '최종 저장';
	    }

	    public get bottomFinalSaveButtonTitle() {
	        if (this.finalApproving) return '최종 저장 중입니다.';
	        if (this.hasSoapDirty) return '변경사항을 저장한 뒤 최종 점검하세요.';
	        if (!this.hasSoapDraft) return '최종 저장할 한의 차트가 없습니다.';
	        if (this.finalSaveBlockingItems.length > 0) return '더 작성할 내용을 점검 내용에서 확인하세요.';
	        if (this.finalSaveWarningItems.length > 0) return '확인만 하면 되는 내용을 점검 내용에서 확인하세요.';
	        if (this.canApproveFinal) return '확인 완료된 차트를 최종 저장합니다.';
	        if (!this.soapReadyForApproval) return '최종 점검을 완료한 뒤 최종 저장할 수 있습니다.';
	        if (this.busy) return '현재 작업이 끝난 뒤 최종 저장할 수 있습니다.';
	        return '최종 저장 대기 상태입니다.';
	    }

	    public get finalSaveSubmitButtonTitle() {
	        if (this.finalApproving) return '최종 저장 중입니다.';
	        if (this.hasSoapDirty) return '변경사항을 저장한 뒤 최종 점검하세요.';
	        if (!this.hasSoapDraft) return '최종 저장할 한의 차트가 없습니다.';
	        if (this.finalSaveBlockingItems.length > 0) return '더 작성할 내용을 보완한 뒤 최종 점검을 갱신하세요.';
	        if (this.finalSaveWarningItems.length > 0) return '확인만 하면 되는 내용을 확인 완료로 표시한 뒤 최종 저장할 수 있습니다.';
	        if (this.canApproveFinal) return '점검 완료된 차트를 최종 저장합니다.';
	        if (!this.soapReadyForApproval) return '최종 점검을 완료한 뒤 최종 저장할 수 있습니다.';
	        if (this.busy) return '현재 작업이 끝난 뒤 최종 저장할 수 있습니다.';
	        return '최종 저장 대기 상태입니다.';
	    }

	    public get bottomBarReviewCount() {
	        return this.readOnlyMode ? 0 : Math.min(99, this.finalSaveBlockingItems.length + this.primaryFinalSaveWarningItems.length);
	    }

	    public get bottomBarReviewLabel() {
	        return `확인 ${this.bottomBarReviewCount}`;
	    }

    public get bottomBarReviewAriaLabel() {
        return `${this.bottomBarReviewLabel}건`;
    }

    public get bottomFinalActionClass() {
        const tone = !this.bottomFinalSaveDisabled ? 'primary' : 'secondary';
        return `button button--${tone} bottom-action--charting`;
    }

    public get finalGateConfirmableLabels() {
        return this.finalGateConfirmableItems.map((item) => this.gateItemTitle(item)).join(', ');
    }

    public get clientFinalGateBlockingItems() {
        const items: WorkspaceMap[] = [];
        if (this.hasSoapDirty) {
            items.push({
                code: 'unsaved_sentences',
                message: '미저장 변경 있음',
                severity: 'blocking',
                category: 'soap',
                proceed_mode: 'resolve_before_save',
            action_label: '저장 후 확인',
                count: this.soapDirtySentenceCount,
            });
        }
        this.soapSemanticConsistencyIssues
            .filter((issue) => issue.code === 'source_anatomic_mismatch')
            .forEach((issue) => {
                items.push({
                    ...issue,
                    message: issue.message || issue.summary,
                    severity: 'blocking',
                    category: 'soap_validation',
                });
            });
        return items;
    }

    private finalGateItemKey(item: WorkspaceMap) {
        if (!item) return '';
        const code = String(item.code || item.message || item.title || '');
        const category = String(item.category || '');
        const details = item.details && typeof item.details === 'object' ? item.details : {};
        const locator = String(
            item.sentence_id
            || item.section
            || item.soap_section
            || this.asArray(details.sections).join(',')
            || ''
        );
        return `${category}:${code}:${locator}`;
    }

    private uniqueFinalGateItems(items: WorkspaceMap[]) {
        const seen: Record<string, boolean> = {};
        const result: WorkspaceMap[] = [];
        items.forEach((item) => {
            const key = this.finalGateItemKey(item);
            if (key && seen[key]) return;
            if (key) seen[key] = true;
            result.push(item);
        });
        return result;
    }

    public get finalSaveBlockingItems() {
        const activeGateItems = this.finalGateBlockingItems.filter((item) => (
            this.validationIssueCode(item) !== 'source_anatomic_mismatch'
            && !this.isLocallyResolvedFinalGateItem(item)
        ));
        const items = this.clientFinalGateBlockingItems.concat(activeGateItems.map((item) => ({
            ...(item && typeof item === 'object' ? item : { code: String(item || '') }),
            severity: 'blocking',
            category: item && item.category ? item.category : 'final_save',
        })));
        const validationBlocks = this.soapValidationErrors
            .filter((item) => this.validationIssueCode(item) !== 'source_anatomic_mismatch')
            .map((item) => ({
            ...(item && typeof item === 'object' ? item : {}),
            code: this.validationIssueCode(item),
            message: this.validationIssueText(item),
            severity: 'blocking',
            category: 'soap_validation',
        }));
        return this.uniqueFinalGateItems(items.concat(validationBlocks));
    }

    private isLocallyResolvedFinalGateItem(item: WorkspaceMap) {
        if (String(item && item.code || '') !== 'missing_required_sections') return false;
        const sections = this.finalGateItemSections(item);
        return sections.length > 0 && sections.every((section) => this.soapSectionCompactText(section).trim() !== '');
    }

    public get finalSaveWarningItems() {
        const gateWarnings = this.finalGateConfirmed ? [] : this.finalGateConfirmableItems
            .filter((item) => this.validationIssueCode(item) !== 'mixed_anatomic_scope')
            .map((item) => ({
                ...(item && typeof item === 'object' ? item : { code: String(item || '') }),
                severity: 'warning',
                category: item && item.category ? item.category : 'confirmation_required',
            }));
        const validationWarnings = this.finalWarningsAcknowledged ? [] : this.soapValidationWarnings
            .filter((item) => this.validationIssueCode(item) !== 'mixed_anatomic_scope')
            .map((item) => ({
                ...(item && typeof item === 'object' ? item : {}),
                code: this.validationIssueCode(item),
                message: this.validationIssueText(item),
                severity: 'warning',
                category: 'soap_validation',
            }));
        const semanticWarnings = this.finalWarningsAcknowledged ? [] : this.soapSemanticConsistencyIssues
            .filter((issue) => issue.code === 'mixed_anatomic_scope')
            .map((issue) => ({
                ...issue,
                message: issue.message || issue.summary,
                severity: 'warning',
                category: 'soap_validation',
            }));
        return this.uniqueFinalGateItems(gateWarnings.concat(validationWarnings).concat(semanticWarnings));
    }

    public get primaryFinalSaveWarningItems() {
        return this.finalSaveWarningItems.filter((item) => !this.isSoftFinalSaveWarningItem(item));
    }

    private isSoftFinalSaveWarningItem(item: WorkspaceMap) {
        return this.validationIssueCode(item) === 'precheck_unsubmitted';
    }

    public get finalSaveConfirmedItems() {
	        const items: WorkspaceMap[] = [];
	        if (this.precheck && this.precheck.submitted) {
	            items.push({ title: '예진 제출', summary: '제출됨' });
	        }
	        if (this.checklistItems.length && this.checklistPendingCount <= 0) {
	            items.push({ title: '진료 전 확인 완료', summary: `${this.checklistItems.length}개 확인` });
	        }
	        if (this.redFlagItems.length && !this.hasUnresolvedRedFlags) {
	            items.push({ title: '진료 전 주의 확인', summary: `${this.redFlagItems.length}개 확인` });
	        }
        if (this.referenceComparisonItems.length) {
            items.push({ title: '건강인 참조 비교', summary: this.referenceComparisonSummary });
        }
        if (this.standardToolCompletedCount > 0) {
            items.push({ title: '표준도구 점수', summary: `${this.standardToolCompletedCount}개 산출` });
        }
        if (this.soapDraftId) {
            items.push({ title: '한의 차트', summary: this.finalSectionCounts.map((item) => `${this.soapSectionDisplayName(item.section)} ${item.count}`).join(', ') });
        }
        if (this.soapReadyForApproval || this.canApproveFinal || this.readOnlyMode) {
	            items.push({ title: this.readOnlyMode ? '최종 저장됨' : '최종 저장 가능', summary: this.finalGateReadyText });
	        }
        if (this.finalGateConfirmed && this.finalGateConfirmableItems.length) {
            this.finalGateConfirmableItems.forEach((item) => {
                items.push({ title: this.gateItemTitle(item), summary: this.gateItemSummary(item) || '한의사 확인함' });
            });
        }
        return items;
    }

    public get finalSaveAvailabilityLabel() {
        if (this.readOnlyMode) return '최종 저장됨';
        if (this.hasSoapDirty) return '저장 후 점검';
        if (this.finalSaveBlockingItems.length) return '작성 보완 필요';
        if (this.finalGateNeedsConfirmation && !this.finalGateConfirmed) return '확인 후 저장 가능';
        if (this.finalSaveWarningItems.length) return '확인 후 저장 가능';
        if (this.canApproveFinal) return '지금 저장 가능';
        if (!this.soapDraftId) return '진료기록 작성 필요';
        return '최종 점검 전';
    }

    public get finalSaveReadinessHeadline() {
        if (this.readOnlyMode) return this.finalChartSummaryText;
        if (this.hasSoapDirty) return '먼저 변경사항을 저장하세요.';
        if (this.finalSaveBlockingItems.length) return '저장 전에 더 작성해야 할 내용이 있습니다.';
        if (this.finalGateNeedsConfirmation && !this.finalGateConfirmed) return '새로 작성할 내용은 없고 확인만 남았습니다.';
        if (this.finalSaveWarningItems.length) return '확인만 마치면 최종 저장할 수 있습니다.';
        if (this.canApproveFinal) return '추가 작성 없이 최종 저장할 수 있습니다.';
        if (!this.soapDraftId) return '문진부터 진료기록을 작성하세요.';
        return '최종 저장 전 점검을 진행하세요.';
    }

    public get finalSaveAvailabilityDescription() {
        if (this.readOnlyMode) return this.finalChartSummaryText;
        if (this.hasSoapDirty) return `미저장 ${this.soapDirtySentenceCount}문장을 저장한 뒤 최종 점검하세요.`;
        if (this.finalSaveBlockingItems.length) return `${this.finalSaveBlockingItems.length}건을 보완한 뒤 다시 점검하면 저장 가능 여부를 확인할 수 있습니다.`;
        if (this.finalGateNeedsConfirmation && !this.finalGateConfirmed) return `${this.finalGateConfirmableItems.length}건을 확인 완료로 표시하면 최종 저장할 수 있습니다.`;
        if (this.finalSaveWarningItems.length) return `${this.finalSaveWarningItems.length}건을 확인 완료로 표시하면 최종 저장할 수 있습니다.`;
        if (this.canApproveFinal) return '문진, 진찰/소견, 변증/진단, 치료/처방 영역이 최종 저장 가능한 상태입니다.';
        return '영역별 내용을 점검한 뒤 최종 저장할 수 있습니다.';
    }

    public get finalSaveAvailabilityClass() {
        const tone = this.readOnlyMode || this.canApproveFinal
            ? 'success'
            : (this.hasSoapDirty ? 'warning' : (this.finalSaveBlockingItems.length ? 'danger' : ((this.finalGateNeedsConfirmation || this.finalSaveWarningItems.length) ? 'warning' : 'neutral')));
        return `status-chip status-chip--${tone}`;
    }

    public get redFlagSummaryLabel() {
        if (!this.redFlagItems.length) return '진료 중 확인';
        if (this.hasUnresolvedRedFlags) return `확인할 내용 ${this.unresolvedRedFlagCount}건`;
        return '진료 전 주의 확인 완료';
    }

    public get redFlagSummaryClass() {
        const tone = this.hasUnresolvedRedFlags ? 'danger' : 'success';
        return `risk-inline-panel risk-inline-panel--${tone}`;
    }

    public get redFlagStatusBadgeClass() {
        const tone = this.hasUnresolvedRedFlags ? 'danger' : 'success';
        return `status-chip status-chip--${tone}`;
    }

    public get checklistCounts() {
        const counts = this.checklist && this.checklist.counts ? this.checklist.counts : {};
        return {
            total: Number(counts.total || 0),
            completed: Number(counts.completed || 0),
            pending: Number(counts.pending || 0),
        };
    }

    public get checklistDirtyItems() {
        return this.checklistItems.filter((item) => {
            const draft = this.checklistDraft(item);
            return !!(draft && draft.dirty);
        });
    }

    public get checklistDirtyCount() {
        return this.checklistDirtyItems.length;
    }

    public get checklistSummarySaveLabel() {
        if (this.savingChecklistAll || this.savingChecklistItemCode) return '저장 중';
        if (!this.activeChecklistSessionId) return '시작 전';
        if (this.checklistDirtyCount > 0) return `변경 ${this.checklistDirtyCount}`;
        if (this.checklistItems.length && this.checklistLastSavedAt) return '저장됨';
        return '변경 없음';
    }

    public get checklistDrawerStateLabel() {
        if (this.savingChecklistAll || this.savingChecklistItemCode) return '저장 중';
        if (this.checklistDirtyCount > 0) return `변경 ${this.checklistDirtyCount}`;
        if (this.activeChecklistSessionId) return '변경 없음';
        return '시작 전';
    }

    public get checklistDrawerStateClass() {
        const tone = (this.savingChecklistAll || this.savingChecklistItemCode)
            ? 'ai'
            : (this.checklistDirtyCount > 0 ? 'warning' : (this.activeChecklistSessionId ? 'success' : 'neutral'));
        return `status-chip status-chip--${tone}`;
    }

    public get checklistLastSavedAt() {
        const updated = this.checklistItems
            .map((item) => item && item.updated ? String(item.updated) : '')
            .filter((value) => value !== '');
        return updated.length ? updated[0] : '';
    }

	    public get checklistLastSavedLabel() {
	        if (this.savingChecklistAll || this.savingChecklistItemCode) return '저장 중';
	        if (!this.activeChecklistSessionId) return '시작 전';
	        if (this.checklistDirtyCount > 0) return '미저장 변경';
	        if (this.checklistLastSavedAt) return `마지막 저장 ${this.checklistLastSavedAt}`;
	        return '변경 없음';
	    }

    public get checklistSummaryHint() {
        if (!this.activeChecklistSessionId || !this.checklistItems.length) return '시작 전';
        if (this.checklistDirtyCount > 0) return `변경 ${this.checklistDirtyCount}`;
        if (this.checklistRiskOpenCount > 0) return `주의 ${this.checklistRiskOpenCount}`;
        if (this.checklistPendingCount > 0) return `미확인 ${this.checklistPendingCount}`;
        return '확인 완료';
    }

    public get checklistCompactMeta() {
        const parts = [
            `주의 ${this.checklistRiskOpenCount}`,
            `미확인 ${this.checklistPendingCount}`,
        ];
        if (this.checklistDeferredCount) parts.push(`나중에 보기 ${this.checklistDeferredCount}`);
        return parts.join(' · ');
    }

    public get checklistPendingCount() {
        return this.checklistItems.filter((item) => {
            const status = item && item.status ? String(item.status) : 'unchecked';
            return !this.checklistStatusResolved(status);
        }).length;
    }

    public get checklistDeferredCount() {
        return this.checklistItems.filter((item) => item && item.status === 'deferred').length;
    }

    public get checklistRiskOpenCount() {
        return this.checklistItems.filter((item) => this.isRiskChecklistItem(item) && !this.isRiskResolved(item)).length;
    }

    public get checklistPriorityItems() {
        const priority = this.checklistItems.filter((item) => {
            if (!item) return false;
            const status = String(item.status || 'unchecked');
            return this.isRiskChecklistItem(item) && !this.isRiskResolved(item)
                || status === 'deferred'
                || status === 'unchecked'
                || status === '';
        });
        return (priority.length ? priority : this.checklistItems).slice(0, 3);
    }

    public get checklistQueueSummary() {
        if (!this.checklistItems.length) return '진료 전 확인 항목 없음';
        if (this.checklistRiskOpenCount > 0) return `주의 ${this.checklistRiskOpenCount}건 우선`;
        if (this.checklistDeferredCount > 0) return `나중에 보기 ${this.checklistDeferredCount}건`;
        if (this.checklistPendingCount > 0) return `미확인 ${this.checklistPendingCount}건`;
        return '핵심 확인 완료';
    }

    public get checklistProgressText() {
        const counts = this.checklistCounts;
        if (counts.total <= 0) return '0/0';
        return `${counts.completed}/${counts.total}`;
    }

    public get checklistProgressPercent() {
        const counts = this.checklistCounts;
        if (counts.total <= 0) return 0;
        return Math.max(0, Math.min(100, Math.round((counts.completed / counts.total) * 100)));
    }

    public get soapSentenceCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapItems(section).length, 0);
    }

    public get soapActiveSentenceCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapItems(section).filter((item) => item.status !== 'excluded').length, 0);
    }

    public get soapDraftId() {
        return (this.soap && this.soap.draft_id) || (this.soap && this.soap.draft && this.soap.draft.id) || '';
    }

    public get hasSoapDraft() {
        return !!this.soapDraftId || this.soapSentenceCount > 0;
    }

    public get hasSoapDirty() {
        return this.soapSectionOrder.some((section) => this.soapItems(section).some((item) => item.dirty));
    }

    private clinicalAnatomicScopesFromText(value: any) {
        let raw = '';
        if (Array.isArray(value)) raw = value.map((item) => String(item || '')).join(' ');
        else if (value && typeof value === 'object') {
            try {
                raw = JSON.stringify(value);
            } catch (error) {
                raw = String(value || '');
            }
        } else raw = String(value || '');
        const normalized = this.normalizeLookupLabel(raw);
        const definitions = [
            { code: 'low_back', keywords: ['요통', '허리', '요부', '요추'] },
            { code: 'shoulder', keywords: ['어깨', '견부'] },
            { code: 'knee', keywords: ['무릎', '슬관절'] },
            { code: 'head', keywords: ['두통', '머리', '두부'] },
            { code: 'abdomen', keywords: ['복통', '복부', '상복부', '하복부'] },
            { code: 'lower_limb', keywords: ['다리', '하지저림', '하지감각', '하지통증', '하지방사'] },
        ];
        const scopes = definitions
            .filter((definition) => definition.keywords.some((keyword) => normalized.indexOf(this.normalizeLookupLabel(keyword)) >= 0))
            .map((definition) => definition.code);
        if (/(?<![가-힣])하지(?=(?:\s*(?:[,.;:·/]|및|와|과|로|가|는|를|의|에|에서|부위|통증|저림|감각|근력|방사|압통|소견|검사|운동|부종|열감|냉감)|\s*$))/.test(raw)) {
            scopes.push('lower_limb');
        }
        return this.dedupeStrings(scopes);
    }

    private soapSentenceSourceAnatomicScopes(item: SoapSentenceDraft) {
        if (!item) return [];
        const meta = item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
        const primaryValues = [payload.request_text, payload.source_excerpt].filter((value) => !!value);
        const primaryScopes = this.clinicalAnatomicScopesFromText(primaryValues);
        if (primaryScopes.length) return primaryScopes;
        const sourceValues: any[] = [item.source_refs || []];
        const keys = [
            'patient_cues',
            'clinical_evidence',
            'source_excerpt',
            'request_text',
            'chart_focus',
            'clinical_summary',
            'supporting_evidence',
            'connection_reasons',
        ];
        [meta, payload].forEach((source) => {
            keys.forEach((key) => {
                if (source[key] !== undefined && source[key] !== null && source[key] !== '') sourceValues.push(source[key]);
            });
        });
        return this.clinicalAnatomicScopesFromText(sourceValues);
    }

    private anatomicScopesNeedSeparation(scopes: string[]) {
        const unique = this.dedupeStrings(scopes || []);
        if (unique.length < 2) return false;
        const relatedGroups = [
            ['low_back', 'lower_limb'],
        ];
        return !relatedGroups.some((group) => unique.every((scope) => group.indexOf(scope) >= 0));
    }

    public get soapSemanticConsistencyIssues(): ChartQualityIssue[] {
        const issues: ChartQualityIssue[] = [];
        this.soapSectionOrder.forEach((section) => {
            this.soapItems(section)
                .filter((item) => item && item.status !== 'excluded' && String(item.text || '').trim() !== '')
                .forEach((item, index) => {
                    const chartScopes = this.clinicalAnatomicScopesFromText(item.text);
                    const sourceScopes = this.soapSentenceSourceAnatomicScopes(item);
                    const hasUnrelatedExtraScope = chartScopes.some((scope) => sourceScopes.indexOf(scope) < 0)
                        && this.anatomicScopesNeedSeparation(chartScopes.concat(sourceScopes));
                    if (
                        chartScopes.length
                        && sourceScopes.length
                        && (
                            !chartScopes.some((scope) => sourceScopes.indexOf(scope) >= 0)
                            || hasUnrelatedExtraScope
                        )
                    ) {
                        issues.push({
                            id: `${section}-source-anatomic-mismatch-${item.id || index}`,
                            code: 'source_anatomic_mismatch',
                            section,
                            sentence_id: item.id || '',
                            title: '문장과 근거 부위 불일치',
                            message: '차트 문장과 근거의 신체 부위가 일치하지 않습니다.',
                            summary: '근거와 다른 신체 부위가 기록되어 있습니다. 문장 또는 근거를 다시 확인하세요.',
                            tone: 'danger',
                            category: 'soap_validation',
                            details: {
                                sections: [section],
                                chart_scopes: chartScopes,
                                source_scopes: sourceScopes,
                            },
                        });
                    }
                    if (this.anatomicScopesNeedSeparation(chartScopes)) {
                        issues.push({
                            id: `${section}-mixed-anatomic-scope-${item.id || index}`,
                            code: 'mixed_anatomic_scope',
                            section,
                            sentence_id: item.id || '',
                            title: '서로 다른 부위 한 문장에 포함',
                            message: '한 문장에 서로 다른 신체 부위가 함께 기록되어 있습니다.',
                            summary: '부위별 문장으로 나누거나 함께 기록할 이유가 맞는지 확인하세요.',
                            tone: 'warning',
                            category: 'soap_validation',
                            details: {
                                sections: [section],
                                chart_scopes: chartScopes,
                            },
                        });
                    }
                });
        });
        return issues;
    }

    public get chartQualityIssues(): ChartQualityIssue[] {
        const issues: ChartQualityIssue[] = this.soapSemanticConsistencyIssues.slice();
        this.soapSectionOrder.forEach((section) => {
            const text = this.soapSectionCompactText(section);
            const lines = text.split('\n').map((line) => line.trim()).filter((line) => line !== '');
            const seen: Record<string, boolean> = {};
            lines.forEach((line, index) => {
                const key = this.chartDisplayDedupeKey(line, section);
                if (key && seen[key]) {
                    issues.push({
                        id: `${section}-duplicate-${index}`,
                        section,
                        title: '중복 문장 확인',
                        summary: this.truncateText(line, 72),
                        tone: 'warning',
                    });
                }
                if (key) seen[key] = true;
                if (line.length > 220) {
                    issues.push({
                        id: `${section}-long-${index}`,
                        section,
                        title: '긴 문장 나누기',
                        summary: `${line.length}자 · 핵심 내용을 두 문장으로 나누면 읽기 쉽습니다.`,
                        tone: 'warning',
                    });
                }
                if (/^[^:：]{1,20}[:：]\s*$/.test(line)) {
                    issues.push({
                        id: `${section}-empty-${index}`,
                        section,
                        title: '비어 있는 항목',
                        summary: `${line.replace(/[:：]\s*$/, '')} 내용을 입력하거나 항목을 삭제하세요.`,
                        tone: 'warning',
                    });
                }
                if (/(테스트|샘플|lorem|asdf|확인\s*예정)/i.test(line)) {
                    issues.push({
                        id: `${section}-placeholder-${index}`,
                        section,
                        title: '임시 표현 확인',
                        summary: this.truncateText(line, 72),
                        tone: 'warning',
                    });
                }
            });
        });
        const unique: ChartQualityIssue[] = [];
        const keys: string[] = [];
        issues.forEach((issue) => {
            const key = `${issue.section}:${issue.title}:${issue.summary}`;
            if (keys.indexOf(key) >= 0) return;
            keys.push(key);
            unique.push(issue);
        });
        return unique.slice(0, 8);
    }

    public async focusChartQualityIssue(issue: ChartQualityIssue) {
        if (!issue) return;
        this.activeSoapSection = this.normalizedSoapSection(issue.section);
        this.mobilePanel = 'soap';
        this.saveWorkspacePreferences();
        await this.service.render();
        this.focusSoapSectionEditor(issue.section);
    }

    public get soapDirtySentenceCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapSectionDirtyCount(section), 0);
    }

    public get soapUnreviewedAiCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapSectionUnreviewedAiCount(section), 0);
    }

    public get soapNewCandidateReadyCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapItems(section).filter((item) => this.soapCandidateVersionStatus(item) === 'new_candidate_ready').length, 0);
    }

    public get soapPreviousVersionCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapItems(section).filter((item) => this.soapCandidateVersionStatus(item) === 'previous_version').length, 0);
    }

    public get soapManualSentenceCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapSectionManualCount(section), 0);
    }

    public get soapReviewedSentenceCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.soapSectionReviewedCount(section), 0);
    }

    public get soapPendingSuggestionCount() {
        return this.soapSectionOrder.reduce((total, section) => total + this.pendingSoapSuggestions(section).length, 0);
    }

    public get soapValidationErrors() {
        return this.asArray(this.soapValidation.errors);
    }

    public get soapValidationWarnings() {
        return this.asArray(this.soapValidation.warnings);
    }

    public get soapValidationIssueCount() {
        return this.soapValidationErrors.length + this.soapValidationWarnings.length + (this.soapNeedsRegeneration ? 1 : 0);
    }

    public get soapNeedsRegeneration() {
        const draft = this.soap && this.soap.draft ? this.soap.draft : {};
        return !!(
            this.soap
            && (this.soap.needs_regeneration || this.soap.status === 'needs_regeneration' || draft.status === 'needs_regeneration' || draft.source_freshness === 'stale')
        );
    }

    public get soapRegenerationText() {
        const reason = this.soap && this.soap.regeneration_reason ? this.soap.regeneration_reason : '';
        if (reason === 'checklist_response_changed') return '진료 전 확인 변경 내용을 확인해 주세요';
        if (reason === 'precheck_submitted') return '예진 제출 내용을 확인해 주세요';
        if (reason === 'precheck_doctor_supplement_saved') return '예진 보완 내용을 확인해 주세요';
        if (reason === 'precheck_resubmitted_changed') return '예진 재제출 내용을 확인해 주세요';
        if (reason === 'precheck_content_changed') return '예진 수정 내용을 확인해 주세요';
        return reason || '최신 진료 내용을 확인해 주세요';
    }

    public get finalChartId() {
        return (this.finalChart && this.finalChart.final_chart_id)
            || (this.finalChart && this.finalChart.final_chart && this.finalChart.final_chart.id)
            || (this.encounter && this.encounter.final_chart_id)
            || '';
    }

    public get readOnlyMode() {
        return !!(this.finalChart && this.finalChart.read_only) || !!this.finalChartId;
    }

    public get emrExportActionLabel() {
        if (this.emrExporting) return 'EMR 확인 중';
        return this.emr && this.emr.mode === 'sandbox' ? 'EMR Sandbox 검증' : 'EMR 전송';
    }

    public async exportFinalChartToEmr() {
        if (!this.finalChartId || this.emrExporting || !this.emr || this.emr.status === 'disabled') return;
        this.emrExporting = true;
        this.emrMessage = '';
        await this.service.render();
        const result = await this.callApi('export_final_chart_to_emr', {
            encounter_id: this.encounterId,
            final_chart_id: this.finalChartId,
        });
        this.emrExporting = false;
        if (!result.ok) {
            this.emrMessage = result.message || 'EMR 연동을 확인하지 못했습니다.';
            await this.service.render();
            return;
        }
        const payload = result.data && result.data.result ? result.data.result : result.data;
        if (payload && payload.status === 'sandbox_acknowledged') {
            this.emrMessage = 'Sandbox에서 FHIR 묶음·멱등성·매핑을 검증했습니다. 외부 EMR에는 기록하지 않았습니다.';
        } else if (payload && payload.status === 'already_exported') {
            this.emrMessage = '동일한 확정 차트의 EMR 연동 기록이 이미 있습니다.';
        } else {
            this.emrMessage = payload && payload.exported ? 'EMR 전송을 완료했습니다.' : 'EMR 연동 상태를 확인했습니다.';
        }
        await this.service.render();
    }

    public get currentRole() {
        const contextActor = this.requestContext && this.requestContext.actor ? this.requestContext.actor : {};
        return contextActor.role || this.service.auth.session?.role || '';
    }

    public get currentRoleLabel() {
        return hanscribeRoleLabel(this.currentRole);
    }

    public get canUseClinicalActions() {
        return hanscribeCan(this.currentRole, 'clinical_soap_edit');
    }

    public get canGenerateClinicalAi() {
        return hanscribeCan(this.currentRole, 'clinical_ai_generate');
    }

    public get canApproveFinalChart() {
        return hanscribeCan(this.currentRole, 'clinical_final_approve');
    }

    public get canManageEncounterState() {
        return hanscribeCan(this.currentRole, 'board_start_encounter');
    }

    public get clinicalReadOnly() {
        return this.readOnlyMode || !this.canUseClinicalActions;
    }

    public get finalStatus() {
        if (this.readOnlyMode) return 'approved';
        return (this.finalChart && this.finalChart.status) || 'validation_failed';
    }

    public get finalBlockingItems() {
        const errors = this.asArray(this.finalChart && this.finalChart.errors);
        if (errors.length) return errors;
        return this.asArray(this.finalChart && this.finalChart.blocking_reasons).map((code) => ({ code, message: this.finalBlockerLabel(String(code)) }));
    }

    public get finalSectionCounts() {
        const counts = this.finalChart && this.finalChart.section_counts ? this.finalChart.section_counts : {};
        const hasServerCounts = !this.hasSoapDirty && counts && Object.keys(counts).length > 0;
        return this.soapSectionOrder.map((section) => ({
            section,
            count: hasServerCounts
                ? Number(counts[section] || 0)
                : this.soapItems(section).filter((item) => item.status !== 'excluded').length,
        }));
    }

    public get finalSectionReviewRows() {
        return this.finalSectionCounts.map((item) => {
            const section = this.normalizedSoapSection(item.section);
            const count = Number(item.count || 0);
            const blockingTexts = this.finalSectionIssueTexts(section, this.finalSaveBlockingItems);
            const warningTexts = this.finalSectionIssueTexts(section, this.finalSaveWarningItems);
            const details = this.dedupeStrings(
                (count <= 0 ? [`${this.finalSectionMissingHint(section)} 작성`] : [])
                    .concat(blockingTexts)
                    .concat(warningTexts)
            ).slice(0, 3);
            const needsWriting = count <= 0 || blockingTexts.length > 0;
            const needsConfirmOnly = !needsWriting && warningTexts.length > 0;
            const stateLabel = needsWriting ? '더 작성' : (needsConfirmOnly ? '확인만' : '점검 완료');
            const tone = needsWriting ? 'blocking' : (needsConfirmOnly ? 'confirmable' : 'complete');
            let summary = `${count}문장 점검 완료`;
            if (needsWriting && count <= 0) summary = `${this.finalSectionMissingHint(section)} 내용을 한 줄 이상 작성하세요.`;
            else if (needsWriting) summary = blockingTexts[0] || '저장 전 보완이 필요합니다.';
            else if (needsConfirmOnly) summary = warningTexts[0] || '내용 확인 후 저장할 수 있습니다.';
            const visibleDetails = details.filter((detail) => detail !== summary).slice(0, 3);
            return {
                section,
                label: this.soapSectionDisplayName(section),
                count,
                stateLabel,
                tone,
                summary,
                details: visibleDetails,
            };
        });
    }

    public finalSectionReviewRowClass(row: WorkspaceMap) {
        const tone = row && row.tone ? row.tone : 'complete';
        return `final-section-card final-section-card--${tone}`;
    }

    private finalSectionMissingHint(section: string) {
        const hints: WorkspaceMap = {
            S: '주호소·양상·한열 등 문진',
            O: '설진·맥진·복진·압통 등 진찰 소견',
            A: '변증/진단과 판단 근거',
            P: '치료 방향·처방·생활지도',
        };
        return hints[this.normalizedSoapSection(section)] || '진료기록';
    }

    private finalSectionIssueTexts(section: string, items: WorkspaceMap[]) {
        const normalized = this.normalizedSoapSection(section);
        const rows = (items || [])
            .filter((item) => this.finalGateItemMatchesSection(item, normalized))
            .map((item) => {
                const title = this.finalSaveItemTitle(item);
                const summary = this.finalSaveItemSummary(item);
                if (summary && summary !== title) return `${title}: ${summary}`;
                return title;
            })
            .filter((value) => value !== '');
        return this.dedupeStrings(rows).slice(0, 3);
    }

    private finalGateItemMatchesSection(item: WorkspaceMap, section: string) {
        return this.finalGateItemSections(item).indexOf(this.normalizedSoapSection(section)) >= 0;
    }

    private finalGateItemSections(item: WorkspaceMap) {
        const sections: string[] = [];
        const addSection = (value: any) => {
            const raw = String(value || '').trim().toUpperCase();
            if (!raw) return;
            if (this.soapSectionOrder.indexOf(raw) >= 0 && sections.indexOf(raw) < 0) {
                sections.push(raw);
                return;
            }
            const aliases: WorkspaceMap = {
                SUBJECTIVE: 'S',
                OBJECTIVE: 'O',
                ASSESSMENT: 'A',
                PLAN: 'P',
            };
            const alias = aliases[raw];
            if (alias && sections.indexOf(alias) < 0) sections.push(alias);
        };
        const collectSections = (value: any) => {
            if (!value) return;
            if (Array.isArray(value)) {
                value.forEach((entry) => collectSections(entry));
                return;
            }
            if (typeof value === 'object') {
                collectSections(value.soap_section || value.section || value.sections || value.missing_sections);
                return;
            }
            String(value || '').split(/[,\s/]+/).forEach((entry) => addSection(entry));
        };
        const details = item && item.details && typeof item.details === 'object' ? item.details : {};
        collectSections(item && (item.soap_section || item.section || item.sections || item.missing_sections));
        collectSections(details.sections || details.missing_sections);
        this.asArray(item && item.items).forEach((row) => collectSections(row));
        const code = String(item && item.code ? item.code : '');
        if (!sections.length && code === 'precheck_unsubmitted') sections.push('S');
        if (!sections.length && code === 'ap_source_unverified') {
            sections.push('A');
            sections.push('P');
        }
        return this.dedupeStrings(sections);
    }

    public async openFinalGateItem(item: WorkspaceMap) {
        if (!item) return;
        const code = String(item.code || '');
        const itemText = `${code} ${this.finalSaveItemTitle(item)} ${this.finalSaveItemSummary(item)}`;
        const sections = this.finalGateItemSections(item);
        this.finalSaveModalOpen = false;
        if (!sections.length && /(checklist|red_flag|safety|진료 전|주의|알레르기|복약)/i.test(itemText)) {
            await this.openWorkspaceDrawer('checklist');
            return;
        }
        if (!sections.length && /(reference|dataset|참조|데이터셋|건강인)/i.test(itemText)) {
            await this.openWorkspaceDrawer('patient');
            return;
        }
        const section = sections[0] || 'S';
        this.activeSoapSection = this.normalizedSoapSection(section);
        this.mobilePanel = 'soap';
        this.saveWorkspacePreferences();
        await this.service.render();
        this.restoreOverlayTrigger();
        this.focusSoapSectionEditor(this.activeSoapSection);
    }

    public async openFinalSectionReview(row: WorkspaceMap) {
        if (!row || !row.section) return;
        await this.openFinalGateItem({ section: row.section });
    }

    private focusSoapSectionEditor(section: string) {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        window.requestAnimationFrame(() => {
            const textarea = document.querySelector(`[data-testid="soap-compact-textarea-${this.normalizedSoapSection(section)}"]`) as HTMLTextAreaElement;
            if (!textarea) return;
            textarea.focus();
            textarea.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
        });
    }

    public get finalApprovedAt() {
        const chart = this.finalChart && this.finalChart.final_chart ? this.finalChart.final_chart : {};
        return chart.approved_at || '';
    }

    public get canApproveFinal() {
        const serverReady = this.finalChart && (
            this.finalChart.approve_enabled === true
            || (this.finalChart.status === 'confirmation_required' && this.finalGateBlockingItems.length === 0)
        );
        const clientReady = this.soapReadyForApproval && this.finalSaveBlockingItems.length === 0;
        return !this.readOnlyMode
            && this.canApproveFinalChart
            && !!this.soapDraftId
            && !this.finalApproving
            && !this.hasSoapDirty
            && this.finalGateBlockingItems.length === 0
            && this.finalGateConfirmationSatisfied
            && (serverReady || clientReady);
    }

    public get soapReadyForApproval() {
        const soapStatus = this.soap && this.soap.status ? this.soap.status : '';
        return ['ready_for_approval', 'passed'].indexOf(soapStatus) >= 0
            || ['ready_for_approval', 'passed', 'confirmation_required'].indexOf(this.finalStatus) >= 0;
    }

    public get soapBusinessStatusCode() {
        return this.autoChartingStateCode;
    }

    public get soapBusinessStatusLabel() {
        return this.autoChartingStateLabel;
    }

    public get soapBusinessStatusDescription() {
        return this.autoChartingStateDescription;
    }

    public get soapBusinessStatusClass() {
        return this.autoChartingStateClass;
    }

    public get soapWorkflowSteps(): SoapWorkflowStep[] {
        const hasDraft = this.hasSoapDraft;
        const ready = this.soapReadyForApproval || this.readOnlyMode;
        const finalState = this.readOnlyMode ? '완료' : (this.finalGateBlockingItems.length ? '더 작성' : (this.finalGateNeedsConfirmation ? '확인만' : (ready ? '가능' : '작성 전')));
        const finalTone = this.readOnlyMode ? 'success' : (this.finalGateBlockingItems.length ? 'warning' : (this.finalGateNeedsConfirmation ? 'warning' : (ready ? 'primary' : 'neutral')));
        return [
            this.workflowStep('기록 정리', this.soapGenerating ? '정리 중' : (hasDraft ? '확인됨' : '작성 전'), this.soapGenerating ? 'ai' : (hasDraft ? 'success' : 'neutral'), this.soapGenerating || !hasDraft),
            this.workflowStep('직접 기록', this.soapCreating || this.soapSaving || this.hasSoapDirty ? '저장 대기' : (hasDraft ? '확인됨' : '작성 전'), this.soapCreating || this.soapSaving || this.hasSoapDirty ? 'warning' : (hasDraft ? 'success' : 'neutral'), hasDraft && !this.readOnlyMode),
            this.workflowStep('최종 점검', this.soapValidating ? '점검 중' : (ready ? '점검됨' : (hasDraft ? '점검 전' : '작성 전')), this.soapValidating ? 'ai' : (ready ? 'success' : (hasDraft ? 'pending' : 'neutral')), hasDraft && !ready && !this.readOnlyMode),
            this.workflowStep('최종 저장', finalState, finalTone, ready && !this.readOnlyMode),
        ];
    }

    public soapWorkflowStepClass(step: SoapWorkflowStep) {
        const active = step && step.active ? ' is-active' : '';
        return `soap-step-pill soap-step-pill--${step.tone || 'neutral'}${active}`;
    }

    public get soapDraftSaveStateLabel() {
        if (this.readOnlyMode) return '최종 저장됨';
        if (this.soapSaving) return '저장 중';
        if (this.soapDirtySentenceCount > 0) return `저장 대기 ${this.soapDirtySentenceCount}`;
        if (this.hasSoapDraft) return '저장됨';
        return '작성 전';
    }

	    public get soapDraftSaveStateDescription() {
	        if (this.readOnlyMode) return '최종 저장됨';
	        if (this.soapDirtySentenceCount > 0) return '저장 후 검토';
	        if (this.hasSoapDraft) return '저장됨';
	        return '작성 전';
	    }

    public get soapDraftSaveStateClass() {
        const tone = this.soapDirtySentenceCount > 0 || this.soapSaving ? 'warning' : (this.hasSoapDraft || this.readOnlyMode ? 'success' : 'neutral');
        return `status-chip status-chip--${tone}`;
    }

    public get soapValidationStateLabel() {
        if (this.soapValidating) return '검토 중';
        if (this.soapValidationIssueCount > 0 || this.finalGateBlockingItems.length > 0) return `검토 항목 ${this.soapValidationIssueCount + this.finalGateBlockingItems.length}`;
        if (this.soapReadyForApproval || this.readOnlyMode) return '점검됨';
        if (this.hasSoapDraft) return '점검 전';
        return '작성 전';
    }

	    public get soapValidationStateDescription() {
	        if (this.soapValidationIssueCount > 0 || this.finalGateBlockingItems.length > 0) return '더 작성할 내용';
	        if (this.soapReadyForApproval || this.readOnlyMode) return '저장 가능';
	        if (this.hasSoapDraft) return '검토 전';
	        return '작성 전';
	    }

    public get soapValidationStateClass() {
        const tone = this.soapValidating ? 'ai' : ((this.soapValidationIssueCount > 0 || this.finalGateBlockingItems.length > 0) ? 'pending' : ((this.soapReadyForApproval || this.readOnlyMode) ? 'success' : 'neutral'));
        return `status-chip status-chip--${tone}`;
    }

    public get soapSaveCandidateStateLabel() {
        if (this.readOnlyMode) return '최종 저장됨';
        if (this.soapReadying) return '확인 중';
        if (this.soapReadyForApproval) return '최종 저장 가능';
        if (this.hasSoapDraft) return '진료 전 확인';
        return '작성 전';
    }

	    public get soapSaveCandidateStateDescription() {
	        if (this.readOnlyMode) return '저장됨';
	        if (this.soapReadyForApproval) return '저장 가능';
	        if (this.hasSoapDraft) return '확인 후 저장 가능';
	        return '작성 전';
	    }

    public get soapSaveCandidateStateClass() {
        const tone = this.readOnlyMode ? 'success' : (this.soapReadying ? 'ai' : (this.soapReadyForApproval ? 'primary' : 'neutral'));
        return `status-chip status-chip--${tone}`;
    }

    public get canRunAiAssistant() {
        return !this.clinicalReadOnly && this.canGenerateClinicalAi;
    }

    public get canSubmitAiAssistantRequest() {
        return this.canRunAiAssistant && (this.aiAssistantInput || '').trim().length > 0;
    }

    public get llmChatEnabled() {
        return this.llmSettings
            && (
                this.llmSettings.enabled === true
                || (this.llmSettings.api_key_configured === true && this.llmSettings.model_configured === true)
            );
    }

    public get canRunConversationChat() {
        return this.hasEncounter && !this.loading && !this.clinicalReadOnly && this.canGenerateClinicalAi;
    }

    public get canSubmitConversationMessage() {
        return this.canRunConversationChat && !this.conversationSending && !this.conversationStreaming && (this.conversationInput || '').trim().length > 0;
    }

    public get conversationStatusLabel() {
        if (this.conversationSending) return '답변 준비 중';
        if (this.conversationStreaming) return '답변 준비 중';
        if (!this.llmChatEnabled) return '대화 답변 사용 불가';
        if (this.conversationTurns.length > 0) return `${this.conversationTurns.length}건`;
        return '대화 없음';
    }

    public get visibleConversationTurns() {
        return this.conversationTurns
            .filter((turn) => this.conversationDisplayText(turn) !== '');
    }

    public setConversationInput(value: string) {
        this.conversationInput = value || '';
    }

    public conversationRoleLabel(turn: ConversationTurn) {
        if (turn && turn.role === 'assistant' && turn.status === 'streaming') return '답변 준비 중';
        return turn && turn.role === 'assistant' ? '보조 답변' : '한의사';
    }

    public conversationTurnClass(turn: ConversationTurn) {
        const role = turn && turn.role === 'assistant' ? 'assistant' : 'user';
        const status = turn && turn.status ? ` conversation-turn--${turn.status}` : '';
        const entrance = role === 'user' && turn && turn.animateEntrance
            ? ' conversation-turn--user-entering'
            : '';
        return `conversation-turn conversation-turn--${role}${status}${entrance}`;
    }

    public finishConversationTurnEntrance(turn: ConversationTurn, event?: AnimationEvent) {
        if (!turn || !turn.animateEntrance) return;
        if (event && event.target !== event.currentTarget) return;
        turn.animateEntrance = false;
    }

    public conversationTurnHtml(turn: ConversationTurn) {
        const text = this.conversationDisplayText(turn);
        let html = this.escapeHtml(text);
        html = html.replace(/^#{1,6}\s*(.+)$/gm, '<span class="conversation-markdown-heading">$1</span>');
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
        html = html.replace(/\*\*/g, '');
        html = html.replace(/\n/g, '<br>');
        return html;
    }

    private conversationDisplayText(turn: ConversationTurn) {
        let text = String((turn && turn.text) || '').trim();
        text = text
            .replace(/^\s*(진료\s*대화\s*확인|통합\s*회귀(?:\s*(?:검증|제외|최종|완료))?|회귀\s*검증)\s*\d{8,}\s*:\s*/i, '')
            .replace(/^\s*(진료\s*대화\s*확인|통합\s*회귀(?:\s*(?:검증|제외|최종|완료))?|회귀\s*검증)\s*:\s*/i, '');
        return text.trim();
    }

    public async handleConversationKeydown(event: KeyboardEvent) {
        if (!event || event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();
        await this.sendConversationMessage();
    }

    public async sendConversationMessage() {
        const message = (this.conversationInput || '').trim();
        if (!message || !this.canSubmitConversationMessage) return;

        const history = this.conversationTurns.slice(-8).map((turn) => ({
            role: turn.role,
            text: turn.text,
        }));
        this.conversationTurns.push({
            role: 'user',
            text: message,
            createdAt: this.conversationNowLabel(),
            animateEntrance: true,
        });
        this.conversationInput = '';
        this.conversationError = '';
        this.conversationSending = true;
        await this.service.render();
        this.scrollConversationThread(true);

        const result = await this.callConversationApi(message, history);
        this.conversationSending = false;

        if (result.ok) {
            const data = result.data || {};
            const reply = String(data.reply || (data.result && data.result.message) || '').trim();
            if (reply) {
                await this.streamConversationReply(reply);
                await this.addConversationRecommendationFromChat(message, reply);
            } else {
                this.addConversationFailureTurn('응답 실패');
            }
        } else {
            const fallbackReply = this.localConversationFallbackReply(message, result.message || '');
            if (this.conversationCanCreateChartCandidate(message, fallbackReply)) {
                await this.streamConversationReply(fallbackReply);
                await this.addConversationRecommendationFromChat(message, fallbackReply);
            } else {
                this.addConversationFailureTurn(result.message || '응답 실패');
            }
        }
        await this.service.render();
        this.scrollConversationThread();
    }

    private localConversationFallbackReply(message: string, reason: string = '') {
        const signal = this.conversationChartSignal(message, '');
        if (!signal.chartWorth) {
            return reason || '응답이 지연되어 대화 내용을 저장하지 못했습니다. 짧게 다시 입력해 주세요.';
        }
        const sections = signal.sections || {};
        const labels: WorkspaceMap = {
            S: '문진',
            O: '진찰/소견',
            A: '변증/진단',
            P: '치료/처방',
        };
        const active = ['S', 'O', 'A', 'P']
            .filter((section) => Number(sections[section] || 0) > 0)
            .map((section) => labels[section])
            .filter((label) => !!label);
        const target = active.length ? active.join(', ') : '한의 차트';
        const prefix = reason ? `${reason} ` : '';
        return `${prefix}입력한 임상 단서를 기준으로 ${target}에 넣을 내용을 정리했습니다.`;
    }

    private async addConversationRecommendationFromChat(requestText: string, replyText: string) {
        const signal = this.conversationChartSignal(requestText, replyText);
        if (!signal.chartWorth) {
            this.pruneWeakConversationRecommendations();
            return;
        }
        const lines = this.conversationRecommendationLines(replyText);
        const items = this.buildConversationClinicalCandidateItems(requestText, replyText, lines, signal);

        if (!items.length) return;
        this.reconcileConversationRecommendations(items);
        const card = this.aiAssistantResult(
            '임상 단서 정리',
            '임상 단서 참고',
            'ai',
            this.conversationCandidateCardSummary(items),
            items,
            ['진료 대화', '환자 요약', '한의 차트'],
            ['한의사 판단 전'],
            requestText
        );
        this.prependConversationClinicalResult(card, 20);
        this.aiAssistantMessage = '임상 단서 참고 내용이 준비되었습니다.';
        await this.service.render();
        await this.persistConversationRecommendation(card, requestText, replyText);
    }

    private async persistConversationRecommendation(card: AiAssistantResult, requestText: string, replyText: string) {
        if (!card || !this.encounterId) return false;
        const payload = {
            encounter_id: this.encounterId,
            recommendation: JSON.stringify(card),
            request_text: requestText || '',
            reply_text: replyText || '',
        };
        let result = await this.callApiWithTimeout(
            'load_context',
            {
                ...payload,
                save_conversation_recommendation: '1',
            },
            this.candidateDecisionTimeoutMs,
            '진료지원 추천 저장 응답 시간이 초과되었습니다.'
        );
        if (!this.savedConversationRecommendation(result)) {
            result = await this.callApiWithTimeout(
                'save_conversation_recommendation',
                payload,
                this.candidateDecisionTimeoutMs,
                '진료지원 추천 저장 응답 시간이 초과되었습니다.'
            );
        }
        if (!this.savedConversationRecommendation(result)) {
            result = await this.conversationRequestWithTimeout(
                () => this.fetchConversationApi('save_conversation_recommendation', payload),
                this.candidateDecisionTimeoutMs
            );
        }
        const saved = this.savedConversationRecommendation(result);
        if (saved) {
            if (saved && saved.id) card.id = String(saved.id);
            if (saved && saved.source_turn_id) card.source_turn_id = String(saved.source_turn_id);
            card.persisted = true;
            this.markSoapCandidateQueueDirty();
            this.aiAssistantMessage = '진료지원 추천이 준비되었습니다.';
            return true;
        }
        this.aiAssistantMessage = '진료지원 추천은 표시했습니다. 새로고침 유지 저장은 실패했습니다.';
        this.markSoapCandidateQueueDirty();
        return false;
    }

    private savedConversationRecommendation(result: any) {
        if (!result || result.ok !== true) return null;
        const saved = result.data && result.data.recommendation ? result.data.recommendation : null;
        if (!saved || (!saved.id && !saved.source_turn_id)) return null;
        return saved;
    }

    private conversationItemToSoapCandidate(item: AiAssistantResultItem): WorkspaceMap | null {
        if (!item) return null;
        const section = this.normalizedSoapSection(item.soap_section || 'S');
        const text = String(item.soap_sentence_text || item.description || item.title || '').trim();
        if (!text) return null;
        const supportingEvidence = this.dedupeStrings(this.asArray(item.supporting_evidence)
            .concat(section === 'A' ? this.asArray(item.patient_cues) : [])
            .concat(section === 'A' ? [item.clinical_evidence || item.evidence_summary || item.source_excerpt || ''] : [])
            .map((value) => this.clinicalRecordDisplayText(value))
            .filter((value) => value !== ''))
            .slice(0, 4);
        const opposingEvidence = this.dedupeStrings(this.asArray(item.opposing_evidence)
            .map((value) => this.clinicalRecordDisplayText(value))
            .filter((value) => value !== ''))
            .slice(0, 4);
        const followupQuestions = this.dedupeStrings(this.asArray(item.followup_questions)
            .map((value) => this.clinicalRecordDisplayText(value))
            .filter((value) => value !== ''))
            .slice(0, 4);
        const sourceRefs = Array.isArray(item.source_refs) && item.source_refs.length
            ? item.source_refs
            : [{ type: 'conversation', label: '진료 대화', value: item.source_excerpt || item.evidence_summary || text }];
        return {
            client_candidate_id: item.conversation_signal_key || `conversation::${section}::${this.conversationCandidateSignalKey(section, text)}`,
            soap_section: section,
            section,
            title: item.clinical_summary || item.title || this.soapSectionDisplayName(section),
            description: item.clinical_summary || item.description || text,
            text,
            soap_sentence_text: text,
            source: 'conversation_chat',
            generated_by: 'conversation_chat',
            author_type: 'ai_assistant',
            status: 'ai_unreviewed',
            candidate_status: 'new_candidate_ready',
            doctor_decision_status: 'pending_doctor_review',
            source_refs: sourceRefs,
            suggestion_reasons: this.asArray(item.suggestion_reasons).length ? this.asArray(item.suggestion_reasons) : [item.clinical_summary || '임상 단서'],
            source_labels: this.asArray(item.source_labels).length ? this.asArray(item.source_labels) : ['진료 대화', this.soapSectionDisplayName(section)],
            answer_target: item.answer_target || this.soapSectionDisplayName(section),
            chart_value_reason: item.chart_value_reason || '임상 단서',
            evidence_summary: item.clinical_evidence || item.evidence_summary || item.description || text,
            supporting_evidence: supportingEvidence,
            opposing_evidence: opposingEvidence,
            followup_questions: followupQuestions,
            doctor_judgement_note: '',
            patient_cues: this.asArray(item.patient_cues),
            connection_reasons: this.asArray(item.connection_reasons),
            source_payload: {
                input_type: 'conversation_chat',
                kind: item.kind || 'conversation_chart_candidate',
                clinical_summary: item.clinical_summary || item.title || '',
                clinical_evidence: item.clinical_evidence || item.evidence_summary || '',
                chart_sentence: text,
                source_excerpt: item.source_excerpt || item.evidence_summary || '',
                evidence_summary: item.evidence_summary || item.clinical_evidence || '',
                patient_cues: this.asArray(item.patient_cues),
                connection_reasons: this.asArray(item.connection_reasons),
                supporting_evidence: supportingEvidence,
                opposing_evidence: opposingEvidence,
                followup_questions: followupQuestions,
                doctor_judgement_note: '',
                request_text: item.source_excerpt || item.source || '임상 단서 입력',
            },
        };
    }

    private buildConversationClinicalCandidateItems(requestText: string, replyText: string, lines: string[], signal: WorkspaceMap) {
        const context = this.conversationClinicalContextText(requestText, replyText, lines);
        const items: AiAssistantResultItem[] = [];
        const sections = signal && signal.sections ? signal.sections : {};
        const directSections = ['S', 'O', 'A', 'P'].filter((section) => Number(sections[section] || 0) > 0);
        directSections.forEach((section) => {
            const item = this.conversationDirectChartCandidate(section, requestText, signal);
            if (item && !this.conversationItemDuplicate(items, item)) items.push(item);
        });

        const diagnosis = this.conversationDiagnosisCandidate(context, signal);
        if (diagnosis && !this.conversationItemDuplicate(items, diagnosis)) items.push(diagnosis);
        const plan = this.conversationTreatmentCandidate(context, signal);
        if (plan && !this.conversationItemDuplicate(items, plan)) {
            items.push(plan);
        }
        const ranked = this.rankConversationClinicalCandidates(items);
        const selected = ranked.slice(0, 2);
        const primaryDirectSection = directSections.slice().sort((left, right) => (
            Number(sections[right] || 0) - Number(sections[left] || 0)
        ))[0];
        const primaryDirectItem = ranked.find((item) => (
            item.kind === 'conversation_chart_candidate'
            && this.normalizedSoapSection(item.soap_section || 'S') === primaryDirectSection
        ));
        if (
            primaryDirectItem
            && !selected.some((item) => item === primaryDirectItem)
        ) {
            return selected.slice(0, 1).concat([primaryDirectItem]);
        }
        return selected;
    }

    private conversationClinicalContextText(requestText: string, replyText: string, lines: string[]) {
        return `${requestText || ''}\n${replyText || ''}\n${(lines || []).join('\n')}`;
    }

    private conversationDiagnosisCandidate(context: string, signal: WorkspaceMap): AiAssistantResultItem | null {
        const sections = signal && signal.sections ? signal.sections : {};
        if (!sections.A && !sections.S && !sections.O) return null;
        const normalized = this.normalizeLookupLabel(context);
        let title = '';
        let sentence = '';
        if (this.candidateTextHasAny(normalized, ['삭맥', '세맥'])) {
            title = '열상·허증 가능성 감별';
            sentence = '삭맥·세맥 단서상 열상 또는 허증 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['삽맥', '어혈', '혈어'])) {
            title = '어혈성 통증 가능성';
            sentence = '삽맥/어혈 단서상 어혈성 통증 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['긴맥', '현맥'])) {
            title = '기체·한성 통증 가능성';
            sentence = '긴장성 맥상 단서상 기체 또는 한성 통증 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['냉감', '한습', '온찜질', '찬'])) {
            title = '한습성 통증 가능성';
            sentence = '냉감/온열 반응 단서상 한습성 통증 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['묵직', '담습', '담음'])) {
            title = '담습성 양상 가능성';
            sentence = '묵직함/담습 단서상 담습성 양상 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['요통', '허리', '요부'])) {
            title = '요통 병증 감별';
            sentence = '요부 통증 양상상 한습, 어혈, 기체성 요통 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['어깨', '견부'])) {
            title = '견부 통증 병증 감별';
            sentence = '견부 통증 양상과 가동 시 변화를 기준으로 한습, 어혈, 기체성 통증 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['무릎', '슬관절'])) {
            title = '슬관절 통증 병증 감별';
            sentence = '슬관절 통증 양상과 가동 시 변화를 기준으로 한습, 어혈, 기체성 통증 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['두통', '머리'])) {
            title = '두통 병증 감별';
            sentence = '두통 양상상 풍한, 간양, 담음성 두통 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['소화', '식욕', '비위'])) {
            title = '비위 기능 저하 가능성';
            sentence = '소화·식욕 양상상 비위 기능 저하 가능성 감별 중';
        } else if (this.candidateTextHasAny(normalized, ['통증', '아프'])) {
            title = '통증 병증 감별';
            sentence = '통증 부위와 양상, 악화·완화 요인을 기준으로 병증 가능성 감별 중';
        }
        if (!title || !sentence) return null;
        const clinicalSummary = this.conversationClinicalSummaryForSection('A', context);
        const clinicalEvidence = this.conversationClinicalEvidenceSummary('A', context);
        const sourceExcerpt = this.conversationSourceExcerpt(context);
        const clinicalSignals = this.conversationClinicalSignals(context);
        const supportingEvidence = this.dedupeStrings(this.asArray(clinicalSignals.supporting)
            .map((value) => this.clinicalRecordDisplayText(value))
            .filter((value) => value !== ''))
            .slice(0, 4);
        const opposingEvidence = this.conversationNegatedClinicalEvidence(context);
        const followupQuestions = this.dedupeStrings(this.asArray(clinicalSignals.safety)
            .map((value) => `${this.clinicalRecordDisplayText(value)} 관련 문진·진찰 재확인`)
            .filter((value) => value !== ''))
            .slice(0, 3);
        if (!followupQuestions.length) {
            followupQuestions.push('진찰 소견과 변증/진단 가능성의 일치 여부 확인');
        }
        const recordSentence = this.joinSentenceParts([
            `${clinicalEvidence || sourceExcerpt} 단서를 근거로 ${this.conversationAssessmentRecordLabel(title)}`,
        ]) || sentence;
        return {
            kind: 'conversation_diagnosis_candidate',
            title,
            display_name: title,
            description: recordSentence,
            source: '진료 대화',
            soap_section: 'A',
            soap_sentence_text: recordSentence,
            answer_target: '변증/진단',
            decision_status: 'pending_doctor_review',
            applied_to_soap: false,
            evidence_summary: clinicalEvidence || this.truncateText(this.clinicalRecordDisplayText(context), 180),
            supporting_evidence: supportingEvidence.length ? supportingEvidence : [clinicalEvidence || sourceExcerpt].filter((value) => value !== ''),
            opposing_evidence: opposingEvidence,
            followup_questions: followupQuestions,
            doctor_judgement_note: '',
            patient_cues: [clinicalEvidence || sourceExcerpt].filter((value) => value !== ''),
            connection_reasons: [recordSentence],
            conversation_signal_key: this.conversationCandidateSignalKey('A', recordSentence),
            signal_strength: Number(signal && signal.strength ? signal.strength : 1),
            chart_value_reason: '임상 단서',
            clinical_summary: clinicalSummary,
            clinical_evidence: clinicalEvidence,
            source_excerpt: sourceExcerpt,
        };
    }

    private conversationTreatmentCandidate(context: string, signal: WorkspaceMap): AiAssistantResultItem | null {
        const sections = signal && signal.sections ? signal.sections : {};
        if (!sections.P) return null;
        const normalized = this.normalizeLookupLabel(context);
        let title = '';
        let sentence = '';
        if (this.candidateTextHasAny(normalized, ['뜸', '부항', '온열'])) {
            title = '온열·부항 치료 검토';
            sentence = '온열 반응과 압통 양상에 따라 뜸·부항 치료 계획 조정 예정';
        } else if (this.candidateTextHasAny(normalized, ['운동', '자세', '가동범위', 'rom'])) {
            title = '운동·자세 지도 검토';
            sentence = '자세와 가동범위 확인 후 운동·자세 생활지도 계획 예정';
        } else if (this.candidateTextHasAny(normalized, ['수면', '불면'])) {
            title = '수면 관리 지도 검토';
            sentence = '수면 양상 확인 후 수면 관리 생활지도 계획 예정';
        }
        if (!title || !sentence) return null;
        const clinicalSummary = this.conversationClinicalSummaryForSection('P', context);
        const clinicalEvidence = this.conversationClinicalEvidenceSummary('P', context);
        const sourceExcerpt = this.conversationSourceExcerpt(context);
        const recordSentence = this.joinSentenceParts([
            `${clinicalEvidence || sourceExcerpt} 단서를 기준으로 ${sentence}`,
            '시행 전 복약·알레르기·진료 전 주의 확인 후 조정 예정',
        ]);
        return {
            kind: 'conversation_treatment_candidate',
            title,
            description: recordSentence || sentence,
            source: '진료 대화',
            soap_section: 'P',
            soap_sentence_text: recordSentence || sentence,
            answer_target: '치료/처방',
            decision_status: 'pending_doctor_review',
            applied_to_soap: false,
            evidence_summary: clinicalEvidence || this.truncateText(this.clinicalRecordDisplayText(context), 180),
            patient_cues: [clinicalEvidence || sourceExcerpt].filter((value) => value !== ''),
            connection_reasons: [recordSentence || sentence],
            conversation_signal_key: this.conversationCandidateSignalKey('P', recordSentence || sentence),
            signal_strength: Number(signal && signal.strength ? signal.strength : 1),
            chart_value_reason: '치료 계획 단서',
            clinical_summary: clinicalSummary,
            clinical_evidence: clinicalEvidence,
            source_excerpt: sourceExcerpt,
        };
    }

    private conversationDirectChartCandidate(section: string, requestText: string, signal: WorkspaceMap): AiAssistantResultItem | null {
        const normalizedSection = this.normalizedSoapSection(section);
        const text = this.cleanConversationRecommendationText(requestText);
        const shortObjectiveFinding = normalizedSection === 'O' && this.conversationObjectiveFindingValueScore(text) > 0;
        if (!text || (text.length < 8 && !shortObjectiveFinding)) return null;
        const sentence = this.conversationDirectChartSentence(normalizedSection, text);
        const labels: WorkspaceMap = {
            S: ['문진 단서', '문진', '대화 단서'],
            O: ['진찰 소견', '진찰/소견', '진찰 소견'],
            A: ['변증/진단 메모', '변증/진단', '판단 메모'],
            P: ['치료/처방 메모', '치료/처방', '치료 계획 메모'],
        };
        const sectionLabels = labels[normalizedSection] || labels.S;
        const clinicalSummary = this.conversationClinicalSummaryForSection(normalizedSection, text);
        const clinicalEvidence = this.conversationClinicalEvidenceSummary(normalizedSection, text);
        const sourceExcerpt = this.conversationSourceExcerpt(text);
        return {
            kind: 'conversation_chart_candidate',
            title: clinicalSummary || sectionLabels[0],
            description: sentence,
            source: '진료 대화',
            soap_section: normalizedSection,
            soap_sentence_text: sentence,
            answer_target: sectionLabels[1],
            decision_status: 'pending_doctor_review',
            applied_to_soap: false,
            evidence_summary: clinicalEvidence || sourceExcerpt,
            source_refs: [{ type: 'conversation', label: '진료 대화', value: sourceExcerpt }],
            suggestion_reasons: [clinicalSummary || sectionLabels[2], signal && signal.reason ? signal.reason : '차트 반영'],
            source_labels: ['진료 대화', this.soapSectionDisplayName(normalizedSection)],
            conversation_signal_key: this.conversationCandidateSignalKey(normalizedSection, text),
            signal_strength: Number(signal && signal.strength ? signal.strength : 1),
            chart_value_reason: signal && signal.reason ? String(signal.reason) : '임상 단서',
            clinical_summary: clinicalSummary,
            clinical_evidence: clinicalEvidence,
            source_excerpt: sourceExcerpt,
            patient_cues: [clinicalEvidence || sourceExcerpt].filter((value) => value !== ''),
            connection_reasons: [sentence],
        };
    }

    private conversationDirectChartSentence(section: string, text: string) {
        const normalizedSection = this.normalizedSoapSection(section);
        const source = this.cleanConversationRecommendationText(text);
        const signals = this.conversationClinicalSignals(source);
        const qualities = this.conversationSignalPhrase(signals.qualities);
        const aggravating = this.conversationSignalPhrase(signals.aggravating);
        const relieving = this.conversationSignalPhrase(signals.relieving);
        const associated = this.conversationSignalPhrase(signals.associated);
        const findings = this.conversationSignalPhrase(signals.findings);
        const patterns = this.conversationSignalPhrase(signals.patterns);
        const treatments = this.conversationSignalPhrase(signals.treatments);
        const safety = this.conversationSignalPhrase(signals.safety);
        const fallback = this.conversationCondensedClinicalBody(source);
        const mainComplaint = this.conversationMainComplaintPhrase(signals, fallback);
        const cueSummary = this.conversationClinicalCueSummary(signals, fallback, 4);

        if (normalizedSection === 'O') {
            const observedFinding = this.conversationObjectiveFindingValueScore(source) > 0
                || this.conversationHasAnyKeyword(source, ['확인', '관찰', '촉진', '압통', '제한', '저하', '정상', '양성', '음성', '이야', '입니다', '보임', '관찰됨']);
            const status = observedFinding
                ? '확인됨'
                : '확인 예정';
            return this.joinSentenceParts([
                `진찰상 ${findings ? `${findings} 소견` : `${mainComplaint} 관련 소견`} ${status}`,
                qualities ? `통증 양상은 ${qualities}로 문진됨` : '',
                associated ? `${associated} 동반 소견 확인 예정` : '',
                safety ? `${safety} 관련 진료 전 주의 확인 예정` : '',
            ]);
        }

        if (normalizedSection === 'A') {
            const assessment = patterns || this.conversationAssessmentFallback(signals, source);
            return this.joinSentenceParts([
                `${cueSummary} 단서를 근거로 ${this.conversationAssessmentRecordLabel(assessment)}`,
                safety ? `${safety} 배제 여부 확인 후 판단 보완 예정` : '',
            ]);
        }

        if (normalizedSection === 'P') {
            const planFocus = treatments || (patterns ? `${patterns} 양상` : mainComplaint);
            return this.joinSentenceParts([
                `${planFocus} 중심으로 치료 계획 수립 예정`,
                safety ? `시행 전 ${safety} 확인 후 시술·처방 방향 조정 예정` : '치료 반응과 증상 변화를 추적하여 조정 예정',
            ]);
        }

        const modifiers = [
            qualities ? `${qualities} 양상` : '',
            aggravating ? `${aggravating} 시 악화` : '',
            relieving ? `${relieving} 시 완화` : '',
        ].filter((value) => value !== '');
        return this.joinSentenceParts([
            `문진상 ${mainComplaint}${modifiers.length ? `은 ${modifiers.join(', ')}로 확인됨` : ' 호소함'}`,
            associated ? `${associated} 동반 여부 확인됨` : '',
            safety ? `${safety} 관련 안전 문진 추가 확인 예정` : '',
        ]) || `문진상 ${fallback} 확인됨.`;
    }

    private conversationMainComplaintPhrase(signals: WorkspaceMap, fallback: string = '') {
        const symptoms = this.conversationSignalPhrase(signals && signals.symptoms);
        const locations = this.conversationSignalPhrase(signals && signals.locations);
        if (symptoms && locations) return `${locations} ${symptoms}`;
        if (symptoms) return symptoms;
        if (locations) return `${locations} 불편감`;
        return fallback || '임상 단서';
    }

    private conversationClinicalCueSummary(signals: WorkspaceMap, fallback: string = '', limit: number = 4) {
        const rows = this.dedupeStrings(([] as string[])
            .concat(this.asArray(signals && signals.symptoms))
            .concat(this.asArray(signals && signals.locations))
            .concat(this.asArray(signals && signals.qualities))
            .concat(this.asArray(signals && signals.aggravating))
            .concat(this.asArray(signals && signals.relieving))
            .concat(this.asArray(signals && signals.findings))
            .map((value) => this.clinicalRecordDisplayText(value))
            .filter((value) => value !== ''))
            .slice(0, limit);
        return rows.length ? rows.join(', ') : (fallback || '임상 단서');
    }

    private conversationAssessmentRecordLabel(assessment: string) {
        const value = this.trimSentencePeriod(this.clinicalRecordDisplayText(assessment)) || '임상 단서';
        if (value.indexOf('감별') >= 0) return value.endsWith('중') ? value : `${value} 중`;
        if (value.indexOf('가능성') >= 0) return `${value} 감별 중`;
        if (value.indexOf('병증') >= 0 || value.indexOf('기능') >= 0 || value.indexOf('소견') >= 0) {
            return `${value} 관련 변증/진단 감별 중`;
        }
        return `${value} 가능성 감별 중`;
    }

    private conversationCandidateCardSummary(items: AiAssistantResultItem[]) {
        const rows = this.dedupeStrings(this.asArray(items)
            .map((item) => this.clinicalRecordDisplayText(item && (item.clinical_summary || item.title || item.description)))
            .filter((item) => item !== ''))
            .slice(0, 2);
        return rows.length ? rows.join(' / ') : `참고 내용 ${this.asArray(items).length}건`;
    }

    private conversationClinicalSummaryForSection(section: string, text: string) {
        const normalizedSection = this.normalizedSoapSection(section);
        const signals = this.conversationClinicalSignals(text);
        const symptoms = this.conversationSignalPhrase(signals.symptoms);
        const locations = this.conversationSignalPhrase(signals.locations);
        const findings = this.conversationSignalPhrase(signals.findings);
        const patterns = this.conversationSignalPhrase(signals.patterns);
        const treatments = this.conversationSignalPhrase(signals.treatments);
        const modifiers = this.conversationSignalPhrase(
            this.asArray(signals.aggravating).concat(this.asArray(signals.relieving)).concat(this.asArray(signals.associated)),
            ''
        );
        let summary = '';
        if (normalizedSection === 'O') {
            const objective = [locations, findings].filter((value) => value !== '').join(' ');
            summary = objective ? `${objective} 소견 판단` : `${symptoms || '진찰 소견'} 확인`;
        }
        else if (normalizedSection === 'A') {
            const fallback = this.conversationAssessmentFallback(signals, text);
            const hasAnatomicCue = this.asArray(signals.locations).length > 0
                || this.asArray(signals.symptoms).some((value) => value !== '통증');
            summary = `${hasAnatomicCue ? fallback : (patterns || fallback)} 감별`;
        }
        else if (normalizedSection === 'P') summary = `${treatments || '치료 방향'} 검토`;
        else {
            const complaint = locations && symptoms ? `${locations} ${symptoms}` : (symptoms || (locations ? `${locations} 불편감` : '문진 단서'));
            summary = [complaint, modifiers].filter((value) => value !== '').join(' · ');
        }
        return this.truncateText(summary, 72);
    }

    private conversationClinicalEvidenceSummary(section: string, text: string) {
        const normalizedSection = this.normalizedSoapSection(section);
        const signals = this.conversationClinicalSignals(text);
        const symptoms = this.conversationSignalPhrase(signals.symptoms);
        const findings = this.conversationSignalPhrase(signals.findings);
        const supporting = this.conversationSignalPhrase(signals.supporting);
        const aggravating = this.conversationSignalPhrase(signals.aggravating);
        const relieving = this.conversationSignalPhrase(signals.relieving);
        const safety = this.conversationSignalPhrase(signals.safety);
        let parts: string[] = [];
        if (normalizedSection === 'O') {
            parts = [findings || '진찰 단서', symptoms ? `관련 ${symptoms}` : '', safety ? `주의 ${safety}` : ''];
        } else if (normalizedSection === 'A') {
            parts = [supporting || symptoms || findings || '문진·진찰 단서', safety ? `추가 확인 ${safety}` : ''];
        } else if (normalizedSection === 'P') {
            parts = [symptoms || findings || '치료 반응 단서', safety ? `시행 전 ${safety} 확인` : '치료 반응 추적'];
        } else {
            parts = [symptoms || '증상 단서', aggravating ? `악화 ${aggravating}` : '', relieving ? `완화 ${relieving}` : '', safety ? `확인 ${safety}` : ''];
        }
        const summary = parts.filter((value) => value !== '').join(' · ');
        return this.truncateText(summary, 86);
    }

    private conversationSourceExcerpt(text: string) {
        const source = this.cleanConversationRecommendationText(text)
            .replace(/^(대화|문진|소견|기록|판단\s*메모|치료\s*메모)\s*[:：]\s*/g, '')
            .trim();
        return this.truncateText(source || '진료 대화', 180);
    }

    private conversationCondensedClinicalBody(text: string) {
        const source = this.conversationSourceExcerpt(text)
            .replace(/^(환자|보호자|의사)\s*(가|이|는|은)?\s*/g, '')
            .replace(/\s*(라고|다고)\s*(말함|호소함|답함|언급함)$/g, '')
            .trim();
        return this.truncateText(this.trimSentencePeriod(source) || '임상 단서', 96);
    }

    private conversationClinicalSignals(text: string): WorkspaceMap {
        const normalized = this.normalizeLookupLabel(text);
        const symptoms = this.conversationPickSignalLabels(normalized, [
            { label: '요통', keywords: ['요통', '허리', '요부'] },
            { label: '두통', keywords: ['두통', '머리'] },
            { label: '복통', keywords: ['복통', '배가아프', '배아프'] },
            { label: '하지 저림/감각 이상', keywords: ['하지저림', '다리저림', '저림', '감각이상', '감각저하', '방사통'] },
            { label: '소화불량/식욕 저하', keywords: ['소화', '식욕', '더부룩', '오심', '트림', '비위'] },
            { label: '수면 저하', keywords: ['수면', '불면'] },
            { label: '냉감', keywords: ['냉감', '찬', '시림'] },
            { label: '열감', keywords: ['열감', '상열감', '발열', '열상'] },
            { label: '통증', keywords: ['통증', '아프'] },
        ], 3);
        const locations = this.conversationPickSignalLabels(normalized, [
            { label: '요부', keywords: ['요부', '허리', '요추'] },
            { label: '하지', keywords: ['하지저림', '하지통증', '하지감각', '하지방사', '다리'] },
            { label: '두부', keywords: ['두부', '머리'] },
            { label: '복부', keywords: ['복부', '복통', '배가아프', '배아프'] },
            { label: '어깨', keywords: ['어깨', '견부'] },
            { label: '무릎', keywords: ['무릎', '슬관절'] },
        ], 3);
        const qualities = this.conversationPickSignalLabels(normalized, [
            { label: '묵직함', keywords: ['묵직', '무거'] },
            { label: '찌르는 통증', keywords: ['찌르', '자통'] },
            { label: '당김/긴장', keywords: ['당김', '긴장', '뻣뻣'] },
            { label: '간헐적', keywords: ['간헐', '왔다갔다'] },
            { label: '지속적', keywords: ['지속', '계속'] },
        ], 3);
        const aggravating = this.conversationPickSignalLabels(normalized, [
            { label: '움직임', keywords: ['움직', '보행', '걷', '가동'] },
            { label: '굴곡/신전', keywords: ['굽히', '젖히', '굴곡', '신전'] },
            { label: '야간/수면 중', keywords: ['야간', '밤', '수면'] },
            { label: '한랭 자극', keywords: ['찬', '한랭', '냉'] },
            { label: '식후', keywords: ['식후', '식사'] },
            { label: '스트레스', keywords: ['스트레스', '긴장'] },
        ], 3);
        const relieving = this.conversationPickSignalLabels(normalized, [
            { label: '휴식', keywords: ['휴식', '쉬면'] },
            { label: '온열/찜질', keywords: ['온열', '찜질', '따뜻', '핫팩'] },
            { label: '마사지/압박', keywords: ['마사지', '압박'] },
            { label: '운동 후 호전', keywords: ['운동후호전', '움직이면나아'] },
            { label: '복약 후 호전', keywords: ['복약후', '약먹고', '약복용후'] },
        ], 3);
        const associated = this.conversationPickSignalLabels(normalized, [
            { label: '하지 저림', keywords: ['하지저림', '다리저림', '방사통'] },
            { label: '감각 저하', keywords: ['감각저하', '감각이상'] },
            { label: '근력 저하', keywords: ['근력저하', '힘빠'] },
            { label: '대소변 변화', keywords: ['대소변', '배뇨', '배변'] },
            { label: '오심/구역', keywords: ['오심', '구역', '메스꺼'] },
        ], 3);
        const findings = this.conversationPickSignalLabels(normalized, this.conversationObjectiveFindingSignalDefinitions().concat([
            { label: '압통', keywords: ['압통', '압진'] },
            { label: '가동범위 제한', keywords: ['가동범위', 'rom', '제한'] },
            { label: '근력/감각', keywords: ['근력', '감각'] },
        ]), 4);
        const patterns = this.conversationPickSignalLabels(normalized, [
            { label: '어혈', keywords: ['어혈', '혈어', '삽맥'] },
            { label: '한습', keywords: ['한습', '냉감', '찬', '온열'] },
            { label: '담습/담음', keywords: ['담습', '담음', '묵직'] },
            { label: '기체', keywords: ['기체', '긴장', '현맥'] },
            { label: '허증', keywords: ['허증', '세맥', '피로'] },
            { label: '열상', keywords: ['열상', '열감', '삭맥', '홍설', '설홍', '건조'] },
            { label: '비위 기능 저하', keywords: ['비위', '소화', '식욕'] },
        ], 3);
        const treatments = this.conversationPickSignalLabels(normalized, [
            { label: '침구 치료', keywords: ['침', '자침', '침구'] },
            { label: '뜸/온열', keywords: ['뜸', '온열', '찜질'] },
            { label: '부항', keywords: ['부항'] },
            { label: '한약/처방', keywords: ['한약', '처방', '제제'] },
            { label: '운동·자세 지도', keywords: ['운동', '자세', '스트레칭'] },
            { label: '경과 추적', keywords: ['추적', '경과', '재평가'] },
        ], 3);
        const safety = this.conversationPickSignalLabels(normalized, [
            { label: '복약', keywords: ['복약', '약물', '항응고', '진통제'] },
            { label: '알레르기', keywords: ['알레르기'] },
            { label: '시술 금기', keywords: ['금기', '임신', '감염'] },
            { label: '진료 전 주의', keywords: ['레드플래그', '발열', '야간통', '외상', '체중감소'] },
            { label: '신경학적 이상', keywords: ['근력저하', '감각저하', '대소변', '배뇨'] },
        ], 3);
        return {
            symptoms,
            locations,
            qualities,
            aggravating,
            relieving,
            associated,
            findings,
            patterns,
            treatments,
            safety,
            supporting: this.dedupeStrings([]
                .concat(symptoms as any)
                .concat(locations as any)
                .concat(findings as any)
                .concat(qualities as any)
                .concat(aggravating as any)
                .concat(relieving as any))
                .slice(0, 4),
        };
    }

    private conversationNegatedClinicalEvidence(text: string) {
        const normalized = this.normalizeLookupLabel(text);
        const definitions = [
            { label: '야간통 없음', keywords: ['야간통'] },
            { label: '저림/감각 이상 없음', keywords: ['저림', '감각이상', '감각저하', '방사통'] },
            { label: '근력 저하 없음', keywords: ['근력저하', '힘빠짐'] },
            { label: '대소변 변화 없음', keywords: ['대소변', '배뇨', '배변'] },
            { label: '발열 없음', keywords: ['발열'] },
            { label: '외상 없음', keywords: ['외상'] },
            { label: '체중 감소 없음', keywords: ['체중감소'] },
        ];
        const rows = definitions
            .filter((definition) => definition.keywords.some((keyword) => {
                const normalizedKeyword = this.normalizeLookupLabel(keyword);
                return normalized.indexOf(normalizedKeyword) >= 0
                    && this.conversationKeywordAppearsNegated(normalized, normalizedKeyword);
            }))
            .map((definition) => definition.label);
        return this.dedupeStrings(rows).slice(0, 4);
    }

    private conversationPickSignalLabels(normalizedText: string, definitions: { label: string; keywords: string[] }[], limit: number = 3) {
        const rows = (definitions || [])
            .filter((definition) => (definition.keywords || []).some((keyword) => {
                const normalizedKeyword = this.normalizeLookupLabel(keyword);
                return normalizedText.indexOf(normalizedKeyword) >= 0
                    && !this.conversationKeywordAppearsNegated(normalizedText, normalizedKeyword);
            }))
            .map((definition) => definition.label);
        return this.preferSpecificConversationSignalLabels(rows).slice(0, limit);
    }

    private preferSpecificConversationSignalLabels(labels: string[]) {
        const unique = this.dedupeStrings((labels || [])
            .map((label) => this.clinicalRecordDisplayText(label))
            .filter((label) => label !== ''));
        return unique.filter((label) => {
            const normalized = this.normalizeLookupLabel(label);
            if (!normalized) return false;
            return !unique.some((other) => {
                if (other === label) return false;
                const otherNormalized = this.normalizeLookupLabel(other);
                return otherNormalized.length > normalized.length && otherNormalized.indexOf(normalized) >= 0;
            });
        });
    }

    private conversationKeywordAppearsNegated(normalizedText: string, normalizedKeyword: string) {
        if (!normalizedText || !normalizedKeyword) return false;
        let index = normalizedText.indexOf(normalizedKeyword);
        while (index >= 0) {
            const before = normalizedText.slice(Math.max(0, index - 10), index);
            const after = normalizedText.slice(index + normalizedKeyword.length, index + normalizedKeyword.length + 12);
            const afterNegated = /(없|부인|음성|아님|미동반|정상|호소없|동반없)/.test(after);
            const beforeNegated = /(무|미|비|없|부인)$/.test(before);
            if (!afterNegated && !beforeNegated) return false;
            index = normalizedText.indexOf(normalizedKeyword, index + normalizedKeyword.length);
        }
        return true;
    }

    private conversationSignalPhrase(values: any, fallback: string = '') {
        const rows = this.dedupeStrings(this.asArray(values)
            .map((value) => this.clinicalRecordDisplayText(value))
            .filter((value) => value !== ''));
        return rows.length ? rows.join(', ') : fallback;
    }

    private conversationObjectiveFindingSignalDefinitions() {
        const definitions: { label: string; keywords: string[] }[] = [];
        this.asArray(this.objectiveFindingItems).forEach((item: any) => {
            const itemLabel = this.clinicalRecordDisplayText(item && item.label);
            const itemKeywords = this.dedupeStrings([
                itemLabel,
                item && item.code ? String(item.code) : '',
            ]
                .concat(this.asArray(item && item.keys))
                .concat(this.asArray(item && item.labels))
                .concat(this.asArray(item && item.keywords))
                .map((value) => this.clinicalRecordDisplayText(value))
                .filter((value) => value !== ''));
            if (itemLabel && itemKeywords.length) {
                definitions.push({ label: itemLabel, keywords: itemKeywords });
            }
            this.asArray(item && item.fields).forEach((field: any) => {
                const fieldLabel = this.clinicalRecordDisplayText(field && field.label);
                this.asArray(field && field.options).forEach((option: any) => {
                    const optionText = this.clinicalRecordDisplayText(option);
                    if (!optionText || !this.objectiveFindingOptionCanSeedConversation(optionText)) return;
                    definitions.push({
                        label: optionText,
                        keywords: this.dedupeStrings([
                            optionText,
                            fieldLabel ? `${fieldLabel}${optionText}` : '',
                            itemLabel ? `${itemLabel}${optionText}` : '',
                            itemLabel && fieldLabel ? `${itemLabel}${fieldLabel}${optionText}` : '',
                        ].filter((value) => value !== '')),
                    });
                });
            });
        });
        return definitions;
    }

    private conversationObjectiveFindingKeywords() {
        let keywords: string[] = [];
        this.conversationObjectiveFindingSignalDefinitions().forEach((definition) => {
            keywords = keywords.concat(definition.keywords || []).concat([definition.label || '']);
        });
        return this.dedupeStrings(keywords.map((value) => this.clinicalRecordDisplayText(value)).filter((value) => value !== ''));
    }

    private conversationObjectiveFindingValueKeywords() {
        let keywords: string[] = [];
        this.asArray(this.objectiveFindingItems).forEach((item: any) => {
            const itemLabel = this.clinicalRecordDisplayText(item && item.label);
            this.asArray(item && item.fields).forEach((field: any) => {
                const fieldLabel = this.clinicalRecordDisplayText(field && field.label);
                this.asArray(field && field.options).forEach((option: any) => {
                    const optionText = this.clinicalRecordDisplayText(option);
                    if (!optionText || !this.objectiveFindingOptionCanSeedConversation(optionText)) return;
                    keywords = keywords.concat([
                        optionText,
                        fieldLabel ? `${fieldLabel}${optionText}` : '',
                        itemLabel ? `${itemLabel}${optionText}` : '',
                    ]);
                });
            });
        });
        return this.dedupeStrings(keywords.filter((value) => value !== ''));
    }

    private conversationObjectiveFindingValueScore(text: string) {
        return this.conversationKeywordScore(text, this.conversationObjectiveFindingValueKeywords());
    }

    private objectiveFindingOptionCanSeedConversation(value: string) {
        const normalized = this.normalizeLookupLabel(value);
        if (!normalized || normalized.length < 2) return false;
        return ['정상', '중등도', '기타', '부침불명', '상지', '하지', '둔부', '흉복부'].indexOf(normalized) < 0;
    }

    private conversationAssessmentFallback(signals: WorkspaceMap, source: string) {
        const symptoms = this.asArray(signals && signals.symptoms);
        const locations = this.asArray(signals && signals.locations);
        if (symptoms.indexOf('요통') >= 0) return '요통 병증';
        if (locations.indexOf('요부') >= 0) return '요통 병증';
        if (locations.indexOf('어깨') >= 0) return '견부 통증 병증';
        if (locations.indexOf('무릎') >= 0) return '슬관절 통증 병증';
        if (symptoms.indexOf('두통') >= 0) return '두통 병증';
        if (locations.indexOf('두부') >= 0) return '두통 병증';
        if (symptoms.indexOf('소화불량/식욕 저하') >= 0) return '비위 기능';
        if (locations.indexOf('복부') >= 0) return '복부 증상 병증';
        if (locations.indexOf('하지') >= 0) return '하지 증상 병증';
        const findings = this.conversationSignalPhrase(signals && signals.findings);
        if (findings) return `${findings} 소견`;
        const fallback = this.conversationCondensedClinicalBody(source);
        return fallback && fallback !== '임상 단서' ? fallback : '임상 단서';
    }

    private trimSentencePeriod(text: string) {
        return String(text || '').trim().replace(/[.。]+$/g, '');
    }

    private rankConversationClinicalCandidates(items: AiAssistantResultItem[]) {
        const sectionPriority: WorkspaceMap = { O: 4, S: 3, A: 2, P: 1 };
        return items.slice().sort((left, right) => {
            const leftSection = this.normalizedSoapSection(left.soap_section || 'S');
            const rightSection = this.normalizedSoapSection(right.soap_section || 'S');
            const leftScore = Number(left.signal_strength || 0) + Number(sectionPriority[leftSection] || 0);
            const rightScore = Number(right.signal_strength || 0) + Number(sectionPriority[rightSection] || 0);
            return rightScore - leftScore;
        });
    }

    private conversationItemDuplicate(items: AiAssistantResultItem[], next: AiAssistantResultItem) {
        return items.some((item) => this.conversationCandidatesOverlap(item, next));
    }

    private isConversationClinicalResult(result: AiAssistantResult) {
        const type = String((result && result.type) || '');
        return ['임상 단서 정리', '임상 단서 추천', '대화 기반 추천', '대화 기반'].indexOf(type) >= 0;
    }

    public aiAssistantItemDisplayTitle(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!result || !item || !this.isConversationClinicalResult(result)) {
            return this.clinicalRecordDisplayText(item && item.title ? item.title : '');
        }
        const section = this.normalizedSoapSection(item.soap_section || 'S');
        const row: SoapCandidateQueueRow = {
            id: item.recommendation_item_id || item.conversation_signal_key || '',
            kind: 'ai_item',
            status: 'pending',
            title: item.title || '',
            text: item.soap_sentence_text || item.description || item.title || '',
            section,
            target: this.soapSectionDisplayName(section),
            source: item.source || 'conversation_chat',
            meta: '',
            updated: result.createdAt || '',
            sortKey: 0,
            tone: 'warning',
            result,
            item,
        };
        return this.candidateClinicalHintText(row);
    }

    private isAssessmentEvidenceItem(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!result || !item || this.normalizedSoapSection(item.soap_section || '') !== 'A') return false;
        if (this.isPatternCandidateResult(result) || item.kind === 'pattern_candidate') return true;
        if (!this.isConversationClinicalResult(result)) return false;
        return ['conversation_diagnosis_candidate', 'conversation_chart_candidate'].indexOf(String(item.kind || '')) >= 0
            || this.asArray(item.supporting_evidence).length > 0
            || this.asArray(item.patient_cues).length > 0
            || this.displayValue(item.clinical_evidence, '') !== '';
    }

    private reconcileConversationRecommendations(newItems: AiAssistantResultItem[]) {
        let touched = false;
        this.aiAssistantResults.forEach((result) => {
            if (!this.isConversationClinicalResult(result)) return;
            let activeCount = 0;
            if (result.status !== 'excluded') touched = true;
            result.status = 'excluded';
            this.asArray(result.items).forEach((item) => {
                const status = String((item && item.decision_status) || '');
                if (['added', 'modified', 'soap_candidate_sent'].indexOf(status) >= 0) return;
                if (newItems.some((next) => this.conversationCandidatesOverlap(item, next) || this.conversationCandidateWeakerThan(item, next))) {
                    item.decision_status = 'ignored';
                    item.chart_value_reason = '새 임상 단서와 중복되어 정리됨';
                    touched = true;
                    return;
                }
                if (this.conversationCandidateLooksWeak(item)) {
                    item.decision_status = 'ignored';
                    item.chart_value_reason = '차트 반영 가치가 낮아 정리됨';
                    touched = true;
                    return;
                }
                activeCount += 1;
            });
            if (activeCount > 0) {
                result.status = 'pending_doctor_review';
                touched = true;
            }
        });
        if (touched) this.markSoapCandidateQueueDirty();
    }

    private pruneWeakConversationRecommendations() {
        let touched = false;
        this.aiAssistantResults.forEach((result) => {
            if (!this.isConversationClinicalResult(result)) return;
            let activeCount = 0;
            this.asArray(result.items).forEach((item) => {
                const status = String((item && item.decision_status) || '');
                if (['added', 'modified', 'soap_candidate_sent', 'ignored', 'excluded'].indexOf(status) >= 0) return;
                if (this.conversationCandidateLooksWeak(item)) {
                    item.decision_status = 'ignored';
                    item.chart_value_reason = '후속 임상 단서 기준 차트 반영 가치가 낮아 정리됨';
                    touched = true;
                    return;
                }
                activeCount += 1;
            });
            if (!activeCount && result.status !== 'excluded') {
                result.status = 'excluded';
                touched = true;
            }
        });
        if (touched) this.markSoapCandidateQueueDirty();
    }

    private conversationCandidateLooksWeak(item: AiAssistantResultItem) {
        if (!item) return true;
        const text = String(item.soap_sentence_text || item.description || item.title || '').trim();
        if (text.length < 18) return true;
        const strength = Number(item.signal_strength || 0);
        if (strength > 0 && strength < 2) return true;
        return false;
    }

    private conversationCandidateWeakerThan(previous: AiAssistantResultItem, next: AiAssistantResultItem) {
        const previousSection = this.normalizedSoapSection(previous && previous.soap_section ? previous.soap_section : 'S');
        const nextSection = this.normalizedSoapSection(next && next.soap_section ? next.soap_section : 'S');
        if (previousSection !== nextSection) return false;
        const previousStrength = Number(previous && previous.signal_strength ? previous.signal_strength : 1);
        const nextStrength = Number(next && next.signal_strength ? next.signal_strength : 1);
        if (nextStrength < previousStrength) return false;
        return this.conversationTextSimilarity(previous.soap_sentence_text || previous.description || '', next.soap_sentence_text || next.description || '') >= 0.36;
    }

    private conversationCandidatesOverlap(left: AiAssistantResultItem, right: AiAssistantResultItem) {
        if (!left || !right) return false;
        const leftSection = this.normalizedSoapSection(left.soap_section || 'S');
        const rightSection = this.normalizedSoapSection(right.soap_section || 'S');
        if (leftSection !== rightSection) return false;
        const leftKey = left.conversation_signal_key || this.conversationCandidateSignalKey(leftSection, left.soap_sentence_text || left.description || left.title || '');
        const rightKey = right.conversation_signal_key || this.conversationCandidateSignalKey(rightSection, right.soap_sentence_text || right.description || right.title || '');
        if (leftKey && rightKey && leftKey === rightKey) return true;
        return this.conversationTextSimilarity(left.soap_sentence_text || left.description || '', right.soap_sentence_text || right.description || '') >= 0.6;
    }

    private conversationCandidateSignalKey(section: string, text: string) {
        const semantic = this.conversationSemanticText(text);
        return `${this.normalizedSoapSection(section)}:${semantic.slice(0, 80)}`;
    }

    private conversationTextSimilarity(left: string, right: string) {
        const a = this.conversationSemanticText(left);
        const b = this.conversationSemanticText(right);
        if (!a || !b) return 0;
        if (a === b) return 1;
        const minLength = Math.min(a.length, b.length);
        if (minLength >= 12 && (a.indexOf(b) >= 0 || b.indexOf(a) >= 0)) return 0.8;
        const leftTokens = this.conversationSemanticTokens(a);
        const rightTokens = this.conversationSemanticTokens(b);
        if (!leftTokens.length || !rightTokens.length) return 0;
        const rightSet = new Set(rightTokens);
        const overlap = leftTokens.filter((token) => rightSet.has(token)).length;
        return overlap / Math.max(leftTokens.length, rightTokens.length);
    }

    private conversationSemanticTokens(text: string) {
        return this.dedupeStrings(String(text || '').split(/[^0-9a-z가-힣]+/i).map((token) => token.trim()).filter((token) => token.length >= 2));
    }

    private conversationSemanticText(text: string) {
        let value = this.normalizeLookupLabel(text);
        [
            '문진상', '진찰상',
            '진료대화에서확인', '진료중확인한소견', '진료대화기준', '치료처방검토메모',
            '변증진단판단메모', '차트반영문장', '기록후보', '검토메모', '가능성',
            '단서를근거로', '관련소견', '추가확인예정', '확인예정', '확인됨', '호소함',
            '안전문진', '진료전주의',
            '감별대상으로봄', '확인되어', '확인한', '확인', '후보', '기록', '필요',
            '진료대화', '진료중',
        ].forEach((token) => {
            value = value.split(this.normalizeLookupLabel(token)).join('');
        });
        return value;
    }

    private conversationChartSignal(requestText: string, replyText: string = '') {
        const request = this.cleanConversationRecommendationText(requestText);
        const objectiveFindingValueScore = this.conversationObjectiveFindingValueScore(request);
        const objectiveKeywords = this.conversationObjectiveFindingKeywords();
        const sections: WorkspaceMap = {
            S: this.conversationKeywordScore(request, ['주호소', '호소', '증상', '통증', '아프', '저림', '방사통', '냉감', '열감', '악화', '완화', '양상', '수면', '불면', '식욕', '소화', '대변', '소변', '오심', '트림', '더부룩', '두통', '요통', '허리', '어깨', '무릎']),
            O: this.conversationKeywordScore(request, ['진찰', '소견', '설진', '설질', '설태', '맥진', '맥상', '복진', '복부', '복직근', '압통', '가동범위', 'rom', '근력', '감각', '촉진', '관찰'].concat(objectiveKeywords)),
            A: this.conversationKeywordScore(request, ['변증', '진단', '판단', '어혈', '혈어', '한습', '담습', '담음', '기체', '비위', '간기', '허증', '열상', '한열', '허실']),
            P: this.conversationKeywordScore(request, ['치료', '처방', '침', '뜸', '부항', '한약', '제제', '운동', '자세', '생활지도', '추적', '계획', '온열']),
        };
        const clinicalCount = Object.keys(sections).reduce((total, key) => total + Number(sections[key] || 0), 0);
        const factMarkers = this.conversationKeywordScore(request, ['환자', '확인', '호소', '말함', '답함', '부인', '없', '있', '심함', '악화', '완화', '관찰', '촉진', '측정', '메모', '기록', '소견', '이야', '입니다', '보임']);
        const valueModifiers = this.conversationKeywordScore(request, ['없음', '부인', '정상', '음성', '양성', '악화', '완화', '심함', '호전', '지속', '동반']);
        const askOnly = this.conversationLooksLikeQuestionOnly(request);
        const tooShort = request.length < 8 && objectiveFindingValueScore <= 0;
        const chartWorth = !tooShort
            && clinicalCount > 0
            && !askOnly
            && (factMarkers > 0 || valueModifiers > 0 || objectiveFindingValueScore > 0 || clinicalCount >= 2);
        const reason = chartWorth
            ? (sections.O ? '진찰 소견 입력' : (sections.A ? '변증/진단 판단 입력' : (sections.P ? '치료/처방 계획 입력' : '문진 단서 입력')))
            : '차트 반영 가치 낮음';
        return {
            chartWorth,
            sections,
            strength: clinicalCount + factMarkers + valueModifiers + objectiveFindingValueScore,
            reason,
        };
    }

    private conversationLooksLikeQuestionOnly(text: string) {
        const normalized = String(text || '').trim();
        if (!normalized) return true;
        const hasQuestionMark = /[?？]$/.test(normalized);
        const asks = /^(무엇|어떤|어떻게|왜|추천|알려|물어|질문|다음|정리해|작성해)/.test(normalized);
        const observation = this.conversationKeywordScore(normalized, ['환자', '확인', '호소', '말함', '부인', '없', '있', '소견', '관찰', '촉진', '맥', '설', '복진', '압통']);
        return (hasQuestionMark || asks) && observation <= 0;
    }

    private conversationKeywordScore(text: string, keywords: string[]) {
        const normalized = this.normalizeLookupLabel(text);
        return keywords.reduce((count, keyword) => normalized.indexOf(this.normalizeLookupLabel(keyword)) >= 0 ? count + 1 : count, 0);
    }

    private conversationRecommendationLines(text: string) {
        return String(text || '')
            .replace(/\r/g, '')
            .split('\n')
            .map((line) => this.cleanConversationRecommendationText(line))
            .filter((line) => line.length >= 8)
            .filter((line) => this.conversationLineCanBecomeCandidateSource(line))
            .slice(0, 16);
    }

    private cleanConversationRecommendationText(text: string) {
        return String(text || '')
            .replace(/\*\*/g, '')
            .replace(/`/g, '')
            .replace(/^#+\s*/, '')
            .replace(/^\s*[-*•]\s*/, '')
            .replace(/^\s*\d+[.)]\s*/, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private pickConversationRecommendationLine(lines: string[], keywords: string[], fallbackIndex: number, section: string = '') {
        const rows = this.pickConversationRecommendationLines(lines, keywords, 1, section);
        if (rows[0]) return rows[0];
        if (section) return '';
        return lines[fallbackIndex] || lines[0] || '';
    }

    private pickConversationRecommendationLines(lines: string[], keywords: string[], limit: number, section: string = '') {
        const matched = lines.find((line) => {
            const normalized = this.normalizeLookupLabel(line);
            if (section && this.conversationLineLooksLikeHeading(line)) return false;
            if (section && this.inferConversationSoapSection(line) !== section) return false;
            return keywords.some((keyword) => normalized.indexOf(this.normalizeLookupLabel(keyword)) >= 0);
        });
        const rows = lines.filter((line) => {
            const normalized = this.normalizeLookupLabel(line);
            if (section && this.conversationLineLooksLikeHeading(line)) return false;
            if (section && this.inferConversationSoapSection(line) !== section) return false;
            return keywords.some((keyword) => normalized.indexOf(this.normalizeLookupLabel(keyword)) >= 0);
        });
        if (matched && rows.indexOf(matched) < 0) rows.unshift(matched);
        return rows.slice(0, limit);
    }

    private conversationLineLooksLikeHeading(line: string) {
        const normalized = String(line || '').trim();
        return /포인트$/.test(normalized) || /예시$/.test(normalized) || /정리/.test(normalized);
    }

    private conversationLineCanBecomeCandidateSource(line: string) {
        const text = String(line || '').trim();
        if (!text || this.conversationLineLooksLikeHeading(text)) return false;
        if (/^(다음|아래|원하시면|참고로|요약하면|결론적으로)/.test(text)) return false;
        if (text.indexOf('좋겠습니다') >= 0 && text.length < 32) return false;
        return this.conversationHasAnyKeyword(text, [
            '후보', '문장', '기록', '호소', '증상', '통증', '저림', '감각', '근력',
            '악화', '완화', '양상', '방사통', '냉감', '열감', '수면', '식욕', '대소변',
            '설진', '설질', '설태', '맥진', '맥상', '복진', '복부', '복직근', '압통', '가동',
            '변증', '진단', '치료', '처방', '침', '약', '생활지도',
        ].concat(this.conversationObjectiveFindingValueKeywords()));
    }

    private conversationLineCanBecomeChartCandidate(line: string, section: string) {
        const text = String(line || '').trim();
        if (!this.conversationLineCanBecomeCandidateSource(text)) return false;
        if (text.length < 10) return false;
        if (section === 'S') return this.conversationHasAnyKeyword(text, ['호소', '증상', '통증', '저림', '양상', '악화', '완화', '방사통', '냉감', '열감', '수면', '식욕', '대소변']);
        if (section === 'O') return this.conversationHasAnyKeyword(text, ['진찰', '소견', '설진', '설질', '설태', '맥진', '맥상', '복진', '복부', '복직근', '압통', '가동', '감각', '근력'].concat(this.conversationObjectiveFindingValueKeywords()));
        if (section === 'A') return this.conversationHasAnyKeyword(text, ['변증', '진단', '판단', '근거', '반대 근거']);
        if (section === 'P') return this.conversationHasAnyKeyword(text, ['치료', '처방', '침', '약', '생활지도', '추적']);
        return false;
    }

    private conversationCanCreateChartCandidate(requestText: string, replyText: string) {
        return this.conversationChartSignal(requestText, replyText).chartWorth === true;
    }

    private conversationHasAnyKeyword(text: string, keywords: string[]) {
        const normalized = this.normalizeLookupLabel(text);
        return keywords.some((keyword) => normalized.indexOf(this.normalizeLookupLabel(keyword)) >= 0);
    }

    private inferConversationSoapSection(text: string) {
        const normalized = this.normalizeLookupLabel(text);
        if (normalized.indexOf('치료') >= 0 || normalized.indexOf('처방') >= 0 || normalized.indexOf('침') >= 0 || normalized.indexOf('약') >= 0) return 'P';
        if (normalized.indexOf('변증') >= 0 || normalized.indexOf('진단') >= 0 || normalized.indexOf('판단') >= 0) return 'A';
        if (normalized.indexOf('진찰') >= 0 || normalized.indexOf('소견') >= 0 || normalized.indexOf('압통') >= 0 || normalized.indexOf('맥진') >= 0 || normalized.indexOf('설진') >= 0 || normalized.indexOf('복진') >= 0 || normalized.indexOf('설질') >= 0 || normalized.indexOf('설태') >= 0 || normalized.indexOf('맥상') >= 0 || normalized.indexOf('복부') >= 0 || normalized.indexOf('복직근') >= 0 || this.conversationObjectiveFindingValueScore(normalized) > 0) return 'O';
        return 'S';
    }

    private async streamConversationReply(reply: string) {
        const text = String(reply || '').trim();
        if (!text) {
            this.addConversationFailureTurn('응답 실패');
            return;
        }

        const turn: ConversationTurn = {
            role: 'assistant',
            text: '',
            createdAt: this.conversationNowLabel(),
            status: 'streaming',
        };
        this.conversationTurns.push(turn);

        if (this.prefersReducedMotion()) {
            turn.text = text;
            turn.status = 'answered';
            this.conversationStreaming = false;
            await this.service.render();
            this.scrollConversationThread(true);
            return;
        }

        this.conversationStreaming = true;
        await this.service.render();
        this.scrollConversationThread(true);

        const chunks = this.conversationStreamChunks(text);
        for (const chunk of chunks) {
            turn.text += chunk;
            await this.service.render();
            this.scrollConversationThread();
            await this.sleep(this.conversationStreamDelay(chunk));
        }
        turn.status = 'answered';
        this.conversationStreaming = false;
        await this.service.render();
        this.scrollConversationThread();
    }

    private conversationStreamChunks(text: string) {
        const chunks: string[] = [];
        let buffer = '';
        Array.from(text).forEach((char) => {
            buffer += char;
            if (buffer.length >= 4 || char === '\n' || char === ' ' || /[,.!?;:，。！？；：]/.test(char)) {
                chunks.push(buffer);
                buffer = '';
            }
        });
        if (buffer) chunks.push(buffer);
        return chunks;
    }

    private conversationStreamDelay(chunk: string) {
        if (chunk.indexOf('\n') >= 0) return 44;
        if (/[.!?。！？]/.test(chunk)) return 72;
        return Math.max(12, Math.min(34, chunk.length * 9));
    }

    private sleep(ms: number) {
        return new Promise<void>((resolve) => setTimeout(resolve, ms));
    }

    private prefersReducedMotion() {
        try {
            return typeof window !== 'undefined'
                && !!window.matchMedia
                && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch (e) {
            return false;
        }
    }

    private scrollConversationThread(force: boolean = false) {
        try {
            setTimeout(() => {
                const thread = this.conversationThreadRef && this.conversationThreadRef.nativeElement
                    ? this.conversationThreadRef.nativeElement as HTMLElement
                    : document.querySelector('.conversation-thread') as HTMLElement;
                if (!thread) return;
                const distanceFromBottom = thread.scrollHeight - thread.scrollTop - thread.clientHeight;
                if (!force && distanceFromBottom > 96) return;
                const scrollToLatest = () => {
                    thread.scrollTop = thread.scrollHeight;
                };
                scrollToLatest();
                if (force && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                    window.requestAnimationFrame(scrollToLatest);
                }
            }, 0);
        } catch (e) {
            // ignore scroll failures in non-browser test contexts
        }
    }

    private async callConversationApi(message: string, history: any[]) {
        const payload = {
            conversation_chat: '1',
            encounter_id: this.encounterId,
            message,
            history: JSON.stringify(history),
        };
        const primary = await this.conversationRequestWithTimeout(
            () => this.callApi('load_context', payload),
            this.conversationRequestTimeoutMs
        );
        if (primary && primary.timedOut) return primary;
        if (this.conversationApiHasReply(primary)) return primary;

        const tunneled = await this.conversationRequestWithTimeout(
            () => this.fetchConversationApi('load_context', payload),
            this.conversationRequestTimeoutMs
        );
        if (tunneled && tunneled.timedOut) return tunneled;
        if (this.conversationApiHasReply(tunneled)) return tunneled;

        const direct = await this.conversationRequestWithTimeout(
            () => this.fetchConversationApi('conversation_chat', {
                encounter_id: this.encounterId,
                message,
                history: JSON.stringify(history),
            }),
            this.conversationRequestTimeoutMs
        );
        if (this.conversationApiHasReply(direct)) return direct;
        if (direct.ok) return direct;
        if (tunneled.ok) return tunneled;
        return primary;
    }

    private async conversationRequestWithTimeout(factory: () => Promise<any>, timeoutMs: number) {
        let timeoutHandle: any = null;
        const timeoutResult = new Promise<any>((resolve) => {
            timeoutHandle = setTimeout(() => resolve({
                ok: false,
                data: null,
                requestId: '',
                message: '대화 응답 시간이 초과되었습니다.',
                timedOut: true,
            }), timeoutMs);
        });
        try {
            return await Promise.race([factory(), timeoutResult]);
        } finally {
            if (timeoutHandle) clearTimeout(timeoutHandle);
        }
    }

    private conversationApiHasReply(result: any) {
        const data = result && result.data ? result.data : {};
        const reply = String(data.reply || (data.result && data.result.message) || '').trim();
        return result && result.ok === true && reply.length > 0;
    }

    private async fetchConversationApi(action: string, payload: WorkspaceMap) {
        try {
            const params = new URLSearchParams();
            Object.keys(payload || {}).forEach((key) => {
                const value = payload[key];
                if (value !== undefined && value !== null) {
                    params.set(key, String(value));
                }
            });
            const response = await fetch(`/wiz/api/portal.hanscribe.encounter.workspace/${action}`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: params.toString(),
            });
            const body = await response.json();
            const result = this.unwrapResponse(body);
            this.requestId = result.requestId || this.requestId;
            return result;
        } catch (e) {
            return { ok: false, data: null, requestId: '', message: '응답 실패' };
        }
    }

    private addConversationFailureTurn(message: string) {
        const text = String(message || '').trim();
        this.conversationError = '';
        this.conversationStreaming = false;
        this.conversationTurns.push({
            role: 'assistant',
            text: text && text !== '처리 결과를 확인하지 못했습니다.'
                ? text
                : '응답 실패',
            createdAt: this.conversationNowLabel(),
            status: 'failed',
        });
    }

    private conversationNowLabel() {
        return this.koreaTimeLabel();
    }

    public get assistantOpenTaskCount() {
        const unresolvedRedFlags = this.unresolvedRedFlagItems.length;
        const pendingChecklist = this.checklistItems.filter((item) => {
            const status = item && item.status ? String(item.status) : 'unchecked';
            return !this.checklistStatusResolved(status);
        }).length;
        const safetyItems = this.checklistItems.filter((item) => {
            if (!item || !item.safety_level) return false;
            return !this.checklistStatusResolved(String(item.status || ''));
        }).length;
        const unreviewedSoap = this.soapSectionOrder.reduce((total, section) => {
            return total + this.soapItems(section).filter((item) => item.status === 'ai_unreviewed').length;
        }, 0);
        return Math.min(99, pendingChecklist + safetyItems + unresolvedRedFlags + unreviewedSoap + this.checklistSoapQueueRequiredCount + this.pendingAiAssistantDecisionCount + this.finalGateBlockingItems.length + this.finalGateConfirmableItems.length);
    }

    public get assistantOpenTaskLabel() {
        const count = this.assistantOpenTaskCount;
        if (count <= 0) return '대기 작업 없음';
        return `${count}건 확인`;
    }

    public get pendingAiAssistantDecisionCount() {
        return this.aiAssistantResults.reduce((total, result) => {
            const resultStatus = String((result && result.status) || '');
            const resultDone = ['accepted', 'accepted_edited', 'deferred', 'excluded'].indexOf(resultStatus) >= 0;
            const itemCount = this.asArray(result && result.items).filter((item) => this.aiAssistantItemNeedsDecision(item)).length;
            return total + itemCount + (!itemCount && !resultDone ? 1 : 0);
        }, 0);
    }

    public get assistantLatestResult() {
        return this.aiAssistantResults.length ? this.aiAssistantResults[0] : null;
    }

    public get mainAiRecommendationPreviewResults() {
        return this.aiAssistantResults.slice(0, 3);
    }

    public aiResultPreviewItems(result: AiAssistantResult) {
        return this.asArray(result && result.items).slice(0, 3);
    }

    public get assistantLatestTitle() {
        return this.assistantLatestResult ? this.assistantLatestResult.title : '새로 참고할 내용 없음';
    }

    public get assistantRecentCandidateTitle() {
        const result = this.assistantLatestResult;
        return result ? result.title : '최근 내용 없음';
    }

    public get assistantRecentCandidateTypeLabel() {
        const result = this.assistantLatestResult;
        if (!result) return '없음';
        return `${this.clinicalRecordDisplayText(result.type)} · ${this.statusLabel(result.status)}`;
    }

    public get assistantRecentCandidateStatusClass() {
        const result = this.assistantLatestResult;
        return this.panelStatusClass(result ? result.status : 'empty');
    }

    public get assistantUnappliedCandidateCount() {
        const pendingAssistantItems = this.pendingAiAssistantDecisionCount;
        const unreviewedSoap = this.soapSectionOrder.reduce((total, section) => {
            return total + this.soapItems(section).filter((item) => item.status === 'ai_unreviewed').length;
        }, 0);
        return Math.min(99, pendingAssistantItems + unreviewedSoap);
    }

    public get assistantUnappliedCandidateLabel() {
        const count = this.assistantUnappliedCandidateCount;
        return count > 0 ? `${count}건` : '없음';
    }

    public get assistantSummaryHint() {
        const result = this.assistantLatestResult;
        if (!result && this.assistantUnappliedCandidateCount <= 0) return '최근 내용 없음 · 새로 참고할 내용 없음';
        if (!result) return `최근 내용 없음 · 진료 참고 내용 ${this.assistantUnappliedCandidateLabel}`;
        return `${result.createdAt || '최근'} · 진료 참고 내용 ${this.assistantUnappliedCandidateLabel}`;
    }

    public aiAssistantResultCardClass(result: AiAssistantResult) {
        const tone = result && result.tone ? result.tone : 'ai';
        const status = result && result.status ? result.status : 'pending_doctor_review';
        return `ai-result-card ai-result-card--${tone} ai-result-card--${status}`;
    }

	    public aiAssistantResultDestinationLabel(result: AiAssistantResult) {
	        if (this.isFollowupQuestionResult(result)) return '확인 메모 / 문진 기록';
		        if (this.isPatternCandidateResult(result)) return '변증/진단 검토';
        if (this.isSoapImprovementResult(result)) return '차트 보완 내용';
        if (this.isConversationClinicalResult(result)) return '문진 / 진찰 / 한의 차트';
	        if (result && result.type === '진료 전 주의') return '진료 전 확인 / 진찰·치료 기록 / 저장';
	        if (result && result.type === '표준도구 추천') return '표준도구 / 진찰 기록';
	        if (result && result.type === '참조 비교') return '건강인 참조 / 진찰·변증 기록';
	        if (result && result.type === '누락 점검') return '저장 확인 / 치료 기록';
	        return '요약 / 근거';
	    }

    public aiAssistantItemNeedsDecision(item: AiAssistantResultItem) {
        if (!item) return false;
        const status = String(item.decision_status || 'pending_doctor_review');
        if (['referenced', 'deferred', 'ignored', 'excluded', 'added', 'modified', 'soap_suggested', 'soap_candidate_sent', 'checklist_memo_sent', 'edit_staged'].indexOf(status) >= 0) return false;
        if (item.applied_to_checklist && item.applied_to_soap) return false;
        return true;
    }

    public aiItemStatusLabel(item: AiAssistantResultItem) {
        const status = item && item.decision_status ? item.decision_status : 'pending_doctor_review';
        return this.statusLabel(status);
    }

    public aiItemStatusClass(item: AiAssistantResultItem) {
        const status = item && item.decision_status ? item.decision_status : 'pending_doctor_review';
        return this.panelStatusClass(status);
    }

    public canSendAiItemToChecklist(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item) return false;
        const status = String(item.decision_status || '');
        if (['ignored', 'excluded', 'deferred'].indexOf(status) >= 0) return false;
        return !!item.checklist_item_code && !item.applied_to_checklist;
    }

    public canSendAiItemToSoapCandidate(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !result || !item) return false;
        if (this.isSoapImprovementResult(result)) return false;
        const status = String(item.decision_status || '');
        if (['ignored', 'excluded', 'deferred'].indexOf(status) >= 0) return false;
        if (this.isPatternCandidateResult(result)) return !item.applied_to_soap && this.patternCandidateSoapCandidates(item).length > 0;
        return !item.applied_to_soap && this.aiItemSoapCandidateText(result, item).trim().length > 0;
    }

    private canApplyAiItemQueueRow(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !result || !item) return false;
        if (this.isPatternCandidateResult(result)) return this.canSendAiItemToSoapCandidate(result, item);
        const status = String(item.decision_status || 'pending_doctor_review');
        if (['ignored', 'excluded', 'deferred', 'added', 'modified', 'accepted', 'accepted_edited', 'referenced'].indexOf(status) >= 0) return false;
        return this.aiItemSoapCandidateText(result, item).trim().length > 0;
    }

    private async applyAiItemQueueRowToChart(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!this.canApplyAiItemQueueRow(result, item)) return null;
        const section = this.aiItemSoapSection(result, item);
        const text = this.aiItemSoapCandidateText(result, item).trim();
        const patternCandidate = this.isPatternCandidateResult(result)
            ? this.patternCandidateSoapCandidates(item).find((candidate) => this.normalizedSoapSection(candidate.soap_section || candidate.section) === section)
            : null;
        const conversationCandidate = this.isConversationClinicalResult(result)
            ? this.conversationItemToSoapCandidate(item)
            : null;
        const structuredCandidate = patternCandidate || conversationCandidate || {};
        const candidateSourceRefs = Array.isArray(item.source_refs) && item.source_refs.length
            ? item.source_refs
            : (Array.isArray(structuredCandidate.source_refs) ? structuredCandidate.source_refs : []);
        const sourceLabels = this.dedupeStrings([
            this.clinicalRecordDisplayText(result && result.type),
            this.clinicalRecordDisplayText(item && item.source),
            this.clinicalRecordDisplayText(item && item.answer_target),
        ].filter((value) => value !== ''));
        const sourceType = this.isPatternCandidateResult(result)
            ? 'pattern_candidate'
            : (this.isConversationClinicalResult(result)
                ? 'conversation_chat'
                : (this.isFollowupQuestionResult(result) ? 'ai_assistant_followup' : 'ai_assistant'));
        const saved = await this.applySoapCandidateDecision({
            action: 'apply',
            section,
            text,
            source_refs: candidateSourceRefs,
            reason: 'ai_item_queue_apply',
            candidate: {
                ...structuredCandidate,
                client_candidate_id: this.aiAssistantItemCandidateIdentity(result, item, section),
                soap_section: section,
                section,
                title: item.title || `${this.soapSectionDisplayName(section)} 참고 내용`,
                description: item.description || item.evidence_summary || text,
                text,
                soap_sentence_text: text,
                source: sourceType,
                generated_by: 'ai_assistant',
                author_type: 'ai_assistant',
                status: 'ai_unreviewed',
                candidate_status: 'new_candidate_ready',
                doctor_decision_status: 'pending_doctor_review',
                source_refs: candidateSourceRefs,
                suggestion_reasons: sourceLabels.length ? sourceLabels : ['진료지원'],
                source_labels: sourceLabels.length ? sourceLabels : ['진료지원'],
                answer_target: item.answer_target || `${this.soapSectionDisplayName(section)} 차트`,
                evidence_summary: item.evidence_summary || item.description || text,
                source_payload: {
                    ...((structuredCandidate && structuredCandidate.source_payload) || {}),
                    result_type: result && result.type ? result.type : '',
                    item_kind: item.kind || '',
                    request_text: result && result.requestText
                        ? result.requestText
                        : String((structuredCandidate.source_payload && structuredCandidate.source_payload.request_text) || ''),
                },
            },
        });
        if (!saved) return null;
        const objectiveDetailsApplied = this.applyObjectiveFindingDetailsFromAiItem(section, result, item, text);
        item.applied_to_soap = true;
        item.decision_status = 'added';
        this.soapMessage = objectiveDetailsApplied
            ? `${this.soapSectionDisplayName(section)}와 상세 기록에 반영됨.`
            : `${this.soapSectionDisplayName(section)}에 반영됨.`;
        this.aiAssistantMessage = objectiveDetailsApplied
            ? '진료 참고 내용이 차트와 상세 기록에 반영되었습니다.'
            : '진료 참고 내용이 차트에 반영되었습니다.';
        this.soapValidation = {};
        this.clearConversationBusyState();
        this.focusSoapSectionAfterCandidateAction(section);
        this.announceCandidateApplied(section);
        await this.service.render();
        this.scheduleBottomBarClearance();
        return saved;
    }

    private clearConversationBusyState() {
        this.conversationSending = false;
        this.conversationStreaming = false;
    }

    public aiItemSoapSection(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (item && item.soap_section) return this.normalizedSoapSection(item.soap_section);
        if (this.isPatternCandidateResult(result)) return 'A';
        if (result && this.clinicalRecordDisplayText(result.type) === '진료 전 주의') return 'O';
        if (this.isFollowupQuestionResult(result)) return 'S';
        return 'S';
    }

	    public aiItemSoapTargetLabel(result: AiAssistantResult, item: AiAssistantResultItem) {
	        if (this.isPatternCandidateResult(result)) return item && item.applied_to_soap ? '변증/진단 대기' : '변증/진단';
	        const section = this.aiItemSoapSection(result, item);
	        return item && item.applied_to_soap ? `${this.soapSectionDisplayName(section)} 대기` : `${this.soapSectionDisplayName(section)} 참고`;
	    }

    public aiItemHasSoapCandidate(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (this.isPatternCandidateResult(result)) return this.patternCandidateSoapCandidates(item).length > 0;
        return this.aiItemSoapCandidateText(result, item).trim().length > 0;
    }

    public aiItemSoapRouteLabel(result: AiAssistantResult, item: AiAssistantResultItem) {
	        if (this.isPatternCandidateResult(result)) return '변증/진단 · 대기';
        const section = this.aiItemSoapSection(result, item);
        return `${this.soapSectionChipLabel(section)} · 참고`;
    }

    public evidencePanelKey(result: AiAssistantResult, item?: AiAssistantResultItem, index: number = -1) {
        const base = this.safeEvidenceKey(result && (result.id || result.title || result.type), 'ai-result');
        if (!item) return `${base}-result`;
        const itemKey = this.safeEvidenceKey(item.pattern_code || item.title || item.display_name || index, `item-${index}`);
        return `${base}-${index}-${itemKey}`;
    }

    public evidencePanelIsOpen(key: string) {
        return !!this.evidencePanelsOpen[key];
    }

    public async toggleEvidencePanel(key: string) {
        if (!key) return;
        this.evidencePanelsOpen[key] = !this.evidencePanelsOpen[key];
        await this.service.render();
    }

    public evidenceCountForResult(result: AiAssistantResult) {
        return this.evidenceItemsForResult(result).length;
    }

    public evidenceCountForItem(item: AiAssistantResultItem) {
        return this.evidenceItemsForItem(item).length;
    }

    public evidenceCountForSoapSentence(item: SoapSentenceDraft) {
        return this.evidenceItemsForSoapSentence(item).length;
    }

    public evidenceItemsForResult(result: AiAssistantResult): EvidenceSourceItem[] {
        if (!result) return [];
        let rows: EvidenceSourceItem[] = [];
        this.asArray(result.items).forEach((item) => {
            rows = rows.concat(this.evidenceItemsForItem(item));
        });
        if (!rows.length) rows = this.inferEvidenceForAiResult(result);
        return this.dedupeEvidenceRows(rows).slice(0, 8);
    }

    public evidenceItemsForItem(item: AiAssistantResultItem): EvidenceSourceItem[] {
        if (!item) return [];
        const refs = Array.isArray(item.source_refs) ? item.source_refs : [];
        let rows = this.patternEvidenceRowsFromItem(item);
        this.asArray(item.patient_cues).forEach((value) => {
            const text = this.clinicalRecordDisplayText(value);
            if (text) rows.push({ category: '환자 단서', label: item.title || '임상 단서', value: text });
        });
        this.asArray(item.connection_reasons).forEach((value) => {
            const text = this.clinicalRecordDisplayText(value);
            if (text) rows.push({ category: '판단 이유', label: item.title || '임상 단서', value: text });
        });
        this.asArray(item.knowledge_evidence).forEach((evidence) => {
            if (!evidence || typeof evidence !== 'object') return;
            const label = this.clinicalRecordDisplayText(
                evidence.label || evidence.title || evidence.citation || '저작도구 근거',
            );
            const value = this.clinicalRecordDisplayText(
                [evidence.excerpt, evidence.citation, evidence.section].filter((entry) => entry).join(' · '),
            );
            if (!label && !value) return;
            rows.push({
                category: '저작도구 근거',
                label: label || '저작도구 근거',
                value: value || '저작도구 임상 Tool에서 출처 식별자를 확인했습니다.',
                note: '저작도구 임상 Tool 반환 근거 · 진료지원에는 원문 데이터가 아닌 출처 정보만 보존',
                source_ref: String(evidence.url || evidence.id || ''),
            });
        });
        rows = rows.concat(this.resolveEvidenceRefs(refs));
        if (!rows.length) rows = this.inferEvidenceForAiItem(item);
        return this.dedupeEvidenceRows(rows).slice(0, 8);
    }

    public evidenceItemsForSoapSentence(item: SoapSentenceDraft): EvidenceSourceItem[] {
        if (!item) return [];
        const refs = Array.isArray(item.source_refs) ? item.source_refs : [];
        let rows = this.patternEvidenceRowsFromSoapSentence(item);
        rows = rows.concat(this.resolveEvidenceRefs(refs));
        if (!rows.length) {
            const text = this.normalizeLookupLabel([item.text, item.doctor_decision_reason, item.author_type].filter((value) => value).join(' '));
            rows = rows.concat(this.summaryEvidenceByText(text));
            const checklist = this.checklistEvidenceByText(text);
            if (checklist) rows.push(checklist);
        }
        return this.dedupeEvidenceRows(rows).slice(0, 6);
    }

    public patternCandidateSupportingRows(item: AiAssistantResultItem) {
        const rows = this.patternCandidateTextRows(item && item.supporting_evidence, 3);
        if (rows.length) return rows;
        const summary = this.displayValue(item && (item.evidence_summary || item.description), '');
        return summary ? [summary] : ['지지 근거를 더 확인해 주세요'];
    }

    public patternCandidateOpposingRows(item: AiAssistantResultItem) {
        const rows = this.patternCandidateTextRows(item && item.opposing_evidence, 3);
        return rows.length ? rows : ['반대 또는 불확실 근거를 더 확인해 주세요'];
    }

    public patternCandidateFollowupRows(item: AiAssistantResultItem) {
        const rows = this.patternCandidateTextRows(item && item.followup_questions, 3);
        return rows.length ? rows : ['한의사 문진·진찰로 더 확인해 주세요'];
    }

    public patternCandidateCardEvidence(item: AiAssistantResultItem) {
        return this.patternCandidateKeyEvidenceText(item, 2);
    }

    public patternCandidateCardSentence(item: AiAssistantResultItem) {
        return this.patternCandidateChartSentence(item);
    }

    public patternCandidateDoctorNote(item: AiAssistantResultItem) {
        return item && item.doctor_judgement_note ? item.doctor_judgement_note : '';
    }

    public async setPatternCandidateDoctorNote(item: AiAssistantResultItem, text: string) {
        if (this.clinicalReadOnly || !item) return;
        item.doctor_judgement_note = text || '';
        this.aiAssistantMessage = '';
        await this.service.render();
    }

    public patternCandidateDoctorNotePreview(item: AiAssistantResultItem) {
        const note = this.displayValue(item && item.doctor_judgement_note, '');
        return note || '한의사 판단 전';
    }

    public canStagePatternCandidateForEdit(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !result || !item || !this.isPatternCandidateResult(result)) return false;
        const status = String(item.decision_status || '');
        if (['ignored', 'excluded'].indexOf(status) >= 0) return false;
        return this.patternAssessmentCandidateText(item).trim().length > 0;
    }

    public async stagePatternCandidateForEdit(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!this.canStagePatternCandidateForEdit(result, item)) return;
        const text = this.patternAssessmentCandidateText(item).trim();
        this.soapNewText.A = this.soapNewText.A ? this.appendNoteOnce(this.soapNewText.A, text) : text;
        item.decision_status = 'edit_staged';
        item.applied_action = 'edit_stage';
        this.markSoapCandidateQueueDirty();
        this.soapValidation = {};
        this.soapMessage = '편집 문장에 입력됨.';
        this.aiAssistantMessage = '한의사 판단 후 저장.';
        this.candidateQueueModalOpen = false;
        this.evidenceModalOpen = false;
        await this.closeWorkspaceDrawer();
        this.activeSoapSection = 'A';
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    private patternEvidenceRowsFromItem(item: AiAssistantResultItem): EvidenceSourceItem[] {
        if (!item || item.kind !== 'pattern_candidate') return [];
        const title = item.display_name || item.title || '변증/진단 가능성';
        const rows: EvidenceSourceItem[] = [];
        if (item.evidence_summary) {
            rows.push({ category: '요약 근거', label: title, value: item.evidence_summary });
        }
        this.asArray(item.supporting_evidence).forEach((value) => {
            const text = this.displayValue(value, '');
            if (text) rows.push({ category: '근거', label: title, value: text });
        });
        this.asArray(item.opposing_evidence).forEach((value) => {
            const text = this.displayValue(value, '');
            if (text) rows.push({ category: '반대/불확실 근거', label: title, value: text });
        });
        this.asArray(item.followup_questions).forEach((value) => {
            const text = this.displayValue(value, '');
            if (text) rows.push({ category: '추가로 확인할 내용', label: title, value: text });
        });
        this.asArray(item.source_labels).forEach((value) => {
            const text = this.clinicalRecordDisplayText(value);
            if (text) rows.push({ category: '출처', label: title, value: text });
        });
        return rows;
    }

    private patternCandidateTextRows(values: any, limit: number = 3) {
        return this.dedupeStrings(this.asArray(values).map((value) => this.displayValue(value, '')).filter((value) => value !== '')).slice(0, limit);
    }

    private patternEvidenceRowsFromSoapSentence(item: SoapSentenceDraft): EvidenceSourceItem[] {
        const meta = item && item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        const payload = this.patternPayloadFromSoapSentence(item);
        if (String(item.author_type || meta.source || '') !== 'pattern_candidate' && !payload.pattern_code) return [];
        const title = payload.display_name || meta.display_name || '변증/진단 가능성';
        const rows: EvidenceSourceItem[] = [];
        if (payload.evidence_summary) {
            rows.push({ category: '요약 근거', label: title, value: String(payload.evidence_summary || '') });
        }
        this.asArray(payload.supporting_evidence).forEach((value) => {
            const text = this.displayValue(value, '');
            if (text) rows.push({ category: '근거', label: title, value: text });
        });
        this.asArray(payload.opposing_evidence).forEach((value) => {
            const text = this.displayValue(value, '');
            if (text) rows.push({ category: '반대/불확실 근거', label: title, value: text });
        });
        this.asArray(payload.followup_questions).forEach((value) => {
            const text = this.displayValue(value, '');
            if (text) rows.push({ category: '추가로 확인할 내용', label: title, value: text });
        });
        this.asArray(payload.source_labels).forEach((value) => {
            const text = this.clinicalRecordDisplayText(value);
            if (text) rows.push({ category: '출처', label: title, value: text });
        });
        return rows;
    }

    public async setAiAssistantInput(value: string) {
        this.aiAssistantInput = value || '';
        this.aiAssistantMessage = '';
        await this.service.render();
    }

    public async runAiAssistantQuickAction(actionCode: string) {
        if (!this.canRunAiAssistant) return;
        if (actionCode === 'followup_questions') {
            await this.generateFollowupQuestionCandidates('');
            return;
        }
        if (actionCode === 'pattern_candidates') {
            await this.generatePatternCandidateCards('');
            return;
        }
        if (actionCode === 'standard_tools') {
            await this.generateStandardToolCards('');
            return;
        }
        if (actionCode === 'reference_compare') {
            await this.generateReferenceComparisonCard('');
            return;
        }
        const result = this.createAiAssistantResult(actionCode, '');
	        this.prependAiAssistantResult(result);
	        this.aiAssistantMessage = actionCode === 'soap_improvement'
            ? '차트 보완 내용을 추가했습니다.'
            : `${result.title} 추가됨.`;
        this.soapPanelMode = 'assistant';
        this.mobilePanel = 'assistant';
        this.activeWorkspaceDrawer = 'assistant';
        await this.service.render();
    }

    public async submitAiAssistantRequest() {
        if (!this.canSubmitAiAssistantRequest) return;
        const requestText = (this.aiAssistantInput || '').trim();
        const actionCode = this.inferAiAssistantAction(requestText);
        if (actionCode === 'followup_questions') {
            await this.generateFollowupQuestionCandidates(requestText, true);
            return;
        }
        if (actionCode === 'pattern_candidates') {
            await this.generatePatternCandidateCards(requestText, true);
            return;
        }
        if (actionCode === 'standard_tools') {
            await this.generateStandardToolCards(requestText, true);
            return;
        }
        if (actionCode === 'reference_compare') {
            await this.generateReferenceComparisonCard(requestText, true);
            return;
        }
        const result = this.createAiAssistantResult(actionCode, requestText);
        result.title = '직접 요청';
        result.type = '직접 요청';
        if (actionCode === 'soap_improvement') {
            result.title = '직접 보완';
            result.type = '한의 차트 보완';
        }
	        result.summary = `직접 요청 · ${result.summary}`;
	        this.prependAiAssistantResult(result);
	        this.aiAssistantInput = '';
	        this.aiAssistantMessage = actionCode === 'soap_improvement'
            ? '차트 보완 내용을 추가했습니다.'
            : '진료 참고 내용을 추가했습니다.';
        this.soapPanelMode = 'assistant';
        this.mobilePanel = 'assistant';
        this.activeWorkspaceDrawer = 'assistant';
        await this.service.render();
    }

    public async decideAiAssistantResult(result: AiAssistantResult, status: string) {
        if (!result || this.clinicalReadOnly) return;
        if (['accepted', 'accepted_edited', 'deferred', 'excluded'].indexOf(status) === -1) return;
        result.status = status;
	        const messages: WorkspaceMap = {
	            accepted: '반영 전 한 번 더 확인하세요.',
	            accepted_edited: '수정 선택됨.',
	            deferred: '나중에 보기로 표시됨.',
	            excluded: '사용 안 함으로 표시됨.',
	        };
	        this.aiAssistantMessage = messages[status] || '상태 변경됨.';
        await this.service.render();
    }

    public async referenceAiAssistantItem(item: AiAssistantResultItem) {
	        if (this.clinicalReadOnly || !item) return;
	        item.decision_status = 'referenced';
        this.markSoapCandidateQueueDirty();
	        this.aiAssistantMessage = '참고로 표시됨.';
        await this.service.render();
    }

    public async deferAiAssistantItem(item: AiAssistantResultItem) {
	        if (this.clinicalReadOnly || !item) return;
	        item.decision_status = 'deferred';
        this.markSoapCandidateQueueDirty();
	        this.aiAssistantMessage = '나중에 보기로 표시됨.';
        await this.service.render();
    }

    public async ignoreAiAssistantItem(item: AiAssistantResultItem) {
	        if (this.clinicalReadOnly || !item) return;
	        item.decision_status = 'ignored';
        this.markSoapCandidateQueueDirty();
	        this.aiAssistantMessage = '사용 안 함으로 표시됨.';
        await this.service.render();
    }

    public async sendAiItemToChecklistMemo(item: AiAssistantResultItem) {
        await this.applyAiQuestionToChecklist(item);
    }

    public async sendAiItemToSoapCandidate(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!this.canSendAiItemToSoapCandidate(result, item)) return;
        if (item.mcp_managed && item.recommendation_id) {
            const row = this.soapCandidateQueueRows.find((candidate) => {
                return candidate.item && candidate.item.recommendation_id === item.recommendation_id;
            });
            if (row) await this.openClinicalCandidateModal(row);
            return;
        }
        if (this.isPatternCandidateResult(result)) {
            await this.selectPatternCandidate(result, item);
            return;
        }
        await this.applyAiItemQueueRowToChart(result, item);
    }

    public isFollowupQuestionResult(result: AiAssistantResult) {
        return !!result && result.type === '추가 문진';
    }

    public isPatternCandidateResult(result: AiAssistantResult) {
        return !!result && ['변증 후보', '변증/진단 참고 후보', '변증/진단'].indexOf(result.type) >= 0;
    }

    public isSoapImprovementResult(result: AiAssistantResult) {
        if (!result) return false;
        const type = this.clinicalRecordDisplayText(result.type);
        return type === '한의 차트 보완';
    }

    public isMcpCandidateRow(row: SoapCandidateQueueRow | null) {
        return !!row && !!row.item && row.item.mcp_managed === true && !!row.item.recommendation_id;
    }

    public mcpCandidateTargetFieldLabel(row: SoapCandidateQueueRow | null) {
        if (!this.isMcpCandidateRow(row)) return '';
        const target = String((row!.item && row!.item.target_field) || '');
        const labels: WorkspaceMap = {
            'soap.S': '문진(S)',
            'soap.O': '진찰·소견(O)',
            'soap.A': '변증·진단(A)',
            'soap.P': '치료·처방(P)',
        };
        return labels[target] || target;
    }

    public mcpCandidateExistingValue(row: SoapCandidateQueueRow | null) {
        if (!this.isMcpCandidateRow(row)) return '';
        return String((row!.item && row!.item.existing_value) || '').trim();
    }

    private async applyMcpCandidateRow(row: SoapCandidateQueueRow, proposedValue: string) {
        if (!this.isMcpCandidateRow(row) || !row.item) return;
        this.mcpError = '';
        this.mcpMessage = '';
        const result = await this.callApi('apply_mcp_recommendation', {
            encounter_id: this.encounterId,
            recommendation_id: row.item.recommendation_id || '',
            proposed_value: proposedValue,
            approved: '1',
        });
        if (!result.ok) {
            this.mcpError = result.message || 'MCP 추천을 차트에 반영하지 못했습니다.';
            this.error = this.mcpError;
            await this.service.render();
            return;
        }
        row.item.applied_to_soap = true;
        row.item.decision_status = 'added';
        row.result!.status = 'applied';
        this.mcpMessage = '의료진 승인에 따라 차트 편집 영역에 반영했습니다.';
        this.aiAssistantMessage = this.mcpMessage;
        const context = result.data && result.data.context ? result.data.context : null;
        if (context) {
            this.applyContext(context);
        } else {
            await this.loadContext(false);
        }
        this.focusSoapSectionAfterCandidateAction(row.section);
        this.announceCandidateApplied(row.section);
        await this.service.render();
    }

    public soapSupplementReasons(item: AiAssistantResultItem) {
        const reasons = this.asArray(item && item.suggestion_reasons).map((value) => this.clinicalRecordDisplayText(value)).filter((value) => value !== '');
        if (reasons.length) return reasons;
        return this.asArray(item && item.source_labels).map((value) => this.clinicalRecordDisplayText(value)).filter((value) => value !== '');
    }

    public soapSupplementSectionLabel(item: AiAssistantResultItem) {
        const section = item && item.soap_section ? String(item.soap_section) : 'S';
        return this.soapSectionChipLabel(section);
    }

    public soapSupplementStatusLabel(item: AiAssistantResultItem) {
        const status = item && item.decision_status ? item.decision_status : 'pending_doctor_review';
        const labels: WorkspaceMap = {
            added: '추가 반영',
            modified: '수정 반영',
            ignored: '사용 안 함',
        };
        return labels[status] || this.statusLabel(status);
    }

	    public soapSupplementModifyButtonLabel(item: AiAssistantResultItem) {
	        return item && item.target_sentence_preview ? '수정' : '입력칸';
	    }

    public async applySoapSupplementSuggestion(item: AiAssistantResultItem, action: string) {
        if (this.clinicalReadOnly || !item) return;
        if (item.applied_to_soap || item.decision_status === 'ignored' || item.decision_status === 'excluded' || item.decision_status === 'deferred') return;
        const section = this.normalizedSoapSection(item.soap_section || 'S');
        const text = (item.soap_sentence_text || item.description || '').trim();
        if (!text) {
            this.aiAssistantMessage = '반영할 문장 없음.';
            await this.service.render();
            return;
        }

        const target = action === 'modify' ? this.findSoapSupplementTarget(section, item.target_sentence_id || '') : null;
        const candidate = {
            client_candidate_id: `soap-supplement::${item.title || Date.now()}::${section}`,
            soap_section: section,
            text,
            source: item.source || 'ai_assistant',
            generated_by: 'ai_assistant',
            author_type: 'ai_assistant',
            source_refs: Array.isArray(item.source_refs) ? item.source_refs : [],
            doctor_decision_reason: action === 'modify' ? 'ai_soap_supplement_modify' : 'ai_soap_supplement_add',
            target_sentence_id: (target && target.id) || item.target_sentence_id || '',
            target_sentence_preview: (target && target.text) || item.target_sentence_preview || '',
            source_payload: {
                title: item.title || '',
                description: item.description || '',
                suggestion_reasons: this.soapSupplementReasons(item),
            },
        };
        const applied = await this.applySoapCandidateDecision({
            action: action === 'modify' ? 'edit_apply' : 'section_apply',
            sentence_id: target && target.id ? target.id : '',
            target_sentence_id: target && target.id ? target.id : (item.target_sentence_id || ''),
            section,
            text,
            source_refs: candidate.source_refs,
            reason: candidate.doctor_decision_reason,
            candidate,
        });
        if (!applied) {
            this.aiAssistantMessage = this.error || '반영 실패.';
            await this.service.render();
            return;
        }

        if (action === 'modify') {
            item.decision_status = 'modified';
            item.applied_action = 'modify';
            item.applied_to_soap = true;
            this.soapMessage = `${section} 문장 수정 반영됨.`;
            this.aiAssistantMessage = '수정 반영됨.';
        } else {
            item.decision_status = 'added';
            item.applied_action = 'add';
            item.applied_to_soap = true;
            this.soapMessage = `${section} 문장 반영됨.`;
            this.aiAssistantMessage = '섹션 반영됨.';
        }

        this.markSoapCandidateQueueDirty();
        this.soapValidation = {};
        this.focusSoapSectionAfterCandidateAction(section);
        this.announceCandidateApplied(section);
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public async ignoreSoapSupplementSuggestion(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item) return;
        if (item.applied_to_soap) return;
        const section = this.normalizedSoapSection(item.soap_section || 'S');
        const text = (item.soap_sentence_text || item.description || '').trim();
        const saved = await this.applySoapCandidateDecision({
            action: 'exclude',
            section,
            text,
            source_refs: Array.isArray(item.source_refs) ? item.source_refs : [],
            reason: 'ai_soap_supplement_exclude',
            candidate: {
                client_candidate_id: `soap-supplement::${item.title || Date.now()}::${section}`,
                soap_section: section,
                text,
                source: item.source || 'ai_assistant',
                generated_by: 'ai_assistant',
                author_type: 'ai_assistant',
                source_refs: Array.isArray(item.source_refs) ? item.source_refs : [],
                source_payload: {
                    title: item.title || '',
                    description: item.description || '',
                    suggestion_reasons: this.soapSupplementReasons(item),
                },
            },
        });
        if (!saved) {
            this.aiAssistantMessage = this.error || '사용 안 함 결정을 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        item.decision_status = 'excluded';
        item.applied_action = 'ignore';
        this.markSoapCandidateQueueDirty();
        this.aiAssistantMessage = '사용 안 함으로 표시됨.';
        await this.service.render();
    }

    public async deferSoapSupplementSuggestion(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item) return;
        if (item.applied_to_soap) return;
        const section = this.normalizedSoapSection(item.soap_section || 'S');
        const text = (item.soap_sentence_text || item.description || '').trim();
        const saved = await this.applySoapCandidateDecision({
            action: 'defer',
            section,
            text,
            source_refs: Array.isArray(item.source_refs) ? item.source_refs : [],
            reason: 'ai_soap_supplement_defer',
            candidate: {
                client_candidate_id: `soap-supplement::${item.title || Date.now()}::${section}`,
                soap_section: section,
                text,
                source: item.source || 'ai_assistant',
                generated_by: 'ai_assistant',
                author_type: 'ai_assistant',
                source_refs: Array.isArray(item.source_refs) ? item.source_refs : [],
                source_payload: {
                    title: item.title || '',
                    description: item.description || '',
                    suggestion_reasons: this.soapSupplementReasons(item),
                },
            },
        });
        if (!saved) {
            this.aiAssistantMessage = this.error || '나중에 보기 결정을 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        item.decision_status = 'deferred';
        item.applied_action = 'defer';
        this.markSoapCandidateQueueDirty();
        this.aiAssistantMessage = '나중에 보기로 표시됨.';
        await this.service.render();
    }

    public patternCandidateStatusLabel(item: AiAssistantResultItem) {
        const status = item && item.decision_status ? item.decision_status : 'pending_doctor_review';
        if (status === 'soap_candidate_sent') return '변증/진단 기록 대기';
        if (status === 'edit_staged') return '수정 중';
        return this.statusLabel(status);
    }

    public patternCandidateStatusClass(item: AiAssistantResultItem) {
        const status = item && item.decision_status ? item.decision_status : 'pending_doctor_review';
        return this.panelStatusClass(status);
    }

    public async referencePatternCandidate(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item) return;
        item.decision_status = 'referenced';
        this.markSoapCandidateQueueDirty();
	        this.aiAssistantMessage = '참고 표시됨.';
        await this.service.render();
    }

    public async selectPatternCandidate(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!this.canSendAiItemToSoapCandidate(result, item)) return;
        const appended = await this.appendSoapCandidates(this.patternCandidateSoapCandidates(item));
        if (!appended) {
            this.aiAssistantMessage = this.error || '내용을 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        item.applied_to_soap = true;
        item.decision_status = 'soap_candidate_sent';
        item.applied_action = 'select';
        this.markSoapCandidateQueueDirty();
        this.soapValidation = {};
        this.soapMessage = '한의사 판단 전입니다.';
        this.aiAssistantMessage = '변증/진단 내용을 확인 목록에 넣었습니다.';
        this.focusSoapSectionAfterCandidateAction('A');
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public async deferPatternCandidate(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item || item.decision_status === 'deferred') return;
        const saved = await this.persistPatternCandidateQueueDecision(item, 'defer');
        if (!saved) {
            this.aiAssistantMessage = this.error || '나중에 보기로 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        item.decision_status = 'deferred';
        item.applied_action = 'defer';
        this.markSoapCandidateQueueDirty();
        this.aiAssistantMessage = '나중에 보기로 표시됨.';
        await this.service.render();
    }

    public async excludePatternCandidate(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item) return;
        const saved = await this.persistPatternCandidateQueueDecision(item, 'exclude');
        if (!saved) {
            this.aiAssistantMessage = this.error || '사용 안 함으로 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        item.decision_status = 'excluded';
        item.applied_action = 'exclude';
        this.markSoapCandidateQueueDirty();
	        this.aiAssistantMessage = '사용 안 함으로 표시됨.';
        await this.service.render();
    }

    public async addPatternCandidateSoapSuggestion(item: AiAssistantResultItem) {
        const result = this.aiAssistantResult('변증/진단', '변증/진단', 'ai', '변증/진단 검토', [item], ['의심 소견'], ['한의사 판단 전'], '');
        await this.selectPatternCandidate(result, item);
    }

    public async applyAiQuestionToChecklist(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item) return;
        const code = item.checklist_item_code || '';
        if (!code) {
	            this.aiAssistantMessage = '연결된 확인 항목이 없어 문진 기록으로만 남길 수 있습니다.';
            await this.service.render();
            return;
        }
        const target = this.checklistItems.find((row) => row && row.code === code);
        if (!target) {
	            this.aiAssistantMessage = '연결 항목 없음.';
            await this.service.render();
            return;
        }
        const draft = this.checklistDraft(target);
        draft.note = this.appendNoteOnce(draft.note, item.checklist_note_text || this.questionMemoText(item));
        draft.dirty = true;
        item.applied_to_checklist = true;
        item.decision_status = 'checklist_memo_sent';
        this.markSoapCandidateQueueDirty();
	        this.checklistMessage = `${target.label || code} 메모 대기. 저장 필요.`;
	        this.aiAssistantMessage = '확인 메모 대기. 저장이 필요합니다.';
        this.mobilePanel = 'checklist';
        this.activeWorkspaceDrawer = 'checklist';
        await this.service.render();
    }

    public async applyAiQuestionToSoap(item: AiAssistantResultItem) {
        if (this.clinicalReadOnly || !item) return;
        const section = item.soap_section || 'S';
        if (this.soapSectionOrder.indexOf(section) === -1) return;
        const text = (item.soap_sentence_text || this.questionSoapText(item)).trim();
        if (!text) return;
        const appended = await this.appendSoapCandidate({
            client_candidate_id: `followup::${item.checklist_item_code || item.title || Date.now()}::${section}`,
            soap_section: section,
            text,
            source: 'ai_assistant_followup',
            generated_by: 'ai_assistant',
            author_type: 'ai_assistant',
            status: 'ai_unreviewed',
            candidate_status: 'pending_doctor_review',
            doctor_decision_status: 'pending_doctor_review',
            needs_update: this.soapNeedsRegeneration === true,
            source_refs: Array.isArray(item.source_refs) ? item.source_refs : [],
            doctor_decision_reason: 'ai_followup_question_candidate',
            checklist_item_code: item.checklist_item_code || '',
            checklist_item_label: item.checklist_item_label || '',
            source_payload: {
                question: item.question || item.title || '',
                reason: item.reason || item.description || '',
                answer_target: item.answer_target || '',
            },
        });
        if (!appended) {
            this.aiAssistantMessage = this.error || '내용을 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        item.applied_to_soap = true;
        item.decision_status = 'soap_candidate_sent';
        this.markSoapCandidateQueueDirty();
        this.soapValidation = {};
	        this.soapMessage = `${this.soapSectionDisplayName(section)} 참고 내용이 저장되었습니다.`;
	        this.aiAssistantMessage = '참고 내용이 저장되었습니다.';
        this.focusSoapSectionAfterCandidateAction(section);
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public async loadContext(showLoading: boolean = true) {
        if (!this.encounterId) {
            this.error = '방문 ID가 없습니다.';
            this.loading = false;
            await this.service.render();
            return;
        }

        const shouldRenderLoading = showLoading && !this.loading;
        if (showLoading) this.loading = true;
        this.error = '';
        if (shouldRenderLoading) await this.service.render();

        const result = await this.callApiWithRetry('load_context', { encounter_id: this.encounterId }, 1);
        if (showLoading) this.loading = false;

        if (result.ok) {
            this.retryActionLabel = '';
            const context = result.data && result.data.context ? result.data.context : result.data;
            this.applyContext(context);
            if (showLoading) this.triggerEntryReveal();
        } else {
            this.error = result.message;
            this.retryActionLabel = '다시 조회';
        }
        await this.service.render();
        if (showLoading && typeof performance !== 'undefined' && typeof performance.mark === 'function') {
            performance.clearMarks('hanscribe-workspace-ready');
            performance.mark('hanscribe-workspace-ready');
        }
    }

    public get mcpHarnessEnabled() {
        return this.mcpHarness && this.mcpHarness.enabled === true;
    }

    public get mcpTelemetry() {
        return this.mcpHarness && this.mcpHarness.telemetry && typeof this.mcpHarness.telemetry === 'object'
            ? this.mcpHarness.telemetry
            : {};
    }

    public mcpTelemetryTools() {
        return this.asArray(this.mcpTelemetry.tools);
    }

    public mcpTelemetryAlerts() {
        return this.asArray(this.mcpTelemetry.alerts);
    }

    public mcpTelemetryTotal(key: string) {
        const totals = this.mcpTelemetry.totals && typeof this.mcpTelemetry.totals === 'object'
            ? this.mcpTelemetry.totals
            : {};
        return Number(totals[key] || 0);
    }

    public mcpTelemetryStatusLabel() {
        if (!this.mcpHarnessEnabled || this.mcpTelemetry.status === 'disabled') return '연결 전';
        if (this.mcpTelemetry.status === 'forbidden') return '조회 권한 없음';
        if (this.mcpTelemetry.status === 'degraded') return '통계 확인 필요';
        return this.mcpTelemetryTotal('call_count') ? '운영 기록 있음' : '호출 대기';
    }

    public mcpToolDisplayName(toolName: string) {
        const labels: WorkspaceMap = {
            get_patient_context: '환자 컨텍스트',
            apply_chart_draft: '차트 초안 반영',
            search_prescriptions: '처방 지식 조회',
            get_herb_info: '약재 정보 조회',
            get_acupoint_info: '경혈 정보 조회',
            search_clinical_cases: '임상사례 조회',
        };
        return labels[String(toolName || '')] || String(toolName || 'MCP Tool');
    }

    public mcpRateLabel(value: any) {
        const rate = Math.max(0, Math.min(1, Number(value || 0)));
        return `${Math.round(rate * 100)}%`;
    }

    public async requestMcpRecommendations() {
        if (!this.mcpHarnessEnabled || this.mcpGenerating || this.clinicalReadOnly) return;
        this.mcpGenerating = true;
        this.mcpMessage = '';
        this.mcpError = '';
        await this.service.render();
        const result = await this.callApi('generate_mcp_recommendations', {
            encounter_id: this.encounterId,
            purpose_code: 'care_support',
        });
        this.mcpGenerating = false;
        if (!result.ok) {
            this.mcpError = result.message || 'MCP 진료지원 추천을 준비하지 못했습니다.';
            await this.service.render();
            return;
        }
        const harness = result.data && result.data.mcp_harness ? result.data.mcp_harness : {};
        if (harness.status !== 'success') {
            this.mcpError = (harness.error && harness.error.message) || 'MCP 진료지원 추천을 준비하지 못했습니다.';
            await this.service.render();
            return;
        }
        this.mcpMessage = `${this.asArray(harness.recommendations).length}건의 AI 추천을 준비했습니다. 의료진 확인 전에는 차트에 반영되지 않습니다.`;
        await this.loadContext(false);
        await this.service.render();
    }

    private async refreshContextAfterMutation(preserveLocalSoapDrafts: boolean = false) {
        if (!this.encounterId) return false;
        const result = await this.callApiWithRetry('load_context', { encounter_id: this.encounterId }, 1);
        if (!result.ok) {
            this.error = result.message;
            await this.service.render();
            return false;
        }
        const context = result.data && result.data.context ? result.data.context : result.data;
        this.applyContext(context, preserveLocalSoapDrafts);
        return true;
    }

    private async waitForSoapCandidateReady(maxAttempts: number = 5) {
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            await this.delay(650);
            const refreshed = await this.refreshContextAfterMutation();
            if (!refreshed) return false;
            const state = this.soapEntryPrepareState;
            const status = String((state && state.status) || '');
            const runState = String((state && state.state) || '');
            if (this.hasSoapDraft || this.soapCandidateQueueWorkCount > 0 || status === 'candidate_ready' || state.existing_candidate === true) return true;
            if (['failed', 'insufficient_input', 'policy_blocked', 'provider_failed'].indexOf(status) >= 0 || runState === 'manual_only') return false;
            if (status && ['candidate_preparing', 'pending', 'running'].indexOf(status) === -1 && runState !== 'preparing') return false;
        }
        return false;
    }

    private delay(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    public async markInProgress() {
        await this.syncStatus('in_progress');
    }

    public async markDone() {
        await this.syncStatus('done');
    }

    public backToBoard() {
        if (!this.confirmWorkspaceNavigation()) return;
        this.allowNextUnloadOnce();
        this.service.href('/hanscribe/encounters');
    }

    private confirmWorkspaceNavigation() {
        if (!this.hasUnsavedWorkspaceChanges) return true;
        try {
            return window.confirm('저장하지 않은 변경사항이 있습니다. 목록으로 이동할까요?');
        } catch (e) {
            return false;
        }
    }

    private allowNextUnloadOnce() {
        this.allowNextUnload = true;
        setTimeout(() => {
            this.allowNextUnload = false;
        }, 1200);
    }

    public async toggleDebugDetails() {
        if (!this.canViewDebugDetails) return;
        this.debugDetailsOpen = !this.debugDetailsOpen;
        await this.service.render();
    }

    public statusLabel(status: string) {
        const labels: WorkspaceMap = {
            scheduled: '예약',
            waiting: '대기',
            checked_in: '접수',
            in_progress: '진료 중',
            done: '완료',
            cancelled: '취소',
            synced: '동기화됨',
            ready: '준비됨',
            empty: '없음',
            no_candidate: '작성 전',
            pending: '한의사 판단 전',
            stale: '최신 진료 내용 확인',
            previous_version: '이전 문장',
            new_candidate_ready: '진료 참고 내용',
            candidate_preparing: '진료 내용 정리 중',
            pending_apply: '진료 참고 내용',
            applied: '차트 반영 완료',
            needs_temp_save: '저장 필요',
            ready_for_final_save: '최종 저장 가능',
            final_saved: '최종 저장됨',
            skeleton: '대화 없음',
            recommended: '권장',
            started: '시작됨',
            saved: '저장됨',
            completed: '완료',
            unchecked: '미확인',
            confirmed: '확인',
            not_applicable: '해당 없음',
            deferred: '나중에 보기',
            action_completed: '조치 완료',
            needs_regeneration: '최신 진료 내용 확인',
            manual_draft: '직접 작성',
            draft_generated: '준비됨',
            no_draft: '작성 전',
            drafting: '기록 중',
            needs_validation: '한 번 더 확인',
            candidate_ready: '참고 내용 확인',
            pending_doctor_review: '한의사 판단 전',
            generating: '진료 내용 정리 중',
            accepted: '선택',
            accepted_edited: '수정 선택',
            added: '차트 반영 완료',
            modified: '수정 후 반영됨',
            ignored: '사용 안 함',
            referenced: '참고',
            edit_staged: '수정 중',
            checklist_memo_sent: '확인 메모 저장 전',
            soap_candidate_sent: '진료 참고 내용',
	            soap_suggested: '기록 보완',
            doctor_added: '직접 작성',
            doctor_edited: '수정 내용 저장 필요',
            ai_unreviewed: '한의사 판단 전',
            reviewed: '확인됨',
            excluded: '사용 안 함',
            validation_failed: '차트 정리',
            ready_for_approval: '최종 저장 가능',
            confirmation_required: '확인 후 저장',
            passed: '저장 가능',
            approved: '최종 저장됨',
            already_approved: '최종 저장됨',
            needs_review: '한 번 더 확인',
            resolved: '확인됨',
            unresolved_red_flags: '진료 전 주의를 확인해 주세요',
            forbidden: '권한 없음',
            config_missing: '설정 필요',
            policy_blocked: '현재 추천 없음',
            provider_failed: '처리하지 못했습니다',
            snapshot_failed: '예진/문진을 확인해 주세요',
            preview: '미리보기',
            scored: '점수 산출',
        };
        return labels[status] || status || '미정';
    }

    public statusBadgeClass(status: string) {
        return hanscribeStatusBadgeClass(status, 'encounter', 'status-badge');
    }

    public panelStatusClass(status: string) {
        return hanscribeStatusBadgeClass(status, 'common', 'status-chip');
    }

	    public finalBlockerLabel(code: string) {
	        const labels: WorkspaceMap = {
	            unsaved_sentences: '미저장 한의 차트 변경',
	            draft_not_found: '한의 차트 초안 없음',
	            encounter_not_found: '방문 정보 없음',
	            needs_regeneration: '최신 입력 반영 필요',
            validation_failed: '한의 차트 확인',
            soap_validation_required: '한의 차트 확인',
            final_save_candidate_required: '진료 전 확인을 먼저 진행',
            missing_required_sections: '비어 있는 영역 작성',
            unreviewed_ai_sentences: '한의사 판단이 남은 기록',
	            stale_soap_candidates: '최신 진료 내용을 반영할 기록 내용',
	            ap_source_unverified: '변증/치료 근거 확인',
	            unresolved_red_flags: '진료 전 주의를 확인해 주세요',
            blocking_checklist_items: '진료 전 확인',
            required_checklist_unconfirmed: '진료 전 확인 미완료',
            precheck_unsubmitted: '현장 문진으로 진행',
	            nonblocking_red_flag_review: '안전 항목을 확인해 주세요',
	            standard_tool_unscored: '표준도구 점수 미확정',
	            reference_comparison_preview: '참조 비교 미리보기',
	            reference_comparison_attention: '건강인 참조 주의 구간',
	            standard_tool_not_started: '표준도구 미실시',
	            standard_tool_score_preview: '표준도구 미리보기',
	        };
	        return labels[code] || '확인만 하면 되는 내용';
	    }

    public gateItemTitle(item: WorkspaceMap) {
        if (!item) return '확인만 하면 되는 내용';
        if (item.message) return this.clinicalUiText(item.message);
        return this.finalBlockerLabel(String(item.code || ''));
    }

    public finalSaveItemTitle(item: WorkspaceMap) {
        if (!item) return '확인만 하면 되는 내용';
        const code = String(item.code || '');
        const labels: WorkspaceMap = {
            missing_required_sections: '비어 있는 영역을 작성하세요',
            soap_validation_required: '진료기록을 한 번 더 점검하세요',
            final_save_candidate_required: '최종 점검을 다시 진행하세요',
            validation_failed: '진료기록을 보완하세요',
            unreviewed_ai_sentences: '한의사 판단이 남은 문장을 정하세요',
            stale_soap_candidates: '최신 진료 내용을 반영하세요',
            unsaved_sentences: '미저장 변경사항을 저장하세요',
            ap_source_unverified: '변증/치료 근거를 확인하세요',
            required_checklist_unconfirmed: '진료 전 확인 항목을 확인하세요',
            precheck_unsubmitted: '예진 제출 상태를 확인하세요',
            source_anatomic_mismatch: '문장과 근거의 신체 부위를 맞춰 주세요',
            mixed_anatomic_scope: '한 문장에 함께 기록된 신체 부위를 확인하세요',
        };
        if (labels[code]) return labels[code];
        if (item.title) return this.clinicalUiText(item.title);
        return this.gateItemTitle(item);
    }

    public gateItemSummary(item: WorkspaceMap) {
        const rows = this.asArray(item && item.items);
        if (!rows.length) return '';
        return rows.slice(0, 3).map((row) => this.clinicalUiText(row.label || row.code || row.status_label || '확인만 하면 되는 내용')).join(', ')
            + (rows.length > 3 ? ` 외 ${rows.length - 3}건` : '');
    }

    public finalSaveItemSummary(item: WorkspaceMap) {
        if (!item) return '';
        const code = String(item.code || '');
        const details = item.details && typeof item.details === 'object' ? item.details : {};
        const missingSectionSource = details.sections || details.missing_sections || item.sections || item.missing_sections;
        const missingSections = this.dedupeStrings(this.asArray(missingSectionSource)
            .map((section) => this.soapSectionDisplayName(section)));
        if (code === 'missing_required_sections' && missingSections.length) {
            return `${missingSections.join(', ')} 문장 추가`;
        }
        if (code === 'ap_source_unverified') {
            const sections = this.dedupeStrings(this.finalGateItemSections(item).map((section) => this.soapSectionDisplayName(section)));
            return sections.length ? `${sections.join(', ')} 근거 확인` : '변증/치료 근거 확인';
        }
        if (Number(item.count || 0) > 0) return `${Number(item.count || 0)}건`;
        if (item.summary) return this.clinicalUiText(item.summary);
        return this.gateItemSummary(item);
    }

    private clinicalUiText(value: any) {
        return this.clinicalRecordDisplayText(value)
            .replace(/S\s*영역\s*문장이\s*없습니다\.?/g, '문진이 비어 있습니다')
            .replace(/O\s*영역\s*문장이\s*없습니다\.?/g, '진찰/소견이 비어 있습니다')
            .replace(/A\s*영역\s*문장이\s*없습니다\.?/g, '변증/진단이 비어 있습니다')
            .replace(/P\s*영역\s*문장이\s*없습니다\.?/g, '치료/처방이 비어 있습니다')
            .replace(/최종\s*저장\s*후보\s*전환\s*필요/g, '진료 전 확인을 먼저 진행')
            .replace(/미검토/g, '한의사 판단이 남은')
            .replace(/검토\s*대기/g, '한의사 판단 전')
            .replace(/검토\s*전/g, '한의사 판단 전')
            .replace(/반영\s*대기/g, '진료 참고 내용')
            .replace(/신규\s*검토\s*후보/g, '진료 참고 내용')
            .replace(/기록\s*후보/g, '차트에 넣을 내용')
            .replace(/검토\s*후보/g, '검토할 내용')
            .replace(/진료지원\s*제안/g, '진료 참고 내용')
            .replace(/한의\s*차트\s*후보/g, '한의 차트에 넣을 내용')
            .replace(/차트\s*문장/g, '기록 문장')
            .replace(/차트\s*제안/g, '차트에 넣을 내용')
            .replace(/확인\s*필요/g, '확인해 주세요')
            .replace(/갱신\s*필요/g, '최신 진료 내용 확인')
            .replace(/레드\s*플래그/g, '진료 전 주의')
            .replace(/레드플래그/g, '진료 전 주의')
            .replace(/의심\s*후보/g, '의심 소견')
            .replace(/체크리스트/g, '진료 전 확인')
            .replace(/저장\s*전\s*확인\s*항목/g, '최종 저장 전 점검')
            .replace(/저장\s*전\s*확인/g, '최종 저장 전 점검')
            .replace(/보완\s*필요/g, '더 작성할 내용')
            .replace(/차단/g, '진료 전 확인')
            .replace(/게이트/g, '최종 점검');
    }

    public finalGateItemClass(item: WorkspaceMap) {
        const severity = item && item.severity === 'blocking' ? 'blocking' : 'confirmable';
        return `final-gate-item final-gate-item--${severity}`;
    }

    public async setFinalGateConfirmed(event: any) {
        const checked = event && event.target ? event.target.checked : event;
        this.finalGateConfirmed = checked === true;
        this.finalWarningsAcknowledged = checked === true;
        if (this.finalGateConfirmed && !this.finalSaveBlockingItems.length) {
            this.finalError = '';
            this.finalMessage = '확인했습니다. 지금 최종 저장할 수 있습니다.';
        } else if (!this.finalGateConfirmed) {
            this.finalMessage = '';
        }
        await this.service.render();
    }

	    public validationIssueText(item: any) {
	        if (!item) return '확인만 하면 되는 내용';
	        if (item.message) return this.clinicalUiText(item.message);
	        const code = this.validationIssueCode(item);
	        const labels: WorkspaceMap = {
	            unsaved_sentences: '미저장 한의 차트 변경',
	            draft_not_found: '한의 차트 초안 없음',
	            needs_regeneration: '최신 입력 반영 필요',
	            validation_failed: '한의 차트 확인',
	            soap_validation_required: '한의 차트 확인',
	            final_save_candidate_required: '진료 전 확인을 먼저 진행',
	            missing_required_sections: '비어 있는 영역 작성',
	            unreviewed_ai_sentences: '한의사 판단이 남은 기록',
	            stale_soap_candidates: '최신 진료 내용을 반영할 기록 내용',
	            ap_source_unverified: '변증/치료 근거 확인',
	            unresolved_red_flags: '진료 전 주의를 확인해 주세요',
	            blocking_checklist_items: '진료 전 확인',
	            required_checklist_unconfirmed: '진료 전 확인 미완료',
            precheck_unsubmitted: '현장 문진으로 진행',
	            nonblocking_red_flag_review: '안전 항목을 확인해 주세요',
	            standard_tool_unscored: '표준도구 점수 미확정',
	            reference_comparison_preview: '참조 비교 미리보기',
	            reference_comparison_attention: '건강인 참조 주의 구간',
	            standard_tool_not_started: '표준도구 미실시',
	            standard_tool_score_preview: '표준도구 미리보기',
	            empty_text: '빈 기록 문장',
	            invalid_section: '한의 차트 구획 확인',
                source_anatomic_mismatch: '차트 문장과 근거 부위 불일치',
                mixed_anatomic_scope: '서로 다른 신체 부위가 한 문장에 포함됨',
	        };
	        return labels[code] || '확인만 하면 되는 내용';
	    }

    public validationIssueCode(item: any) {
        if (!item) return '';
        if (typeof item === 'string') return item;
        return String(item.code || item.validator_code || item.rule || item.status || '');
    }

    public soapSentenceStatusLabel(item: SoapSentenceDraft) {
        if (item && item.dirty) return '수정 내용 저장 필요';
        const versionStatus = this.soapCandidateVersionStatus(item);
        if (versionStatus === 'previous_version') return '기존 문장';
        if (versionStatus === 'new_candidate_ready') return '진료 참고 내용';
        const labels: WorkspaceMap = {
            accepted: '차트 반영 완료',
            accepted_edited: '수정 후 반영 완료',
            doctor_added: '직접 작성',
            doctor_edited: '직접 수정',
            ai_unreviewed: '한의사 판단 전',
            reviewed: '확인됨',
            excluded: '사용 안 함',
        };
        const status = item && item.status ? item.status : '';
        return labels[status] || '한 번 더 확인';
    }

    public soapDebugSourceId(item: SoapSentenceDraft) {
        return item && item.source_ai_request_id ? String(item.source_ai_request_id) : '';
    }

    public precheckLabel() {
        if (!this.precheck) return '현장 문진';
        return this.precheck.submitted ? '예진 제출됨' : '현장 문진';
    }

    public precheckBadgeClass() {
        return hanscribePrecheckBadgeClass(!!(this.precheck && this.precheck.submitted), 'status-badge');
    }

    public checklistItemClass(item: WorkspaceMap) {
        const base = 'checklist-card';
        if (item && item.status === 'confirmed') return `${base} checklist-card--confirmed`;
        if (item && item.status === 'not_applicable') return `${base} checklist-card--not-applicable`;
        if (item && item.status === 'excluded') return `${base} checklist-card--not-applicable`;
        if (item && item.status === 'action_completed') return `${base} checklist-card--confirmed`;
        if (this.isRiskChecklistItem(item) && !this.isRiskResolved(item)) return `${base} checklist-card--red-flag`;
        if (item && item.status === 'deferred') return `${base} checklist-card--deferred`;
        if (item && item.safety_level) return `${base} checklist-card--safety`;
        return base;
    }

    public checklistMarkClass(item: WorkspaceMap) {
        const base = 'checklist-mark';
        if (item && item.status === 'confirmed') return `${base} checklist-mark--confirmed`;
        if (item && item.status === 'not_applicable') return `${base} checklist-mark--not-applicable`;
        if (item && item.status === 'excluded') return `${base} checklist-mark--not-applicable`;
        if (item && item.status === 'action_completed') return `${base} checklist-mark--confirmed`;
        if (item && item.status === 'deferred') return `${base} checklist-mark--deferred`;
        if (this.isRiskChecklistItem(item) && !this.isRiskResolved(item)) return `${base} checklist-mark--risk`;
        return base;
    }

    public checklistMarkText(item: WorkspaceMap) {
        if (item && item.status === 'confirmed') return '✓';
        if (item && item.status === 'not_applicable') return 'N';
        if (item && item.status === 'excluded') return 'X';
        if (item && item.status === 'action_completed') return '✓';
        if (this.isRiskChecklistItem(item) && !this.isRiskResolved(item)) return '!';
        if (item && item.status === 'deferred') return '!';
        return '·';
    }

    public isRiskChecklistItem(item: WorkspaceMap) {
        if (!item) return false;
        return !!(
            item.requires_resolution
            || item.flag_type
            || item.safety_level
            || item.risk_badge
            || item.category === 'safety'
        );
    }

    public isRiskResolved(item: WorkspaceMap) {
        if (!item) return false;
        const status = String(item.status || 'unchecked');
        return this.checklistStatusResolved(status) || item.resolved === true;
    }

    public riskBadgeLabel(item: WorkspaceMap) {
        if (!item) return '진료 전 확인';
        if (!this.isRiskResolved(item)) {
            const label = item.risk_badge || (item.flag_type === 'red_flag' ? '진료 전 주의' : '진료 전 확인');
            return this.clinicalUiText(label);
        }
        return '주의 확인 완료';
    }

    public riskBadgeClass(item: WorkspaceMap) {
        if (this.isRiskResolved(item)) return 'risk-chip risk-chip--success';
        const tone = item && item.risk_tone ? String(item.risk_tone) : (item && item.flag_type === 'red_flag' ? 'danger' : 'warning');
        return `risk-chip risk-chip--${tone === 'danger' ? 'danger' : 'warning'}`;
    }

    public riskResolutionBadgeClass(item: WorkspaceMap) {
        return this.isRiskResolved(item) ? 'status-chip status-chip--success' : 'status-chip status-chip--danger';
    }

    public riskResolutionLabel(item: WorkspaceMap) {
        return this.isRiskResolved(item) ? '확인 완료' : '확인해 주세요';
    }

    public riskItemDetails(item: WorkspaceMap) {
        return this.asArray(item && item.risk_details).map((value) => this.displayValue(value, '')).filter((value) => value !== '');
    }

    public riskItemSummary(item: WorkspaceMap) {
        const label = item && (item.label || item.code) ? this.clinicalUiText(item.label || item.code) : '진료 전 주의';
        const status = this.statusLabel(item && item.status ? item.status : 'unchecked');
        return `${label} · ${status}`;
    }

    public checklistDraft(item: WorkspaceMap): ChecklistDraft {
        const code = item && item.code ? item.code : '';
        if (!this.checklistDrafts[code]) {
            this.checklistDrafts[code] = {
                status: item && item.status ? item.status : 'unchecked',
                note: item && item.note ? item.note : '',
                dirty: false,
            };
        }
        return this.checklistDrafts[code];
    }

    public checklistStateButtonClass(item: WorkspaceMap, status: string) {
        const draft = this.checklistDraft(item);
        const base = 'segmented-action';
        if (draft.status === status && status === 'confirmed') return `${base} segmented-action--confirmed`;
        if (draft.status === status && status === 'not_applicable') return `${base} segmented-action--not-applicable`;
        if (draft.status === status && status === 'excluded') return `${base} segmented-action--not-applicable`;
        if (draft.status === status && status === 'deferred') return `${base} segmented-action--deferred`;
        if (draft.status === status && status === 'action_completed') return `${base} segmented-action--confirmed`;
        return base;
    }

    public checklistSoapTargets(item: WorkspaceMap) {
        if (item && (item.soap_candidate_disabled || item.soap_queue_state === 'excluded')) return [];
        const explicit = this.asArray(item && (item.soap_sections || item.soap_targets || item.soap_section))
            .map((value) => this.normalizedSoapSection(String(value || '')))
            .filter((section) => this.soapSectionOrder.indexOf(section) >= 0);
        if (explicit.length) return this.orderSoapSections(this.dedupeStrings(explicit));

        const draft = this.checklistDraft(item);
        const status = String((draft && draft.status) || (item && item.status) || 'unchecked');
        const categoryText = [
            item && item.category,
            item && item.action_type,
            item && item.safety_level,
            item && item.risk_badge,
            item && item.flag_type,
            item && item.label,
            item && item.prompt,
        ].filter((value) => value).join(' ').toLowerCase();
        const targets: string[] = [];

        if (categoryText.indexOf('history') >= 0 || categoryText.indexOf('medication') >= 0 || categoryText.indexOf('문진') >= 0 || categoryText.indexOf('증상') >= 0 || categoryText.indexOf('통증') >= 0) {
            targets.push('S');
        }
        if (item && (item.safety_level || item.flag_type || item.risk_badge || item.action_type === 'safety_review') || categoryText.indexOf('safety') >= 0 || categoryText.indexOf('neurologic') >= 0 || categoryText.indexOf('안전') >= 0 || categoryText.indexOf('경고') >= 0 || categoryText.indexOf('레드') >= 0) {
            targets.push('O');
        }
        if (categoryText.indexOf('assessment') >= 0 || categoryText.indexOf('평가') >= 0 || categoryText.indexOf('변증') >= 0) {
            targets.push('A');
        }
        if (status === 'deferred' || categoryText.indexOf('plan') >= 0 || categoryText.indexOf('follow') >= 0 || categoryText.indexOf('치료') >= 0 || categoryText.indexOf('계획') >= 0 || (item && (item.requires_resolution || item.block_final_save))) {
            targets.push('P');
        }
        if (!targets.length) targets.push('S');
        return this.orderSoapSections(this.dedupeStrings(targets));
    }

    public checklistSoapTargetLabel(item: WorkspaceMap) {
        const sections = this.checklistSoapTargets(item);
        if (!sections.length) return '차트에 넣을 내용 없음';
        return sections.map((section) => this.soapSectionChipLabel(section)).join(' / ');
    }

	    public checklistSoapTargetAssist(item: WorkspaceMap) {
	        const draft = this.checklistDraft(item);
        if (item && item.soap_queue_state === 'excluded') return '차트 반영 안 함';
        if (item && item.soap_queue_state === 'action_completed') return '조치 결과 반영';
        if (item && item.soap_queue_state === 'deferred') return '나중에 볼 사유 반영';
	        const hasDecision = draft && draft.status && draft.status !== 'unchecked';
	        const hasNote = !!(draft && draft.note && draft.note.trim());
        if (!hasDecision && !hasNote) return '상태/메모 필요';
	        if (draft && draft.dirty) return '확인 내용 저장 전';
	        return '섹션 선택 가능';
	    }

	    public checklistSoapCandidateButtonText(item: WorkspaceMap, section: string) {
	        const normalized = this.normalizedSoapSection(section);
	        const label = this.soapSectionDisplayName(normalized);
	        return this.checklistSoapCandidateQueued(item, normalized) ? `${label}에 보관됨` : `${label}에 보관`;
	    }

    public canSendChecklistItemToSoapCandidate(item: WorkspaceMap, section: string) {
        if (this.clinicalReadOnly || !this.activeChecklistSessionId || !item) return false;
        const normalized = this.normalizedSoapSection(section);
        if (this.checklistSoapCandidateQueued(item, normalized)) return false;
        return this.checklistSoapCandidateText(item, normalized).trim().length > 0;
    }

    public checklistSoapCandidateQueued(item: WorkspaceMap, section: string) {
        const normalized = this.normalizedSoapSection(section);
        const key = this.checklistSoapCandidateKey(item, normalized);
        if (this.checklistSoapCandidateQueue[key]) return true;
        const code = item && item.code ? String(item.code) : '';
        return this.soapItems(normalized).some((row) => {
            const meta = row && row.sentence_meta ? row.sentence_meta : {};
            if (meta.client_candidate_id && meta.client_candidate_id === key) return true;
            return meta.source === 'checklist' && code !== '' && meta.checklist_item_code === code;
        });
    }

    public async sendChecklistItemToSoapCandidate(item: WorkspaceMap, section: string) {
        if (!this.canSendChecklistItemToSoapCandidate(item, section)) return;
        const normalized = this.normalizedSoapSection(section);
        const text = this.checklistSoapCandidateText(item, normalized).trim();
        const label = item && (item.label || item.code) ? this.clinicalUiText(item.label || item.code) : '진료 전 확인 항목';
        const draft = this.checklistDraft(item);
        const statusLabel = this.statusLabel((draft && draft.status) || (item && item.status) || 'unchecked');
        const sourceLabels = this.dedupeStrings([
            '진료 전 확인',
            label,
            statusLabel,
            item && item.risk_badge ? String(item.risk_badge) : '',
        ]);
	        const candidate: AiAssistantResultItem = {
	            kind: 'soap_supplement',
	            title: `${this.soapSectionDisplayName(normalized)} 확인 내용 반영`,
	            description: `${label} · ${this.soapSectionDisplayName(normalized)} 기록`,
            source: '진료 전 확인',
            checklist_item_code: item && item.code ? String(item.code) : '',
            checklist_item_label: label,
            soap_section: normalized,
            soap_sentence_text: text,
            answer_target: `${this.soapSectionDisplayName(normalized)} 차트에 넣을 내용`,
            suggestion_reasons: sourceLabels,
            source_labels: sourceLabels,
            source_refs: item && item.code ? [{ type: 'checklist_item', id: item.code, source_id: item.code, label }] : [],
            decision_status: 'pending_doctor_review',
            applied_to_soap: false,
        };
        const queueKey = this.checklistSoapCandidateKey(item, normalized);
        const appended = await this.appendSoapCandidate({
            ...candidate,
            client_candidate_id: queueKey,
            source: 'checklist',
            generated_by: 'checklist',
            author_type: 'checklist',
            status: 'ai_unreviewed',
            candidate_status: 'pending_doctor_review',
            doctor_decision_status: 'pending_doctor_review',
            needs_update: this.soapNeedsRegeneration === true || !!(item && item.soap_impact && item.soap_impact.should_mark_needs_regeneration),
            source_payload: {
                checklist_item_code: item && item.code ? String(item.code) : '',
                checklist_item_label: label,
                status: (draft && draft.status) || (item && item.status) || 'unchecked',
                status_label: statusLabel,
                note: (draft && draft.note) || (item && item.note) || '',
                queue_state: item && item.soap_queue_state ? String(item.soap_queue_state) : '',
                soap_targets: this.checklistSoapTargets(item),
            },
        });
        if (!appended) {
            this.aiAssistantMessage = this.error || '내용을 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        candidate.applied_to_soap = true;
        candidate.decision_status = 'soap_candidate_sent';
	        const card = this.aiAssistantResult(
	            '한의 차트 보완',
	            `${this.soapSectionDisplayName(normalized)} 확인 내용`,
	            this.isRiskChecklistItem(item) ? 'danger' : 'warning',
	            `진료 전 확인 → ${this.soapSectionDisplayName(normalized)}`,
            [candidate],
            sourceLabels,
            ['한의사 판단 전'],
            ''
        );
        this.checklistSoapCandidateQueue[queueKey] = true;
	        this.prependAiAssistantResult(card);
        this.soapValidation = {};
	        this.soapMessage = `${this.soapSectionDisplayName(normalized)}에 참고 내용이 추가되었습니다.`;
	        this.checklistMessage = `${label} 차트 반영 전입니다.`;
        this.soapPanelMode = 'assistant';
        this.mobilePanel = 'soap';
        this.activeWorkspaceDrawer = 'assistant';
        await this.service.render();
    }

    public checklistTabClass(definition: WorkspaceMap) {
        const base = 'checklist-tab';
        if (definition && definition.code === this.selectedChecklistCode) return `${base} checklist-tab--active`;
        return base;
    }

    public patientFactClass(item: PatientClinicalItem) {
        const base = 'patient-fact';
        if (item && item.emphasis) return `${base} patient-fact--emphasis`;
        return base;
    }

    public async selectChecklistCode(code: string) {
        if (this.busy || this.clinicalReadOnly || !!this.activeChecklistSessionId) return;
        this.selectedChecklistCode = code || this.selectedChecklistCode;
        await this.onChecklistSelectionChange();
    }

    public async onChecklistSelectionChange() {
        this.error = '';
        this.checklistMessage = '';
        await this.service.render();
    }

    public async startChecklistSession() {
        if (this.clinicalReadOnly) return;
        this.startingChecklist = true;
        this.error = '';
        this.checklistMessage = '';
        await this.service.render();

        const result = await this.callApi('start_checklist_session', {
            encounter_id: this.encounterId,
            checklist_code: this.selectedChecklistCode,
        });
        this.startingChecklist = false;

        if (result.ok) {
            const context = result.data && result.data.context ? result.data.context : result.data;
            this.applyContext(context);
            this.checklistMessage = '진료 전 확인을 시작했습니다.';
        } else {
            this.error = result.message;
        }
        await this.service.render();
    }

    public async setChecklistStatus(item: WorkspaceMap, status: string) {
        if (this.clinicalReadOnly) return;
        const draft = this.checklistDraft(item);
        draft.status = status;
        draft.dirty = true;
        this.resetChecklistSoapCandidateQueue(item);
        this.error = '';
        this.checklistMessage = '';
        await this.service.render();
    }

    public async setChecklistNote(item: WorkspaceMap, note: string) {
        if (this.clinicalReadOnly) return;
        const draft = this.checklistDraft(item);
        draft.note = note || '';
        draft.dirty = true;
        this.resetChecklistSoapCandidateQueue(item);
        this.error = '';
        this.checklistMessage = '';
        await this.service.render();
    }

    public checklistItemSaving(item: WorkspaceMap) {
        return !!item && this.savingChecklistItemCode === item.code;
    }

    public async saveChecklistItem(item: WorkspaceMap) {
        if (this.clinicalReadOnly) return;
        if (!item || !item.code) return;
        const draft = this.checklistDraft(item);
        if (!draft.status || draft.status === 'unchecked') {
	            this.error = '상태를 선택해 주세요.';
            await this.service.render();
            return;
        }

        this.savingChecklistItemCode = item.code;
        this.error = '';
        this.checklistMessage = '';
        await this.service.render();

        const result = await this.callApi('save_checklist_response', {
            encounter_id: this.encounterId,
            session_id: this.activeChecklistSessionId,
            checklist_code: this.selectedChecklistCode,
            item_code: item.code,
            answer: JSON.stringify({
                status: draft.status,
                note: draft.note || '',
            }),
        });
        this.savingChecklistItemCode = '';

        if (result.ok) {
            const context = result.data && result.data.context ? result.data.context : result.data;
            this.applyContext(context, true);
            this.checklistMessage = `${item.label || item.code} 저장됨`;
        } else {
            this.error = result.message;
        }
        await this.service.render();
    }

    public async saveDirtyChecklistItems() {
        if (this.clinicalReadOnly) return;
        const pendingItems = this.checklistDirtyItems;
        if (!pendingItems.length) {
            this.checklistMessage = '변경 없음.';
            await this.service.render();
            return;
        }

        const payloads = pendingItems.map((item) => {
            const draft = this.checklistDraft(item);
            return {
                code: item && item.code ? String(item.code) : '',
                label: item && (item.label || item.code) ? this.clinicalUiText(item.label || item.code) : '진료 전 확인 항목',
                status: draft.status || 'unchecked',
                note: draft.note || '',
            };
        });
        const invalid = payloads.find((item) => !item.code || !item.status || item.status === 'unchecked');
        if (invalid) {
	            this.error = `${invalid.label} 상태를 선택해 주세요.`;
            await this.service.render();
            return;
        }

        this.savingChecklistAll = true;
        this.error = '';
        this.checklistMessage = '';
        let latestContext: WorkspaceMap | null = null;
        let failedMessage = '';
        let sessionId = this.activeChecklistSessionId;
        await this.service.render();

        for (const payload of payloads) {
            this.savingChecklistItemCode = payload.code;
            await this.service.render();
            const result = await this.callApi('save_checklist_response', {
                encounter_id: this.encounterId,
                session_id: sessionId,
                checklist_code: this.selectedChecklistCode,
                item_code: payload.code,
                answer: JSON.stringify({
                    status: payload.status,
                    note: payload.note,
                }),
            });
            if (result.ok) {
                latestContext = result.data && result.data.context ? result.data.context : result.data;
                const nextChecklist = latestContext && latestContext.checklist ? latestContext.checklist : {};
                sessionId = nextChecklist.session_id || (nextChecklist.session && nextChecklist.session.id) || sessionId;
            } else {
                failedMessage = result.message || `${payload.label} 저장에 실패했습니다.`;
                break;
            }
        }

        this.savingChecklistItemCode = '';
        this.savingChecklistAll = false;

        if (failedMessage) {
            this.error = failedMessage;
        } else if (latestContext) {
            this.applyContext(latestContext, true);
            this.checklistMessage = `진료 전 확인 변경 ${payloads.length}건이 저장되었습니다.`;
            this.showWorkspaceFeedback(`진료 전 확인 ${payloads.length}건을 저장했습니다.`, 'success');
        }
        await this.service.render();
    }

    public async generateSummaryCandidate() {
        if (this.clinicalReadOnly || !this.canGenerateClinicalAi) return;
        this.summaryGenerating = true;
        this.summaryCandidateDecision = '';
        this.summaryCandidateMessage = '';
        this.summaryCandidateError = '';
        this.error = '';
        await this.service.render();

        const result = await this.callApi('generate_summary_candidate', {
            encounter_id: this.encounterId,
        });
        this.summaryGenerating = false;

        if (result.ok) {
            const run = result.data && result.data.candidate ? result.data.candidate : result.data;
            this.summaryCandidateRun = run || null;
            if (run && run.status === 'candidate_ready' && run.candidate) {
                this.summaryCandidateDraft = this.toSummaryCandidateDraft(run.candidate);
	                this.summaryCandidateMessage = '요약 준비됨.';
            } else {
                this.summaryCandidateDraft = this.emptySummaryCandidateDraft();
	                this.summaryCandidateError = this.aiResultMessage(run, '요약 준비 실패');
            }
        } else {
            this.summaryCandidateError = result.message;
        }
        await this.service.render();
    }

    public async setSummaryCandidateOverview(text: string) {
        if (this.clinicalReadOnly) return;
        this.summaryCandidateDraft.overview = text || '';
        this.summaryCandidateDraft.dirty = true;
        this.summaryCandidateDecision = '';
        this.summaryCandidateMessage = '';
        await this.service.render();
    }

    public async setSummaryCandidatePointLabel(point: SummaryCandidatePoint, label: string) {
        if (this.clinicalReadOnly) return;
        point.label = label || '';
        point.dirty = true;
        this.summaryCandidateDraft.dirty = true;
        this.summaryCandidateDecision = '';
        this.summaryCandidateMessage = '';
        await this.service.render();
    }

    public async setSummaryCandidatePointText(point: SummaryCandidatePoint, text: string) {
        if (this.clinicalReadOnly) return;
        point.text = text || '';
        point.dirty = true;
        this.summaryCandidateDraft.dirty = true;
        this.summaryCandidateDecision = '';
        this.summaryCandidateMessage = '';
        await this.service.render();
    }

    public async acceptSummaryCandidate(edited: boolean = false) {
        if (this.clinicalReadOnly) return;
        if (!this.hasSummaryCandidate) return;
        this.summaryCandidateDecision = edited || this.summaryCandidateDraft.dirty ? 'accepted_edited' : 'accepted';
        this.summaryCandidateError = '';
	        this.summaryCandidateMessage = this.summaryCandidateDecision === 'accepted_edited'
	            ? '수정 요약 반영됨.'
	            : '요약 반영됨.';
        await this.service.render();
    }

    public async excludeSummaryCandidate() {
        if (this.clinicalReadOnly) return;
        if (!this.hasSummaryCandidate) return;
        this.summaryCandidateDecision = 'excluded';
        this.summaryCandidateError = '';
	        this.summaryCandidateMessage = '요약을 사용하지 않도록 표시했습니다.';
        await this.service.render();
    }

    public soapItems(section: string) {
        return this.soapDrafts[section] || [];
    }

    public soapEditorItems(section: string) {
        const normalized = this.normalizedSoapSection(section);
        return this.soapEditorItemsBySection[normalized] || [];
    }

    private soapCompactEditorItems(section: string) {
        const normalized = this.normalizedSoapSection(section);
        const items = this.soapEditorItems(normalized);
        if (normalized !== 'O') return items;
        return items.filter((item) => !this.isStructuredObjectiveFindingSummarySentence(item));
    }

    private isStructuredObjectiveFindingSummarySentence(item: SoapSentenceDraft) {
        if (!item) return false;
        const meta = item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        return (meta.source === 'structured_objective_finding' && meta.summary === true)
            || String(item.id || '').indexOf('local-O-objective-structured-') === 0;
    }

    public soapSectionCompactText(section: string) {
        return String(this.soapSectionCompactTextBySection[this.normalizedSoapSection(section)] || '');
    }

    public async setSoapSectionCompactText(section: string, text: string) {
        if (this.clinicalReadOnly) return;
        const normalized = this.normalizedSoapSection(section);
        const value = text || '';
        const visibleItems = this.soapCompactEditorItems(normalized);
        const items = this.soapItems(normalized);
        const lines = value
            .replace(/\r/g, '')
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line !== '');
        const desiredLines = lines.length ? lines : [''];
        desiredLines.forEach((line, index) => {
            let item = visibleItems[index];
            if (!item) {
                item = {
                    id: `local-${normalized}-${Date.now()}-${items.length}`,
                    soap_section: normalized,
                    text: '',
                    status: 'doctor_added',
                    author_type: 'doctor',
                    source_ai_request_id: '',
                    sentence_order: items.length + 1,
                    source_refs: [],
                    doctor_decision_reason: '',
                    updated: '',
                    dirty: true,
                    persisted: false,
                };
                items.push(item);
            }
            const changed = String(item.text || '') !== line;
            if (changed && item.persisted) this.markSoapSentenceAsDirectDoctorEdit(item);
            item.text = line;
            item.dirty = true;
            if (item.status !== 'excluded' && item.persisted) item.status = 'doctor_edited';
            if (item.status !== 'excluded' && !item.persisted) item.status = 'doctor_added';
        });
        visibleItems.slice(desiredLines.length).forEach((item) => {
            item.status = 'excluded';
            item.doctor_decision_status = 'excluded';
            item.doctor_decision_reason = 'compact_section_line_removed';
            item.dirty = true;
        });
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        this.refreshClinicalChartViewState();
        await this.service.render();
    }

    private markSoapSentenceAsDirectDoctorEdit(item: SoapSentenceDraft) {
        if (!item) return;
        const meta = item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        const previousSource = String(item.candidate_source || meta.source || item.author_type || '');
        const priorCandidateEvidence: WorkspaceMap = {};
        [
            'source_payload',
            'patient_cues',
            'clinical_evidence',
            'source_excerpt',
            'request_text',
            'chart_focus',
            'clinical_summary',
            'supporting_evidence',
            'connection_reasons',
            'source_labels',
            'suggestion_reasons',
        ].forEach((key) => {
            const value = meta[key];
            if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
                priorCandidateEvidence[key] = value;
            }
        });
        const nextMeta: WorkspaceMap = { ...meta };
        Object.keys(priorCandidateEvidence).forEach((key) => delete nextMeta[key]);
        item.author_type = 'doctor';
        item.candidate_source = 'doctor';
        item.source_ai_request_id = '';
        item.source_refs = [];
        item.candidate_status = 'doctor_edited';
        item.doctor_decision_status = 'modified';
        item.doctor_decision_reason = 'doctor_direct_section_edit';
        item.sentence_meta = {
            ...nextMeta,
            candidate: false,
            candidate_status: 'doctor_edited',
            doctor_decision_status: 'modified',
            source: 'doctor_direct_section_edit',
            source_payload: {},
            prior_candidate_source: previousSource,
            prior_candidate_source_ai_request_id: String(meta.source_ai_request_id || ''),
            prior_candidate_evidence: priorCandidateEvidence,
        };
    }

    private soapLabeledLineRange(text: string, label: string): WorkspaceMap | null {
        const source = String(text || '');
        const normalizedLabel = String(label || '').trim();
        if (!normalizedLabel) return null;
        const escapedLabel = normalizedLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = new RegExp(`(^|\\n)([\\t ]*${escapedLabel}[\\t ]*[:：][\\t ]*)([^\\n]*)`).exec(source);
        if (!match) return null;
        const lineStart = match.index + match[1].length;
        const valueStart = lineStart + match[2].length;
        const valueEnd = valueStart + match[3].length;
        return {
            lineStart,
            valueStart,
            valueEnd,
            value: match[3].trim(),
        };
    }

    private soapSectionLabeledLineRange(section: string, label: string) {
        return this.soapLabeledLineRange(this.soapSectionCompactText(section), label);
    }

    private scheduleSoapLabeledLineFocus(section: string, label: string, selectValue: boolean = false) {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        const normalized = this.normalizedSoapSection(section);
        window.requestAnimationFrame(() => {
            const textarea = document.querySelector(`[data-testid="soap-compact-textarea-${normalized}"]`) as HTMLTextAreaElement;
            if (!textarea) return;
            const range = this.soapLabeledLineRange(textarea.value, label);
            const start = range ? Number(range.valueStart) : textarea.value.length;
            const end = range && selectValue && range.value ? Number(range.valueEnd) : start;
            textarea.focus();
            textarea.setSelectionRange(start, end);
            if (range && Number(range.valueEnd) >= textarea.value.length - 1) {
                textarea.scrollTop = textarea.scrollHeight;
            }
        });
    }

    private soapItemVisibleInEditor(item: SoapSentenceDraft) {
        if (!item || item.status === 'excluded' || item.status === 'deferred') return false;
        if (!this.soapIsAiCandidate(item)) return true;
        return ['reviewed', 'doctor_added', 'doctor_edited'].indexOf(item.status) >= 0;
    }

    private refreshClinicalChartViewState() {
        const editorItems: Record<string, SoapSentenceDraft[]> = { S: [], O: [], A: [], P: [] };
        const compactText: WorkspaceMap = { S: '', O: '', A: '', P: '' };
        const countLabels: WorkspaceMap = {};
        const stateLabels: WorkspaceMap = {};
        const stateClasses: WorkspaceMap = {};
        const assistTexts: WorkspaceMap = {};

        this.soapSectionOrder.forEach((section) => {
            const normalized = this.normalizedSoapSection(section);
            const items = this.soapItems(normalized).filter((item) => this.soapItemVisibleInEditor(item));
            const compactItems = normalized === 'O'
                ? items.filter((item) => !this.isStructuredObjectiveFindingSummarySentence(item))
                : items;
            const displayTexts = compactItems
                .map((item) => this.cleanAppliedChartText(normalized, String(item && item.text ? item.text : '').trim()))
                .filter((text) => !!text);
            editorItems[normalized] = items;
            compactText[normalized] = this.dedupeChartDisplayTexts(displayTexts, normalized).join('\n');
            const activeCount = items.filter((item) => (item.text || '').trim() !== '').length;
            const dirtyCount = items.filter((item) => item.dirty).length;
            const unreviewedCount = this.soapItems(normalized).filter((item) => item.status !== 'excluded' && this.soapIsAiCandidate(item) && item.status !== 'reviewed').length;
            const pendingCount = this.pendingSoapSuggestions(normalized).length;
            countLabels[normalized] = activeCount > 0 ? `${activeCount}문장` : '입력 전';
            let stateLabel = '기록 중';
            if (dirtyCount > 0) stateLabel = '저장 필요';
            else if (unreviewedCount > 0) stateLabel = '한의사 판단 전';
            else if (pendingCount > 0) stateLabel = '진료 참고 내용';
            else if (activeCount <= 0) stateLabel = '시작 전';
            else if (this.soapReadyForApproval || this.readOnlyMode) stateLabel = '최종 저장 가능';
            else if (this.soapValidationIssueCount > 0) stateLabel = '차트 보완';
            stateLabels[normalized] = stateLabel;
            const tone = stateLabel === '시작 전' ? 'neutral'
                : stateLabel === '최종 저장 가능' ? 'success'
                    : stateLabel === '기록 중' ? 'primary'
                        : 'warning';
            stateClasses[normalized] = `status-chip status-chip--${tone}`;
            assistTexts[normalized] = pendingCount > 0 ? `참고 ${pendingCount}`
                : unreviewedCount > 0 ? `판단 ${unreviewedCount}`
                    : dirtyCount > 0 ? `저장 필요 ${dirtyCount}`
                        : '';
        });

        this.soapEditorItemsBySection = editorItems;
        this.soapSectionCompactTextBySection = compactText;
        this.soapSectionCountLabelBySection = countLabels;
        this.soapSectionStateLabelBySection = stateLabels;
        this.soapSectionStateClassBySection = stateClasses;
        this.soapSectionAssistTextBySection = assistTexts;
        const structuredMarker = this.soapItems('O').find((item) => this.isStructuredObjectiveFindingSummarySentence(item));
        const objectiveSourceText = structuredMarker
            ? (structuredMarker.status === 'excluded' ? '' : String(structuredMarker.text || ''))
            : (compactText.O || '');
        this.restoreObjectiveFindingDraftsFromChartText(objectiveSourceText);
        this.objectiveFindingPreviewTextValue = this.buildObjectiveFindingPreviewText(compactText.O || '');
        this.objectiveFindingPriorityItems = this.buildObjectiveFindingPriorityItems();
        this.objectiveFindingEmrGroupItems = this.buildObjectiveFindingEmrGroups();
        this.objectiveFindingAssistGroupItems = this.buildObjectiveFindingAssistGroups();
        if (this.hasSoapDirty) this.scheduleWorkspaceAutosave();
    }

    private dedupeChartDisplayTexts(texts: string[], section: string) {
        const normalizedSection = this.normalizedSoapSection(section);
        const rows: string[] = [];
        const keys: string[] = [];
        (texts || []).forEach((text) => {
            const value = String(text || '').trim();
            if (!value) return;
            const key = this.chartDisplayDedupeKey(value, normalizedSection);
            const duplicateIndex = key
                ? keys.indexOf(key)
                : rows.findIndex((row) => this.conversationTextSimilarity(row, value) >= 0.86);
            if (duplicateIndex >= 0) {
                if (this.chartDisplayTextPreferred(value, rows[duplicateIndex])) rows[duplicateIndex] = value;
                return;
            }
            rows.push(value);
            keys.push(key);
        });
        return rows;
    }

    private chartDisplayDedupeKey(text: string, section: string) {
        const normalizedSection = this.normalizedSoapSection(section);
        if (normalizedSection === 'O') {
            const objectiveKeys = this.objectiveFindingFieldMatchesFromText(text)
                .map((match) => `${match.itemCode}:${match.fieldCode}:${match.option}`)
                .sort();
            if (objectiveKeys.length) return `${normalizedSection}:objective:${objectiveKeys.join('|')}`;
        }
        const semantic = this.conversationSemanticText(text);
        return semantic ? `${normalizedSection}:text:${semantic}` : '';
    }

    private chartDisplayTextPreferred(next: string, current: string) {
        const nextLength = this.normalizeLookupLabel(next).length;
        const currentLength = this.normalizeLookupLabel(current).length;
        if (!currentLength) return true;
        return nextLength > 0 && nextLength < currentLength;
    }

    public soapSectionActiveCount(section: string) {
        return this.soapEditorItems(section).filter((item) => (item.text || '').trim() !== '').length;
    }

    public soapSectionCountLabel(section: string) {
        const normalized = this.normalizedSoapSection(section);
        return String(this.soapSectionCountLabelBySection[normalized] || (normalized === 'S' ? '문진 시작' : '입력 전'));
    }

    public soapSectionManualCount(section: string) {
        return this.soapEditorItems(section).filter((item) => item.author_type === 'doctor').length;
    }

    public soapSectionUnreviewedAiCount(section: string) {
        return this.soapItems(section).filter((item) => item.status !== 'excluded' && this.soapIsAiCandidate(item) && item.status !== 'reviewed').length;
    }

    public soapSectionDirtyCount(section: string) {
        return this.soapEditorItems(section).filter((item) => item.dirty).length;
    }

    public soapSectionReviewedCount(section: string) {
        return this.soapEditorItems(section).filter((item) => item.status === 'reviewed' || item.status === 'doctor_added' || item.status === 'doctor_edited').length;
    }

    public soapSectionPendingSuggestionCount(section: string) {
        return this.pendingSoapSuggestions(section).length;
    }

    public soapSectionStateLabel(section: string) {
        return String(this.soapSectionStateLabelBySection[this.normalizedSoapSection(section)] || '시작 전');
    }

    public soapSectionStateClass(section: string) {
        return String(this.soapSectionStateClassBySection[this.normalizedSoapSection(section)] || 'status-chip status-chip--neutral');
    }

    public soapSectionAssistText(section: string) {
        return String(this.soapSectionAssistTextBySection[this.normalizedSoapSection(section)] || '');
    }

    public soapSectionQuickLabel(section: string) {
        const labels: WorkspaceMap = {
            S: '문진',
            O: '진찰/소견',
            A: '변증/진단 판단',
            P: '치료/처방',
        };
        return String(labels[this.normalizedSoapSection(section)] || '차트 본문');
    }

    public soapSectionQuickRows(section: string) {
        const rows: WorkspaceMap = {
            S: 5,
            O: 4,
            A: 4,
            P: 4,
        };
        return rows[this.normalizedSoapSection(section)] || 4;
    }

    public soapSectionAssistPanelTitle(section: string) {
        const labels: WorkspaceMap = {
            S: '문진 항목',
            O: '진찰 소견 상세',
            A: '판단 근거',
            P: '치료 계획',
        };
        return String(labels[this.normalizedSoapSection(section)] || '보조 항목');
    }

    public soapSectionAssistPanelOpen(section: string) {
        const normalized = this.normalizedSoapSection(section);
        return this.soapSectionAssistPanelOpenState[normalized] !== false;
    }

    public async setSoapSectionAssistPanelOpen(section: string, event: any) {
        const normalized = this.normalizedSoapSection(section);
        const target = event && event.target ? event.target : null;
        this.soapSectionAssistPanelOpenState = {
            ...this.soapSectionAssistPanelOpenState,
            [normalized]: !!(target && target.open),
        };
        this.saveWorkspacePreferences();
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    public soapSectionAssistPanelSummary(section: string) {
        const normalized = this.normalizedSoapSection(section);
        if (normalized === 'S') {
            const rows = this.subjectiveInquiryRows();
            const complete = rows.filter((item) => item && item.state === 'complete').length;
            return complete > 0 ? `${complete}개 입력` : '';
        }
        if (normalized === 'O') {
            const entered = this.objectiveFindingItems.filter((item) => this.objectiveFindingFieldText(item) !== '').length;
            return entered ? `${entered}개 입력` : '';
        }
        if (normalized === 'A') {
            const rows = this.patternAssessmentRows();
            return rows.length ? this.patternAssessmentSummaryLabel(rows) : '';
        }
        if (normalized === 'P') {
            return this.treatmentPlanInputSummary();
        }
        return '접어서 보기';
    }

    public soapSectionPurposeText(section: string) {
        const purposes: WorkspaceMap = {
            S: '주호소와 증상 맥락, 전신 상태, 과거력·복약·알레르기를 요약합니다.',
            O: '설진·맥진·복진과 압통·가동범위 등 진찰 소견을 항목별로 입력합니다.',
            A: '의심 변증/진단, 지지 근거, 반대/불확실 근거, 추가 확인, 한의사 판단 메모를 나눠 봅니다.',
            P: '복약·알레르기 등 안전 확인을 먼저 보고 치료 방향과 생활지도를 정리합니다.',
        };
        return String(purposes[this.normalizedSoapSection(section)] || '');
    }

    public soapClinicalItems(section: string) {
        const normalized = this.normalizedSoapSection(section);
        return this.soapClinicalStructure[normalized] || [];
    }

    public soapClinicalPreviewItems(section: string) {
        return this.soapClinicalItems(section).slice(0, 4);
    }

    public soapClinicalItemDisplayValue(section: string, item: SoapClinicalStructureItem) {
        const state = this.soapClinicalItemActionState(section, item);
        if (state.state === 'editing') return '입력 중';
        return this.soapClinicalItemValue(section, item) || '직접 입력';
    }

    public soapClinicalItemClass(section: string, item: SoapClinicalStructureItem) {
        return `soap-clinical-item soap-clinical-item--${this.soapClinicalItemActionState(section, item).state}`;
    }

    public soapClinicalItemActionLabel(section: string, item: SoapClinicalStructureItem) {
        return this.soapClinicalItemActionState(section, item).status;
    }

    public subjectiveInquiryRows() {
        const groups = [
            { label: '증상', codes: ['chief_complaint', 'pattern'] },
            { label: '전신', codes: ['cold_heat', 'sleep', 'appetite', 'bowel_urine'] },
            { label: '안전', codes: ['past_history', 'medication', 'allergy'] },
        ];
        const rows: WorkspaceMap[] = [];
        groups.forEach((group) => {
            this.soapClinicalItems('S')
                .filter((item) => group.codes.indexOf(item.code) >= 0)
                .forEach((item) => rows.push(this.subjectiveInquiryRow(item, group.label)));
        });
        const redFlagValue = this.subjectiveRiskSignalValue();
        const redFlagState = this.subjectiveInquiryActionState('주의 신호', redFlagValue);
        rows.push({
            code: 'risk_signal',
            label: '주의 신호',
            hint: '야간통, 외상, 진행성 저림/근력저하, 대소변 이상',
            group: '안전',
            value: redFlagValue,
            status: redFlagState.status,
            state: redFlagState.state,
        });
        return rows;
    }

    public visibleSubjectiveInquiryRows() {
        const rows = this.subjectiveInquiryRows();
        if (this.showAllSubjectiveInquiry) return rows;
        const priority = rows.filter((item) => item.state !== 'complete');
        const complete = rows.filter((item) => item.state === 'complete');
        const active = rows.find((item) => item.code === this.activeSubjectiveInquiryCode);
        return (active ? [active].concat(priority.filter((item) => item.code !== active.code), complete.filter((item) => item.code !== active.code)) : priority.concat(complete)).slice(0, 6);
    }

    public async appendSubjectiveInquiryLine(row: WorkspaceMap) {
        if (this.busy || this.clinicalReadOnly || !row) return;
        this.activeSubjectiveInquiryCode = String(row.code || '');
        const label = String(row.label || '').trim();
        if (!label) return;
        const current = this.soapSectionCompactText('S').replace(/\s+$/, '');
        const existing = this.soapLabeledLineRange(current, label);
        if (existing) {
            this.showWorkspaceFeedback(`${label} 입력 위치로 이동했습니다.`, 'info');
            await this.service.render();
            this.scheduleSoapLabeledLineFocus('S', label, true);
            return;
        }
        const value = String(row.value || '').trim();
        const line = value ? `${label}: ${value}` : `${label}: `;
        await this.setSoapSectionCompactText('S', current ? `${current}\n${line}` : line);
        this.showWorkspaceFeedback(
            value ? `${label} 내용을 차트에 추가했습니다.` : `${label} 항목을 추가했습니다. 이어서 내용을 입력하세요.`,
            value ? 'success' : 'info',
        );
        await this.service.render();
        this.scheduleSoapLabeledLineFocus('S', label, false);
    }

    public async applySoapClinicalQuickInput(section: string, item: SoapClinicalStructureItem) {
        if (this.busy || this.clinicalReadOnly || !item) return;
        const normalized = this.normalizedSoapSection(section);
        const current = this.soapSectionCompactText(normalized).replace(/\s+$/, '');
        const existing = this.soapLabeledLineRange(current, item.label);
        if (existing) {
            this.showWorkspaceFeedback(`${item.label} 입력 위치로 이동했습니다.`, 'info');
            await this.service.render();
            this.scheduleSoapLabeledLineFocus(normalized, item.label, true);
            return;
        }
        const value = this.soapClinicalItemValue(normalized, item);
        const line = value ? `${item.label}: ${value}` : `${item.label}: `;
        await this.setSoapSectionCompactText(normalized, current ? `${current}\n${line}` : line);
        this.showWorkspaceFeedback(
            value ? `${item.label} 내용을 차트에 추가했습니다.` : `${item.label} 항목을 추가했습니다. 이어서 내용을 입력하세요.`,
            value ? 'success' : 'info',
        );
        await this.service.render();
        this.scheduleSoapLabeledLineFocus(normalized, item.label, false);
    }

    public patternAssessmentRows() {
        const rows: WorkspaceMap[] = [];
        const seen: Record<string, boolean> = {};
        const pushRow = (key: string, row: WorkspaceMap) => {
            const normalizedKey = this.safeEvidenceKey(key || row.id || row.title, `pattern-row-${rows.length}`);
            if (seen[normalizedKey]) return;
            seen[normalizedKey] = true;
            rows.push(row);
        };
        this.aiAssistantResults.filter((result) => this.isPatternCandidateResult(result)).forEach((result, resultIndex) => {
            this.asArray(result.items).forEach((item, itemIndex) => {
                if (!item) return;
                const status = String(item.decision_status || '');
                if (status === 'ignored' || status === 'excluded') return;
                const title = this.displayValue(item.display_name || item.title, '변증/진단 참고');
                pushRow(item.pattern_code || item.conversation_signal_key || title, {
                    id: this.safeEvidenceKey(`${result.id || resultIndex}-${item.pattern_code || title}-${itemIndex}`, `pattern-${resultIndex}-${itemIndex}`),
                    result,
                    item,
                    title,
                    statusLabel: this.patternCandidateStatusLabel(item),
                    statusClass: this.patternCandidateStatusClass(item),
                    confidence: this.isPatternCandidateResult(result)
                        ? this.patternCandidateConfidenceText(item.confidence_label)
                        : '진료 대화 단서',
                    supporting: this.patternCandidateSupportingRows(item),
                    opposing: this.patternCandidateOpposingRows(item),
                    followup: this.patternCandidateFollowupRows(item),
                });
            });
        });
        this.soapItems('A').slice().reverse().forEach((sentence, sentenceIndex) => {
            if (!sentence || sentence.status === 'excluded' || !this.soapSentenceHasAssessmentEvidence(sentence)) return;
            const payload = this.patternPayloadFromSoapSentence(sentence);
            const sentenceMeta = sentence.sentence_meta && typeof sentence.sentence_meta === 'object' ? sentence.sentence_meta : {};
            const payloadKind = String(payload.item_kind || payload.kind || '');
            const payloadSource = String(payload.input_type || payload.source || sentenceMeta.source || sentence.author_type || '');
            const isConversationEvidence = payloadKind.indexOf('conversation_') === 0
                || payloadSource === 'conversation_chat'
                || payloadSource.indexOf('대화') >= 0;
            const conversationTitle = this.conversationClinicalSummaryForSection(
                'A',
                this.cleanAppliedChartText('A', sentence.text || '')
            );
            const title = this.displayValue(
                (isConversationEvidence ? conversationTitle : '')
                    || payload.display_name
                    || payload.clinical_summary
                    || this.conversationClinicalSummaryForSection('A', sentence.text || ''),
                '변증/진단 참고'
            );
            let supporting = this.patternCandidateTextRows(payload.supporting_evidence, 3);
            if (!supporting.length && payload.evidence_summary) supporting = [this.clinicalRecordDisplayText(payload.evidence_summary)];
            if (!supporting.length && sentence.text) {
                supporting = [this.conversationClinicalEvidenceSummary('A', sentence.text)];
            }
            const opposing = this.patternCandidateTextRows(payload.opposing_evidence, 3);
            const followup = this.patternCandidateTextRows(payload.followup_questions, 3);
            const item: AiAssistantResultItem = {
                kind: payload.item_kind || 'pattern_candidate',
                title,
                display_name: title,
                description: this.clinicalRecordDisplayText(payload.evidence_summary || sentence.text) || '변증/진단 참고',
                source: '변증/진단 탭',
                pattern_code: payload.pattern_code || '',
                confidence_label: this.patternCandidateConfidenceText(payload.confidence_label || ''),
                confidence_score: Number(payload.confidence_score || 0),
                evidence_summary: this.clinicalRecordDisplayText(payload.evidence_summary || ''),
                supporting_evidence: supporting,
                opposing_evidence: opposing,
                followup_questions: followup,
                doctor_judgement_note: this.displayValue(payload.doctor_judgement_note, ''),
                source_labels: this.asArray(payload.source_labels).map((value) => this.clinicalRecordDisplayText(value)).filter((value) => value !== ''),
                source_refs: Array.isArray(sentence.source_refs) ? sentence.source_refs : [],
                answer_target: '변증/진단 탭',
                soap_section: 'A',
                soap_sentence_text: sentence.text || '',
                soap_candidates: [],
                decision_status: sentence.doctor_decision_status || sentence.candidate_status || sentence.status || 'pending_doctor_review',
                applied_to_soap: true,
            };
            pushRow(payload.pattern_code || title || sentence.id, {
                id: this.safeEvidenceKey(`sentence-${sentence.id || sentenceIndex}-${payload.pattern_code || title}`, `pattern-sentence-${sentenceIndex}`),
                result: null,
                item,
                sentence,
                title,
                statusLabel: this.soapSentenceStatusLabel(sentence),
                statusClass: this.panelStatusClass(sentence.doctor_decision_status || sentence.candidate_status || sentence.status || 'pending_doctor_review'),
                confidence: this.soapSentenceIsPatternCandidate(sentence)
                    ? this.patternCandidateConfidenceText(item.confidence_label)
                    : '진료 대화 단서',
                supporting: this.patternCandidateSupportingRows(item),
                opposing: this.patternCandidateOpposingRows(item),
                followup: this.patternCandidateFollowupRows(item),
            });
        });
        return rows.slice(0, 5);
    }

    public patternAssessmentSummaryLabel(rows?: WorkspaceMap[]) {
        const list = Array.isArray(rows) ? rows : this.patternAssessmentRows();
        if (!list.length) return '새로 참고할 내용 없음';
        const resolvedStatuses = ['deferred', 'excluded', 'reviewed', 'accepted', 'accepted_edited', 'soap_candidate_sent', 'doctor_added', 'doctor_edited'];
        const pending = list.filter((row) => {
            const status = String(row && row.item ? row.item.decision_status || '' : '');
            return resolvedStatuses.indexOf(status) < 0;
        }).length;
        if (pending) return `${pending}건 한의사 판단 전`;
        return `${list.length}건 판단 흐름 정리`;
    }

    public soapManualAddButtonLabel(section: string) {
        const normalized = this.normalizedSoapSection(section);
        if (normalized === 'A') return '판단 추가';
        if (normalized === 'P') return '계획 추가';
        return '기록 추가';
    }

	    public treatmentPlanInputSummary() {
	        const recorded = this.treatmentPlanItems.filter((item) => this.treatmentPlanFieldText(item) !== '').length;
	        return recorded ? `${recorded}개 입력` : '';
	    }

    public treatmentPlanDraft(item: TreatmentPlanInput): TreatmentPlanDraft {
        const code = item && item.code ? item.code : '';
        if (!code) return { chips: [], note: '' };
        if (!this.treatmentPlanDrafts[code]) this.treatmentPlanDrafts[code] = { chips: [], note: '' };
        return this.treatmentPlanDrafts[code];
    }

    public treatmentPlanDraftNote(item: TreatmentPlanInput) {
        return this.treatmentPlanDraft(item).note;
    }

	    public treatmentPlanDraftPreview(item: TreatmentPlanInput) {
	        return this.treatmentPlanFieldText(item) || '작성 전';
	    }

    public treatmentPlanCardClass(item: TreatmentPlanInput) {
        const state = this.treatmentPlanFieldText(item) ? 'complete' : 'empty';
        return `treatment-plan-card treatment-plan-card--${state}`;
    }

    public treatmentPlanChipClass(item: TreatmentPlanInput, chip: string) {
        const selected = this.treatmentPlanDraft(item).chips.indexOf(chip) >= 0;
        return selected ? 'treatment-plan-chip is-selected' : 'treatment-plan-chip';
    }

    public async toggleTreatmentPlanChip(item: TreatmentPlanInput, chip: string) {
        if (this.busy || this.clinicalReadOnly || !item) return;
        const draft = this.treatmentPlanDraft(item);
        const index = draft.chips.indexOf(chip);
        if (index >= 0) draft.chips.splice(index, 1);
        else draft.chips.push(chip);
        this.error = '';
        this.soapMessage = '';
        this.refreshClinicalChartViewState();
        await this.service.render();
    }

    public async setTreatmentPlanNote(item: TreatmentPlanInput, note: string) {
        if (this.clinicalReadOnly || !item) return;
        this.treatmentPlanDraft(item).note = note || '';
        this.error = '';
        this.soapMessage = '';
        this.refreshClinicalChartViewState();
        await this.service.render();
    }

    public treatmentPlanFieldText(item: TreatmentPlanInput) {
        const draft = this.treatmentPlanDraft(item);
        return this.dedupeStrings((draft.chips || []).concat([draft.note || ''])
            .map((value) => this.displayValue(value, ''))
            .filter((value) => value !== ''))
            .join(' / ');
    }

	    public treatmentPlanSentencePreview(item: TreatmentPlanInput) {
        return this.treatmentPlanSentenceText(item, 'candidate') || '입력 전';
	    }

    public treatmentPlanHasItemText(item: TreatmentPlanInput) {
        return this.treatmentPlanFieldText(item) !== '';
    }

	    public treatmentPlanHasAnyText() {
	        return this.treatmentPlanEnteredItems().length > 0;
	    }

    public treatmentPlanReferenceActionLabel() {
        return this.treatmentPlanSummaryCandidateQueued() ? '참고 내용 갱신' : '참고 내용으로 보관';
    }

    public treatmentPlanDirectActionLabel() {
        return this.treatmentPlanSummaryApplied() ? '계획 갱신' : '계획에 반영';
    }

	    public treatmentSafetyRows() {
	        const medication = this.treatmentSafetyDisplayValue(this.summaryLookup(['medication', 'medicine'], ['복약', '복용약'], ''));
	        const allergy = this.treatmentSafetyDisplayValue(this.summaryLookup(['allergy'], ['알레르기'], ''));
	        const medicationAllergyComplete = this.hasClinicalValue(medication) && this.hasClinicalValue(allergy);
	        const redFlag = this.hasUnresolvedRedFlags
	            ? `${this.unresolvedRedFlagItems.length}건 확인`
	            : (this.redFlagItems.length ? `${this.redFlagItems.length}건 확인 완료` : '확인 기록 없음');
	        const safetyChecklistLabels = this.treatmentSafetyChecklistLabels();
	        const finalGateLabels = this.treatmentFinalGateLabels();
	        return [
	            {
	                label: '복약/알레르기',
	                value: `복약 ${medication} · 알레르기 ${allergy}`,
	                detail: medicationAllergyComplete ? '확인 완료' : '확인해 주세요',
	                state: medicationAllergyComplete ? 'complete' : 'empty',
	                target: 'patient',
	            },
	            {
	                label: '진료 전 주의',
	                value: redFlag,
	                detail: this.hasUnresolvedRedFlags
	                    ? this.unresolvedRedFlagItems.slice(0, 2).map((item) => this.riskItemSummary(item)).join(', ')
	                    : (this.redFlagItems.length ? '확인 완료' : '확인해 주세요'),
	                state: this.hasUnresolvedRedFlags ? 'warning' : (this.redFlagItems.length ? 'complete' : 'empty'),
	                target: 'checklist',
	            },
	            {
	                label: '금기/주의',
	                value: safetyChecklistLabels.length ? safetyChecklistLabels.join(' · ') : '확인 기록 없음',
	                detail: safetyChecklistLabels.length ? `${safetyChecklistLabels.length}건 확인` : '확인해 주세요',
	                state: safetyChecklistLabels.length ? 'warning' : 'empty',
	                target: 'checklist',
	            },
	            {
	                label: '안전 확인',
	                value: finalGateLabels.length ? `${finalGateLabels.length}건 보완 필요` : '저장 가능',
	                detail: finalGateLabels.length ? '점검 내용에서 확인' : '저장 가능',
	                state: this.finalGateBlockingItems.length ? 'warning' : (finalGateLabels.length ? 'empty' : 'complete'),
	                target: 'final',
	            },
	        ];
	    }

	    public treatmentSafetyAttentionRows() {
	        return this.treatmentSafetyRows().filter((row) => row && row.state !== 'complete').slice(0, 3);
	    }

	    public treatmentSafetySummaryLabel(rows: WorkspaceMap[]) {
	        const pending = (rows || []).filter((row) => row && row.state !== 'complete').length;
	        return pending ? `${pending}건 확인` : '확인 완료';
	    }

	    public async openTreatmentSafetyRow(row: WorkspaceMap) {
	        if (!row) return;
	        if (row.target === 'patient') {
	            await this.openPatientSummaryModal();
	            return;
	        }
	        if (row.target === 'final') {
	            await this.openFinalSaveModal();
	            return;
	        }
	        await this.openWorkspaceDrawer('checklist');
	    }

	    public treatmentPlanChartPreviewText() {
        return this.treatmentPlanSummarySentenceText('final') || '입력 전';
	    }

    public async sendTreatmentPlanAsCandidate(item: TreatmentPlanInput) {
        if (this.busy || this.clinicalReadOnly || !item || !this.treatmentPlanHasItemText(item)) return;
        const text = this.treatmentPlanSentenceText(item, 'candidate');
        const appended = await this.appendSoapCandidate({
            client_candidate_id: `treatment-plan::${item.code || 'plan'}::${Date.now()}`,
            soap_section: 'P',
            text,
            source: 'structured_treatment_plan',
            generated_by: 'doctor_input',
            author_type: 'doctor_candidate',
            status: 'ai_unreviewed',
            candidate_status: 'pending_doctor_review',
            doctor_decision_status: 'pending_doctor_review',
            doctor_decision_reason: 'doctor_treatment_plan_review_candidate',
            source_payload: this.treatmentPlanSourcePayload(item, 'candidate'),
        });
	        if (!appended) {
	            this.soapMessage = this.error || '내용을 추가하지 못했습니다.';
	            await this.service.render();
	            return;
	        }
	        this.soapValidation = {};
	        this.soapMessage = `${item.label} 참고 내용이 추가되었습니다.`;
	        await this.service.render();
	    }

    public async sendTreatmentPlanSummaryAsCandidate() {
        if (this.busy || this.clinicalReadOnly || !this.treatmentPlanHasAnyText()) return;
        const updating = this.treatmentPlanSummaryCandidateQueued();
        const text = this.treatmentPlanSummarySentenceText('candidate');
        const appended = await this.appendSoapCandidate({
            client_candidate_id: 'treatment-plan-summary',
            soap_section: 'P',
            text,
            source: 'structured_treatment_plan',
            generated_by: 'doctor_input',
            author_type: 'doctor_candidate',
            status: 'ai_unreviewed',
            candidate_status: 'pending_doctor_review',
            doctor_decision_status: 'pending_doctor_review',
            doctor_decision_reason: 'doctor_treatment_plan_review_candidate',
            source_payload: this.treatmentPlanSummarySourcePayload('candidate'),
        });
	        if (!appended) {
	            this.soapMessage = this.error || '내용을 추가하지 못했습니다.';
	            await this.service.render();
	            return;
	        }
	        this.soapValidation = {};
	        this.soapMessage = updating ? '치료/처방 참고 내용이 갱신되었습니다.' : '치료/처방 참고 내용이 추가되었습니다.';
            this.showWorkspaceFeedback(this.soapMessage, 'success');
	        await this.service.render();
	    }

    public async addTreatmentPlanDirectSentence(item: TreatmentPlanInput) {
        if (this.busy || this.clinicalReadOnly || !item || !this.treatmentPlanHasItemText(item)) return;
        const text = this.treatmentPlanSentenceText(item, 'final');
        const items = this.soapItems('P');
        items.push({
            id: `local-P-treatment-${item.code || 'plan'}-${Date.now()}-${items.length}`,
            soap_section: 'P',
            text,
            status: 'doctor_added',
            author_type: 'doctor',
            source_ai_request_id: '',
            sentence_order: items.length + 1,
            source_refs: [],
	            doctor_decision_reason: 'doctor_treatment_plan_direction',
	            doctor_decision_status: 'accepted',
	            candidate_status: 'doctor_reviewed',
	            candidate_source: 'structured_treatment_plan',
	            sentence_meta: this.treatmentPlanSourcePayload(item, 'final'),
	            updated: '',
	            dirty: true,
            persisted: false,
        });
        this.error = '';
	        this.soapMessage = `${item.label} 반영됨.`;
	        this.soapValidation = {};
	        this.refreshClinicalChartViewState();
	        await this.service.render();
	    }

	    public async addTreatmentPlanSummaryDirectSentence() {
	        if (this.busy || this.clinicalReadOnly || !this.treatmentPlanHasAnyText()) return;
	        const text = this.treatmentPlanSummarySentenceText('final');
	        const items = this.soapItems('P');
	        const existing = items.find((item) => this.isTreatmentPlanSummarySentence(item));
	        if (existing) {
	            existing.text = text;
	            existing.status = existing.persisted ? 'doctor_edited' : 'doctor_added';
	            existing.author_type = 'doctor';
	            existing.doctor_decision_reason = 'doctor_treatment_plan_direction';
	            existing.doctor_decision_status = 'accepted';
	            existing.candidate_status = 'doctor_reviewed';
	            existing.candidate_source = 'structured_treatment_plan';
	            existing.sentence_meta = this.treatmentPlanSummarySourcePayload('final');
	            existing.dirty = true;
	        } else {
	            items.push({
	                id: `local-P-treatment-summary-${Date.now()}-${items.length}`,
	                soap_section: 'P',
	                text,
	                status: 'doctor_added',
	                author_type: 'doctor',
	                source_ai_request_id: '',
	                sentence_order: items.length + 1,
	                source_refs: [],
	                doctor_decision_reason: 'doctor_treatment_plan_direction',
	                doctor_decision_status: 'accepted',
	                candidate_status: 'doctor_reviewed',
	                candidate_source: 'structured_treatment_plan',
	                sentence_meta: this.treatmentPlanSummarySourcePayload('final'),
	                updated: '',
	                dirty: true,
	                persisted: false,
	            });
	        }
	        this.error = '';
	        this.soapMessage = existing ? '본문 갱신됨.' : '본문 반영됨.';
            this.showWorkspaceFeedback(
                existing ? '치료 계획을 갱신했습니다.' : '치료 계획을 차트에 반영했습니다.',
                'success',
            );
	        this.soapValidation = {};
	        this.refreshClinicalChartViewState();
	        await this.service.render();
	    }

	    private treatmentPlanSentenceText(item: TreatmentPlanInput, mode: 'candidate' | 'final') {
	        if (!item) return '';
	        const value = this.treatmentPlanFieldText(item);
	        if (!value) return '';
	        const label = this.treatmentPlanSentenceLabel(item);
	        if (mode === 'final') {
	            return this.joinSentenceParts([
	                this.treatmentPlanSafetySentenceText(),
	                `${label}: ${value}`,
	            ]);
	        }
	        return this.joinSentenceParts([
	            `${label}: ${value}`,
	            '복약·알레르기·진료 전 주의 확인 후 한의사 검토',
	        ]);
	    }

    private treatmentPlanEnteredItems() {
        return this.treatmentPlanItems.filter((item) => this.treatmentPlanHasItemText(item));
    }

    private treatmentPlanSummarySentenceText(mode: 'candidate' | 'final') {
        const items = this.treatmentPlanEnteredItems();
        if (!items.length) return '';
	        const rows = items.map((item) => {
	            const value = this.treatmentPlanFieldText(item);
	            const label = this.treatmentPlanSentenceLabel(item);
	            return `${label}: ${value}`;
	        });
	        const tail = mode === 'candidate'
	            ? '복약·알레르기·진료 전 주의 확인 후 차트에 반영'
	            : '치료 방향은 실행 전 안전 확인과 반응에 따라 조정 계획';
	        const safety = mode === 'final' ? [this.treatmentPlanSafetySentenceText()] : [];
	        return this.joinSentenceParts(safety.concat(rows).concat([tail]));
	    }

	    private treatmentPlanSourcePayload(item: TreatmentPlanInput, mode: 'candidate' | 'final') {
	        return {
	            source: 'structured_treatment_plan',
	            treatment_plan_block: item && item.code ? item.code : '',
	            treatment_plan_label: item && item.label ? item.label : '',
	            treatment_plan_text: this.treatmentPlanFieldText(item),
	            safety_rows: this.treatmentSafetyRows(),
	            review_candidate: mode === 'candidate',
	            final_decision_flow: mode === 'final' ? 'doctor_chart_body' : 'doctor_review_required',
	            auto_prescription: false,
	        };
	    }

	    private treatmentPlanSummarySourcePayload(mode: 'candidate' | 'final') {
	        return {
	            source: 'structured_treatment_plan',
	            summary: true,
	            treatment_plan_blocks: this.treatmentPlanEnteredItems().map((item) => ({
	                code: item.code,
	                label: item.label,
	                text: this.treatmentPlanFieldText(item),
	            })),
            safety_rows: this.treatmentSafetyRows(),
            review_candidate: mode === 'candidate',
            final_decision_flow: mode === 'final' ? 'doctor_chart_body' : 'doctor_review_required',
            auto_prescription: false,
	            auto_final_write: false,
	        };
	    }

	    private treatmentPlanSentenceLabel(item: TreatmentPlanInput) {
	        const code = item && item.code ? item.code : '';
	        const labels: WorkspaceMap = {
	            acupuncture: '침구 계획',
	            herbal: '한약/제제 계획',
	            lifestyle: '생활지도 계획',
	            followup: '추적 관찰 계획',
	        };
	        return labels[code] || (item && item.label ? item.label : '치료 방향 계획');
	    }

    private treatmentSafetyDisplayValue(value: any) {
        const text = this.patientSummaryText(value, 80);
        return text && text !== '-' ? text : '입력 전';
    }

	    private treatmentSafetyChecklistLabels() {
	        return this.checklistItems
	            .filter((item) => item && this.isRiskChecklistItem(item) && !this.isRiskResolved(item))
	            .map((item) => item.label || item.code || '주의 항목')
	            .slice(0, 3);
	    }

	    private treatmentFinalGateLabels() {
	        return this.finalGateBlockingItems.concat(this.finalGateConfirmableItems)
	            .map((item) => this.gateItemTitle(item))
	            .filter((value) => value !== '')
	            .slice(0, 3);
	    }

	    private treatmentPlanSafetySentenceText() {
	        const rows = this.treatmentSafetyRows()
	            .map((row) => `${row.label}: ${this.truncateText(this.clinicalRecordDisplayText(row.value), 72)}`)
	            .filter((value) => value !== '');
	        return rows.length ? `실행 전 확인: ${rows.join(' / ')}` : '';
	    }

	    private isTreatmentPlanSummarySentence(item: SoapSentenceDraft) {
	        if (!item || item.status === 'excluded') return false;
	        const meta = item.sentence_meta || {};
	        if (meta.source === 'structured_treatment_plan' && meta.summary === true) return true;
	        return String(item.id || '').indexOf('local-P-treatment-summary-') === 0;
	    }

    private treatmentPlanSummaryCandidateQueued() {
        return this.soapItems('P').some((item) => {
            if (!item || item.status === 'excluded' || item.status === 'reviewed') return false;
            const meta = item.sentence_meta || {};
            const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
            const status = String(item.doctor_decision_status || item.candidate_status || item.status || '');
            return meta.source === 'structured_treatment_plan'
                && payload.summary === true
                && ['deferred', 'excluded', 'accepted', 'accepted_edited', 'reviewed'].indexOf(status) < 0;
        });
    }

    private treatmentPlanSummaryApplied() {
        return this.soapItems('P').some((item) => this.isTreatmentPlanSummarySentence(item));
    }

    public objectiveFindingPriorityPoints() {
        return this.objectiveFindingPriorityItems;
    }

    public objectiveFindingPrioritySummary() {
        const count = this.objectiveFindingPriorityItems.length;
        return count ? `${count}개 먼저 확인` : '기본 확인';
    }

    public objectiveFindingPriorityPointClass(point: ObjectiveFindingPriorityPoint) {
        const tone = point && point.tone ? point.tone : 'neutral';
        return `objective-priority-card objective-priority-card--${tone}`;
    }

    public async focusObjectiveFindingPriorityPoint(point: ObjectiveFindingPriorityPoint) {
        if (this.busy || this.clinicalReadOnly || !point) return;
        const relatedCodes = this.asArray(point.relatedCodes).map((code) => String(code || '')).filter((code) => code !== '');
        const openState: WorkspaceMap = { ...this.objectiveFindingOpenGroupState };
        relatedCodes.forEach((code) => {
            const groupCode = this.objectiveFindingGroupCodeForItemCode(code);
            if (groupCode) openState[groupCode] = true;
        });
        this.objectiveFindingOpenGroupState = openState;
        this.showWorkspaceFeedback(`${point.label} 입력 위치로 이동했습니다.`, 'info');
        await this.service.render();
        const targetCode = relatedCodes[0] || '';
        if (!targetCode) return;
        try {
            setTimeout(() => {
                const field = document.querySelector(`[data-testid^="objective-finding-field-${targetCode}-"]`) as HTMLElement;
                if (!field) return;
                field.scrollIntoView({ block: 'center', behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
                field.focus();
            }, 0);
        } catch (e) {
            // ignore focus failures outside the browser
        }
    }

    public objectiveFindingDetailSummary() {
        const entered = this.objectiveFindingItems.filter((item) => this.objectiveFindingFieldText(item) !== '').length;
        const total = this.objectiveFindingItems.length;
        return entered ? `${entered}/${total} 기록` : `${total}개 항목`;
    }

    public objectiveFindingAssistSummary() {
        const count = this.objectiveFindingAssistGroupItems
            .reduce((total, group) => total + (group && group.items ? group.items.length : 0), 0);
        return count ? `${count}개` : '없음';
    }

    public objectiveFindingGroupOpen(group: WorkspaceMap) {
        if (!group) return false;
        const groupCode = String(group.code || '');
        if (groupCode && Object.prototype.hasOwnProperty.call(this.objectiveFindingOpenGroupState, groupCode)) {
            return !!this.objectiveFindingOpenGroupState[groupCode];
        }
        const items = this.asArray(group.items) as ObjectiveFindingInput[];
        if (items.some((item) => this.objectiveFindingFieldText(item) !== '')) return true;
        return false;
    }

    public setObjectiveFindingGroupOpen(group: WorkspaceMap, event: any) {
        const groupCode = String(group && group.code ? group.code : '');
        if (!groupCode) return;
        const target = event && event.target ? event.target : null;
        this.objectiveFindingOpenGroupState = {
            ...this.objectiveFindingOpenGroupState,
            [groupCode]: !!(target && target.open),
        };
    }

    public objectiveFindingDraftPreview(item: ObjectiveFindingInput) {
        return this.objectiveFindingFieldText(item) || '입력 전';
    }

    public objectiveFindingDraftNote(item: ObjectiveFindingInput) {
        return this.objectiveFindingDraft(item.code).note;
    }

    public objectiveFindingGroupSummary(group: WorkspaceMap) {
        return String(group && group.summary ? group.summary : '');
    }

    public objectiveFindingEmrGroups() {
        return this.objectiveFindingEmrGroupItems;
    }

    private buildObjectiveFindingPriorityItems(): ObjectiveFindingPriorityPoint[] {
        const rows: ObjectiveFindingPriorityPoint[] = [];
        const risks = this.unresolvedRedFlagItems.slice(0, 2)
            .map((item) => this.riskItemSummary(item) || item.label || item.code || '')
            .filter((value) => value !== '');
        if (risks.length) {
            rows.push({
                code: 'risk',
                label: '주의 소견 배제',
                detail: this.truncateText(risks.join(' / '), 82),
                meta: '진료 전 주의',
                tone: 'danger',
                relatedCodes: ['tender_rom', 'other'],
                value: `주의 소견: ${risks.join(' / ')}`,
            });
        }

        const followups = this.currentFollowupQuestionTitles(2);
        if (followups.length) {
            const relatedCodes = this.objectiveFindingCodesForText(followups.join(' '));
            rows.push({
                code: 'followup',
                label: '추가 확인',
                detail: this.truncateText(followups.join(' / '), 82),
                meta: 'AI 참고',
                tone: 'warning',
                relatedCodes: relatedCodes.length ? relatedCodes : ['other'],
                value: `추가 확인: ${followups.join(' / ')}`,
            });
        }

        this.objectiveFindingClinicalFocusPoints().forEach((point) => rows.push(point));
        return this.dedupeObjectiveFindingPriorityPoints(rows).slice(0, 3);
    }

    private buildObjectiveFindingEmrGroups() {
        const koreanItems = this.objectiveFindingItems.filter((item) => ['tongue', 'pulse', 'abdomen'].indexOf(item.code) >= 0);
        const physicalItems = this.objectiveFindingItems.filter((item) => ['tender_rom', 'other'].indexOf(item.code) >= 0);
        return [
            {
                code: 'korean',
                label: '한의학적 진찰',
                summary: this.objectiveFindingEmrGroupSummary(koreanItems),
                items: koreanItems,
            },
            {
                code: 'physical',
                label: '이학적 검사',
                summary: this.objectiveFindingEmrGroupSummary(physicalItems),
                items: physicalItems,
            },
        ];
    }

    private objectiveFindingEmrGroupSummary(items: ObjectiveFindingInput[]) {
        const entered = (items || []).filter((item) => this.objectiveFindingFieldText(item) !== '').length;
        return entered ? `${entered}개 기록` : '';
    }

    private objectiveFindingClinicalFocusPoints(): ObjectiveFindingPriorityPoint[] {
        const text = [
            this.patientChiefComplaint,
            this.patientPrecheckCoreText,
            this.soapSectionCompactText('S'),
            this.soapSectionCompactText('A'),
            this.currentPatternCandidateNames(2).join(' '),
            this.checklistItems.map((item) => `${item.label || ''} ${item.memo || ''} ${item.note || ''}`).join(' '),
        ].join(' ');
        const rows: ObjectiveFindingPriorityPoint[] = [];
        const add = (code: string, label: string, detail: string, relatedCodes: string[], tone: string = 'primary') => {
            rows.push({
                code,
                label,
                detail,
                meta: '환자 기준',
                tone,
                relatedCodes,
                value: `${label}: ${detail}`,
            });
        };

        if (this.textContainsClinicalSignal(text, ['저림', '감각저하', '근력저하', '마비', '반사', '대소변', '방사통', '하지'])) {
            add('neuro', '신경학적 동반 소견', '감각·근력·방사통 확인', ['tender_rom', 'other'], 'danger');
        }
        if (this.textContainsClinicalSignal(text, ['요통', '경항통', '견통', '어깨', '무릎', '관절', '염좌', '디스크', '좌골', '통증', '압통', 'ROM', '가동범위'])) {
            add('msk', '압통/가동범위', '압통점·ROM 제한·동작 악화 확인', ['tender_rom'], 'primary');
        }
        if (this.textContainsClinicalSignal(text, ['소화', '더부룩', '복통', '복부', '명치', '속쓰림', '설사', '변비', '식후', '대변'])) {
            add('abdomen', '복진', '복부 긴장·압통·냉감 확인', ['abdomen'], 'primary');
        }
        if (this.textContainsClinicalSignal(text, ['한열', '오한', '열감', '냉증', '상열', '피로', '불면', '식욕', '담습', '어혈', '기허', '혈허'])) {
            add('tongue_pulse', '설진·맥진', '한열·허실·습담 단서 확인', ['tongue', 'pulse'], 'neutral');
        }
        if (!rows.length) {
            add('baseline', '설진·맥진', '설질·설태와 맥상 기본 확인', ['tongue', 'pulse'], 'neutral');
        }
        return rows;
    }

    private objectiveFindingCodesForText(text: string) {
        const codes: string[] = [];
        if (this.textContainsClinicalSignal(text, ['설진', '설질', '설태', '혀', '한열', '담습', '어혈'])) codes.push('tongue');
        if (this.textContainsClinicalSignal(text, ['맥진', '맥상', '부맥', '침맥', '삭맥', '현맥', '허실'])) codes.push('pulse');
        if (this.textContainsClinicalSignal(text, ['복진', '복부', '소화', '복통', '명치', '대변', '변비', '설사'])) codes.push('abdomen');
        if (this.textContainsClinicalSignal(text, ['압통', '가동범위', 'ROM', '통증', '저림', '감각', '근력', '방사통'])) codes.push('tender_rom');
        if (this.textContainsClinicalSignal(text, ['부종', '피부', '열감', '냉감', '신경학적', '특이'])) codes.push('other');
        return this.dedupeStrings(codes);
    }

    private objectiveFindingPriorityInputCodes() {
        const codes: string[] = [];
        this.objectiveFindingPriorityItems.forEach((point) => {
            (point.relatedCodes || []).forEach((code) => codes.push(code));
        });
        return this.dedupeStrings(codes);
    }

    private dedupeObjectiveFindingPriorityPoints(points: ObjectiveFindingPriorityPoint[]) {
        const seen: Record<string, boolean> = {};
        return (points || []).filter((point) => {
            if (!point) return false;
            const key = `${point.label}|${point.detail}`;
            if (seen[key]) return false;
            seen[key] = true;
            return true;
        });
    }

    public objectiveFindingAssistGroups() {
        return this.objectiveFindingAssistGroupItems;
    }

    private buildObjectiveFindingAssistGroups() {
        return [
            this.objectiveFindingAiSuggestionGroup(),
            this.objectiveFindingRiskGroup(),
        ].filter((group) => group && group.items && group.items.length);
    }

    public objectiveFindingEmrRowState(item: ObjectiveFindingInput) {
        return this.objectiveFindingFieldText(item) ? 'complete' : 'empty';
    }

    public objectiveFindingEmrChipClass(item: ObjectiveFindingInput, chip: string) {
        const selected = this.objectiveFindingDraft(item.code).chips.indexOf(chip) >= 0;
        return selected ? 'objective-emr-chip is-selected' : 'objective-emr-chip';
    }

    public objectiveFindingFieldValue(item: ObjectiveFindingInput, field: ObjectiveFindingFieldInput) {
        if (!item || !field) return '';
        const draft = this.objectiveFindingDraft(item.code);
        const values = draft.fields || {};
        return String(values[field.code] || '');
    }

    public async setObjectiveFindingFieldValue(item: ObjectiveFindingInput, field: ObjectiveFindingFieldInput, value: string) {
        if (this.clinicalReadOnly || !item || !field) return;
        const key = String(field.code || '').trim();
        if (!key) return;
        const draft = this.objectiveFindingDraft(item.code);
        if (!draft.fields) draft.fields = {};
        const normalizedValue = String(value || '').trim();
        if (normalizedValue) draft.fields[key] = normalizedValue;
        else delete draft.fields[key];
        this.objectiveFindingDrafts = { ...this.objectiveFindingDrafts };
        this.error = '';
        this.soapMessage = '';
        await this.syncObjectiveFindingSummaryToSoap();
    }

    public objectiveFindingCompactGroups() {
        const rows = [
            this.objectiveFindingCompactGroup('korean', '한의진찰', ['tongue', 'pulse', 'abdomen']),
            this.objectiveFindingCompactGroup('physical', '이학확인', ['tender_rom', 'other']),
            this.objectiveFindingAiSuggestionGroup(),
            this.objectiveFindingRiskGroup(),
        ].filter((group) => group && group.items && group.items.length);
        return rows;
    }

    public objectiveFindingCompactPointClass(item: WorkspaceMap) {
        const state = item && item.state === 'complete' ? 'complete' : 'empty';
        return `objective-point-chip objective-point-chip--${state}`;
    }

    public async appendObjectiveFindingCompactPoint(item: WorkspaceMap) {
        if (this.busy || this.clinicalReadOnly || !item) return;
        const label = String(item.label || '').trim();
        if (!label) return;
        const current = this.objectiveFindingPreviewText().trim();
        const value = String(item.value || '').trim();
        const line = value || `${label}: `;
        if (current.indexOf(label) >= 0 || (value && current.indexOf(value) >= 0)) {
            await this.service.render();
            return;
        }
        await this.setObjectiveFindingManualText(current ? `${current}\n${line}` : line);
    }

    public objectiveFindingHasText() {
        return this.objectiveFindingPreviewText().trim() !== '';
    }

    public objectiveFindingPreviewText() {
        return this.objectiveFindingPreviewTextValue;
    }

    private buildObjectiveFindingPreviewText(fallbackText: string = '') {
        const manual = String(this.objectiveFindingManualText || '').trim();
        if (manual) return manual;
        const composed = this.objectiveFindingComposedSentence();
        if (composed) return composed;
        return fallbackText || this.soapSectionCompactText('O');
    }

    public async toggleObjectiveFindingChip(item: ObjectiveFindingInput, chip: string) {
        if (this.busy || this.clinicalReadOnly || !item || !chip) return;
        const draft = this.objectiveFindingDraft(item.code);
        const index = draft.chips.indexOf(chip);
        if (index >= 0) {
            draft.chips.splice(index, 1);
        } else {
            draft.chips.push(chip);
        }
        this.objectiveFindingDrafts = { ...this.objectiveFindingDrafts };
        await this.syncObjectiveFindingSummaryToSoap();
    }

    public async setObjectiveFindingNote(item: ObjectiveFindingInput, text: string) {
        if (this.clinicalReadOnly || !item) return;
        const draft = this.objectiveFindingDraft(item.code);
        draft.note = text || '';
        this.objectiveFindingDrafts = { ...this.objectiveFindingDrafts };
        this.error = '';
        this.soapMessage = '';
        await this.syncObjectiveFindingSummaryToSoap();
    }

    public async setObjectiveFindingManualText(text: string) {
        if (this.clinicalReadOnly) return;
        this.objectiveFindingManualText = text || '';
        this.error = '';
        this.soapMessage = '';
        await this.setSoapSectionCompactText('O', text || '');
    }

    public quantitativeObservationItems() {
        const observations = this.biometric && this.biometric.observations ? this.biometric.observations : {};
        return this.asArray(observations.items);
    }

    public quantitativeObservationSummary() {
        const observations = this.biometric && this.biometric.observations ? this.biometric.observations : {};
        const counts = observations.counts || {};
        const total = this.quantitativeObservationItems().length;
        if (!total) return '저장된 정량값 없음';
        return `ROM ${Number(counts.rom || 0)} · 설진 ${Number(counts.tongue || 0)} · 맥진 ${Number(counts.pulse || 0)}`;
    }

    public quantitativeObservationLatestItems() {
        return this.quantitativeObservationItems().slice(0, 4);
    }

    public quantitativeObservationValueLabel(item: WorkspaceMap) {
        if (!item) return '';
        const site = item.body_site_code ? ` · ${item.body_site_code}` : '';
        const side = item.laterality ? ` · ${item.laterality}` : '';
        return `${item.metric_name || item.metric_code} ${item.value} ${item.unit || ''}${site}${side}`.trim();
    }

    public setQuantitativeObservationValue(code: string, value: any) {
        if (this.clinicalReadOnly) return;
        this.quantitativeObservationDraft = {
            ...this.quantitativeObservationDraft,
            [code]: value,
        };
        this.quantitativeObservationMessage = '';
    }

    public async saveQuantitativeObservations() {
        if (this.savingQuantitativeObservations || this.clinicalReadOnly) return;
        const draft = this.quantitativeObservationDraft || {};
        const rows: WorkspaceMap[] = [];
        const add = (draftCode: string, metricCode: string, extra: WorkspaceMap = {}) => {
            const raw = draft[draftCode];
            if (raw === '' || raw === null || raw === undefined) return;
            rows.push({ metric_code: metricCode, value: Number(raw), method: 'manual', ...extra });
        };
        const site = String(draft.body_site_code || 'lumbar_spine');
        add('rom_flexion', 'rom_flexion_degree', { body_site_code: site, laterality: 'midline', method: 'goniometer' });
        add('rom_extension', 'rom_extension_degree', { body_site_code: site, laterality: 'midline', method: 'goniometer' });
        add('rom_rotation_left', 'rom_rotation_degree', { body_site_code: site, laterality: 'left', method: 'goniometer' });
        add('rom_rotation_right', 'rom_rotation_degree', { body_site_code: site, laterality: 'right', method: 'goniometer' });
        add('pulse_rate', 'pulse_rate_bpm');
        add('tongue_coating_ratio', 'tongue_coating_ratio_pct');
        add('tongue_moisture_index', 'tongue_moisture_index');
        if (!rows.length) {
            this.quantitativeObservationMessage = '저장할 정량값을 하나 이상 입력해 주세요.';
            await this.service.render();
            return;
        }
        this.savingQuantitativeObservations = true;
        this.quantitativeObservationMessage = '';
        await this.service.render();
        const result = await this.callApi('save_clinical_observations', {
            encounter_id: this.encounterId,
            observations: rows,
            measured_at: this.koreaIsoNow(),
        });
        this.savingQuantitativeObservations = false;
        if (!result.ok) {
            this.quantitativeObservationMessage = result.message || '정량 진찰값을 저장하지 못했습니다.';
            await this.service.render();
            return;
        }
        const context = result.data && result.data.context ? result.data.context : null;
        if (context) this.applyContext(context, true);
        this.quantitativeObservationMessage = `${rows.length}개 정량 진찰값을 저장했습니다.`;
        this.quantitativeObservationDraft = {
            ...this.quantitativeObservationDraft,
            rom_flexion: '', rom_extension: '', rom_rotation_left: '', rom_rotation_right: '',
            pulse_rate: '', tongue_coating_ratio: '', tongue_moisture_index: '',
        };
        await this.service.render();
    }

    private async syncObjectiveFindingSummaryToSoap() {
        const text = this.objectiveFindingComposedSentence().trim();
        const items = this.soapItems('O');
        let target = items.find((item) => this.isStructuredObjectiveFindingSummarySentence(item));
        if (!text) {
            if (target && target.status !== 'excluded') {
                target.status = 'excluded';
                target.doctor_decision_status = 'excluded';
                target.doctor_decision_reason = 'structured_objective_cleared';
                target.dirty = true;
            }
            this.refreshClinicalChartViewState();
            await this.service.render();
            return;
        }
        if (!target) {
            const legacyStructuredItems = items.filter((item) => this.isLegacyStructuredObjectiveFindingSentence(item));
            legacyStructuredItems.forEach((item) => {
                item.status = 'excluded';
                item.doctor_decision_status = 'excluded';
                item.doctor_decision_reason = 'structured_objective_migrated';
                item.dirty = true;
            });
            target = {
                id: `local-O-objective-structured-${Date.now()}-${items.length}`,
                soap_section: 'O',
                text: '',
                status: 'doctor_added',
                author_type: 'doctor',
                source_ai_request_id: '',
                sentence_order: items.length + 1,
                source_refs: [],
                doctor_decision_reason: 'structured_objective_update',
                candidate_source: 'structured_objective_finding',
                sentence_meta: {},
                updated: '',
                dirty: true,
                persisted: false,
            };
            items.push(target);
            if (legacyStructuredItems.length) {
                this.soapMessage = '기존 진찰 소견을 상세 항목으로 정리했습니다.';
            }
        }
        target.text = text;
        target.status = target.persisted ? 'doctor_edited' : 'doctor_added';
        target.doctor_decision_status = '';
        target.doctor_decision_reason = 'structured_objective_update';
        target.candidate_source = 'structured_objective_finding';
        target.sentence_meta = {
            ...(target.sentence_meta || {}),
            source: 'structured_objective_finding',
            summary: true,
            structured_fields: this.objectiveFindingPayloadFields(),
        };
        target.dirty = true;
        this.refreshClinicalChartViewState();
        await this.service.render();
    }

    private isLegacyStructuredObjectiveFindingSentence(item: SoapSentenceDraft) {
        if (!item || item.status === 'excluded' || this.isStructuredObjectiveFindingSummarySentence(item)) return false;
        const text = String(item.text || '').trim();
        if (!text || text.indexOf('\n') >= 0) return false;
        if (!this.objectiveFindingFieldMatchesFromText(text).length) return false;
        return /^(진찰상\s*)?(설진|맥진|복진|압통|가동범위|이학적\s*검사|기타\s*소견)/.test(text)
            && /(소견\s*)?(확인됨|확인\s*예정|관찰됨)[.。]?$/.test(text);
    }

    public async resetObjectiveFindingInput() {
        if (this.clinicalReadOnly) return;
        this.clearObjectiveFindingInputs();
        await this.syncObjectiveFindingSummaryToSoap();
    }

    public async addObjectiveFindingAsSentence() {
        if (this.busy || this.clinicalReadOnly) return;
        const text = this.objectiveFindingComposedSentence().trim();
        if (!text) {
            this.error = '진찰 소견을 입력해 주세요.';
            await this.service.render();
            return;
        }
        await this.syncObjectiveFindingSummaryToSoap();
        this.soapMessage = '';
        await this.service.render();
    }

    public async sendObjectiveFindingAsCandidate() {
        if (this.busy || this.clinicalReadOnly) return;
        const text = this.objectiveFindingPreviewText().trim();
        if (!text) {
            this.error = '진찰 소견을 입력해 주세요.';
            await this.service.render();
            return;
        }
        const fieldPayload = this.objectiveFindingPayloadFields();
        const ok = await this.appendSoapCandidate({
            kind: 'structured_objective_finding',
            title: '진찰 소견',
            description: text,
            source: 'structured_objective_finding',
            author_type: 'doctor_candidate',
            generated_by: 'doctor',
            soap_section: 'O',
            text,
            soap_sentence_text: text,
            answer_target: '진찰/소견',
            suggestion_reasons: this.objectiveFindingCandidateReasons(fieldPayload),
            source_labels: this.objectiveFindingCandidateReasons(fieldPayload),
            decision_status: 'pending_doctor_review',
            candidate_status: 'pending_doctor_review',
            doctor_decision_status: 'pending_doctor_review',
            source_payload: {
                input_type: 'structured_objective_finding',
                fields: fieldPayload,
            },
            request_text: 'structured_objective_finding',
        });
        if (ok) {
            this.clearObjectiveFindingInputs();
            this.soapMessage = '진찰/소견 참고 내용이 추가되었습니다.';
            await this.service.render();
        }
    }

    public pendingSoapSuggestions(section: string) {
        const normalized = this.normalizedSoapSection(section);
        const suggestions: AiAssistantResultItem[] = [];
        this.aiAssistantResults.forEach((result) => {
            if (!this.isSoapImprovementResult(result)) return;
            this.asArray(result.items).forEach((item) => {
                const targetSection = this.normalizedSoapSection(item && item.soap_section ? String(item.soap_section) : 'S');
                const status = String((item && item.decision_status) || '');
                if (targetSection !== normalized) return;
                if (item.applied_to_soap || status === 'ignored' || status === 'excluded' || status === 'deferred') return;
                suggestions.push(item);
            });
        });
        return suggestions.slice(0, 3);
    }

    public soapText(item: any) {
        if (item === null || item === undefined) return '';
        if (typeof item === 'string') return item;
        return item.text || item.original_text || item.display_text || item.value || '';
    }

    public soapItemClass(item: SoapSentenceDraft) {
        const base = 'soap-sentence';
        if (item.status === 'excluded') return `${base} soap-sentence--excluded`;
        if (item.dirty) return `${base} soap-sentence--dirty`;
        return base;
    }

    public soapAuthorLabel(item: SoapSentenceDraft) {
        if (item.candidate_source === 'structured_objective_finding') return '진찰 입력';
        if (item.candidate_source === 'structured_treatment_plan') return '치료 계획';
        if (item.candidate_source === 'checklist') return '예진 정보';
        if (item.candidate_source === 'pattern_candidate') return '변증 판단';
        if (item.author_type === 'doctor') return '직접 작성';
        if (item.author_type === 'doctor_candidate') return '직접 입력';
        if (item.author_type === 'ai') return '진료지원';
        if (item.author_type === 'ai_assistant') return '진료지원';
        if (item.author_type === 'checklist') return '예진 정보';
        if (item.author_type === 'pattern_candidate') return '변증 판단';
        return '작성자 미정';
    }

    public soapIsAiCandidate(item: SoapSentenceDraft) {
        const candidateStatus = String((item && item.candidate_status) || '').trim();
        const doctorDecisionStatus = String((item && item.doctor_decision_status) || '').trim();
        return !!item && (
            ['ai', 'ai_assistant', 'checklist', 'pattern_candidate'].indexOf(String(item.author_type || '')) >= 0
            || ['ai', 'ai_assistant', 'checklist', 'pattern_candidate', 'structured_treatment_plan', 'conversation_chat'].indexOf(String(item.candidate_source || '')) >= 0
            || item.author_type === 'doctor_candidate'
            || item.status === 'ai_unreviewed'
            || ['new_candidate_ready', 'pending_doctor_review'].indexOf(candidateStatus) >= 0
            || ['new_candidate_ready', 'pending_doctor_review'].indexOf(doctorDecisionStatus) >= 0
            || !!item.source_ai_request_id
        );
    }

    public soapAcceptButtonText(item: SoapSentenceDraft) {
        if (item && (item.dirty || item.status === 'doctor_edited')) return '수정 반영';
        return '확인 완료';
    }

    public async acceptSoapSentence(item: SoapSentenceDraft) {
        if (this.clinicalReadOnly) return;
        if (!this.soapIsAiCandidate(item) || item.status === 'excluded') return;
        const edited = item.dirty || item.status === 'doctor_edited';
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        const saved = await this.applySoapCandidateDecision({
            action: edited ? 'edit_apply' : 'apply',
            sentence_id: item.id,
            section: item.soap_section,
            text: item.text,
            source_refs: item.source_refs || [],
            reason: edited ? 'doctor_edit_accept' : 'doctor_accept',
            candidate: this.soapCandidatePayloadFromSentence(item),
        });
        if (saved) {
            this.soapMessage = edited ? '수정 반영됨.' : '반영됨.';
            await this.service.render();
        }
    }

    public async setSoapText(item: SoapSentenceDraft, text: string) {
        if (this.clinicalReadOnly) return;
        item.text = text || '';
        item.dirty = true;
        if (item.status !== 'excluded' && item.persisted) item.status = 'doctor_edited';
        if (item.status !== 'excluded' && !item.persisted) item.status = 'doctor_added';
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        this.refreshClinicalChartViewState();
        await this.service.render();
    }

    public async setSoapNewText(section: string, text: string) {
        if (this.clinicalReadOnly) return;
        this.soapNewText[section] = text || '';
        this.error = '';
        this.soapMessage = '';
        await this.service.render();
    }

    public async addSoapSentence(section: string) {
        if (this.clinicalReadOnly) return;
        const text = (this.soapNewText[section] || '').trim();
        if (!text) {
	            this.error = `${section} 문장을 입력해 주세요.`;
            await this.service.render();
            return;
        }
        const items = this.soapItems(section);
        items.push({
            id: `local-${section}-${Date.now()}-${items.length}`,
            soap_section: section,
            text,
            status: 'doctor_added',
            author_type: 'doctor',
            source_ai_request_id: '',
            sentence_order: items.length + 1,
            source_refs: [],
            doctor_decision_reason: '',
            updated: '',
            dirty: true,
            persisted: false,
        });
        this.soapNewText[section] = '';
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        this.refreshClinicalChartViewState();
        await this.service.render();
    }

    public async excludeSoapSentence(item: SoapSentenceDraft) {
        if (this.clinicalReadOnly) return;
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        const saved = await this.applySoapCandidateDecision({
            action: 'exclude',
            sentence_id: item.persisted ? item.id : '',
            section: item.soap_section,
            text: item.text,
            source_refs: item.source_refs || [],
            reason: item.doctor_decision_reason || 'manual_exclude',
            candidate: this.soapCandidatePayloadFromSentence(item),
        });
        if (saved) this.soapMessage = '사용 안 함으로 표시됨.';
        await this.service.render();
    }

    public async restoreSoapSentence(item: SoapSentenceDraft) {
        if (this.clinicalReadOnly) return;
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        if (!item.persisted) {
            item.status = 'doctor_added';
            item.doctor_decision_status = 'pending_doctor_review';
            item.dirty = true;
            await this.service.render();
            return;
        }
        const saved = await this.applySoapCandidateDecision({
            action: 'restore',
            sentence_id: item.id,
            section: item.soap_section,
            text: item.text,
            source_refs: item.source_refs || [],
            reason: 'manual_restore',
            candidate: this.soapCandidatePayloadFromSentence(item),
        });
        if (saved) this.soapMessage = '복원됨.';
        await this.service.render();
    }

    public async createManualSoapDraft() {
        if (this.clinicalReadOnly) return;
        this.soapCreating = true;
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        await this.service.render();

        const result = await this.callApi('create_manual_soap_draft', {
            encounter_id: this.encounterId,
            draft_id: this.soapDraftId,
        });
        this.soapCreating = false;

        if (result.ok) {
            const context = result.data && result.data.context ? result.data.context : result.data;
            this.applyContext(context);
	            this.soapMessage = '한의 차트 편집 준비됨.';
        } else {
            this.error = result.message;
        }
        await this.service.render();
    }

    public async prepareSoapCandidate(forceRetry: boolean = false) {
        if (this.clinicalReadOnly || !this.canGenerateClinicalAi || this.soapEntryPreparing) return;
        this.soapEntryPreparing = true;
        this.soapEntryPrepareRun = {
            status: 'candidate_preparing',
            state: 'preparing',
            manual_available: true,
        };
        this.soapGenerationError = '';
        this.soapMessage = '';
        this.soapValidation = {};
        this.error = '';
        await this.service.render();

        const result = await this.callApi('prepare_soap_candidate', {
            encounter_id: this.encounterId,
            purpose_code: 'care_support',
            force_retry: forceRetry ? 'true' : 'false',
        });
        this.soapEntryPreparing = false;

        if (result.ok) {
            const payload = result.data || {};
            const run = payload.result || payload.draft_candidate || {};
            this.soapEntryPrepareRun = run || {};
            let candidateReady = run && run.status === 'candidate_ready';
            const preparing = run && (run.status === 'candidate_preparing' || run.status === 'pending' || run.state === 'preparing');
            if (payload.context) {
                this.applyContext(payload.context);
            } else if (candidateReady) {
                await this.refreshContextAfterMutation();
            }
            if (preparing) {
                candidateReady = await this.waitForSoapCandidateReady();
            }
            if (candidateReady) {
                this.soapMessage = '진료 참고 내용을 확인할 수 있습니다.';
            } else if (run && run.status === 'candidate_preparing') {
                this.soapMessage = '진료 참고 내용을 정리 중입니다.';
            } else if (run && (run.status === 'insufficient_input' || run.state === 'manual_only')) {
                this.soapMessage = '입력 부족. 직접 작성 가능.';
            } else if (run && run.manual_available) {
                this.soapMessage = '직접 작성 가능.';
            } else if (run && run.message) {
                this.soapMessage = this.aiResultMessage(run, '상태 확인됨.');
            }
        } else {
            this.soapEntryPrepareRun = {
                status: 'failed',
                state: 'failed',
                message: result.message,
                manual_available: true,
            };
            this.soapGenerationError = result.message;
        }
        await this.service.render();
    }

    public async generateSoapDraft() {
        if (this.clinicalReadOnly || !this.canGenerateClinicalAi) return;
        this.soapGenerating = true;
        this.soapGenerationError = '';
        this.soapMessage = '';
        this.soapValidation = {};
        this.error = '';
        await this.service.render();

        const result = await this.callApi('generate_draft', {
            encounter_id: this.encounterId,
            purpose_code: 'care_support',
        });
        this.soapGenerating = false;

        if (result.ok) {
            const payload = result.data || {};
            const run = payload.draft_candidate || payload.candidate || payload.result || {};
            this.soapGenerationRun = run;
            if (run && run.status === 'candidate_ready') {
                if (payload.context) {
                    this.applyContext(payload.context);
                } else {
                    await this.loadContext(false);
                }
	                this.soapMessage = '진료 참고 내용을 확인할 수 있습니다.';
	            } else {
	                this.soapGenerationError = this.aiResultMessage(run, '진료 참고 내용을 정리하지 못했습니다');
            }
        } else {
            this.soapGenerationError = result.message;
        }
        await this.service.render();
    }

    public async saveSoapDraft(showMessage: boolean = true, automatic: boolean = false) {
        if (this.clinicalReadOnly) return false;
        this.soapSaving = true;
        if (automatic) this.autosaveState = 'saving';
        this.error = '';
        if (showMessage) this.soapMessage = '';
        this.soapValidation = {};
        this.finalWarningsAcknowledged = false;
        this.finalGateConfirmed = false;
        await this.service.render();

        const result = await this.callApiWithRetry('save_soap_draft', {
            encounter_id: this.encounterId,
            draft_id: this.soapDraftId,
            expected_updated: this.soapDraftRevision,
            sentences: JSON.stringify(this.serializeSoapDrafts()),
        }, 1);
        this.soapSaving = false;

        if (result.ok) {
            const context = result.data && result.data.context ? result.data.context : result.data;
            this.applyContext(context);
            await this.refreshContextAfterMutation();
            this.lastAutosavedAt = this.koreaTimeLabel();
            this.autosaveState = 'saved';
            this.retryActionLabel = '';
            this.clearWorkspaceRecovery();
		            if (showMessage) this.soapMessage = '저장됨. 최종 점검을 진행하세요.';
            if (showMessage) this.showWorkspaceFeedback('임시 저장 완료 · 최종 점검을 진행할 수 있습니다.', 'success');
        } else {
            this.error = result.message;
            this.autosaveState = 'failed';
            this.retryActionLabel = automatic && result.message.indexOf('다른 화면') >= 0 ? '최신 기록 불러오기' : '임시 저장';
            this.persistWorkspaceRecovery();
            if (automatic) this.showWorkspaceFeedback(
                this.retryActionLabel === '최신 기록 불러오기'
                    ? '다른 화면의 저장을 감지했습니다. 현재 입력은 이 브라우저에 보관했습니다.'
                    : '자동 저장에 실패했습니다. 입력 내용은 이 브라우저에 보관했습니다.',
                'warning'
            );
        }
        await this.service.render();
        return result.ok;
    }

    public async validateSoapDraft(markReady: boolean = false) {
        if (this.clinicalReadOnly) return;
        if (this.hasSoapDirty) {
            const saved = await this.saveSoapDraft(false);
            if (!saved) return;
        }
        if (!this.soapDraftId) {
            await this.createManualSoapDraft();
            if (!this.soapDraftId) return;
        }

        this.soapValidating = !markReady;
        this.soapReadying = markReady;
        this.error = '';
        this.soapMessage = '';
        this.finalMessage = '';
        this.finalError = '';
        this.soapValidation = {};
        this.finalWarningsAcknowledged = false;
        this.finalGateConfirmed = false;
        await this.service.render();

        const result = await this.callApiWithRetry('validate_soap_draft', {
            encounter_id: this.encounterId,
            draft_id: this.soapDraftId,
            mark_ready: markReady ? 'true' : 'false',
        }, 1);
        this.soapValidating = false;
        this.soapReadying = false;

        if (result.ok) {
            const payload = result.data || {};
            const validation = payload.result || {};
            const context = payload.context ? payload.context : payload;
            this.soapValidation = validation.validation_summary || validation;
            this.applyContext(context);
            await this.refreshContextAfterMutation();
            const blockingCount = this.finalSaveBlockingItems.length;
            const warningCount = this.finalSaveWarningItems.length;
            if (blockingCount > 0) {
                this.soapMessage = '저장 전 더 작성할 내용이 있습니다.';
                this.finalError = `더 작성할 내용 ${blockingCount}건을 점검 내용에서 확인하세요.`;
                this.finalSaveModalOpen = true;
            } else if (warningCount > 0) {
                this.soapMessage = '확인만 하면 저장할 수 있는 내용이 있습니다.';
                this.finalMessage = `확인만 하면 되는 내용 ${warningCount}건을 확인하면 최종 저장할 수 있습니다.`;
                this.finalSaveModalOpen = true;
            } else if (validation.valid) {
		                this.soapMessage = markReady ? '최종 점검 완료.' : '점검 완료.';
                this.finalMessage = '최종 점검이 완료되었습니다. 지금 최종 저장할 수 있습니다.';
            } else {
	                this.soapMessage = '저장 전 더 작성할 내용이 있습니다.';
                this.finalError = '더 작성할 내용을 점검 내용에서 확인하세요.';
                this.finalSaveModalOpen = true;
            }
        } else {
            this.error = result.message;
            this.finalError = '최종 점검을 완료하지 못했습니다. 잠시 후 다시 시도하세요.';
            this.finalSaveModalOpen = true;
        }
        await this.service.render();
        if (this.finalSaveModalOpen) this.focusActiveOverlay('final-save-modal');
    }

    public async approveFinalChart() {
        if (!this.canApproveFinal || this.finalApproving) return;
        this.finalError = '';
        this.finalMessage = '';
        this.soapMessage = '';
        this.error = '';

        if (this.hasSoapDirty) {
                this.finalError = '미저장 변경을 저장한 뒤 최종 점검을 진행하세요.';
            this.finalSaveModalOpen = true;
            await this.service.render();
            return;
        }
        if (!this.soapDraftId) {
            this.finalError = '최종 저장할 한의 차트가 없습니다.';
            await this.service.render();
            return;
        }
        if (this.finalGateNeedsConfirmation && !this.finalGateConfirmed) {
            this.finalError = '확인만 하면 되는 내용을 확인 완료로 표시한 뒤 최종 저장하세요.';
            this.finalSaveModalOpen = true;
            await this.service.render();
            return;
        }
        if (this.finalSaveWarningItems.length > 0) {
            this.finalError = '확인만 하면 되는 내용을 확인 완료로 표시한 뒤 최종 저장하세요.';
            this.finalSaveModalOpen = true;
            await this.service.render();
            return;
        }

        this.finalApproving = true;
        await this.service.render();

        const result = await this.callApi('approve_final_chart', {
            encounter_id: this.encounterId,
            draft_id: this.soapDraftId,
            client_dirty: this.hasSoapDirty ? 'true' : 'false',
            confirmed_gate_codes: this.finalGateConfirmed ? this.finalGateConfirmableItems.map((item) => item.code || '').filter((code) => code !== '').join(',') : '',
        });
        this.finalApproving = false;

        if (result.ok) {
            const payload = result.data || {};
            const approval = payload.result || {};
            if (payload.context) {
                this.applyContext(payload.context);
            }
            await this.refreshContextAfterMutation();
            if (approval.approved) {
			                this.finalMessage = approval.status === 'already_approved' ? '이미 최종 저장됨.' : '최종 저장 완료.';
                this.soapValidation = {};
                this.finalWarningsAcknowledged = true;
                this.showWorkspaceFeedback('최종 저장이 완료되었습니다.', 'success');
            } else {
                this.finalChart = payload.context && payload.context.final_chart ? payload.context.final_chart : { ...this.finalChart, ...approval };
                this.soapValidation = approval.validation && approval.validation.validation_summary ? approval.validation.validation_summary : {
                    errors: approval.errors || [],
                    warnings: approval.warnings || [],
                };
	                this.finalError = '더 작성할 내용을 점검 내용에서 확인하세요.';
                this.finalSaveModalOpen = true;
            }
        } else {
            this.finalError = result.message;
            this.finalSaveModalOpen = true;
        }
        await this.service.render();
    }

    private sexLabel(value: any) {
        const code = String(value || '').trim().toUpperCase();
        const labels: WorkspaceMap = {
            M: '남',
            MALE: '남',
            F: '여',
            FEMALE: '여',
            U: '미상',
        };
        return labels[code] || this.displayValue(value, '성별 없음');
    }

    private summaryLookup(keys: string[], labels: string[], fallback: string = '-') {
        const normalizedKeys = (keys || []).map((key) => String(key || '').toLowerCase());
        const normalizedLabels = (labels || []).map((label) => this.normalizeLookupLabel(label));
        const sources = ([] as any[]).concat(this.summarySections, this.summaryFacts, this.encounterFacts);

        for (const item of sources) {
            if (!item) continue;
            const key = String(item.key || item.code || '').toLowerCase();
            const label = this.normalizeLookupLabel(item.label || '');
            const keyMatched = key !== '' && normalizedKeys.indexOf(key) >= 0;
            const labelMatched = label !== '' && normalizedLabels.some((target) => target === label || label.indexOf(target) >= 0 || target.indexOf(label) >= 0);
            if (!keyMatched && !labelMatched) continue;
            const text = this.displayValue(item.display_text || item.text || item.value, '');
            if (text !== '') return text;
        }
        return fallback;
    }

    private normalizeLookupLabel(value: any) {
        return String(value || '').replace(/\s+/g, '').toLowerCase();
    }

    public displayValue(value: any, fallback: string = '-') {
        if (value === null || value === undefined || value === '') return fallback;
        if (Array.isArray(value)) {
            const text = value.map((item) => this.displayValue(item, '')).filter((item) => item !== '').join(', ');
            return text || fallback;
        }
        if (typeof value === 'object') {
            if (value.display_text || value.text || value.value || value.label) {
                return this.displayValue(value.display_text || value.text || value.value || value.label, fallback);
            }
            const text = Object.keys(value).map((key) => `${key}: ${this.displayValue(value[key], '')}`).filter((item) => item.indexOf(': ') !== item.length - 2).join(', ');
            return text || fallback;
        }
        return String(value);
    }

    private patientSummaryText(value: any, limit: number = 96) {
        const raw = this.displayValue(value, '').trim();
        if (!raw || raw === '-') return '-';
        const cleaned = raw
            .replace(/\bReviewOps\s+[a-z0-9_-]{8,}\b/gi, '')
            .replace(/\bevidence[a-z0-9_-]*\b/gi, '')
            .replace(/\btest[a-z0-9_-]*\b/gi, '')
            .replace(/\bQA\b/g, '')
            .replace(/테스트성?\s*문구/g, '')
            .replace(/테스트\s*입력/g, '')
            .replace(/개발용\s*문구/g, '')
            .replace(/\b(?:desktop|mobile)-\d{3,4}x\d{3,4}\b/gi, '')
            .replace(/\b1[0-9]{12,}\b/g, '')
            .replace(/회귀\s*기준\s*예진/g, '')
            .replace(/\s*[:：]\s*/g, ': ')
            .replace(/^\s*[:：]\s*/, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
        return this.truncateText(cleaned || raw, limit);
    }

    private clinicalPersonName(value: any, fallback: string) {
        const raw = this.displayValue(value, '').trim();
        if (!raw) return fallback;
        if (/ReviewOps|QA|테스트|검증|AI\s*검증/i.test(raw)) {
            if (/한의사|의사|doctor/i.test(raw)) return '담당 한의사';
            return '진료 환자';
        }
        return raw.replace(/[0-9]{6,}$/g, '').trim() || fallback;
    }

    private visibleClinicalIdentifier(value: any, fallback: string) {
        const raw = this.displayValue(value, '').trim();
        if (!raw || raw === '-') return fallback;
        if (/(?:^|\b)(QA|DEMO)-(HS|ENC)-/i.test(raw)) return fallback;
        if (/reviewops|patient_id|encounter_id/i.test(raw)) return fallback;
        if (/^[a-z0-9]{24,}$/i.test(raw) && !/[가-힣\s-]/.test(raw)) return fallback;
        return raw;
    }

    public sourceCount(item: any) {
        if (!item || !Array.isArray(item.source_refs)) return 0;
        return item.source_refs.length;
    }

    private safeEvidenceKey(value: any, fallback: string) {
        const text = String(value || fallback || 'evidence').trim();
        return text.replace(/[^0-9A-Za-z가-힣_-]+/g, '_').replace(/^_+|_+$/g, '') || fallback;
    }

    private evidenceRefLabel(ref: any) {
        if (!ref) return '';
        if (typeof ref === 'string') return this.evidenceSourceTextLabel(ref);
        if (typeof ref !== 'object') return this.evidenceSourceTextLabel(ref);
        const type = String(ref.type || ref.source || ref.kind || '').trim();
        const sourceId = String(ref.id || ref.source_id || ref.response_id || ref.code || '').trim();
        const typeLabel = this.evidenceSourceTypeLabel(type);
        const itemLabel = this.evidenceRefItemLabel(type, sourceId, ref.label || ref.title || ref.key || ref.item_label || ref.item_code || '');
        if (typeLabel && itemLabel) return `${typeLabel} · ${itemLabel}`;
        if (typeLabel) return typeLabel;
        if (itemLabel) return itemLabel;
        return '현재 화면 자료';
    }

    private evidenceRefDebugLabel(ref: any) {
        return '';
    }

    private evidenceSourceTypeLabel(value: any) {
        const source = String(value || '').trim().toLowerCase();
        if (!source) return '';
        if (source.indexOf('interview') >= 0 || source.indexOf('precheck') >= 0) return '예진 입력';
        if (source.indexOf('patient.profile_summary') >= 0 || source === 'patient_summary' || source === 'profile_summary') return '환자 요약';
        if (source.indexOf('checklist') >= 0) return '진료 전 확인';
        if (source.indexOf('soap') >= 0) return '기록 문장';
        if (source.indexOf('ai_request') >= 0 || source.indexOf('ai_result') >= 0) return '진료 참고 이력';
        if (source === 'summary' || source === 'clinical_source' || source === 'source') return '현재 화면 자료';
        return '';
    }

    private evidenceSourceTextLabel(value: any) {
        const text = this.displayValue(value, '').trim();
        if (!text) return '';
        const typeLabel = this.evidenceSourceTypeLabel(text);
        if (typeLabel) return typeLabel;
        if (this.looksTechnicalEvidenceValue(text)) return '현재 화면 자료';
        return text;
    }

    private evidenceRefItemLabel(sourceType: string, sourceId: string, rawLabel: any) {
        const source = String(sourceType || '').toLowerCase();
        const label = this.clinicalSourceLabel(rawLabel, '');
        if (label) return label;

        if (source.indexOf('checklist') >= 0) {
            const item = this.findChecklistEvidenceItem(sourceId, '');
            return this.clinicalSourceLabel(item && item.label, '항목');
        }
        if (source.indexOf('interview') >= 0 || source.indexOf('precheck') >= 0) {
            const item = this.findSummaryEvidenceItem(sourceId, '', ['interview_response']);
            return this.clinicalSourceLabel(item && (item.label || item.key || item.code), '항목');
        }
        if (source.indexOf('patient.profile_summary') >= 0 || source === 'patient_summary' || source === 'profile_summary') {
            const item = this.findSummaryEvidenceItem(sourceId, '', ['patient.profile_summary']);
            return this.clinicalSourceLabel(item && (item.label || item.key || item.code), '메모');
        }
        if (source.indexOf('soap') >= 0) return '기록 문장';
        if (source.indexOf('ai_request') >= 0 || source.indexOf('ai_result') >= 0) return '';
        return this.clinicalSourceLabel(sourceId, '');
    }

    private clinicalSourceLabel(value: any, fallback: string = '') {
        const text = this.displayValue(value, '').trim();
        if (!text) return fallback;
        const keyLabel = this.clinicalKeyLabel(text);
        if (keyLabel) return keyLabel;
        if (this.looksTechnicalEvidenceValue(text)) return fallback;
        return text;
    }

    private clinicalKeyLabel(value: any) {
        const key = String(value || '').trim().toLowerCase();
        const labels: WorkspaceMap = {
            chief_complaint: '주호소',
            symptom_detail: '증상 상세',
            symptom_details: '증상 상세',
            past_history: '과거력',
            medication: '복약',
            medicine: '복약',
            allergy: '알레르기',
            memo: '메모',
            core_memo: '핵심 메모',
            diagnosis: '진단/평가',
            care_plan: '진료 계획',
            profile_summary: '메모',
        };
        return labels[key] || '';
    }

    private looksTechnicalEvidenceValue(value: any) {
        const text = String(value || '').trim();
        if (!text) return false;
        const lower = text.toLowerCase();
        if (this.evidenceSourceTypeLabel(lower)) return true;
        if (/^[a-f0-9]{24,}$/i.test(text)) return true;
        if (/^[a-z0-9]{24,}$/i.test(text) && !/[가-힣\s]/.test(text)) return true;
        if (/^[a-z0-9_-]{18,}$/i.test(text) && text.indexOf('_') >= 0 && !/[가-힣]/.test(text)) return true;
        if (/^[a-z]+_[a-z0-9_]+$/.test(lower) && !this.clinicalKeyLabel(lower)) return true;
        return false;
    }

    private evidenceSourceRefsForResult(result: AiAssistantResult) {
        const refs: string[] = [];
        this.asArray(result && result.items).forEach((item) => {
            refs.push(...this.evidenceSourceRefsForItem(item));
        });
        if (!refs.length && result && Array.isArray(result.sources)) {
            refs.push(...result.sources.map((source) => this.evidenceSourceTextLabel(source)).filter((label) => label !== ''));
        }
        return this.dedupeStrings(refs).slice(0, 8);
    }

    private evidenceSourceRefsForItem(item: AiAssistantResultItem) {
        const refs = this.asArray(item && item.source_refs).map((ref) => this.evidenceRefLabel(ref)).filter((label) => label !== '');
        if (!refs.length && item && item.source) refs.push(this.evidenceSourceTextLabel(item.source));
        return this.dedupeStrings(refs).slice(0, 6);
    }

    private evidenceSourceRefsForSoapSentence(item: SoapSentenceDraft) {
        const refs = this.asArray(item && item.source_refs).map((ref) => this.evidenceRefLabel(ref)).filter((label) => label !== '');
        if (item && (item.source_ai_request_id || item.id || item.author_type === 'ai')) refs.push('기록 문장');
        return this.dedupeStrings(refs).slice(0, 6);
    }

    private evidenceDebugRefsForResult(result: AiAssistantResult) {
        return [];
    }

    private evidenceDebugRefsForItem(item: AiAssistantResultItem) {
        return [];
    }

    private evidenceDebugRefsForSoapSentence(item: SoapSentenceDraft) {
        return [];
    }

    private evidenceReflectionLabelForResult(result: AiAssistantResult) {
        const labels = this.asArray(result && result.items)
            .map((item) => this.evidenceReflectionLabelForItem(item))
            .filter((label) => label && label !== '아직 차트에 넣지 않음');
        if (labels.length) return this.dedupeStrings(labels).join(', ');
        if (result && result.status && result.status !== 'pending_doctor_review') return this.statusLabel(result.status);
        return '아직 차트에 넣지 않음';
    }

    private evidenceReflectionLabelForItem(item: AiAssistantResultItem) {
        if (!item) return '아직 차트에 넣지 않음';
        if (item.applied_to_soap && item.applied_to_checklist) return '차트 내용과 확인 메모 저장 전';
        if (item.applied_to_soap) {
            if (['added', 'modified'].indexOf(String(item.decision_status || '')) >= 0) return '차트 반영 완료';
            return '한의사 판단 전';
        }
        if (item.applied_to_checklist) return '확인 메모 저장 전';
        const status = String(item.decision_status || 'pending_doctor_review');
        const labels: WorkspaceMap = {
            referenced: '참고로 표시',
            deferred: '나중에 보기',
            ignored: '사용 안 함',
            excluded: '사용 안 함',
            pending_doctor_review: '아직 차트에 넣지 않음',
        };
        return labels[status] || this.statusLabel(status);
    }

    private evidenceReflectionLabelForSoapSentence(item: SoapSentenceDraft) {
        if (!item) return '아직 차트에 넣지 않음';
        if (item.status === 'reviewed') return '차트 반영됨';
        if (item.status === 'ai_unreviewed') return '한의사 판단 전';
        if (item.status === 'excluded') return '사용 안 함';
        if (item.dirty) return '수정 내용 저장 필요';
        if (item.persisted) return '저장됨';
        return this.statusLabel(item.status || 'pending_doctor_review');
    }

    private resolveEvidenceRefs(refs: any[]): EvidenceSourceItem[] {
        const rows: EvidenceSourceItem[] = [];
        (refs || []).forEach((ref) => {
            const row = this.resolveEvidenceRef(ref);
            if (row) rows.push(row);
        });
        return this.dedupeEvidenceRows(rows);
    }

    private resolveEvidenceRef(ref: any): EvidenceSourceItem | null {
        if (!ref || typeof ref !== 'object') return null;
        const sourceType = String(ref.type || '').toLowerCase();
        const sourceId = String(ref.id || ref.source_id || ref.response_id || ref.code || '').trim();
        const label = String(ref.label || '').trim();
        const refLabel = this.evidenceRefLabel(ref);
        let row: EvidenceSourceItem | null = null;

        if (sourceType.indexOf('checklist') >= 0) {
            row = this.evidenceFromChecklistItem(this.findChecklistEvidenceItem(sourceId, label), label);
        } else if (sourceType.indexOf('interview') >= 0 || sourceType.indexOf('precheck') >= 0) {
            row = this.evidenceFromSummaryItem(this.findSummaryEvidenceItem(sourceId, label, ['interview_response']), '예진 입력');
        } else if (sourceType.indexOf('patient.profile_summary') >= 0 || sourceType === 'patient_summary') {
            row = this.evidenceFromSummaryItem(this.findSummaryEvidenceItem(sourceId, label, ['patient.profile_summary']), '환자 요약');
        }
        if (row && refLabel) row.source_ref = refLabel;
        if (row) {
            const debugRef = this.evidenceRefDebugLabel(ref);
            if (debugRef) row.debug_ref = this.dedupeStrings([row.debug_ref || '', debugRef]).join(' · ');
        }
        return row;
    }

    private findSummaryEvidenceItem(sourceId: string, label: string, allowedSources: string[] = []) {
        const normalizedLabel = this.normalizeLookupLabel(label);
        const allowed = allowedSources.map((item) => String(item || '').toLowerCase());
        const sections = this.summarySections.filter((item) => {
            if (!item) return false;
            const source = String(item.source || '').toLowerCase();
            if (allowed.length && allowed.indexOf(source) === -1) return false;
            return source === 'interview_response' || source === 'patient.profile_summary';
        });

        if (sourceId) {
            for (const item of sections) {
                const ids = [item.response_id, item.id, item.key, item.code].map((value) => String(value || '').trim());
                if (ids.indexOf(sourceId) >= 0) return item;
            }
        }
        if (normalizedLabel) {
            const priority = allowed.length ? allowed : ['interview_response', 'patient.profile_summary'];
            for (const sourceName of priority) {
                for (const item of sections) {
                    if (String(item.source || '').toLowerCase() !== sourceName) continue;
                    const itemLabel = this.normalizeLookupLabel(item.label || '');
                    if (itemLabel && (itemLabel === normalizedLabel || itemLabel.indexOf(normalizedLabel) >= 0 || normalizedLabel.indexOf(itemLabel) >= 0)) {
                        return item;
                    }
                }
            }
        }
        return null;
    }

    private evidenceFromSummaryItem(item: any, category: string = ''): EvidenceSourceItem | null {
        if (!item) return null;
        const source = String(item.source || '').toLowerCase();
        if (source !== 'interview_response' && source !== 'patient.profile_summary') return null;
        const value = this.displayValue(item.display_text || item.text || item.value, '');
        if (!value) return null;
        const sourceLabel = category || (source === 'interview_response' ? '예진 입력' : '환자 요약');
        const label = this.clinicalSourceLabel(item.label || item.key || item.code, '항목');
        return {
            category: sourceLabel,
            label,
            value,
            source_ref: `${sourceLabel} · ${label}`,
        };
    }

    private findChecklistEvidenceItem(sourceId: string, label: string) {
        const normalizedLabel = this.normalizeLookupLabel(label);
        if (sourceId) {
            for (const item of this.checklistItems) {
                const ids = [item && item.id, item && item.response_id, item && item.code].map((value) => String(value || '').trim());
                if (ids.indexOf(sourceId) >= 0) return item;
            }
        }
        if (normalizedLabel) {
            for (const item of this.checklistItems) {
                const itemLabel = this.normalizeLookupLabel(item && item.label);
                if (itemLabel && (itemLabel === normalizedLabel || itemLabel.indexOf(normalizedLabel) >= 0 || normalizedLabel.indexOf(itemLabel) >= 0)) {
                    return item;
                }
            }
        }
        return null;
    }

    private evidenceFromChecklistItem(item: any, fallbackLabel: string = ''): EvidenceSourceItem | null {
        if (!item) return null;
        const label = this.clinicalSourceLabel(item.label || fallbackLabel || item.code, '항목');
        if (!label) return null;
        const status = this.statusLabel(item.status || 'unchecked');
        const answer = this.displayValue(item.display_answer || item.answer, '');
        const note = this.displayValue(item.note, '');
        const value = [status, answer, note].filter((part, index, parts) => part && parts.indexOf(part) === index).join(' · ');
        return {
            category: '진료 전 확인',
            label,
            value: value || status || '-',
            note: this.displayValue(item.prompt, ''),
            source_ref: `진료 전 확인 · ${label}`,
        };
    }

    private inferEvidenceForAiResult(result: AiAssistantResult): EvidenceSourceItem[] {
        const sourceText = this.normalizeLookupLabel([result.type, result.title, result.summary, (result.sources || []).join(' ')].join(' '));
        let rows: EvidenceSourceItem[] = [];
        if (sourceText.indexOf('예진') >= 0 || sourceText.indexOf('요약') >= 0 || sourceText.indexOf('환자') >= 0 || result.type === '직접 요청') {
            rows = rows.concat(this.summaryEvidenceByLabels(['주호소', '증상 상세', '핵심 메모', '메모', '복약', '알레르기']));
        }
        if (sourceText.indexOf('체크리스트') >= 0 || sourceText.indexOf('체크') >= 0 || sourceText.indexOf('레드플래그') >= 0) {
            rows = rows.concat(this.checklistEvidenceRows(4));
        }
        if (!rows.length) rows = this.summaryEvidenceByLabels(['주호소', '증상 상세', '핵심 메모']).concat(this.checklistEvidenceRows(3));
        return this.dedupeEvidenceRows(rows);
    }

    private inferEvidenceForAiItem(item: AiAssistantResultItem): EvidenceSourceItem[] {
        let rows: EvidenceSourceItem[] = [];
        if (item.checklist_item_code) {
            const checklistItem = this.findChecklistEvidenceItem(item.checklist_item_code, item.checklist_item_label || '');
            const evidence = this.evidenceFromChecklistItem(checklistItem, item.checklist_item_label || '');
            if (evidence) rows.push(evidence);
        }

        const text = this.normalizeLookupLabel([
            item.title,
            item.display_name,
            item.description,
            item.source,
            item.checklist_item_label,
            item.answer_target,
        ].filter((value) => value).join(' '));

        rows = rows.concat(this.summaryEvidenceByText(text));
        if (text.indexOf('체크리스트') >= 0 || text.indexOf('레드플래그') >= 0 || text.indexOf('안전확인') >= 0) {
            const matched = this.checklistEvidenceByText(text);
            if (matched) rows.push(matched);
        }
        if (!rows.length && (text.indexOf('예진') >= 0 || text.indexOf('환자요약') >= 0 || text.indexOf('요약') >= 0)) {
            rows = rows.concat(this.summaryEvidenceByLabels(['주호소', '증상 상세', '핵심 메모']).slice(0, 3));
        }
        return this.dedupeEvidenceRows(rows);
    }

    private summaryEvidenceByText(text: string): EvidenceSourceItem[] {
        const matches = [
            { labels: ['주호소'], tokens: ['주호소', '오늘가장불편', '증상'] },
            { labels: ['증상 상세', '증상상세'], tokens: ['증상상세', '양상', '시작', '경과'] },
            { labels: ['과거력'], tokens: ['과거력', '진단', '수술', '외상', '만성질환'] },
            { labels: ['복약', '복용약'], tokens: ['복약', '복용약', '약', '진통소염제', '항응고제'] },
            { labels: ['알레르기'], tokens: ['알레르기', '이상반응'] },
            { labels: ['핵심 메모', '메모'], tokens: ['핵심메모', '메모'] },
        ];
        let rows: EvidenceSourceItem[] = [];
        matches.forEach((entry) => {
            if (!entry.tokens.some((token) => text.indexOf(this.normalizeLookupLabel(token)) >= 0)) return;
            rows = rows.concat(this.summaryEvidenceByLabels(entry.labels));
        });
        return this.dedupeEvidenceRows(rows);
    }

    private summaryEvidenceByLabels(labels: string[]): EvidenceSourceItem[] {
        const rows: EvidenceSourceItem[] = [];
        (labels || []).forEach((label) => {
            const item = this.findSummaryEvidenceItem('', label, ['interview_response', 'patient.profile_summary']);
            const evidence = this.evidenceFromSummaryItem(item);
            if (evidence) rows.push(evidence);
        });
        return this.dedupeEvidenceRows(rows);
    }

    private checklistEvidenceByText(text: string): EvidenceSourceItem | null {
        for (const item of this.checklistItems) {
            const itemText = this.normalizeLookupLabel([item && item.label, item && item.prompt, item && item.risk_badge].filter((value) => value).join(' '));
            if (itemText && (text.indexOf(itemText) >= 0 || itemText.indexOf(text) >= 0)) {
                return this.evidenceFromChecklistItem(item);
            }
        }
        return null;
    }

    private checklistEvidenceRows(limit: number = 4): EvidenceSourceItem[] {
        const rows: EvidenceSourceItem[] = [];
        const prioritized = this.checklistItems.filter((item) => {
            const status = String(item && item.status || 'unchecked');
            return item && (status !== 'unchecked' || item.note || item.display_answer || item.safety_level || item.requires_resolution);
        });
        const items = prioritized.length ? prioritized : this.checklistItems;
        items.slice(0, limit).forEach((item) => {
            const evidence = this.evidenceFromChecklistItem(item);
            if (evidence) rows.push(evidence);
        });
        return this.dedupeEvidenceRows(rows);
    }

    private dedupeEvidenceRows(rows: EvidenceSourceItem[]): EvidenceSourceItem[] {
        const result: EvidenceSourceItem[] = [];
        const seen: Record<string, boolean> = {};
        (rows || []).forEach((row) => {
            if (!row || !row.label || !row.value) return;
            const key = [row.category, row.label, row.value, row.note || ''].join('|');
            if (seen[key]) return;
            seen[key] = true;
            result.push(row);
        });
        return result;
    }

    private createAiAssistantResult(actionCode: string, requestText: string): AiAssistantResult {
        if (actionCode === 'followup_questions') return this.createAiQuestionResult(requestText);
        if (actionCode === 'pattern_candidates') return this.createAiPatternFallbackResult(requestText);
        if (actionCode === 'red_flags') return this.createAiRedFlagResult(requestText);
        if (actionCode === 'soap_improvement') return this.createAiSoapResult(requestText);
        if (actionCode === 'missing_review') return this.createAiMissingReviewResult(requestText);
        return this.createAiPrecheckSummaryResult(requestText);
    }

    private async generateFollowupQuestionCandidates(requestText: string = '', directRequest: boolean = false) {
        if (!this.canRunAiAssistant) return;
        this.aiAssistantGenerating = true;
        this.aiAssistantMessage = '';
        this.error = '';
        this.soapPanelMode = 'assistant';
        this.mobilePanel = 'assistant';
        this.activeWorkspaceDrawer = 'assistant';
        await this.service.render();

        const result = await this.callApi('recommend_followup_questions', {
            encounter_id: this.encounterId,
            request_text: requestText || '',
            max_questions: '6',
        });
        this.aiAssistantGenerating = false;

        if (result.ok) {
            const payload = result.data || {};
            const recommendation = payload.recommendation || payload.result || payload;
	            const card = this.followupRecommendationToResult(recommendation, requestText);
	            if (directRequest) {
	                card.title = '직접 요청 문진';
	                card.summary = `직접 요청 · ${card.summary}`;
	                this.aiAssistantInput = '';
	            }
		            this.prependAiAssistantResult(card);
	            this.aiAssistantMessage = '문진 준비됨.';
	        } else {
	            this.aiAssistantMessage = result.message || '문진 준비 실패.';
	        }
        await this.service.render();
    }

    private async generatePatternCandidateCards(requestText: string = '', directRequest: boolean = false) {
        if (!this.canRunAiAssistant) return;
        this.aiAssistantGenerating = true;
        this.aiAssistantMessage = '';
        this.error = '';
        this.soapPanelMode = 'assistant';
        this.mobilePanel = 'assistant';
        this.activeWorkspaceDrawer = 'assistant';
        await this.service.render();

        const result = await this.callApi('suggest_pattern_candidates', {
            encounter_id: this.encounterId,
            request_text: requestText || '',
            max_candidates: '3',
        });
        this.aiAssistantGenerating = false;

        if (result.ok) {
            const payload = result.data || {};
            const recommendation = payload.recommendation || payload.result || payload;
	            const card = this.patternRecommendationToResult(recommendation, requestText);
	            if (directRequest) {
	                card.title = '직접 요청 변증';
	                card.summary = `직접 요청 · ${card.summary}`;
	                this.aiAssistantInput = '';
	            }
		            this.prependAiAssistantResult(card);
	            this.aiAssistantMessage = '변증/진단 준비됨.';
	        } else {
	            this.aiAssistantMessage = result.message || '변증/진단 준비 실패.';
        }
        await this.service.render();
    }

    private async generateStandardToolCards(requestText: string = '', directRequest: boolean = false) {
        if (!this.canRunAiAssistant) return;
        this.aiAssistantGenerating = true;
        this.aiAssistantMessage = '';
        this.error = '';
        this.soapPanelMode = 'assistant';
        this.mobilePanel = 'assistant';
        this.activeWorkspaceDrawer = 'assistant';
        await this.service.render();

        const result = await this.callApi('recommend_standard_tools', {
            encounter_id: this.encounterId,
            request_text: requestText || '',
        });
        this.aiAssistantGenerating = false;

        if (result.ok) {
            const payload = result.data || {};
            const recommendation = payload.recommendation || payload.result || payload;
            this.standardTools = recommendation || {};
            this.referenceComparison = recommendation && recommendation.comparison ? recommendation.comparison : this.referenceComparison;
            const card = this.standardToolRecommendationToResult(recommendation, requestText);
            if (directRequest) {
                card.title = '직접 요청 표준도구';
                card.summary = `직접 요청 · ${card.summary}`;
                this.aiAssistantInput = '';
            }
            this.prependAiAssistantResult(card);
            this.aiAssistantMessage = '표준도구 준비됨.';
        } else {
            this.aiAssistantMessage = result.message || '표준도구 추천 실패.';
        }
        await this.service.render();
    }

    private async generateReferenceComparisonCard(requestText: string = '', directRequest: boolean = false) {
        if (!this.canRunAiAssistant) return;
        this.aiAssistantGenerating = true;
        this.aiAssistantMessage = '';
        this.error = '';
        this.soapPanelMode = 'assistant';
        this.mobilePanel = 'assistant';
        this.activeWorkspaceDrawer = 'assistant';
        await this.service.render();

        const result = await this.callApi('explain_reference_comparison', {
            encounter_id: this.encounterId,
            request_text: requestText || '',
        });
        this.aiAssistantGenerating = false;

        if (result.ok) {
            const payload = result.data || {};
            const recommendation = payload.recommendation || payload.result || payload;
            const overview = recommendation && recommendation.overview ? recommendation.overview : {};
            if (overview && overview.status) {
                this.standardTools = overview;
                this.referenceComparison = overview.comparison || this.referenceComparison;
            }
            const card = this.referenceComparisonToResult(recommendation, requestText);
            if (directRequest) {
                card.title = '직접 요청 참조 비교';
                card.summary = `직접 요청 · ${card.summary}`;
                this.aiAssistantInput = '';
            }
            this.prependAiAssistantResult(card);
            this.aiAssistantMessage = '참조 비교 준비됨.';
        } else {
            this.aiAssistantMessage = result.message || '참조 비교 설명 실패.';
        }
        await this.service.render();
    }

    public async startStandardTool(tool: WorkspaceMap) {
        const toolCode = tool && tool.tool_code ? String(tool.tool_code) : '';
        if (!toolCode || this.clinicalReadOnly) return;
        this.standardToolStartingCode = toolCode;
        this.error = '';
        await this.service.render();
        const result = await this.callApi('start_standard_tool_session', {
            encounter_id: this.encounterId,
            tool_code: toolCode,
            prefill_defaults: 'true',
        });
        this.standardToolStartingCode = '';
        if (result.ok) {
            const context = result.data && result.data.context ? result.data.context : null;
            if (context) this.applyContext(context);
            this.aiAssistantMessage = `${tool.tool_name || '표준도구'} 점수와 참조 비교가 준비됨.`;
        } else {
            this.error = result.message || '표준도구를 시작하지 못했습니다.';
        }
        await this.service.render();
    }

    private standardToolRecommendationToResult(recommendation: WorkspaceMap, requestText: string): AiAssistantResult {
        const tools = this.asArray(recommendation && recommendation.recommended_tools);
        const previews = this.asArray(recommendation && recommendation.previews);
        const warnings = this.asArray(recommendation && recommendation.warnings);
        const items: AiAssistantResultItem[] = tools.map((tool) => {
            const preview = previews.find((item) => item && item.tool_code === tool.tool_code) || {};
            const score = preview.score || {};
            const comparison = preview.comparison || {};
            return {
                kind: 'standard_tool',
                title: tool.tool_name || tool.tool_code || '표준도구',
                description: [
                    tool.description || '',
                    score.metric_value !== undefined ? `${score.metric_name || tool.metric_name}: ${score.metric_value}${score.unit || tool.unit || ''}` : '',
                    comparison.interpretation_label || '',
                ].filter((value) => value !== '').join(' · '),
                source: '표준도구 카탈로그',
                evidence_summary: tool.recommendation_reason || tool.description || '',
                soap_section: 'O',
                soap_sentence_text: `${tool.tool_name || '표준도구'} 확인: ${tool.recommendation_reason || tool.description || '표준도구 확인'}`,
                source_refs: this.asArray(tool.source_refs),
                answer_target: '표준도구 실시 / 진찰 기록',
                decision_status: 'pending_doctor_review',
            };
        });
        return this.aiAssistantResult(
            '표준도구 확인',
            '표준도구 확인',
            warnings.length ? 'warning' : 'ai',
            `표준도구 ${tools.length}건`,
            items,
            ['예진 요약', '진료 전 확인', '표준도구 카탈로그'],
            warnings.map((item) => item.message || item.code || '확인만 하면 되는 내용').filter((item) => item !== ''),
            requestText
        );
    }

    private referenceComparisonToResult(recommendation: WorkspaceMap, requestText: string): AiAssistantResult {
        const items = this.asArray(recommendation && recommendation.items).map((item) => ({
            kind: 'reference_comparison',
            title: item.title || item.metric_name || item.metric_code || '참조 비교',
            description: item.description || item.interpretation_label || '',
            source: item.source || item.dataset_label || '건강인 참조 데이터셋',
            evidence_summary: item.evidence_summary || `${item.dataset_version || ''} · 평균 ${this.displayValue(item.reference_mean, '-')}`,
            soap_section: item.soap_section || 'O',
            soap_sentence_text: item.soap_sentence_text || `${item.metric_name || '참조 지표'} ${item.patient_value}${item.unit || ''}: ${item.interpretation_label || ''}`,
            source_refs: this.asArray(item.source_refs),
            answer_target: item.answer_target || '진찰/변증',
            decision_status: 'pending_doctor_review',
        }));
        return this.aiAssistantResult(
            '참조 비교',
            recommendation && recommendation.title ? String(recommendation.title) : '건강인 참조 비교',
            items.some((item) => String(item.description || '').indexOf('20%') >= 0) ? 'warning' : 'ai',
            (recommendation && recommendation.summary) || this.referenceComparisonSummary,
            items,
            this.asArray(recommendation && recommendation.sources).length ? this.asArray(recommendation.sources) : ['표준도구 점수', '건강인 참조 데이터셋'],
            this.asArray(recommendation && recommendation.uncertainty).map((item) => this.displayValue(item, '')).filter((item) => item !== ''),
            requestText
        );
    }

    private patternRecommendationToResult(recommendation: WorkspaceMap, requestText: string): AiAssistantResult {
        const candidates = this.asArray(recommendation && recommendation.candidates);
        const items = candidates.map((item) => this.patternCandidateToItem(item)).filter((item) => item.title !== '');
        const sources = this.asArray(recommendation && recommendation.sources).map((item) => this.displayValue(item, '')).filter((item) => item !== '');
        const uncertainty = this.asArray(recommendation && recommendation.uncertainty).map((item) => this.displayValue(item, '')).filter((item) => item !== '');
        return {
            id: (recommendation && recommendation.audit && recommendation.audit.event_id) || `변증-${Date.now()}-${this.aiAssistantResults.length}`,
            type: '변증/진단',
	            title: '변증/진단',
	            tone: items.length ? 'ai' : 'warning',
	            status: 'pending_doctor_review',
            summary: (recommendation && recommendation.summary) || '예진·체크 기준',
            items,
            sources: sources.length ? sources : ['예진 요약', '진료 전 확인 상태', '환자 요약'],
	            uncertainty: uncertainty.length ? uncertainty : ['한의사 판단 전'],
            createdAt: this.timeLabel(),
            requestText: requestText || '',
        };
    }

    private patternCandidateToItem(item: WorkspaceMap): AiAssistantResultItem {
        const title = this.displayValue(item && (item.display_name || item.title), '');
        const support = this.asArray(item && item.supporting_evidence).map((value) => this.displayValue(value, '')).filter((value) => value !== '');
        const opposing = this.asArray(item && item.opposing_evidence).map((value) => this.displayValue(value, '')).filter((value) => value !== '');
        const questions = this.asArray(item && item.followup_questions).map((value) => this.displayValue(value, '')).filter((value) => value !== '');
        const sourceLabels = this.asArray(item && item.source_labels).map((value) => this.clinicalRecordDisplayText(value)).filter((value) => value !== '');
        return {
            kind: 'pattern_candidate',
            title,
            display_name: title,
	            description: this.clinicalRecordDisplayText(item && (item.description || item.evidence_summary)) || '검토',
            source: this.clinicalRecordDisplayText(item && item.source) || '예진·체크',
            pattern_code: item && item.pattern_code ? String(item.pattern_code) : '',
	            confidence_label: this.patternCandidateConfidenceText(item && item.confidence_label),
            confidence_score: Number(item && item.confidence_score ? item.confidence_score : 0),
            priority_state: item && item.priority_state ? String(item.priority_state) : '',
            evidence_summary: this.clinicalRecordDisplayText(item && item.evidence_summary),
            supporting_evidence: support,
            opposing_evidence: opposing,
            followup_questions: questions,
            doctor_judgement_note: this.displayValue(item && item.doctor_judgement_note, ''),
            source_labels: sourceLabels,
            source_refs: item && Array.isArray(item.source_refs) ? item.source_refs : [],
            answer_target: this.clinicalRecordDisplayText(item && item.answer_target) || '변증/진단',
            soap_section: item && item.soap_section ? String(item.soap_section) : 'A',
            soap_sentence_text: item && item.soap_sentence_text ? String(item.soap_sentence_text) : '',
            soap_candidates: item && Array.isArray(item.soap_candidates) ? item.soap_candidates : [],
            decision_status: 'pending_doctor_review',
            applied_to_soap: false,
        };
    }

    private followupRecommendationToResult(recommendation: WorkspaceMap, requestText: string): AiAssistantResult {
        const questions = this.asArray(recommendation && recommendation.questions);
        const items = questions.map((item) => this.followupQuestionToItem(item)).filter((item) => item.title !== '');
        const sources = this.asArray(recommendation && recommendation.sources).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== '');
        const uncertainty = this.asArray(recommendation && recommendation.uncertainty).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== '');
        return {
            id: (recommendation && recommendation.audit && recommendation.audit.event_id) || `추가 문진-${Date.now()}-${this.aiAssistantResults.length}`,
            type: '추가 문진',
	            title: '추가 문진',
            tone: items.length ? 'warning' : 'ai',
            status: 'pending_doctor_review',
	            summary: this.clinicalRecordDisplayText(recommendation && recommendation.summary) || '예진·체크 기준',
            items,
            sources: sources.length ? sources : ['예진 요약', '진료 전 확인 상태'],
	            uncertainty: uncertainty.length ? uncertainty : ['한의사 판단 전'],
            createdAt: this.timeLabel(),
            requestText: requestText || '',
        };
    }

    private followupQuestionToItem(item: WorkspaceMap): AiAssistantResultItem {
        const question = this.displayValue(item && (item.question || item.title), '');
        const reason = this.clinicalRecordDisplayText(item && (item.reason || item.description));
        const target = this.clinicalRecordDisplayText(item && item.answer_target);
	        const description = [reason, target ? `위치: ${target}` : ''].filter((value) => value !== '').join(' ');
        return {
            title: question,
            description,
            source: this.clinicalRecordDisplayText(item && (item.related_source || item.source)) || '예진·진료 전 확인',
            question,
            reason,
            checklist_item_code: item && item.checklist_item_code ? String(item.checklist_item_code) : '',
            checklist_item_label: item && item.checklist_item_label ? String(item.checklist_item_label) : '',
            checklist_note_text: item && item.checklist_note_text ? String(item.checklist_note_text) : '',
            soap_section: item && item.soap_section ? String(item.soap_section) : 'S',
            soap_sentence_text: item && item.soap_sentence_text ? String(item.soap_sentence_text) : '',
            source_refs: item && Array.isArray(item.source_refs) ? item.source_refs : [],
            patient_cues: item && Array.isArray(item.patient_cues) ? item.patient_cues : [],
            connection_reasons: item && Array.isArray(item.connection_reasons) ? item.connection_reasons : [],
            answer_target: target,
            applied_to_checklist: false,
            applied_to_soap: false,
        };
    }

    private questionMemoText(item: AiAssistantResultItem) {
        const question = item.question || item.title || '';
        const reason = item.reason || item.description || '';
        return ['[추가 문진] ' + question, reason ? '사유: ' + reason : ''].filter((value) => value !== '').join('\n');
    }

    private questionSoapText(item: AiAssistantResultItem) {
        let question = String(item.question || item.title || '').trim().replace(/[?？.。]+$/g, '');
        ['있나요', '있습니까', '인가요', '입니까', '나요', '까요'].forEach((ending) => {
            if (question.endsWith(ending)) question = question.slice(0, -ending.length).trim();
        });
        if (!question) return '';
        return `${question} 확인 예정`;
    }

    private aiItemSoapCandidateText(result: AiAssistantResult, item: AiAssistantResultItem) {
        if (!item) return '';
	        if (this.isPatternCandidateResult(result)) return this.patternAssessmentCandidateText(item);
        if (item.soap_sentence_text) return item.soap_sentence_text;
        if (this.isFollowupQuestionResult(result)) return this.questionSoapText(item);
        if (result && this.clinicalRecordDisplayText(result.type) === '진료 전 주의') {
            return this.joinSentenceParts([
                `안전 확인: ${item.title || '주의 항목'}`,
                item.description || '',
            ]);
        }
        return this.joinSentenceParts([item.title || '', item.description || '']);
    }

    private patternCandidateSoapCandidates(item: AiAssistantResultItem) {
        if (!item) return [];
        const existing = this.asArray(item.soap_candidates)
            .filter((candidate) => candidate && typeof candidate === 'object')
            .map((candidate) => this.normalizePatternSoapCandidate(item, candidate));
        const bySection: Record<string, WorkspaceMap> = {};
        existing.forEach((candidate) => {
            const section = this.normalizedSoapSection(candidate.soap_section || candidate.section || 'A');
            const text = String(candidate.text || candidate.soap_sentence_text || '').trim();
            if (!text || bySection[section]) return;
            bySection[section] = { ...candidate, soap_section: section, section, text, soap_sentence_text: text };
        });
        if (!bySection.A) bySection.A = this.normalizePatternSoapCandidate(item, {
            soap_section: 'A',
            title: '변증/진단',
            description: '한의사 최종 판단 전 참고',
            text: this.patternAssessmentCandidateText(item),
            doctor_decision_reason: 'pattern_candidate_assessment_review',
            answer_target: '변증/진단',
        });
        return ['A'].map((section) => bySection[section]).filter((candidate) => !!candidate);
    }

    private normalizePatternSoapCandidate(item: AiAssistantResultItem, candidate: WorkspaceMap) {
        const section = this.normalizedSoapSection(candidate.soap_section || candidate.section || 'A');
        const rawText = String(candidate.text || candidate.soap_sentence_text || '').trim();
        const text = section === 'A' ? this.patternAssessmentCandidateText(item) : rawText;
        const title = candidate.title || (section === 'P' ? '치료/처방' : '변증/진단');
        return {
            ...candidate,
            client_candidate_id: candidate.client_candidate_id || `pattern::${item.pattern_code || item.title || Date.now()}::${section}`,
            soap_section: section,
            section,
            text,
            soap_sentence_text: text,
            title,
            source: 'pattern_candidate',
            generated_by: candidate.generated_by || 'pattern_candidate',
            author_type: 'pattern_candidate',
            status: candidate.status || 'ai_unreviewed',
            candidate_status: candidate.candidate_status || 'pending_doctor_review',
            doctor_decision_status: candidate.doctor_decision_status || 'pending_doctor_review',
            needs_update: this.soapNeedsRegeneration === true || candidate.needs_update === true,
            source_refs: Array.isArray(candidate.source_refs) ? candidate.source_refs : (Array.isArray(item.source_refs) ? item.source_refs : []),
            doctor_decision_reason: candidate.doctor_decision_reason || (section === 'P' ? 'pattern_candidate_plan_review' : 'pattern_candidate_assessment_review'),
            pattern_code: candidate.pattern_code || item.pattern_code || '',
            display_name: candidate.display_name || item.display_name || item.title || '',
            source_payload: this.patternCandidateSourcePayload(item, candidate.source_payload),
        };
    }

    private patternCandidateConfidenceText(value: any) {
        const text = this.displayValue(value, '자료 부합도 낮음');
        return text
            .replace(/참고\s*확신도/g, '자료 부합도')
            .replace(/확신도/g, '자료 부합도');
    }

    private patternCandidateSourcePayload(item: AiAssistantResultItem, existing?: any) {
        const payload = existing && typeof existing === 'object' ? { ...existing } : {};
        return {
            ...payload,
            pattern_code: payload.pattern_code || item.pattern_code || '',
            display_name: payload.display_name || item.display_name || item.title || '',
            confidence_label: payload.confidence_label || item.confidence_label || '',
            confidence_score: payload.confidence_score !== undefined ? payload.confidence_score : (item.confidence_score || 0),
            evidence_summary: payload.evidence_summary || item.evidence_summary || item.description || '',
            supporting_evidence: this.asArray(payload.supporting_evidence).length ? this.asArray(payload.supporting_evidence) : this.asArray(item.supporting_evidence),
            opposing_evidence: this.asArray(payload.opposing_evidence).length ? this.asArray(payload.opposing_evidence) : this.asArray(item.opposing_evidence),
            followup_questions: this.asArray(payload.followup_questions).length ? this.asArray(payload.followup_questions) : this.asArray(item.followup_questions),
            patient_cues: this.asArray(payload.patient_cues).length ? this.asArray(payload.patient_cues) : this.asArray(item.patient_cues),
            connection_reasons: this.asArray(payload.connection_reasons).length ? this.asArray(payload.connection_reasons) : this.asArray(item.connection_reasons),
            source_labels: this.asArray(payload.source_labels).length ? this.asArray(payload.source_labels) : this.asArray(item.source_labels),
            doctor_judgement_note: payload.doctor_judgement_note || item.doctor_judgement_note || '',
            review_candidate: true,
            final_diagnosis: false,
            auto_prescription: false,
        };
    }

    private patternPayloadFromSoapSentence(item: SoapSentenceDraft) {
        const meta = item && item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
        const supportingEvidence = this.asArray(payload.supporting_evidence).length
            ? this.asArray(payload.supporting_evidence)
            : this.asArray(payload.patient_cues || meta.patient_cues);
        return {
            ...payload,
            item_kind: payload.item_kind || payload.kind || meta.item_kind || '',
            pattern_code: payload.pattern_code || meta.pattern_code || '',
            display_name: payload.display_name || meta.display_name || payload.clinical_summary || meta.clinical_summary || '',
            clinical_summary: payload.clinical_summary || meta.clinical_summary || '',
            clinical_evidence: payload.clinical_evidence || meta.clinical_evidence || '',
            evidence_summary: payload.evidence_summary || meta.evidence_summary || payload.clinical_evidence || meta.clinical_evidence || '',
            supporting_evidence: supportingEvidence,
            opposing_evidence: this.asArray(payload.opposing_evidence || meta.opposing_evidence),
            followup_questions: this.asArray(payload.followup_questions || meta.followup_questions),
            patient_cues: this.asArray(payload.patient_cues || meta.patient_cues),
            connection_reasons: this.asArray(payload.connection_reasons || meta.connection_reasons),
            source_labels: this.asArray(payload.source_labels || meta.source_labels),
            doctor_judgement_note: payload.doctor_judgement_note || meta.doctor_judgement_note || '',
        };
    }

    private patternCandidateKeyEvidenceText(item: AiAssistantResultItem, limit: number = 2) {
        if (!item) return '지지 근거를 더 확인해 주세요';
        const rows = this.patternCandidateTextRows(item.supporting_evidence, limit);
        if (rows.length) return rows.join(' / ');
        const summary = this.displayValue(item.evidence_summary || item.description, '');
        return summary ? this.truncateText(this.clinicalRecordDisplayText(summary), 100) : '지지 근거를 더 확인해 주세요';
    }

    private patternCandidateChartSentence(item: AiAssistantResultItem) {
        const name = item && (item.display_name || item.title) ? (item.display_name || item.title) : '변증/진단 가능성';
        const evidence = this.patternCandidateKeyEvidenceText(item, 2);
        const opposing = this.patternCandidateTextRows(item && item.opposing_evidence, 2);
        const followup = this.patternCandidateTextRows(item && item.followup_questions, 2);
        const note = this.displayValue(item && item.doctor_judgement_note, '');
        return this.joinSentenceParts([
            `${name} 가능성을 우선 감별 중`,
            evidence && evidence !== '지지 근거를 더 확인해 주세요' ? `지지 근거로 ${evidence} 확인됨` : '지지 근거는 문진·진찰로 추가 확인 예정',
            opposing.length ? `반대 또는 불확실 근거로 ${opposing.join(' / ')} 기록됨` : '',
            followup.length ? `추가 확인 항목은 ${followup.join(' / ')}임` : '',
            note ? `한의사 판단 메모로 ${note} 기록됨` : '',
        ]);
    }

    private patternAssessmentCandidateText(item: AiAssistantResultItem) {
        return this.patternCandidateChartSentence(item);
    }

    private patternPlanCandidateText(item: AiAssistantResultItem) {
        const name = item.display_name || item.title || '변증/진단 가능성';
        const reviewPoints = this.dedupeStrings(this.asArray(item.opposing_evidence).concat(this.asArray(item.followup_questions)))
            .slice(0, 3)
            .map((value) => this.displayValue(value, ''))
            .filter((value) => value !== '');
        return this.joinSentenceParts([
            `${name} 양상 기준으로 치료 계획 수립 예정`,
            reviewPoints.length ? `${reviewPoints.join(' ')} 확인 후 복약·시술 안전성과 생활지도 방향 조정 예정` : '지지 근거와 반대/불확실 근거는 문진·진찰로 확인 예정',
        ]);
    }

    private patternCandidateLinkedSoapSentences(item: AiAssistantResultItem) {
        const code = String((item && item.pattern_code) || '').trim();
        const name = String((item && (item.display_name || item.title)) || '').trim();
        const rows: SoapSentenceDraft[] = [];
        ['A', 'P'].forEach((section) => {
            this.soapItems(section).forEach((sentence) => {
                const meta = sentence && sentence.sentence_meta && typeof sentence.sentence_meta === 'object' ? sentence.sentence_meta : {};
                const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
                const sentenceCode = String(meta.pattern_code || payload.pattern_code || '').trim();
                const sentenceName = String(meta.display_name || payload.display_name || '').trim();
                const source = String(sentence.author_type || sentence.candidate_source || meta.source || '').trim();
                if (code && sentenceCode === code) {
                    rows.push(sentence);
                    return;
                }
                if (!code && name && sentenceName === name && source === 'pattern_candidate') rows.push(sentence);
            });
        });
        return rows;
    }

    private async persistPatternCandidateQueueDecision(item: AiAssistantResultItem, action: 'defer' | 'exclude') {
        const candidates = this.patternCandidateSoapCandidates(item);
        if (!candidates.length) return false;
        const linked = this.patternCandidateLinkedSoapSentences(item);
        const decisionStatus = action === 'defer' ? 'deferred' : 'excluded';
        for (const candidate of candidates) {
            const section = this.normalizedSoapSection(candidate.soap_section || candidate.section || 'A');
            const existing = linked.find((sentence) => this.normalizedSoapSection(sentence.soap_section) === section);
            const saved = await this.applySoapCandidateDecision({
                action,
                sentence_id: existing && existing.persisted ? existing.id : '',
                section,
                text: candidate.text || candidate.soap_sentence_text || '',
                source_refs: candidate.source_refs || [],
                reason: action === 'defer' ? 'pattern_candidate_defer' : 'pattern_candidate_exclude',
                candidate: {
                    ...candidate,
                    candidate_status: decisionStatus,
                    doctor_decision_status: decisionStatus,
                },
            });
            if (!saved) return false;
        }
        return true;
    }

    private orderSoapSections(sections: string[]) {
        return this.soapSectionOrder.filter((section) => (sections || []).indexOf(section) >= 0);
    }

    private checklistSoapCandidateKey(item: WorkspaceMap, section: string) {
        const code = item && (item.code || item.label) ? String(item.code || item.label) : 'checklist';
        return `${code}::${this.normalizedSoapSection(section)}`;
    }

    private resetChecklistSoapCandidateQueue(item: WorkspaceMap) {
        const code = item && (item.code || item.label) ? String(item.code || item.label) : '';
        if (!code) return;
        const prefix = `${code}::`;
        Object.keys(this.checklistSoapCandidateQueue).forEach((key) => {
            if (key.indexOf(prefix) === 0) delete this.checklistSoapCandidateQueue[key];
        });
    }

    private checklistSoapCandidateText(item: WorkspaceMap, section: string) {
        if (!item) return '';
        const normalized = this.normalizedSoapSection(section);
        const draft = this.checklistDraft(item);
        const statusCode = String((draft && draft.status) || item.status || 'unchecked');
        const note = String((draft && draft.note) || item.display_answer || item.note || '').trim();
        const label = this.clinicalUiText(item.label || item.code || '진료 전 확인 항목');
        const prompt = String(item.prompt || '').trim();
        const hasDecision = statusCode !== 'unchecked';
        if (!hasDecision && !note) return '';

        const status = this.statusLabel(statusCode);
        const noteText = note ? `확인 메모: ${note}` : '';
        const promptText = !note && prompt ? `확인 기준: ${prompt}` : '';
        if (normalized === 'P' || statusCode === 'deferred') {
            return this.joinSentenceParts([
                `${label}은 ${status} 상태입니다`,
                noteText || promptText,
	                '치료 계획 전 확인 예정',
            ]);
        }
        if (normalized === 'O') {
            return this.joinSentenceParts([
                `${label}: ${status}`,
                noteText || promptText,
            ]);
        }
        if (normalized === 'A') {
            return this.joinSentenceParts([
	                `${label} 평가 참고`,
                noteText || promptText || `현재 상태: ${status}`,
            ]);
        }
        return this.joinSentenceParts([
            `${label}: ${status}`,
            noteText || promptText,
        ]);
    }

    private appendNoteOnce(current: string, addition: string, section?: string) {
        const base = String(current || '').trim();
        const next = String(addition || '').trim();
        if (!next) return base;
        if (base.indexOf(next) >= 0) return base;
        if (!section) return base ? `${base}\n\n${next}` : next;
        const normalized = this.normalizedSoapSection(section);
        const lines = base.split(/\n+/).map((line) => this.cleanAppliedChartText(normalized, line)).filter((line) => !!line);
        this.cleanAppliedChartText(normalized, next).split(/\n+/).filter((line) => !!line).forEach((line) => {
            const existingIndex = lines.findIndex((existing) => this.conversationTextSimilarity(existing, line) >= 0.62);
            if (existingIndex >= 0) {
                if (line.length > lines[existingIndex].length + 8) lines[existingIndex] = line;
                return;
            }
            lines.push(line);
        });
        return this.dedupeStrings(lines).join('\n\n').trim();
    }

    private soapCandidatePayloadFromSentence(item: SoapSentenceDraft) {
        const meta = item && item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        return {
            id: item && item.id ? item.id : '',
            client_candidate_id: meta.client_candidate_id || '',
            soap_section: item && item.soap_section ? item.soap_section : 'S',
            text: item && item.text ? item.text : '',
            source: item && item.candidate_source ? item.candidate_source : (meta.source || (item && item.author_type) || ''),
            generated_by: meta.generated_by || (item && item.author_type) || '',
            author_type: item && item.author_type ? item.author_type : '',
            source_refs: item && Array.isArray(item.source_refs) ? item.source_refs : [],
            candidate_status: item && item.candidate_status ? item.candidate_status : (meta.candidate_status || ''),
            doctor_decision_status: item && item.doctor_decision_status ? item.doctor_decision_status : (meta.doctor_decision_status || ''),
            source_ai_request_id: item && item.source_ai_request_id ? item.source_ai_request_id : '',
            sentence_meta: meta,
        };
    }

    private async applySoapCandidateDecision(payload: WorkspaceMap) {
        if (this.clinicalReadOnly) return null;
        const section = this.normalizedSoapSection(payload.section || payload.soap_section || (payload.candidate && payload.candidate.soap_section) || 'S');
        const action = String(payload.action || 'apply');
        const rawText = String(payload.text || (payload.candidate && (payload.candidate.text || payload.candidate.soap_sentence_text)) || '').trim();
        const text = this.shouldCleanAppliedCandidateText(action) ? this.cleanAppliedChartText(section, rawText) : rawText;
        if (action !== 'restore' && action !== 'exclude' && !text) {
            this.error = '반영할 기록 문장이 없습니다.';
            await this.service.render();
            return null;
        }

        const candidatePayload = payload.candidate && typeof payload.candidate === 'object'
            ? {
                ...payload.candidate,
                text,
                soap_sentence_text: text,
                original_candidate_text: rawText !== text ? rawText : (payload.candidate.original_candidate_text || ''),
            }
            : {};

        this.soapSaving = true;
        this.error = '';
        await this.service.render();
        const result = await this.callApiWithTimeout('apply_soap_candidate', {
            encounter_id: this.encounterId,
            draft_id: this.soapDraftId,
            sentence_id: payload.sentence_id || '',
            target_sentence_id: payload.target_sentence_id || '',
            action,
            section,
            text,
            source_refs: JSON.stringify(Array.isArray(payload.source_refs) ? payload.source_refs : []),
            reason: payload.reason || '',
            purpose_code: 'care_support',
            candidate: JSON.stringify(candidatePayload),
        }, this.candidateDecisionTimeoutMs, '차트 반영 결정 응답 시간이 초과되었습니다.');
        this.soapSaving = false;

        if (result.ok) {
            const context = result.data && result.data.context ? result.data.context : null;
            if (context) this.applyContext(context);
            await this.refreshContextAfterMutation();
            this.soapValidation = {};
            await this.service.render();
            return result.data && result.data.result ? result.data.result : result.data;
        }
        if (result.timedOut) {
            await this.applyLocalSoapCandidateDecision(action, section, text);
            return {
                action,
                section,
                text,
                timedOut: true,
                applied: ['apply', 'edit_apply', 'section_apply'].indexOf(action) >= 0,
                excluded: action === 'exclude',
                status: 'local_timeout_applied',
            };
        }

        this.error = result.message || '차트 반영 결정을 저장하지 못했습니다.';
        await this.service.render();
        return null;
    }

    private async applyLocalSoapCandidateDecision(action: string, section: string, text: string) {
        const normalized = this.normalizedSoapSection(section || 'S');
        const shouldApply = ['apply', 'edit_apply', 'section_apply'].indexOf(action) >= 0;
        if (shouldApply && text) {
            const current = this.soapSectionCompactText(normalized).trim();
            await this.setSoapSectionCompactText(normalized, current ? this.appendNoteOnce(current, text, normalized) : text);
            this.soapMessage = '차트에 반영됨.';
        } else if (action === 'restore' && text) {
            const targetKey = this.chartDisplayDedupeKey(text, normalized);
            const next = this.soapSectionCompactText(normalized)
                .split('\n')
                .filter((line) => this.chartDisplayDedupeKey(line, normalized) !== targetKey)
                .join('\n')
                .trim();
            await this.setSoapSectionCompactText(normalized, next);
            this.soapMessage = '차트 반영을 취소했습니다.';
        } else if (action === 'exclude') {
            this.soapMessage = '사용 안 함으로 표시됨.';
        } else if (action === 'defer') {
            this.soapMessage = '나중에 보기로 표시됨.';
        }
        this.error = '';
        this.soapValidation = {};
        this.markSoapCandidateQueueDirty();
        this.focusSoapSectionAfterCandidateAction(normalized);
        await this.service.render();
        this.scheduleBottomBarClearance();
    }

    private shouldCleanAppliedCandidateText(action: string) {
        return ['apply', 'edit_apply', 'section_apply'].indexOf(String(action || 'apply')) >= 0;
    }

    private cleanAppliedChartText(section: string, text: string) {
        const normalized = this.normalizedSoapSection(section);
        const lines = String(text || '')
            .split(/\n+/)
            .map((line) => this.cleanAppliedChartLine(normalized, line))
            .filter((line) => line !== '');
        return this.dedupeStrings(lines).join('\n').trim();
    }

    private cleanAppliedChartLine(section: string, line: string) {
        let value = String(line || '').replace(/\s+/g, ' ').trim();
        if (!value) return '';
        const explicitClinicalLabel = this.soapClinicalItems(section)
            .some((item) => !!this.soapLabeledLineRange(value, item.label));
        if (explicitClinicalLabel) return value;
        const conversationPrefixPattern = /^(대화|문진|소견|진료\s*소견|판단\s*메모|치료\s*메모)\s*[:：]\s*/;
        const shouldReframeConversationCopy = conversationPrefixPattern.test(value);
        const dropPatterns = [
            /^한의사\s*검토\s*후.*반영[.。]?$/,
            /^복약·알레르기·레드플래그\s*확인\s*후\s*한의사\s*판단으로\s*반영[.。]?$/,
            /^한의사가\s*확인\s*후.*결정합니다[.。]?$/,
        ];
        if (dropPatterns.some((pattern) => pattern.test(value))) return '';
        const prefixPatterns = [
            /^((대화|소견|문진|기록)\s*[:：]\s*)?(진료\s*대화\s*확인|통합\s*회귀(?:\s*(?:검증|제외|최종|완료))?|회귀\s*검증)\s*\d{8,}\s*[:：]\s*/i,
            /^((대화|소견|문진|기록)\s*[:：]\s*)?(진료\s*대화\s*확인|통합\s*회귀(?:\s*(?:검증|제외|최종|완료))?|회귀\s*검증)\s*[:：]\s*/i,
            /^차트\s*반영\s*문장\s*[:：]\s*/,
            /^기록\s*후보\s*[:：]\s*/,
            /^(문진|진찰\/소견|변증\/진단|치료\/처방)\s*(기록|차트\s*문장|검토)?\s*후보\s*[:：]\s*/,
            /^진료\s*대화에서\s*확인한\s*(문진\s*단서|진찰\s*소견)\s*[:：]\s*/,
            /^진료\s*중\s*확인한\s*소견\s*[:：]\s*/,
            /^진료\s*대화\s*기준\s*변증\/진단\s*판단\s*메모\s*[:：]\s*/,
            /^치료\/처방\s*검토\s*메모\s*[:：]\s*/,
            /^(대화|문진|소견|진료\s*소견|판단\s*메모|치료\s*메모)\s*[:：]\s*/,
        ];
        prefixPatterns.forEach((pattern) => {
            value = value.replace(pattern, '');
        });
        value = value.replace(/^(.{1,24})\s*검토\s*후보\s*[:：]\s*/, '$1: ');
        value = value.replace(/\s*[.。]?\s*한의사\s*검토\s*후[^.。]*반영[.。]?$/, '');
        value = value.replace(/\s*[.。]?\s*복약·알레르기·레드플래그\s*확인\s*후\s*한의사\s*판단으로\s*반영[.。]?$/, '');
        value = value.replace(/\s*[.。]?\s*한의사가\s*확인\s*후[^.。]*결정합니다[.。]?$/, '');
        if (section === 'A') {
            value = value.replace(/^변증\/진단\s*검토\s*후보\s*[:：]\s*/, '');
            value = value.replace(/감별\s*필요/g, '감별 중');
            value = value.replace(/판단\s*필요/g, '판단 예정');
        }
        if (section === 'P') {
            value = value.replace(/^치료\/처방\s*검토\s*후보\s*[:：]\s*/, '');
            value = value.replace(/중심으로\s*검토함?/g, '중심으로 치료 계획 수립 예정');
            value = value.replace(/기준으로\s*검토함?/g, '기준으로 치료 계획 수립 예정');
            value = value.replace(/검토\s*필요/g, '검토 예정');
        }
        value = value
            .replace(/추가\s*문진\s*필요/g, '추가 문진 예정')
            .replace(/진찰\s*확인\s*필요/g, '진찰 확인 예정')
            .replace(/추가\s*확인\s*필요/g, '추가 확인 예정')
            .replace(/확인\s*필요/g, '확인 예정');
        if (section === 'S' && /^추가\s*확인\s*예정\s*[:：]\s*/.test(value)) {
            const body = value.replace(/^추가\s*확인\s*예정\s*[:：]\s*/, '').replace(/[?？.。]+$/g, '').trim();
            value = body ? `${body} 확인 예정` : '';
        }
        if (shouldReframeConversationCopy) {
            value = this.conversationDirectChartSentence(section, value);
        }
        value = this.polishAppliedChartLine(section, value);
        if (section === 'A') value = this.dedupeAssessmentLineSentences(value);
        return value.trim();
    }

    private dedupeAssessmentLineSentences(text: string) {
        const sentences = String(text || '')
            .split(/(?<=[.!?。])\s+/)
            .map((sentence) => sentence.trim())
            .filter((sentence) => sentence !== '');
        if (sentences.length < 2) return String(text || '').trim();
        const rows: string[] = [];
        const keys: string[] = [];
        sentences.forEach((sentence) => {
            const key = this.assessmentConclusionKey(sentence);
            const duplicateIndex = key ? keys.indexOf(key) : -1;
            if (duplicateIndex < 0) {
                rows.push(sentence);
                keys.push(key);
                return;
            }
            if (this.chartDisplayTextPreferred(sentence, rows[duplicateIndex])) {
                rows[duplicateIndex] = sentence;
            }
        });
        return rows.join(' ');
    }

    private assessmentConclusionKey(sentence: string) {
        const source = String(sentence || '').trim();
        const explicit = /(?:단서를\s*근거로|근거로)\s*(.+?)(?:[.!?。]|$)/.exec(source);
        if (explicit && explicit[1]) return this.normalizeLookupLabel(explicit[1]);
        const conclusion = /([^.?!。]{2,48}?(?:감별\s*중|감별\s*예정|판단\s*예정))(?:[.!?。]|$)/.exec(source);
        return conclusion && conclusion[1] ? this.normalizeLookupLabel(conclusion[1]) : '';
    }

    private polishAppliedChartLine(section: string, line: string) {
        let value = String(line || '').replace(/\s+/g, ' ').trim();
        if (!value) return '';
        value = value
            .replace(/(문진상\s*){2,}/g, '문진상 ')
            .replace(/(진찰상\s*){2,}/g, '진찰상 ')
            .replace(/(추가\s*확인\s*예정[.。]?\s*){2,}/g, '추가 확인 예정. ')
            .replace(/(확인\s*예정[.。]?\s*){2,}/g, '확인 예정. ')
            .replace(/추가\s*확인\s*예정\s*(?:및|,|\/)?\s*확인\s*예정/g, '추가 확인 예정')
            .replace(/확인\s*예정\s*(?:및|,|\/)?\s*추가\s*확인\s*예정/g, '추가 확인 예정')
            .replace(/확인\s*필요/g, '확인 예정')
            .replace(/추가\s*확인\s*필요/g, '추가 확인 예정')
            .replace(/추가\s*문진\s*필요/g, '추가 문진 예정')
            .replace(/감별\s*필요/g, '감별 중')
            .replace(/판단\s*필요/g, '판단 예정')
            .replace(/검토\s*필요/g, '검토 예정')
            .replace(/(\S+)\s*후보\s*가능성/g, '$1 가능성')
            .replace(/(?:기록|차트|한의 차트|검토)?\s*후보/g, '')
            .replace(/예정 예정/g, '예정')
            .replace(/감별 중 중/g, '감별 중')
            .replace(/\s+([,.!?。])/g, '$1')
            .replace(/([.。])\s*([.。])+/g, '$1')
            .trim();
        if (section === 'S') {
            value = value
                .replace(/^문진상\s+(.+?)\s+추가\s*확인\s*예정([.。]?)$/g, '$1 확인 예정$2')
                .replace(/^문진상\s+(.+?)\s+확인\s*예정([.。]?)$/g, '$1 확인 예정$2');
        }
        return value;
    }

    private async appendSoapCandidate(candidate: WorkspaceMap) {
        return this.appendSoapCandidates(candidate ? [candidate] : []);
    }

    private async appendSoapCandidates(candidates: WorkspaceMap[]) {
        if (this.clinicalReadOnly || !Array.isArray(candidates) || !candidates.length) return false;
        const payloads: WorkspaceMap[] = [];
        for (const candidate of candidates) {
            if (!candidate) continue;
            const section = this.normalizedSoapSection(candidate.soap_section || candidate.section || 'S');
            const text = String(candidate.text || candidate.soap_sentence_text || '').trim();
            if (!text) {
                this.error = '저장할 기록 문장이 없습니다.';
                await this.service.render();
                return false;
            }
            payloads.push({
                ...candidate,
                soap_section: section,
                section,
                text,
                soap_sentence_text: text,
                candidate_status: candidate.candidate_status || candidate.decision_status || 'pending_doctor_review',
                doctor_decision_status: candidate.doctor_decision_status || candidate.decision_status || 'pending_doctor_review',
                needs_update: candidate.needs_update === true || this.soapNeedsRegeneration === true,
                source_refs: Array.isArray(candidate.source_refs) ? candidate.source_refs : [],
            });
        }
        if (!payloads.length) return false;

        this.soapCandidateAppending = true;
        this.error = '';
        await this.service.render();
        const result = await this.callApiWithTimeout('append_soap_candidates', {
            encounter_id: this.encounterId,
            draft_id: this.soapDraftId,
            purpose_code: 'care_support',
            candidates: JSON.stringify(payloads),
        }, this.candidateDecisionTimeoutMs, '기록 내용 저장 응답 시간이 초과되었습니다.');
        this.soapCandidateAppending = false;

        if (result.ok) {
            const context = result.data && result.data.context ? result.data.context : result.data;
            if (context) this.applyContext(context);
            await this.refreshContextAfterMutation();
            await this.service.render();
            return true;
        }
        if (result.timedOut) {
            this.soapValidation = {};
            this.markSoapCandidateQueueDirty();
            await this.service.render();
            return true;
        }

        this.error = result.message || '기록 내용을 저장하지 못했습니다.';
        await this.service.render();
        return false;
    }

    private createAiMissingReviewResult(requestText: string): AiAssistantResult {
        const gateItems = this.finalSaveBlockingItems.concat(this.finalSaveWarningItems);
        const standardWarnings: AiAssistantResultItem[] = this.standardToolWarnings.map((item) => ({
            title: item.message || item.code || '표준도구 확인',
            description: this.gateItemSummary(item) || item.message || '확인만 하면 되는 내용',
            source: '표준도구 확인',
            soap_section: 'P',
            soap_sentence_text: `진료 전 확인: ${item.message || item.code || '표준도구 확인'}`,
            source_refs: this.asArray(item.source_refs),
            answer_target: '진료 전 확인 / 치료',
            decision_status: 'pending_doctor_review',
        }));
        const items: AiAssistantResultItem[] = gateItems.map((item) => ({
            title: this.finalSaveItemTitle(item),
            description: this.finalSaveItemSummary(item) || this.statusLabel(item.severity || 'needs_review'),
            source: item.category || '최종 저장 확인',
            soap_section: item.category === 'soap' ? 'A' : 'P',
            soap_sentence_text: `진료 전 확인: ${this.finalSaveItemTitle(item)}`,
            source_refs: this.asArray(item.source_refs),
            answer_target: '최종 저장 확인',
            decision_status: 'pending_doctor_review',
        })).concat(standardWarnings);

        if (!items.length) {
            items.push({
                title: '추가로 확인할 내용 없음',
                description: '저장 가능',
                source: '저장 상태',
                decision_status: 'referenced',
            });
        }

        return this.aiAssistantResult(
            '최종 점검',
            '최종 점검',
            this.finalSaveBlockingItems.length ? 'warning' : (items.length ? 'warning' : 'success'),
            this.finalSaveBlockingItems.length
                ? `더 작성할 내용 ${this.finalSaveBlockingItems.length}건 · 확인만 하면 되는 내용 ${this.finalSaveWarningItems.length}건`
                : `확인만 하면 되는 내용 ${this.finalSaveWarningItems.length + standardWarnings.length}건`,
            items,
            ['최종 저장', '최종 점검', '한의 차트', '표준도구'],
            this.finalSaveBlockingItems.length ? ['더 작성할 내용'] : ['한의사 확인 후 저장 가능'],
            requestText
        );
    }

    private createAiPrecheckSummaryResult(requestText: string): AiAssistantResult {
        const clinicalItems = this.patientClinicalItems.filter((item) => item.value && item.value !== '-');
        let items = clinicalItems.slice(0, 4).map((item) => ({
            title: item.label,
            description: item.value,
            source: '예진/문진',
        }));
        if (!items.length) {
            items = [{
	                title: '현장 문진으로 시작',
	                description: '주호소부터 입력',
                source: '예진/문진',
            }];
        }

        const chief = this.summaryLookup(['chief_complaint'], ['주호소'], '-');
        const submitted = !!(this.precheck && this.precheck.submitted);
	        const uncertainty = submitted
	            ? this.missingClinicalSummaryItems()
	            : ['현장 문진으로 시작'];
	        const summary = chief !== '-'
	            ? `주호소: ${chief}`
	            : '문진 출발점';

        return this.aiAssistantResult(
	            '예진/문진',
	            '예진/문진',
            'ai',
            summary,
            items,
            ['예진/문진', '환자 핵심 요약'],
            uncertainty,
            requestText
        );
    }

    private createAiPatternFallbackResult(requestText: string): AiAssistantResult {
        return this.aiAssistantResult(
	            '변증/진단',
	            '변증/진단',
	            'warning',
	            '자료 부족',
            [{
	                kind: 'pattern_candidate',
	                title: '자료 부족',
	                description: '추가 문진',
	                source: '예진·진료 전 확인',
	                confidence_label: '자료 부합도 낮음',
	                evidence_summary: '지지 근거 부족',
                supporting_evidence: [],
                opposing_evidence: ['지지 근거 부족'],
	                followup_questions: ['주호소, 증상 양상, 악화/완화 확인'],
                decision_status: 'pending_doctor_review',
            }],
	            ['예진', '진료 전 확인'],
	            ['재실행 필요'],
            requestText
        );
    }

    private createAiQuestionResult(requestText: string): AiAssistantResult {
        const pendingItems = this.checklistItems.filter((item) => {
            const status = item && item.status ? String(item.status) : 'unchecked';
            return !this.checklistStatusResolved(status);
        });
	        let items: AiAssistantResultItem[] = pendingItems.slice(0, 5).map((item) => {
            const question = item.prompt || `${item.label || item.code} 확인`;
            return {
                title: `${item.label || item.code} 확인 질문`,
                description: item.prompt
                    ? `${item.prompt} 답변 확인`
                    : '추가 문진 예정',
                source: item.status === 'deferred' ? '진료 전 확인 나중에 보기' : '진료 전 확인 미완료',
                question,
                checklist_item_code: item.code || '',
                checklist_item_label: item.label || item.code || '',
                checklist_note_text: item.prompt ? `[추가 문진] ${item.prompt}` : `[추가 문진] ${item.label || item.code} 확인`,
                soap_section: 'S',
                soap_sentence_text: this.questionSoapText({ question } as AiAssistantResultItem),
                answer_target: '확인 메모 / 문진 기록',
            };
        });

        if (!items.length) {
            items = [
	                {
	                    title: '증상 시작 시점 확인',
	                    description: '시점·경과·악화 확인',
	                    source: '예진 보완',
	                },
	                {
	                    title: '악화·완화 요인 확인',
	                    description: '활동·자세·식사·수면',
	                    source: '예진 보완',
	                },
	                {
	                    title: '복약·알레르기 최신성 확인',
	                    description: '복약·알레르기 재확인',
	                    source: '안전 확인',
	                },
            ];
        }

	        const uncertainty = pendingItems.length
	            ? ['진료 전 확인 대상']
	            : ['일반 문진'];

        return this.aiAssistantResult(
	            '추가 문진',
	            '추가 문진',
	            'warning',
	            '미확인·나중에 보기 기준',
            items,
	            ['예진', '진료 전 확인'],
            uncertainty,
            requestText
        );
    }

    private createAiRedFlagResult(requestText: string): AiAssistantResult {
        const items: AiAssistantResultItem[] = [];
        const unresolvedCodes = this.unresolvedRedFlagItems.map((item) => String(item.code || '')).filter((code) => code !== '');
        this.unresolvedRedFlagItems.forEach((item) => {
	            const details = this.riskItemDetails(item);
	            items.push({
	                title: `${this.clinicalUiText(item.label || item.code || '진료 전 주의')} 확인`,
	                description: [
	                    item.prompt || '최종 저장 전 진료 전 확인',
	                    details.length ? details.join(', ') : '',
	                    this.statusLabel(item.status || 'unchecked'),
	                ].filter((value) => value !== '').join(' '),
                source: this.clinicalUiText(item.risk_badge || '진료 전 확인 주의 항목'),
                checklist_item_code: item.code || '',
                checklist_item_label: item.label || item.code || '',
                checklist_note_text: item.prompt ? `[주의 확인] ${item.prompt}` : `[주의 확인] ${item.label || item.code}`,
                soap_section: 'O',
                soap_sentence_text: `진료 전 주의 확인: ${item.label || item.code || '주의 항목'}`,
                answer_target: '진료 전 확인 / 진찰',
            });
        });
        const allergy = this.summaryLookup(['allergy'], ['알레르기'], '-');
	        if (allergy !== '-' && allergy !== '없음' && allergy !== '무') {
	            items.push({
	                title: '알레르기 확인',
	                description: `${allergy} 재확인`,
	                source: '예진 알레르기',
	            });
	        }

        const medication = this.summaryLookup(['medication', 'medicine'], ['복약', '복용약'], '-');
	        if (medication === '-' || medication === '없음') {
	            items.push({
	                title: '복약 확인',
	                description: '현재 복용약 확인',
	                source: '예진 복약',
	            });
	        }

        this.checklistItems.filter((item) => item && item.safety_level && unresolvedCodes.indexOf(String(item.code || '')) === -1).slice(0, 4).forEach((item) => {
	            items.push({
	                title: item.label || item.code || '안전 확인 항목',
	                description: item.prompt || `${item.safety_level} 안전 확인`,
                source: '진료 전 확인 주의 항목',
                checklist_item_code: item.code || '',
                checklist_item_label: item.label || item.code || '',
                checklist_note_text: item.prompt ? `[안전 확인] ${item.prompt}` : `[안전 확인] ${item.label || item.code}`,
                soap_section: 'O',
                soap_sentence_text: `안전 확인: ${item.label || item.code || '주의 항목'}`,
                answer_target: '진료 전 확인 / 진찰',
            });
        });

	        this.finalGateBlockingItems.slice(0, 4).forEach((item) => {
	            items.push({
	                title: '진료 전 확인',
	                description: this.gateItemTitle(item),
	                source: '저장 상태',
	            });
        });
        this.finalGateConfirmableItems.slice(0, 3).forEach((item) => {
            items.push({
                title: '확인 후 진행',
                description: this.gateItemTitle(item),
                source: '저장 상태',
            });
        });

	        if (!items.length) {
	            items.push({
	                title: '저장 가능',
	                description: '재확인',
                source: '현재 진료 상태',
	            });
	        }

	        const tone = this.hasUnresolvedRedFlags || this.finalGateBlockingItems.length || items.some((item) => item.source === '진료 전 확인 주의 항목') ? 'danger' : 'warning';
	        const uncertainty = this.hasUnresolvedRedFlags
	            ? ['진료 전 주의 확인 전']
	            : ['안전 판단은 한의사 기록'];

        return this.aiAssistantResult(
	            '진료 전 주의',
	            '진료 전 주의 확인',
	            tone,
	            this.hasUnresolvedRedFlags
	                ? '확인할 주의 항목 있음'
	                : '안전 확인',
            items,
            ['예진', '진료 전 확인', '저장 상태'],
            uncertainty,
            requestText
        );
    }

    private createAiSoapResult(requestText: string): AiAssistantResult {
        const items = this.soapSectionOrder
            .map((section) => this.createSoapSectionSupplementCandidate(section))
            .filter((item) => item && item.title !== '');
        const unresolved = this.unresolvedRedFlagItems.length;
        const unreviewed = this.soapSectionOrder.reduce((count, section) => {
            return count + this.soapItems(section).filter((item) => item.status === 'ai_unreviewed').length;
        }, 0);
        const validationCount = this.soapValidationErrors.length + this.soapValidationWarnings.length;
	        const staleText = this.soapNeedsRegeneration ? ` 최신 입력: ${this.soapRegenerationText}.` : '';
	        const uncertainty = this.soapNeedsRegeneration ? ['최신 입력 확인'] : [];
        return this.aiAssistantResult(
	            '한의 차트 보완',
	            '한의 차트 보완',
	            unresolved ? 'danger' : (validationCount || unreviewed || this.soapNeedsRegeneration ? 'pending' : 'ai'),
	            `차트 보완${staleText}`,
	            items,
	            ['한의 차트', '예진', '진료 전 확인', '진료 전 주의'],
            uncertainty,
            requestText
        );
    }

    private createSoapSectionSupplementCandidate(section: string): AiAssistantResultItem {
        const normalized = this.normalizedSoapSection(section);
        const activeItems = this.soapItems(normalized).filter((item) => item.status !== 'excluded' && (item.text || '').trim() !== '');
        const target = activeItems.length ? activeItems[0] : null;
        const targetPreview = target ? this.truncateText(target.text, 96) : '';
        const reasons = this.soapSupplementBaseReasons(normalized, activeItems);
	        let title = `${this.soapSectionDisplayName(normalized)} 보완`;
        let description = '';
        let text = '';

        if (normalized === 'S') {
            const chief = this.summaryLookup(['chief_complaint'], ['주호소'], '');
            const symptom = this.summaryLookup(['symptom_detail'], ['증상 상세', '핵심 메모', '메모'], '');
            if (chief) reasons.push('주호소');
            if (symptom) reasons.push('증상 상세');
            if (activeItems.length && !this.textContainsClinicalSignal(activeItems.map((item) => item.text).join(' '), [chief, symptom])) {
	                reasons.push('문진 보완');
	            }
	            title = activeItems.length ? '문진 보완' : '문진 추가';
	            description = '주호소/양상';
            text = this.joinSentenceParts([
                chief ? `주관적 호소: ${chief}` : '',
	                symptom ? `설명: ${symptom}` : '',
	                !chief && !symptom ? '추가 문진 필요' : '',
            ]);
        }

        if (normalized === 'O') {
            const structuredFinding = this.objectiveFindingComposedSentence();
            const redFlags = this.unresolvedRedFlagItems.slice(0, 3).map((item) => this.riskItemSummary(item));
            const resolvedRiskCount = this.redFlagItems.filter((item) => this.isRiskResolved(item)).length;
            const checklistRows = this.relevantChecklistLabels(['confirmed', 'deferred'], 3);
            reasons.push(structuredFinding ? '진찰 입력' : '진찰 확인');
            if (redFlags.length) reasons.push('진료 전 주의 확인 전');
	        if (resolvedRiskCount > 0) reasons.push('진료 전 주의 확인 완료');
	        if (checklistRows.length) reasons.push('진료 전 확인');
	        title = activeItems.length ? '진찰 보완' : '진찰 추가';
	        description = '설·맥·복진';
            text = this.joinSentenceParts([
                structuredFinding || '설진·맥진·복진 확인 예정',
                redFlags.length ? `진료 전 주의 확인: ${redFlags.join(', ')}` : '',
	            !redFlags.length && resolvedRiskCount > 0 ? '진료 전 주의 확인 완료' : '',
	            checklistRows.length ? `진료 전 확인: ${checklistRows.join(', ')}` : '',
            ]);
        }

        if (normalized === 'A') {
            const chief = this.summaryLookup(['chief_complaint'], ['주호소'], '');
            const patternNames = this.currentPatternCandidateNames(2);
            const validationTexts = this.soapValidationErrors.concat(this.soapValidationWarnings).slice(0, 2).map((item) => this.validationIssueText(item));
            if (patternNames.length) reasons.push(`의심 소견: ${patternNames.join(', ')}`);
            if (validationTexts.length) reasons.push('차트 보완');
            if (this.hasUnresolvedRedFlags) reasons.push('진료 전 주의 확인 전 평가 주의');
	            title = activeItems.length ? '변증/진단 보완' : '변증/진단 추가';
	            description = '변증/진단 보완';
	            text = this.joinSentenceParts([
	                chief ? `참고: ${chief}` : '',
	                patternNames.length ? `의심 소견: ${patternNames.join(', ')}` : '',
	                this.hasUnresolvedRedFlags ? '진료 전 주의 확인 후 판단' : '',
	                validationTexts.length ? `보완: ${validationTexts.join(' ')}` : '',
	                !chief && !patternNames.length && !this.hasUnresolvedRedFlags && !validationTexts.length ? '차트 보완' : '',
            ]);
        }

        if (normalized === 'P') {
            const followups = this.currentFollowupQuestionTitles(2);
            const pending = this.relevantChecklistLabels(['unchecked', 'deferred'], 3);
            const redFlags = this.unresolvedRedFlagItems.slice(0, 2).map((item) => this.clinicalUiText(item.label || item.code || '진료 전 주의'));
            if (followups.length) reasons.push(`추가 문진: ${followups.join(', ')}`);
            if (pending.length) reasons.push('진료 전 확인');
            if (redFlags.length) reasons.push('진료 전 주의 확인 전');
	            title = activeItems.length ? '치료/처방 보완' : '치료/처방 추가';
	            description = '치료/처방 보완';
	            text = this.joinSentenceParts([
	                redFlags.length ? `진료 전 주의 확인: ${redFlags.join(', ')}` : '',
	                !redFlags.length && followups.length ? `추가 문진: ${followups.join(', ')}` : '',
	                !redFlags.length && !followups.length && pending.length ? `미확인 항목: ${pending.join(', ')}` : '',
	                !redFlags.length && !followups.length && !pending.length ? '치료 반응 추적' : '',
            ]);
        }

        return {
            kind: 'soap_supplement',
            title,
            description,
            source: '현재 화면 자료',
            soap_section: normalized,
            soap_sentence_text: text,
            target_sentence_id: target ? target.id : '',
            target_sentence_preview: targetPreview,
	            answer_target: target ? `${this.soapSectionDisplayName(normalized)} 수정` : `${this.soapSectionDisplayName(normalized)} 추가`,
            suggestion_reasons: this.dedupeStrings(reasons).slice(0, 6),
            source_labels: this.dedupeStrings(reasons).slice(0, 6),
            decision_status: 'pending_doctor_review',
            applied_to_soap: false,
        };
    }

    private aiAssistantResult(type: string, title: string, tone: string, summary: string, items: AiAssistantResultItem[], sources: string[], uncertainty: string[], requestText: string): AiAssistantResult {
        return {
            id: `${type}-${Date.now()}-${this.aiAssistantResults.length}`,
            type,
            title,
            tone,
            status: 'pending_doctor_review',
            summary,
            items,
            sources,
            uncertainty,
            createdAt: this.timeLabel(),
            requestText: requestText || '',
        };
    }

    private inferAiAssistantAction(requestText: string) {
        const text = String(requestText || '').toLowerCase();
        if (text.indexOf('표준') >= 0 || text.indexOf('설문') >= 0 || text.indexOf('진단도구') >= 0 || text.indexOf('도구') >= 0) return 'standard_tools';
        if (text.indexOf('참조') >= 0 || text.indexOf('데이터셋') >= 0 || text.indexOf('건강인') >= 0 || text.indexOf('비교') >= 0 || text.indexOf('하위') >= 0 || text.indexOf('percentile') >= 0) return 'reference_compare';
        if (text.indexOf('누락') >= 0 || text.indexOf('미확인') >= 0 || text.indexOf('저장 전') >= 0 || text.indexOf('게이트') >= 0) return 'missing_review';
        if (text.indexOf('레드') >= 0 || text.indexOf('위험') >= 0 || text.indexOf('안전') >= 0 || text.indexOf('주의') >= 0) return 'red_flags';
        if (text.indexOf('soap') >= 0 || text.indexOf('s/o/a/p') >= 0 || text.indexOf('초안') >= 0 || text.indexOf('문장') >= 0) return 'soap_improvement';
        if (text.indexOf('설진') >= 0 || text.indexOf('맥진') >= 0 || text.indexOf('복진') >= 0 || text.indexOf('망진') >= 0 || text.indexOf('절진') >= 0 || text.indexOf('진찰') >= 0) return 'soap_improvement';
        if (text.indexOf('질문') >= 0 || text.indexOf('문진') >= 0 || text.indexOf('물어') >= 0 || text.indexOf('확인') >= 0) return 'followup_questions';
        if (text.indexOf('변증') >= 0 || text.indexOf('패턴') >= 0 || text.indexOf('한열') >= 0 || text.indexOf('허실') >= 0 || text.indexOf('비위') >= 0 || text.indexOf('간기') >= 0 || text.indexOf('담음') >= 0) return 'pattern_candidates';
        return 'precheck_summary';
    }

    private get soapCandidateQueueRows(): SoapCandidateQueueRow[] {
        const rows: SoapCandidateQueueRow[] = [];
        let sequence = 0;
        this.soapSectionOrder.forEach((section) => {
            this.soapItems(section).forEach((sentence) => {
                if (!this.soapSentenceBelongsInCandidateQueue(sentence)) return;
                rows.push(this.soapSentenceToQueueRow(sentence, sequence++));
            });
        });

        this.aiAssistantResults.forEach((result) => {
            this.asArray(result && result.items).forEach((item) => {
                const status = String((item && item.decision_status) || 'pending_doctor_review');
                if (status === 'soap_candidate_sent' && item.applied_to_soap) return;
                if (this.isSoapImprovementResult(result)) {
                    rows.push(this.soapSupplementToQueueRow(result, item, sequence++));
                    return;
                }
                if (!this.aiItemHasSoapCandidate(result, item)) return;
                rows.push(this.aiItemToQueueRow(result, item, sequence++));
            });
        });

        return this.dedupeSoapCandidateQueueRows(rows.sort((left, right) => right.sortKey - left.sortKey));
    }

    private dedupeSoapCandidateQueueRows(rows: SoapCandidateQueueRow[]) {
        const visibleRows = rows.filter((row) => {
            return !this.isActionableSoapCandidateRow(row) || !this.soapCandidateRowResolvedByLedger(row);
        });
        const resolvedRows = visibleRows.filter((row) => {
            return row && ['applied', 'deferred', 'excluded'].indexOf(row.status) >= 0;
        });
        const accepted: SoapCandidateQueueRow[] = [];
        return visibleRows.filter((row) => {
            if (!row) return false;
            if (['applied', 'deferred', 'excluded', 'previous_version'].indexOf(row.status) >= 0) return true;
            const alreadyResolved = resolvedRows.some((existing) => {
                return existing !== row
                    && this.soapCandidateResolvedRowCanSuppress(existing, row);
            });
            if (alreadyResolved) return false;
            const duplicate = accepted.some((existing) => this.soapCandidateQueueRowsOverlap(existing, row));
            if (duplicate) return false;
            accepted.push(row);
            return true;
        });
    }

    private soapCandidateResolvedRowCanSuppress(existing: SoapCandidateQueueRow, row: SoapCandidateQueueRow) {
        if (row.kind === 'ai_item' && row.result && this.isConversationClinicalResult(row.result)) {
            const existingIdentity = this.soapCandidateResolutionIdentity(existing);
            const rowIdentity = this.soapCandidateResolutionIdentity(row);
            if (existingIdentity && existingIdentity === rowIdentity) return true;
            return !!row.item && this.conversationItemAlreadyInSoap(row.item);
        }
        return this.soapCandidateQueueRowsOverlap(existing, row);
    }

    private soapCandidateResolutionIdentity(row: SoapCandidateQueueRow) {
        if (!row) return '';
        if (row.sentence) {
            const meta = row.sentence.sentence_meta && typeof row.sentence.sentence_meta === 'object'
                ? row.sentence.sentence_meta
                : {};
            return String(meta.client_candidate_id || '');
        }
        if (row.item && row.result) {
            return this.aiAssistantItemCandidateIdentity(row.result, row.item, row.section);
        }
        return '';
    }

    private aiAssistantItemCandidateIdentity(result: AiAssistantResult, item: AiAssistantResultItem, sectionValue: any = '') {
        if (!item) return '';
        const section = this.normalizedSoapSection(sectionValue || this.aiItemSoapSection(result, item));
        if (item.conversation_signal_key) return String(item.conversation_signal_key);
        if (this.isPatternCandidateResult(result)) {
            return `pattern::${item.pattern_code || item.title || 'candidate'}::${section}`;
        }
        if (this.isSoapImprovementResult(result)) {
            return `soap-supplement::${item.title || 'suggestion'}::${section}`;
        }
        const text = this.aiItemSoapCandidateText(result, item);
        return `${result && result.id ? result.id : 'ai'}::${section}::${this.conversationCandidateSignalKey(section, text)}`;
    }

    private soapCandidateDecisionLedger(): WorkspaceMap {
        const draftMeta = this.soap && this.soap.draft_meta && typeof this.soap.draft_meta === 'object'
            ? this.soap.draft_meta
            : (this.soap && this.soap.draft && this.soap.draft.draft_meta && typeof this.soap.draft.draft_meta === 'object'
                ? this.soap.draft.draft_meta
                : {});
        const ledger = draftMeta.resolved_candidate_decisions;
        return ledger && typeof ledger === 'object' ? ledger : {};
    }

    private soapCandidateRowResolvedByLedger(row: SoapCandidateQueueRow) {
        const identity = this.soapCandidateResolutionIdentity(row);
        if (!identity) return false;
        const decision = this.soapCandidateDecisionLedger()[identity];
        if (!decision || typeof decision !== 'object') return false;
        return ['apply', 'edit_apply', 'section_apply', 'defer', 'exclude'].indexOf(String(decision.action || '')) >= 0;
    }

    private syncResolvedSoapCandidateDecisions() {
        const ledger = this.soapCandidateDecisionLedger();
        this.aiAssistantResults.forEach((result) => {
            this.asArray(result && result.items).forEach((item) => {
                const identity = this.aiAssistantItemCandidateIdentity(result, item);
                const decision = identity ? ledger[identity] : null;
                if (!decision || typeof decision !== 'object') return;
                const action = String(decision.action || '');
                if (['apply', 'section_apply', 'edit_apply'].indexOf(action) >= 0) {
                    item.applied_to_soap = true;
                    item.decision_status = action === 'edit_apply' ? 'modified' : 'added';
                    return;
                }
                if (action === 'defer') item.decision_status = 'deferred';
                if (action === 'exclude') item.decision_status = 'excluded';
            });
        });
    }

    private soapCandidateQueueRowsOverlap(left: SoapCandidateQueueRow, right: SoapCandidateQueueRow) {
        if (!left || !right) return false;
        if (this.normalizedSoapSection(left.section || 'S') !== this.normalizedSoapSection(right.section || 'S')) return false;
        const leftText = left.text || left.title || '';
        const rightText = right.text || right.title || '';
        return this.conversationTextSimilarity(leftText, rightText) >= 0.62;
    }

    private soapSentenceBelongsInCandidateQueue(item: SoapSentenceDraft) {
        if (!item) return false;
        const source = String(item.author_type || item.candidate_source || '');
        if (this.soapIsAiCandidate(item)) return true;
        if (['ai_assistant', 'checklist', 'pattern_candidate'].indexOf(source) >= 0) return true;
        if (item.candidate_status || item.doctor_decision_status) return true;
        return Array.isArray(item.source_refs) && item.source_refs.length > 0 && item.status === 'reviewed';
    }

    private soapSentenceToQueueRow(item: SoapSentenceDraft, sequence: number): SoapCandidateQueueRow {
        const section = this.normalizedSoapSection(item.soap_section);
        const status = this.soapQueueStatusForSentence(item);
        const patternCandidate = this.soapSentenceIsPatternCandidate(item);
        return {
            id: `sentence-${item.id || section}-${sequence}`,
            kind: 'soap_sentence',
            status,
            title: patternCandidate ? `${this.soapSectionDisplayName(section)} 검토` : `${this.soapSectionDisplayName(section)} 기록`,
            text: this.truncateText(item.text || '', 180),
            section,
            target: this.soapQueueTargetLabel(section),
            source: this.soapAuthorLabel(item),
            meta: this.soapSentenceStatusLabel(item),
            updated: item.updated || '최근',
            sortKey: this.queueSortKey(item.updated, sequence),
            tone: this.soapQueueTone(status),
            sentence: item,
        };
    }

    private soapSentenceIsPatternCandidate(item: SoapSentenceDraft) {
        const meta = item && item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
        return item && (item.author_type === 'pattern_candidate' || item.candidate_source === 'pattern_candidate' || meta.source === 'pattern_candidate' || !!meta.pattern_code || !!payload.pattern_code);
    }

    private soapSentenceHasAssessmentEvidence(item: SoapSentenceDraft) {
        if (!item || this.normalizedSoapSection(item.soap_section || '') !== 'A') return false;
        if (this.soapSentenceIsPatternCandidate(item)) return true;
        const meta = item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        const payload = meta.source_payload && typeof meta.source_payload === 'object' ? meta.source_payload : {};
        const itemKind = String(payload.item_kind || payload.kind || meta.item_kind || '');
        const source = String(meta.source || item.author_type || item.candidate_source || '');
        if (source !== 'conversation_chat' && itemKind.indexOf('conversation_') !== 0) return false;
        return itemKind === 'conversation_diagnosis_candidate'
            || itemKind === 'conversation_chart_candidate'
            || this.asArray(payload.supporting_evidence).length > 0
            || this.asArray(payload.patient_cues).length > 0
            || this.displayValue(payload.clinical_evidence || meta.clinical_evidence, '') !== ''
            || String(item.text || '').trim() !== '';
    }

    private soapCandidateVersionStatus(item: SoapSentenceDraft): SoapCandidateQueueStatus | '' {
        const meta = item && item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        const value = String(meta.regeneration_status || meta.candidate_status || item.candidate_status || '').trim();
        if (value === 'previous_version') return 'previous_version';
        if (value === 'new_candidate_ready') return 'new_candidate_ready';
        return '';
    }

    private soapSupplementToQueueRow(result: AiAssistantResult, item: AiAssistantResultItem, sequence: number): SoapCandidateQueueRow {
        const section = this.normalizedSoapSection(item && item.soap_section ? item.soap_section : 'S');
        const status = this.soapQueueStatusForAssistantItem(item, true);
        return {
            id: `supplement-${result && result.id ? result.id : 'result'}-${section}-${sequence}`,
            kind: 'soap_supplement',
            status,
            title: item && item.title ? item.title : `${this.soapSectionDisplayName(section)} 보완`,
            text: this.truncateText((item && (item.soap_sentence_text || item.description)) || '', 180),
            section,
            target: this.soapQueueTargetLabel(section),
            source: '진료지원',
            meta: this.soapSupplementStatusLabel(item),
            updated: result && result.createdAt ? result.createdAt : '최근',
            sortKey: this.queueSortKey(result && result.createdAt, sequence),
            tone: this.soapQueueTone(status),
            result,
            item,
        };
    }

    private aiItemToQueueRow(result: AiAssistantResult, item: AiAssistantResultItem, sequence: number): SoapCandidateQueueRow {
        const section = this.aiItemSoapSection(result, item);
        const status = this.soapQueueStatusForAssistantItem(item, false);
        return {
            id: `ai-item-${result && result.id ? result.id : 'result'}-${section}-${sequence}`,
            kind: 'ai_item',
            status,
            title: item && item.title ? item.title : `${result && result.type ? this.clinicalRecordDisplayText(result.type) : '한의 추천'}`,
            text: this.truncateText(this.aiItemSoapCandidateText(result, item), 180),
            section,
            target: this.soapQueueTargetLabel(section),
            source: result && result.type ? this.clinicalRecordDisplayText(result.type) : '한의 추천',
            meta: this.aiItemStatusLabel(item),
            updated: result && result.createdAt ? result.createdAt : '최근',
            sortKey: this.queueSortKey(result && result.createdAt, sequence),
            tone: this.soapQueueTone(status),
            result,
            item,
        };
    }

    private soapQueueStatusForSentence(item: SoapSentenceDraft): SoapCandidateQueueStatus {
        const raw = String((item && (item.doctor_decision_status || item.candidate_status || item.status)) || 'pending_doctor_review');
        if (raw === 'excluded' || item.status === 'excluded') return 'excluded';
        if (raw === 'deferred') return 'deferred';
        if (['reviewed', 'accepted', 'accepted_edited', 'applied', 'added', 'modified'].indexOf(raw) >= 0 || item.status === 'reviewed') return 'applied';
        const versionStatus = this.soapCandidateVersionStatus(item);
        if (versionStatus) return versionStatus;
        if (item.needs_update === true || (this.soapNeedsRegeneration && ['ai_unreviewed', 'pending_doctor_review'].indexOf(raw) >= 0)) return 'stale';
        return 'pending';
    }

    private soapQueueStatusForAssistantItem(item: AiAssistantResultItem, soapSupplement: boolean): SoapCandidateQueueStatus {
        const status = String((item && item.decision_status) || 'pending_doctor_review');
        if (['ignored', 'excluded'].indexOf(status) >= 0) return 'excluded';
        if (status === 'deferred') return 'deferred';
        if (['added', 'modified', 'referenced', 'accepted', 'accepted_edited'].indexOf(status) >= 0) return 'applied';
        if (!soapSupplement && status === 'soap_candidate_sent') return 'applied';
        if (this.soapNeedsRegeneration) return 'stale';
        return 'pending';
    }

    private soapQueueTone(status: SoapCandidateQueueStatus) {
        const tones: Record<SoapCandidateQueueStatus, string> = {
            new_candidate_ready: 'warning',
            previous_version: 'neutral',
            pending: 'warning',
            stale: 'pending',
            applied: 'success',
            deferred: 'neutral',
            excluded: 'danger',
        };
        return tones[status] || 'neutral';
    }

    private soapQueueTargetLabel(section: string) {
        const normalized = this.normalizedSoapSection(section);
        return `${this.soapSectionChipLabel(normalized)} · 참고`;
    }

    private queueSortKey(value: any, sequence: number) {
        const parsed = Date.parse(String(value || ''));
        if (!Number.isNaN(parsed)) return parsed;
        return 100000 - sequence;
    }

    private async applySoapQueueSentence(item: SoapSentenceDraft, edit: boolean) {
        if (this.clinicalReadOnly || !item || item.status === 'excluded') return;
        const edited = edit || item.dirty || item.status === 'doctor_edited';
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        const saved = await this.applySoapCandidateDecision({
            action: edited ? 'edit_apply' : 'apply',
            sentence_id: item.id,
            section: item.soap_section,
            text: item.text,
            source_refs: item.source_refs || [],
            reason: edited ? 'doctor_edit_accept' : 'doctor_accept',
            candidate: this.soapCandidatePayloadFromSentence(item),
        });
        if (saved) {
            this.soapMessage = edited ? '수정 후 반영됨.' : '차트에 반영됨.';
            this.focusSoapSectionAfterCandidateAction(item.soap_section);
            this.announceCandidateApplied(item.soap_section);
            await this.service.render();
            this.scheduleBottomBarClearance();
        }
    }

    private async deferSoapQueueSentence(item: SoapSentenceDraft) {
        if (this.clinicalReadOnly || !item || item.status === 'excluded') return;
        this.error = '';
        this.soapMessage = '';
        this.soapValidation = {};
        const saved = await this.applySoapCandidateDecision({
            action: 'defer',
            sentence_id: item.persisted ? item.id : '',
            section: item.soap_section,
            text: item.text,
            source_refs: item.source_refs || [],
            reason: item.doctor_decision_reason || 'doctor_defer',
            candidate: this.soapCandidatePayloadFromSentence(item),
        });
        if (saved) {
            this.soapMessage = '차트 참고 내용을 나중에 보기로 표시했습니다.';
            await this.service.render();
        }
    }

    private activeSoapSectionCount(section: string) {
        return this.soapItems(section).filter((item) => item.status !== 'excluded' && (item.text || '').trim() !== '').length;
    }

    private normalizedSoapSection(section: any) {
        const value = String(section || 'S').trim().toUpperCase();
        return this.soapSectionOrder.indexOf(value) >= 0 ? value : 'S';
    }

    private findSoapSupplementTarget(section: string, targetId: string) {
        const rows = this.soapItems(section).filter((item) => item.status !== 'excluded');
        if (targetId) {
            const target = rows.find((item) => item.id === targetId);
            if (target) return target;
        }
        return rows.length ? rows[0] : null;
    }

    private soapSupplementBaseReasons(section: string, activeItems: SoapSentenceDraft[]) {
        const reasons: string[] = [];
        if (!activeItems.length) {
            reasons.push(`${this.soapSectionDisplayName(section)} 없음`);
        } else {
            reasons.push(`${this.soapSectionDisplayName(section)} ${activeItems.length}문장`);
        }
        const unreviewed = activeItems.filter((item) => item.status === 'ai_unreviewed').length;
        if (unreviewed > 0) reasons.push(`한의사 판단 전 ${unreviewed}`);
        if (this.soapNeedsRegeneration) reasons.push(`최신 입력 반영 전: ${this.soapRegenerationText}`);
        return reasons;
    }

    private textContainsClinicalSignal(text: string, values: string[]) {
        const haystack = this.normalizeLookupLabel(text);
        if (!haystack) return false;
        return (values || []).filter((value) => !!value).some((value) => {
            const normalized = this.normalizeLookupLabel(value);
            if (!normalized || normalized === '-') return false;
            if (normalized.length <= 2) return haystack.indexOf(normalized) >= 0;
            const tokens = normalized.split(/[,.·/()\\[\\]\\s]+/).filter((token) => token.length >= 2);
            if (!tokens.length) return haystack.indexOf(normalized.slice(0, Math.min(12, normalized.length))) >= 0;
            return tokens.slice(0, 3).some((token) => haystack.indexOf(token) >= 0);
        });
    }

    private joinSentenceParts(parts: string[]) {
        return (parts || [])
            .map((part) => String(part || '').trim())
            .filter((part) => part !== '' && part !== '-')
            .map((part) => /[.!?。]$/.test(part) ? part : `${part}.`)
            .join(' ');
    }

    private applyObjectiveFindingDetailsFromAiItem(section: string, result: AiAssistantResult, item: AiAssistantResultItem, chartText: string) {
        if (this.normalizedSoapSection(section) !== 'O' || !item) return false;
        const source = this.objectiveFindingSourceTextForAiItem(result, item, chartText);
        const changed = this.applyObjectiveFindingDraftsFromText(source, true);
        if (changed) this.refreshClinicalChartViewState();
        return changed;
    }

    private objectiveFindingSourceTextForAiItem(result: AiAssistantResult, item: AiAssistantResultItem, chartText: string) {
        const sourceRefText = this.asArray(item && item.source_refs)
            .map((ref) => {
                if (!ref || typeof ref !== 'object') return this.clinicalRecordDisplayText(ref);
                return this.clinicalRecordDisplayText(ref.value || ref.text || ref.label || '');
            })
            .filter((text) => text !== '');
        return [
            chartText,
            item.soap_sentence_text || '',
            item.description || '',
            item.evidence_summary || '',
            item.clinical_evidence || '',
            item.source_excerpt || '',
            result && result.requestText ? result.requestText : '',
        ].concat(sourceRefText).join(' ');
    }

    private applyObjectiveFindingDraftsFromText(text: string, openMatchedGroups: boolean = false) {
        const matches = this.objectiveFindingFieldMatchesFromText(text);
        if (!matches.length) return false;
        const accepted: WorkspaceMap[] = [];
        matches.forEach((match) => {
            if (accepted.some((item) => item.itemCode === match.itemCode && item.fieldCode === match.fieldCode)) return;
            const longerSameItem = accepted.some((item) => {
                return item.itemCode === match.itemCode
                    && item.optionNormalized
                    && match.optionNormalized
                    && item.optionNormalized.length > match.optionNormalized.length
                    && item.optionNormalized.indexOf(match.optionNormalized) >= 0;
            });
            if (longerSameItem && !match.explicitField) return;
            accepted.push(match);
        });

        let changed = false;
        const openState: WorkspaceMap = { ...this.objectiveFindingOpenGroupState };
        accepted.forEach((match) => {
            const draft = this.objectiveFindingDraft(match.itemCode);
            if (!draft.fields) draft.fields = {};
            if (String(draft.fields[match.fieldCode] || '') === match.option) return;
            draft.fields[match.fieldCode] = match.option;
            changed = true;
            if (openMatchedGroups) {
                openState[this.objectiveFindingGroupCodeForItemCode(match.itemCode)] = true;
            }
        });
        if (!changed) return false;
        this.objectiveFindingDrafts = { ...this.objectiveFindingDrafts };
        if (openMatchedGroups) this.objectiveFindingOpenGroupState = openState;
        return true;
    }

    private restoreObjectiveFindingDraftsFromChartText(text: string) {
        if (!String(text || '').trim()) return false;
        return this.applyObjectiveFindingDraftsFromText(text, false);
    }

    private objectiveFindingFieldMatchesFromText(text: string) {
        const normalized = this.normalizeLookupLabel(text);
        if (!normalized) return [];
        const matches: WorkspaceMap[] = [];
        this.asArray(this.objectiveFindingItems).forEach((item: ObjectiveFindingInput) => {
            const itemLabel = this.clinicalRecordDisplayText(item && item.label);
            const itemLabelNormalized = this.normalizeLookupLabel(itemLabel);
            this.asArray(item && item.fields).forEach((field: ObjectiveFindingFieldInput) => {
                const fieldLabel = this.clinicalRecordDisplayText(field && field.label);
                const fieldLabelNormalized = this.normalizeLookupLabel(fieldLabel);
                this.asArray(field && field.options).forEach((option: string) => {
                    const optionText = this.clinicalRecordDisplayText(option);
                    const optionNormalized = this.normalizeLookupLabel(optionText);
                    if (!optionNormalized || normalized.indexOf(optionNormalized) < 0) return;
                    const explicitField = this.objectiveFindingTextHasSchemaKeyword(normalized, [fieldLabel, optionText])
                        || this.objectiveFindingTextHasSchemaKeyword(normalized, [itemLabel, fieldLabel, optionText]);
                    const explicitItem = this.objectiveFindingTextHasSchemaKeyword(normalized, [itemLabel, optionText]);
                    if (!this.objectiveFindingOptionCanSeedConversation(optionText) && !explicitField) return;
                    matches.push({
                        itemCode: item.code,
                        fieldCode: field.code,
                        option: optionText,
                        optionNormalized,
                        explicitField,
                        score: optionNormalized.length
                            + (explicitField ? 100 : 0)
                            + (explicitItem ? 20 : 0)
                            + (fieldLabelNormalized ? 4 : 0)
                            + (itemLabelNormalized ? 2 : 0),
                    });
                });
            });
        });
        return matches.sort((left, right) => Number(right.score || 0) - Number(left.score || 0));
    }

    private objectiveFindingTextHasSchemaKeyword(normalizedText: string, parts: string[]) {
        const keyword = this.normalizeLookupLabel((parts || []).filter((part) => String(part || '').trim() !== '').join(' '));
        return keyword !== '' && normalizedText.indexOf(keyword) >= 0;
    }

    private objectiveFindingGroupCodeForItemCode(itemCode: string) {
        if (['tongue', 'pulse', 'abdomen'].indexOf(String(itemCode || '')) >= 0) return 'korean';
        if (['tender_rom', 'other'].indexOf(String(itemCode || '')) >= 0) return 'physical';
        return String(itemCode || '');
    }

    private objectiveFindingDraft(code: string): ObjectiveFindingDraft {
        const key = String(code || '').trim();
        if (!key) return { chips: [], fields: {}, note: '' };
        if (!this.objectiveFindingDrafts[key]) {
            this.objectiveFindingDrafts[key] = { chips: [], fields: {}, note: '' };
        }
        if (!Array.isArray(this.objectiveFindingDrafts[key].chips)) this.objectiveFindingDrafts[key].chips = [];
        if (!this.objectiveFindingDrafts[key].fields) this.objectiveFindingDrafts[key].fields = {};
        if (typeof this.objectiveFindingDrafts[key].note !== 'string') this.objectiveFindingDrafts[key].note = '';
        return this.objectiveFindingDrafts[key];
    }

    private objectiveFindingCompactGroup(code: string, label: string, itemCodes: string[]) {
        const items = this.objectiveFindingItems
            .filter((item) => itemCodes.indexOf(item.code) >= 0)
            .map((item) => {
                const value = this.objectiveFindingFieldText(item);
                return {
                    code: item.code,
                    label: item.label,
                    hint: item.hint,
                    value,
                    state: value ? 'complete' : 'empty',
                };
            });
        return { code, label, items };
    }

    private objectiveFindingAiSuggestionGroup() {
        const followups = this.currentFollowupQuestionTitles(3)
            .map((label, index) => ({
                code: `ai_${index}`,
                label,
                hint: '추가 확인',
                value: `추가 확인: ${label}`,
                state: 'empty',
            }));
        return { code: 'ai', label: '추천', items: followups };
    }

    private objectiveFindingRiskGroup() {
        const risks = this.unresolvedRedFlagItems.slice(0, 3)
            .map((item, index) => {
                const label = this.riskItemSummary(item) || item.label || item.code || '주의 소견';
                return {
                    code: `risk_${index}`,
                    label,
                    hint: '주의 소견',
                    value: `주의 소견: ${label}`,
                    state: 'empty',
                };
            });
        return { code: 'risk', label: '주의', items: risks };
    }

    private objectiveFindingFieldText(item: ObjectiveFindingInput) {
        if (!item) return '';
        const draft = this.objectiveFindingDraft(item.code);
        const fieldParts = (item.fields || [])
            .map((field) => {
                const value = String((draft.fields || {})[field.code] || '').trim();
                return value ? `${field.label} ${value}` : '';
            })
            .filter((part) => part !== '');
        const parts = this.dedupeStrings(fieldParts.concat(draft.chips || []).concat([draft.note || '']))
            .map((part) => String(part || '').trim())
            .filter((part) => part !== '');
        if (!parts.length) return '';
        return `${item.label}: ${parts.join(', ')}`;
    }

    private objectiveFindingComposedSentence() {
        const parts = this.objectiveFindingItems
            .map((item) => this.objectiveFindingFieldText(item))
            .filter((text) => text !== '');
        return this.joinSentenceParts(parts);
    }

    private objectiveFindingPayloadFields() {
        return this.objectiveFindingItems
            .map((item) => {
                const draft = this.objectiveFindingDraft(item.code);
                return {
                    code: item.code,
                    label: item.label,
                    fields: (item.fields || []).map((field) => ({
                        code: field.code,
                        label: field.label,
                        value: String((draft.fields || {})[field.code] || ''),
                    })).filter((field) => field.value !== ''),
                    chips: (draft.chips || []).slice(),
                    note: draft.note || '',
                    text: this.objectiveFindingFieldText(item),
                };
            })
            .filter((item) => item.text !== '');
    }

    private objectiveFindingCandidateReasons(fields: WorkspaceMap[]) {
        const labels = (fields || []).map((item) => String(item.label || '').trim()).filter((label) => label !== '');
        const reasons = labels.length ? [`입력된 진찰 항목: ${labels.join(', ')}`] : [];
        reasons.push('한의사 확인 후 차트 반영');
        return reasons;
    }

    private clearObjectiveFindingInputs() {
        this.objectiveFindingDrafts = {};
        this.objectiveFindingManualText = '';
    }

    private relevantChecklistLabels(statuses: string[], limit: number = 3) {
        const targetStatuses = (statuses || []).map((item) => String(item || ''));
        const rows = this.checklistItems.filter((item) => {
            if (!item) return false;
            const status = String(item.status || 'unchecked');
            return targetStatuses.indexOf(status) >= 0;
        });
        return rows.slice(0, limit).map((item) => {
            const label = this.clinicalUiText(item.label || item.code || '진료 전 확인');
            const status = this.statusLabel(item.status || 'unchecked');
            return `${label} ${status}`;
        });
    }

    private currentPatternCandidateNames(limit: number = 2) {
        const names: string[] = [];
        this.aiAssistantResults.forEach((result) => {
            this.asArray(result.items).forEach((item) => {
                if (!this.isAssessmentEvidenceItem(result, item)) return;
                if (!item || ['excluded', 'ignored', 'deferred'].indexOf(String(item.decision_status || '')) >= 0) return;
                const name = this.displayValue(item.display_name || item.title, '');
                if (name) names.push(name);
            });
        });
        return this.dedupeStrings(names).slice(0, limit);
    }

    private currentFollowupQuestionTitles(limit: number = 2) {
        const titles: string[] = [];
        this.aiAssistantResults.filter((result) => this.isFollowupQuestionResult(result)).forEach((result) => {
            this.asArray(result.items).forEach((item) => {
                if (!item) return;
                const title = this.displayValue(item.question || item.title, '');
                if (title) titles.push(title);
            });
        });
        return this.dedupeStrings(titles).slice(0, limit);
    }

    private soapClinicalItemHasValue(section: string, item: SoapClinicalStructureItem) {
        return this.hasClinicalValue(this.soapClinicalItemValue(section, item));
    }

    public soapClinicalItemActionState(section: string, item: SoapClinicalStructureItem) {
        if (!item) return { status: '직접 입력', state: 'empty' };
        const normalized = this.normalizedSoapSection(section);
        const line = this.soapSectionLabeledLineRange(normalized, item.label);
        if (line) {
            return line.value
                ? { status: '차트 반영됨', state: 'complete' }
                : { status: '입력 중', state: 'editing' };
        }
        return this.hasClinicalValue(this.soapClinicalItemValue(normalized, item))
            ? { status: '차트에 추가', state: 'available' }
            : { status: '직접 입력', state: 'empty' };
    }

    private subjectiveInquiryRow(item: SoapClinicalStructureItem, group: string) {
        const value = this.soapClinicalItemValue('S', item);
        const actionState = this.subjectiveInquiryActionState(item.label, value);
        return {
            code: item.code,
            label: item.label,
            hint: item.hint,
            group,
            value,
            status: actionState.status,
            state: actionState.state,
        };
    }

    private subjectiveInquiryActionState(label: string, value: string) {
        const line = this.soapSectionLabeledLineRange('S', label);
        if (line) {
            return line.value
                ? { status: '차트 반영됨', state: 'complete' }
                : { status: '입력 중', state: 'editing' };
        }
        return this.hasClinicalValue(value)
            ? { status: '차트에 추가', state: 'available' }
            : { status: '직접 입력', state: 'empty' };
    }

    private subjectiveRiskSignalValue() {
        const text = [
            this.soapSectionCompactText('S'),
            this.summaryLookup(['red_flags', 'risk_flags', 'warning_signs'], ['레드플래그', '위험 신호', '응급 배제'], ''),
            this.checklistItems.map((item) => `${item.label || ''} ${item.memo || ''} ${item.status || ''}`).join(' '),
        ].join(' ');
        const keywords = ['야간통', '외상', '골절', '대소변', '마비', '근력저하', '감각저하', '진행성', '발열', '체중감소'];
        if (!this.textContainsClinicalSignal(text, keywords)) return '';
        const matches = keywords.filter((keyword) => this.textContainsClinicalSignal(text, [keyword]));
        return this.dedupeStrings(matches).slice(0, 3).join(', ');
    }

    private soapClinicalItemValue(section: string, item: SoapClinicalStructureItem) {
        if (!item) return '';
        const normalized = this.normalizedSoapSection(section);
        const labeledLine = this.soapSectionLabeledLineRange(normalized, item.label);
        if (labeledLine) return this.patientSummaryText(String(labeledLine.value || ''), 58);

        const dynamicValue = this.soapClinicalDynamicValue(item);
        if (this.hasClinicalValue(dynamicValue)) return this.patientSummaryText(dynamicValue, 58);

        // A 섹션의 근거·불확실성·판단은 환자 요약이나 다른 A 문장을
        // 키워드로 재사용하지 않고, 명시 입력 또는 실제 판단 후보만 사용한다.
        if (normalized === 'A') return '';

        const summaryValue = this.summaryLookup(item.keys || [], item.labels || [item.label], '');
        if (this.hasClinicalValue(summaryValue)) return this.patientSummaryText(summaryValue, 58);

        const sentenceValue = this.soapClinicalSentenceValue(normalized, item);
        if (this.hasClinicalValue(sentenceValue)) return this.patientSummaryText(sentenceValue, 58);
        return '';
    }

    private soapClinicalDynamicValue(item: SoapClinicalStructureItem) {
        const dynamic = item && item.dynamic ? String(item.dynamic) : '';
        if (dynamic === 'pattern_candidates') return this.currentPatternCandidateNames(2).join(', ');
        if (dynamic === 'pattern_support') return this.currentPatternSupportingEvidence(2).join(', ');
        if (dynamic === 'pattern_opposing') return this.currentPatternOpposingEvidence(2).join(', ');
        if (dynamic === 'followup_questions') {
            const titles = this.currentFollowupQuestionTitles(2);
            if (titles.length) return titles.join(', ');
            const questions: string[] = [];
            this.activePatternCandidateItems().forEach((candidate) => {
                this.asArray(candidate.followup_questions).forEach((value) => {
                    const text = this.clinicalRecordDisplayText(value);
                    if (text) questions.push(text);
                });
            });
            return this.dedupeStrings(questions).slice(0, 2).join(', ');
        }
        return '';
    }

    private soapClinicalSentenceValue(section: string, item: SoapClinicalStructureItem) {
        const keywords = this.dedupeStrings([item.label].concat(item.keywords || []));
        const rows = this.soapEditorItems(section).filter((row) => row && row.status !== 'excluded' && (row.text || '').trim() !== '');
        for (const row of rows) {
            const text = row.text || '';
            const labeled = this.soapLabeledLineRange(text, item.label);
            if (labeled) return String(labeled.value || '');
        }
        for (const row of rows) {
            const lines = String(row.text || '').split(/\n+/).map((line) => line.trim()).filter((line) => line !== '');
            const matchingLine = lines.find((line) => this.textContainsClinicalSignal(line, keywords));
            if (matchingLine) return matchingLine;
        }
        return '';
    }

    private activePatternCandidateItems() {
        const items: AiAssistantResultItem[] = [];
        this.aiAssistantResults.forEach((result) => {
            this.asArray(result.items).forEach((item) => {
                if (!this.isAssessmentEvidenceItem(result, item)) return;
                if (!item) return;
                const status = String(item.decision_status || '');
                if (['excluded', 'ignored', 'deferred'].indexOf(status) >= 0) return;
                items.push(item);
            });
        });
        return items;
    }

    private currentPatternSupportingEvidence(limit: number = 2) {
        const rows: string[] = [];
        this.activePatternCandidateItems().forEach((item) => {
            this.asArray(item.supporting_evidence).forEach((value) => {
                const text = this.clinicalRecordDisplayText(value);
                if (text) rows.push(text);
            });
            const evidence = this.clinicalRecordDisplayText(item.evidence_summary);
            if (evidence) rows.push(evidence);
        });
        return this.dedupeStrings(rows).slice(0, limit);
    }

    private currentPatternOpposingEvidence(limit: number = 2) {
        const rows: string[] = [];
        this.activePatternCandidateItems().forEach((item) => {
            this.asArray(item.opposing_evidence).forEach((value) => {
                const text = this.clinicalRecordDisplayText(value);
                if (text) rows.push(text);
            });
        });
        return this.dedupeStrings(rows).slice(0, limit);
    }

    private hasClinicalValue(value: any) {
        const normalized = this.normalizeLookupLabel(value);
        return normalized !== ''
            && normalized !== '-'
            && normalized !== '미기록'
            && normalized !== '미확인'
            && normalized !== '확인필요'
            && normalized !== 'unknown'
            && normalized !== 'none';
    }

    private dedupeStrings(values: string[]) {
        const result: string[] = [];
        const seen: Record<string, boolean> = {};
        (values || []).forEach((value) => {
            const text = String(value || '').trim();
            if (!text || seen[text]) return;
            seen[text] = true;
            result.push(text);
        });
        return result;
    }

    private truncateText(value: string, limit: number = 80) {
        const text = String(value || '').replace(/\s+/g, ' ').trim();
        if (text.length <= limit) return text;
        return `${text.slice(0, Math.max(0, limit - 1))}…`;
    }

    private escapeHtml(value: string) {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private missingClinicalSummaryItems() {
        const missing: string[] = [];
	        if (this.summaryLookup(['chief_complaint'], ['주호소'], '-') === '-') missing.push('주호소 입력 전');
	        if (this.summaryLookup(['medication', 'medicine'], ['복약', '복용약'], '-') === '-') missing.push('복약 입력 전');
	        if (this.summaryLookup(['allergy'], ['알레르기'], '-') === '-') missing.push('알레르기 입력 전');
        return missing;
    }

    private timeLabel() {
        try {
            return this.koreaTimeLabel();
        } catch (e) {
            return '';
        }
    }

    private koreaDateTimeParts(date: Date = new Date()) {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: 'Asia/Seoul',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(date);
        const values: WorkspaceMap = {};
        parts.forEach((part) => {
            if (part.type !== 'literal') values[part.type] = part.value;
        });
        return values;
    }

    private koreaTimeLabel(date: Date = new Date()) {
        const parts = this.koreaDateTimeParts(date);
        return `${parts.hour}:${parts.minute}`;
    }

    private koreaIsoNow(date: Date = new Date()) {
        const parts = this.koreaDateTimeParts(date);
        return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}+09:00`;
    }

    private emptySummaryCandidateDraft(): SummaryCandidateDraft {
        return {
            overview: '',
            key_points: [],
            limitations: [],
            source_refs: [],
            dirty: false,
        };
    }

    private toSummaryCandidateDraft(candidate: WorkspaceMap): SummaryCandidateDraft {
        const summary = candidate && candidate.summary ? candidate.summary : {};
        const keyPoints = this.asArray(summary.key_points).map((item) => ({
            label: item && item.label ? String(item.label) : '요약',
            text: item && item.text ? String(item.text) : this.displayValue(item, ''),
            source_refs: item && Array.isArray(item.source_refs) ? item.source_refs : [],
            dirty: false,
        })).filter((item) => item.text !== '');
        return {
            overview: summary && summary.overview ? String(summary.overview) : '',
            key_points: keyPoints,
            limitations: this.asArray(summary.limitations).map((item) => this.displayValue(item, '')).filter((item) => item !== ''),
            source_refs: candidate && Array.isArray(candidate.source_refs) ? candidate.source_refs : [],
            dirty: false,
        };
    }

    private aiResultMessage(run: any, fallback: string) {
        if (!run) return fallback;
        if (run.message) return run.message;
        if (run.error && run.error.message) return run.error.message;
        if (run.error_message) return run.error_message;
        const code = run.error_code || run.status || '';
        if (code) return `${fallback} (${code})`;
        return fallback;
    }

    private workflowStep(label: string, state: string, tone: string, active: boolean): SoapWorkflowStep {
        return { label, state, tone, active };
    }

    private async syncStatus(status: string) {
        if (this.readOnlyMode || !this.canManageEncounterState) return;
        this.saving = true;
        this.error = '';
        await this.service.render();

        const result = await this.callApi('sync_state', {
            encounter_id: this.encounterId,
            status,
            precheck_status: this.precheck && this.precheck.submitted ? 'submitted' : '',
        });
        this.saving = false;

        if (result.ok) {
            const payload = result.data && result.data.context ? result.data.context : result.data;
            if (payload.encounter) {
                this.encounter = payload.encounter;
                this.precheck = payload.encounter.precheck || this.precheck;
                this.patient = payload.encounter.patient || this.patient;
            }
            await this.loadContext(false);
            return;
        }
        this.error = result.message;
        await this.service.render();
    }

    private applyContext(context: WorkspaceMap, preserveLocalSoapDrafts: boolean = false) {
        const localSoapDrafts = preserveLocalSoapDrafts ? this.captureDirtySoapDrafts() : [];
        this.data = context || null;
        this.encounter = context && context.encounter ? context.encounter : null;
        this.patient = context && context.patient ? context.patient : null;
        this.precheck = context && context.precheck ? context.precheck : null;
        this.summary = context && context.summary ? context.summary : {};
        this.checklist = context && context.checklist ? context.checklist : {};
        this.standardTools = context && context.standard_tools ? context.standard_tools : {};
        this.referenceComparison = context && context.reference_comparison ? context.reference_comparison : (this.standardTools && this.standardTools.comparison ? this.standardTools.comparison : {});
        this.biometric = context && context.biometric ? context.biometric : {};
        this.emr = context && context.emr ? context.emr : {};
        this.soap = context && context.soap ? context.soap : {};
        this.soapDraftRevision = this.soapDraftRevisionValue(this.soap);
        this.finalChart = context && context.final_chart ? context.final_chart : {};
        this.finalGateConfirmed = false;
        this.finalWarningsAcknowledged = false;
        this.auth = context && context.auth ? context.auth : {};
        this.requestContext = context && context.request_context ? context.request_context : {};
        this.llmSettings = context && context.llm_settings ? context.llm_settings : {};
        this.mcpHarness = context && context.mcp_harness ? context.mcp_harness : {};
        const conversationContext = context && context.conversation ? context.conversation : {};
        this.syncConversationTurns(conversationContext);
        this.syncConversationRecommendations(conversationContext);
        this.syncMcpRecommendations(this.mcpHarness);
        this.encounterId = (context && context.encounter_id) || (this.encounter && (this.encounter.encounter_id || this.encounter.id)) || this.encounterId;
        this.soapEntryPrepareRun = (context && context.soap_entry_prepare) || (this.soap && this.soap.entry_prepare) || {};
        this.selectedChecklistCode = (this.checklist.session && this.checklist.session.checklist_code)
            || (this.checklist.selected_checklist && this.checklist.selected_checklist.code)
            || this.selectedChecklistCode
            || 'low_back_pain';
        this.syncChecklistDrafts();
        this.syncSoapDrafts();
        this.syncResolvedSoapCandidateDecisions();
        if (localSoapDrafts.length) this.restoreDirtySoapDrafts(localSoapDrafts);
        this.refreshClinicalChartViewState();
        this.markSoapCandidateQueueDirty();
    }

    private syncMcpRecommendations(harness: WorkspaceMap) {
        const mapped = this.asArray(harness && harness.recommendations)
            .map((recommendation) => this.mcpRecommendationResult(recommendation))
            .filter((result) => !!result) as AiAssistantResult[];
        const nonMcp = this.aiAssistantResults.filter((result) => result.mcp_managed !== true);
        this.aiAssistantResults = mapped.concat(nonMcp).slice(0, 40);
        if (mapped.length) this.markSoapCandidateQueueDirty();
    }

    private mcpRecommendationResult(recommendation: WorkspaceMap): AiAssistantResult | null {
        if (!recommendation || !recommendation.id || !recommendation.recommendation_type) return null;
        const patch = recommendation.target_patch && typeof recommendation.target_patch === 'object'
            ? recommendation.target_patch
            : {};
        const target = String(patch.target_field || '');
        const section = target.indexOf('soap.') === 0 ? target.slice(5).toUpperCase() : '';
        const status = String(recommendation.status || 'pending_doctor_review');
        const sourceRefs = this.asArray(recommendation.evidence_refs);
        const resultMeta = recommendation.result && typeof recommendation.result === 'object' ? recommendation.result : {};
        const clinical = resultMeta.clinical_candidate && typeof resultMeta.clinical_candidate === 'object'
            ? resultMeta.clinical_candidate
            : {};
        const pattern = clinical.pattern && typeof clinical.pattern === 'object' ? clinical.pattern : {};
        const prescription = clinical.prescription && typeof clinical.prescription === 'object' ? clinical.prescription : {};
        const confidence = clinical.confidence && typeof clinical.confidence === 'object' ? clinical.confidence : {};
        const providerMeta = recommendation.provider_meta && typeof recommendation.provider_meta === 'object'
            ? recommendation.provider_meta
            : {};
        const patientEvidence = this.asArray(clinical.patient_evidence);
        const item: AiAssistantResultItem = {
            kind: String(recommendation.recommendation_type || ''),
            title: String(recommendation.title || recommendation.type_label || 'AI 추천'),
            description: String(recommendation.summary || ''),
            reason: String(recommendation.rationale || ''),
            source: 'MCP 진료지원 AI',
            source_refs: sourceRefs,
            source_labels: sourceRefs.map((ref) => String((ref && ref.label) || '')).filter((value) => value !== ''),
            decision_status: status === 'applied' ? 'added' : status,
            applied_to_soap: status === 'applied',
            recommendation_id: String(recommendation.id),
            recommendation_type: String(recommendation.recommendation_type),
            mcp_managed: true,
            target_field: target,
            existing_value: String(patch.existing_value || ''),
            proposed_value: String(patch.proposed_value || ''),
            patch_hash: String(patch.patch_hash || ''),
            soap_section: ['S', 'O', 'A', 'P'].indexOf(section) >= 0 ? section : undefined,
            soap_sentence_text: String(patch.proposed_value || ''),
            target_sentence_preview: String(patch.existing_value || ''),
            answer_target: target ? this.mcpTargetFieldDisplayText(target) : '의료진 검토',
            evidence_summary: String(recommendation.rationale || recommendation.summary || ''),
            doctor_judgement_note: String(recommendation.disclaimer || ''),
            pattern_code: String(pattern.code || ''),
            pattern_name: String(pattern.name || ''),
            display_name: String(pattern.name || recommendation.title || ''),
            prescription_name: String(prescription.name || ''),
            prescription_herbs: this.asArray(prescription.herbs).map((herb) => String((herb && herb.name) || '')).filter((value) => value !== ''),
            prescription_indications: this.asArray(prescription.indications).map((value) => String(value || '')).filter((value) => value !== ''),
            prescription_verification_status: String(prescription.verification_status || ''),
            supporting_evidence: patientEvidence.map((evidence) => String((evidence && evidence.summary) || '')).filter((value) => value !== ''),
            opposing_evidence: this.asArray(clinical.opposing_evidence).map((value) => String(value || '')).filter((value) => value !== ''),
            followup_questions: this.asArray(clinical.followup_questions).map((value) => String(value || '')).filter((value) => value !== ''),
            contraindication_checks: this.asArray(clinical.contraindication_checks).map((value) => String(value || '')).filter((value) => value !== ''),
            missing_information: this.asArray(clinical.missing_information).map((value) => String(value || '')).filter((value) => value !== ''),
            knowledge_evidence: this.asArray(clinical.knowledge_evidence),
            tool_provenance: this.asArray(clinical.tool_provenance),
            tool_trace: this.asArray(providerMeta.tool_trace),
            query_expansion: clinical.query_expansion && typeof clinical.query_expansion === 'object' ? clinical.query_expansion : {},
            herb_details: this.asArray(clinical.herb_details),
            acupoint_details: this.asArray(clinical.acupoint_details),
            clinical_cases: this.asArray(clinical.clinical_cases),
            routing_summary: providerMeta.routing_summary && typeof providerMeta.routing_summary === 'object' ? providerMeta.routing_summary : {},
            confidence_label: String(confidence.level || ''),
            confidence_score: Number(confidence.score || 0),
        };
        return {
            id: String(recommendation.id),
            type: String(recommendation.type_label || 'AI 추천'),
            title: String(recommendation.title || recommendation.type_label || 'AI 추천'),
            tone: status === 'applied' ? 'success' : 'warning',
            status,
            summary: String(recommendation.summary || ''),
            items: [item],
            sources: item.source_labels || [],
            uncertainty: [String(recommendation.disclaimer || this.mcpHarness.disclaimer || 'AI 추천이며 의료진의 최종 확인이 필요합니다.')],
            createdAt: String(recommendation.created_at || recommendation.updated_at || '최근'),
            requestText: '표준 PatientContext 기반 MCP 추천',
            mcp_managed: true,
            disclaimer: String(recommendation.disclaimer || ''),
            persisted: true,
        };
    }

    private mcpTargetFieldDisplayText(target: string) {
        const labels: WorkspaceMap = {
            'soap.S': '문진(S) 초안',
            'soap.O': '진찰·소견(O) 초안',
            'soap.A': '변증·진단(A) 초안',
            'soap.P': '치료·처방(P) 초안',
        };
        return labels[String(target || '')] || '의료진 검토';
    }

    public isPrescriptionCandidateItem(item: AiAssistantResultItem) {
        return String((item && item.recommendation_type) || (item && item.kind) || '') === 'prescription_candidate';
    }

    public prescriptionVerificationLabel(item: AiAssistantResultItem) {
        return item && item.prescription_verification_status === 'tool_verified'
            ? '저작도구 임상 Tool 확인 완료'
            : '저작도구 임상 Tool 확인 필요';
    }

    public prescriptionToolTrace(item: AiAssistantResultItem) {
        const provenance = this.asArray(item && item.tool_provenance);
        return provenance.length ? provenance : this.asArray(item && item.tool_trace);
    }

    public authoringEvidenceLabel(evidence: WorkspaceMap) {
        return this.clinicalRecordDisplayText(
            (evidence && (evidence.label || evidence.title || evidence.citation)) || '저작도구 근거',
        );
    }

    public authoringEvidenceDetail(evidence: WorkspaceMap) {
        const values = [
            evidence && evidence.excerpt,
            evidence && evidence.citation,
            evidence && evidence.section,
        ].filter((value) => value);
        return this.clinicalRecordDisplayText(values.join(' · ') || '출처 식별자 확인');
    }

    public authoringExpansionTerms(item: AiAssistantResultItem) {
        const expansion = item && item.query_expansion && typeof item.query_expansion === 'object'
            ? item.query_expansion
            : {};
        return this.asArray(expansion.expanded_terms).filter((term) => !!term && !!term.term).slice(0, 12);
    }

    public authoringExpansionOriginalTerms(item: AiAssistantResultItem) {
        const expansion = item && item.query_expansion && typeof item.query_expansion === 'object'
            ? item.query_expansion
            : {};
        return this.asArray(expansion.original_terms).map((term) => String(term || '')).filter((term) => term !== '').slice(0, 8);
    }

    public authoringExpansionTermLabel(term: WorkspaceMap) {
        const weight = Math.max(0, Math.min(1, Number((term && term.weight) || 0)));
        const relation = String((term && term.relation) || 'related');
        const relationLabels: WorkspaceMap = {
            synonym: '동의어', western_symptom: '양방 증상 연결', related: '관련어', pattern: '변증 연결',
        };
        return `${this.clinicalRecordDisplayText(term && term.term)} · ${relationLabels[relation] || '관련어'}${weight ? ` ${Math.round(weight * 100)}%` : ''}`;
    }

    public selectedAuthoringEvidence(item: AiAssistantResultItem) {
        const evidence = this.asArray(item && item.knowledge_evidence);
        if (!evidence.length) return null;
        const rawIndex = Number((item && item.authoring_evidence_selected_index) || 0);
        const index = Number.isFinite(rawIndex) ? Math.max(0, Math.min(evidence.length - 1, rawIndex)) : 0;
        return evidence[index];
    }

    public authoringEvidenceIsSelected(item: AiAssistantResultItem, index: number) {
        const selected = Number((item && item.authoring_evidence_selected_index) || 0);
        return selected === index;
    }

    public async selectAuthoringEvidence(item: AiAssistantResultItem, index: number) {
        if (!item || !this.asArray(item.knowledge_evidence).length) return;
        item.authoring_evidence_selected_index = Math.max(0, Math.min(this.asArray(item.knowledge_evidence).length - 1, Number(index || 0)));
        await this.service.render();
    }

    public authoringEvidenceLocator(evidence: WorkspaceMap) {
        const values = [
            evidence && evidence.title,
            evidence && evidence.section ? `절 ${evidence.section}` : '',
            evidence && evidence.page ? `${evidence.page}쪽` : '',
            evidence && evidence.locator,
        ].filter((value) => value);
        return this.clinicalRecordDisplayText(values.join(' · ') || '위치 정보 없음');
    }

    public authoringEvidenceExcerpt(evidence: WorkspaceMap) {
        return this.clinicalRecordDisplayText(
            (evidence && evidence.excerpt) || '저작도구가 반환한 원문 구간이 없습니다.',
        );
    }

    public authoringEvidenceCitation(evidence: WorkspaceMap) {
        return this.clinicalRecordDisplayText(
            (evidence && evidence.citation) || 'citation 정보 없음',
        );
    }

    public activeClinicalEvidenceItem() {
        return this.activeClinicalCandidateRow && this.activeClinicalCandidateRow.item
            ? this.activeClinicalCandidateRow.item
            : null;
    }

    public toolTraceStatusLabel(trace: WorkspaceMap) {
        if (!trace || trace.status !== 'success') return '실행 확인 필요';
        return trace.matched_resource_id ? '후보 일치 확인' : '조회 완료';
    }

    private syncConversationTurns(conversation: WorkspaceMap) {
        if (this.conversationSending || this.conversationStreaming) return;
        const turns = this.asArray(conversation && conversation.turns)
            .map((turn) => ({
                role: String((turn && turn.role) || ''),
                text: String((turn && turn.text) || ''),
                createdAt: String((turn && turn.createdAt) || (turn && turn.created_at) || ''),
                status: String((turn && turn.status) || ''),
            }))
            .filter((turn) => ['user', 'assistant'].indexOf(turn.role) >= 0 && turn.text.trim() !== '');
        this.conversationTurns = turns;
        this.scrollConversationThread(true);
    }

    private syncConversationRecommendations(conversation: WorkspaceMap) {
        const persisted = this.asArray(conversation && conversation.recommendations)
            .map((item) => this.normalizePersistedConversationRecommendation(item))
            .filter((item) => !!item) as AiAssistantResult[];
        const localConversation = this.aiAssistantResults.filter((result) => this.isConversationClinicalResult(result));
        const nonConversation = this.aiAssistantResults.filter((result) => !this.isConversationClinicalResult(result));
        const recovered = this.restoreConversationRecommendationsFromTurns(persisted.concat(localConversation));
        if (!persisted.length) {
            const restored = recovered ? [recovered] : [];
            this.aiAssistantResults = this.dedupeConversationRecommendations(restored.concat(localConversation).concat(nonConversation)).slice(0, 30);
            if (recovered) this.markSoapCandidateQueueDirty();
            return;
        }

        const merged = persisted
            .sort((left, right) => this.queueSortKey(right.createdAt, 0) - this.queueSortKey(left.createdAt, 0))
            .map((result) => {
                const local = localConversation.find((item) => this.conversationRecommendationMatches(item, result));
                if (local) this.mergeConversationRecommendationLocalState(result, local);
                return result;
            });
        const transient = localConversation.filter((result) => {
            if (result.persisted || result.source_turn_id) return false;
            return !merged.some((item) => this.conversationRecommendationMatches(result, item));
        });
        const restored = recovered ? [recovered] : [];
        this.aiAssistantResults = this.dedupeConversationRecommendations(merged.concat(transient).concat(restored).concat(nonConversation)).slice(0, 30);
        if (recovered) this.markSoapCandidateQueueDirty();
    }

    private restoreConversationRecommendationsFromTurns(existingResults: AiAssistantResult[] = []) {
        if (this.readOnlyMode) return null;
        const existingItems = existingResults.reduce((items, result) => items.concat(this.asArray(result && result.items)), [] as AiAssistantResultItem[]);
        const recoveredItems: AiAssistantResultItem[] = [];
        const sourceRequests: string[] = [];
        let latestCreatedAt = '';
        let inspectedUserTurns = 0;

        for (let index = this.conversationTurns.length - 1; index >= 0 && inspectedUserTurns < 16; index -= 1) {
            const turn = this.conversationTurns[index];
            if (!turn || turn.role !== 'user') continue;
            inspectedUserTurns += 1;
            const requestText = String(turn.text || '').trim();
            const nextTurn = this.conversationTurns[index + 1];
            const replyText = nextTurn && nextTurn.role === 'assistant' && nextTurn.status !== 'failed'
                ? String(nextTurn.text || '')
                : '';
            const signal = this.conversationChartSignal(requestText, replyText);
            if (!signal.chartWorth) continue;
            const lines = this.conversationRecommendationLines(replyText);
            const items = this.buildConversationClinicalCandidateItems(requestText, replyText, lines, signal);
            items.forEach((item) => {
                if (recoveredItems.length >= 8
                    || this.conversationItemDuplicate(existingItems, item)
                    || this.conversationItemDuplicate(recoveredItems, item)
                    || this.conversationItemAlreadyInSoap(item)) return;
                recoveredItems.push(item);
            });
            if (sourceRequests.length < 4) sourceRequests.push(requestText);
            if (!latestCreatedAt && turn.createdAt) latestCreatedAt = turn.createdAt;
            if (recoveredItems.length >= 8) break;
        }

        if (!recoveredItems.length) return null;
        const card = this.aiAssistantResult(
            '임상 단서 정리',
            '이전 대화 임상 단서',
            'ai',
            this.conversationCandidateCardSummary(recoveredItems),
            recoveredItems,
            ['이전 진료 대화', '환자 요약', '한의 차트'],
            ['한의사 판단 전'],
            sourceRequests.join('\n')
        );
        card.id = `conversation-recovered-${this.conversationTurns.length}-${recoveredItems.length}`;
        card.createdAt = latestCreatedAt || card.createdAt;
        return card;
    }

    private conversationItemAlreadyInSoap(item: AiAssistantResultItem) {
        const section = this.normalizedSoapSection(item && item.soap_section ? item.soap_section : 'S');
        const candidateText = [
            item && (item.soap_sentence_text || item.description || item.title),
            item && item.clinical_evidence,
            item && item.source_excerpt,
        ].filter((value) => !!value).join(' ');
        const sections = this.soap && this.soap.sections && typeof this.soap.sections === 'object' ? this.soap.sections : {};
        if (!candidateText) return false;
        const soapTexts = this.asArray(sections[section]).map((sentence) => {
            const text = typeof sentence === 'string'
                ? sentence
                : String((sentence && (sentence.text || sentence.display_text)) || '');
            return text.trim();
        }).filter((text) => text !== '');
        if (soapTexts.some((text) => this.conversationTextSimilarity(candidateText, text) >= 0.62)) return true;
        const candidateSignals = this.conversationComparableSignals(section, candidateText);
        if (!candidateSignals.length) return false;
        const soapSignals = this.dedupeStrings(soapTexts.reduce((values, text) => {
            return values.concat(this.conversationComparableSignals(section, text));
        }, [] as string[]));
        return candidateSignals.every((signal) => soapSignals.indexOf(signal) >= 0);
    }

    private conversationComparableSignals(section: string, text: string) {
        const signals = this.conversationClinicalSignals(text);
        const normalized = this.normalizedSoapSection(section);
        let values: any[] = [];
        if (normalized === 'O') values = this.asArray(signals.findings);
        else if (normalized === 'A') {
            values = this.asArray(signals.patterns)
                .concat(this.asArray(signals.symptoms))
                .concat(this.asArray(signals.findings));
        }
        else if (normalized === 'P') values = this.asArray(signals.treatments);
        else {
            values = this.asArray(signals.symptoms)
                .concat(this.asArray(signals.locations))
                .concat(this.asArray(signals.qualities))
                .concat(this.asArray(signals.aggravating))
                .concat(this.asArray(signals.relieving))
                .concat(this.asArray(signals.associated));
        }
        return this.dedupeStrings(values.map((value) => this.normalizeLookupLabel(value)).filter((value) => value !== ''));
    }

    private normalizePersistedConversationRecommendation(value: any): AiAssistantResult | null {
        if (!value || typeof value !== 'object') return null;
        const items = this.asArray(value.items)
            .map((item) => this.normalizePersistedConversationRecommendationItem(item))
            .filter((item) => !!item) as AiAssistantResultItem[];
        if (!items.length) return null;
        return {
            id: String(value.id || value.source_turn_id || `conversation-${Date.now()}-${items.length}`),
            type: String(value.type || '임상 단서 정리'),
            title: String(value.title || '임상 단서 참고'),
            tone: String(value.tone || 'ai'),
            status: String(value.status || 'pending_doctor_review'),
            summary: String(value.summary || this.conversationCandidateCardSummary(items)),
            items,
            sources: this.asArray(value.sources).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            uncertainty: this.asArray(value.uncertainty).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            createdAt: String(value.createdAt || value.created_at || this.timeLabel()),
            requestText: String(value.requestText || value.request_text || ''),
            persisted: value.persisted === true,
            source_turn_id: String(value.source_turn_id || ''),
        };
    }

    private normalizePersistedConversationRecommendationItem(value: any): AiAssistantResultItem | null {
        if (!value || typeof value !== 'object') return null;
        const title = String(value.title || value.clinical_summary || value.soap_sentence_text || '').trim();
        const description = String(value.description || value.evidence_summary || value.soap_sentence_text || title).trim();
        const soapText = String(value.soap_sentence_text || value.text || description || title).trim();
        if (!title && !description && !soapText) return null;
        return {
            kind: String(value.kind || 'conversation_chart_candidate'),
            title: title || this.soapSectionDisplayName(value.soap_section || 'S'),
            display_name: String(value.display_name || value.title || value.clinical_summary || ''),
            description: description || soapText,
            source: String(value.source || '진료 대화'),
            evidence_summary: String(value.evidence_summary || value.clinical_evidence || ''),
            supporting_evidence: this.asArray(value.supporting_evidence).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            opposing_evidence: this.asArray(value.opposing_evidence).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            followup_questions: this.asArray(value.followup_questions).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            doctor_judgement_note: String(value.doctor_judgement_note || ''),
            source_labels: this.asArray(value.source_labels).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            decision_status: String(value.decision_status || 'pending_doctor_review'),
            soap_section: value.soap_section ? this.normalizedSoapSection(value.soap_section) : '',
            soap_sentence_text: soapText,
            source_refs: this.asArray(value.source_refs),
            suggestion_reasons: this.asArray(value.suggestion_reasons).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            answer_target: String(value.answer_target || ''),
            applied_to_checklist: value.applied_to_checklist === true,
            applied_to_soap: value.applied_to_soap === true,
            conversation_signal_key: String(value.conversation_signal_key || ''),
            signal_strength: Number(value.signal_strength || 0),
            chart_value_reason: String(value.chart_value_reason || ''),
            clinical_summary: String(value.clinical_summary || ''),
            clinical_evidence: String(value.clinical_evidence || ''),
            source_excerpt: String(value.source_excerpt || ''),
            patient_cues: this.asArray(value.patient_cues).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            connection_reasons: this.asArray(value.connection_reasons).map((item) => this.clinicalRecordDisplayText(item)).filter((item) => item !== ''),
            recommendation_item_id: String(value.recommendation_item_id || ''),
        };
    }

    private conversationRecommendationMatches(left: AiAssistantResult, right: AiAssistantResult) {
        const leftId = String((left && (left.source_turn_id || left.id)) || '');
        const rightId = String((right && (right.source_turn_id || right.id)) || '');
        if (leftId && rightId && leftId === rightId) return true;
        const leftKey = `${left && left.requestText ? left.requestText : ''}::${left && left.summary ? left.summary : ''}`;
        const rightKey = `${right && right.requestText ? right.requestText : ''}::${right && right.summary ? right.summary : ''}`;
        if (leftKey !== '::' && leftKey === rightKey) return true;
        return this.conversationTextSimilarity(leftKey, rightKey) >= 0.7;
    }

    private mergeConversationRecommendationLocalState(target: AiAssistantResult, local: AiAssistantResult) {
        if (!target || !local) return;
        if (local.status && local.status !== 'pending_doctor_review') target.status = local.status;
        target.items.forEach((item) => {
            const localItem = this.asArray(local.items).find((candidate) => this.conversationRecommendationItemMatches(item, candidate));
            if (!localItem) return;
            ['decision_status', 'applied_action', 'chart_value_reason'].forEach((key) => {
                if ((localItem as any)[key]) (item as any)[key] = (localItem as any)[key];
            });
            item.applied_to_soap = localItem.applied_to_soap === true;
            item.applied_to_checklist = localItem.applied_to_checklist === true;
        });
    }

    private conversationRecommendationItemMatches(left: AiAssistantResultItem, right: AiAssistantResultItem) {
        if (!left || !right) return false;
        if (left.recommendation_item_id && right.recommendation_item_id && left.recommendation_item_id === right.recommendation_item_id) return true;
        if (left.conversation_signal_key && right.conversation_signal_key && left.conversation_signal_key === right.conversation_signal_key) return true;
        if (this.normalizedSoapSection(left.soap_section || 'S') !== this.normalizedSoapSection(right.soap_section || 'S')) return false;
        return this.conversationTextSimilarity(left.soap_sentence_text || left.description || left.title || '', right.soap_sentence_text || right.description || right.title || '') >= 0.62;
    }

    private dedupeConversationRecommendations(rows: AiAssistantResult[]) {
        const accepted: AiAssistantResult[] = [];
        rows.forEach((row) => {
            if (!row) return;
            if (accepted.some((existing) => this.conversationRecommendationMatches(existing, row))) return;
            accepted.push(row);
        });
        return accepted;
    }

    private captureDirtySoapDrafts() {
        const rows: SoapSentenceDraft[] = [];
        this.soapSectionOrder.forEach((section) => {
            this.soapItems(section).forEach((item) => {
                if (!item || !item.dirty) return;
                rows.push({
                    ...item,
                    soap_section: item.soap_section || section,
                    source_refs: Array.isArray(item.source_refs) ? item.source_refs.slice() : [],
                });
            });
        });
        return rows;
    }

    private restoreDirtySoapDrafts(rows: SoapSentenceDraft[]) {
        rows.forEach((item) => {
            const section = this.normalizedSoapSection(item.soap_section || 'S');
            const list = this.soapDrafts[section] || [];
            const index = list.findIndex((row) => row.id === item.id);
            if (index >= 0) {
                list[index] = { ...list[index], ...item, dirty: true };
            } else {
                list.push({ ...item, dirty: true });
            }
            this.soapDrafts[section] = list;
        });
    }

    private syncChecklistDrafts() {
        const drafts: Record<string, ChecklistDraft> = {};
        this.checklistItems.forEach((item) => {
            const code = item.code || '';
            drafts[code] = {
                status: item.status || 'unchecked',
                note: item.note || '',
                dirty: false,
            };
        });
        this.checklistDrafts = drafts;
    }

    private syncSoapDrafts() {
        const drafts = this.blankSoapDrafts();
        this.objectiveFindingDrafts = {};
        this.objectiveFindingManualText = '';
        const sections = this.soap && this.soap.sections ? this.soap.sections : {};
        this.soapSectionOrder.forEach((section) => {
            this.asArray(sections[section]).forEach((item, index) => {
                drafts[section].push(this.toSoapDraft(item, section, index));
            });
        });
        this.soapDrafts = drafts;
        this.soapValidation = this.soap && this.soap.validation_summary ? this.soap.validation_summary : {};
    }

    private blankSoapDrafts(): Record<string, SoapSentenceDraft[]> {
        return {
            S: [],
            O: [],
            A: [],
            P: [],
        };
    }

    private toSoapDraft(item: any, section: string, index: number): SoapSentenceDraft {
        const id = item && item.id ? String(item.id) : `payload-${section}-${index}`;
        const persisted = id.indexOf('payload-') !== 0 && id.indexOf('local-') !== 0;
        const sentenceMeta = item && item.sentence_meta && typeof item.sentence_meta === 'object' ? item.sentence_meta : {};
        return {
            id,
            soap_section: item && item.soap_section ? item.soap_section : section,
            text: this.soapText(item),
            status: item && item.status ? item.status : 'doctor_edited',
            author_type: item && item.author_type ? item.author_type : 'doctor',
            source_ai_request_id: item && item.source_ai_request_id ? item.source_ai_request_id : '',
            sentence_order: Number(item && item.sentence_order ? item.sentence_order : index + 1),
            source_refs: item && Array.isArray(item.source_refs) ? item.source_refs : [],
            doctor_decision_reason: item && item.doctor_decision_reason ? item.doctor_decision_reason : '',
            doctor_decision_status: sentenceMeta.doctor_decision_status || '',
            candidate_status: sentenceMeta.candidate_status || '',
            candidate_source: sentenceMeta.source || '',
            needs_update: sentenceMeta.needs_update === true,
            sentence_meta: sentenceMeta,
            updated: item && item.updated ? item.updated : '',
            dirty: false,
            persisted,
        };
    }

    private serializeSoapDrafts() {
        const rows: any[] = [];
        this.soapSectionOrder.forEach((section) => {
            this.soapItems(section).forEach((item, index) => {
                rows.push({
                    id: item.id,
                    soap_section: section,
                    text: item.text,
                    status: item.status,
                    author_type: item.author_type || 'doctor',
                    sentence_order: index + 1,
                    source_refs: item.source_refs || [],
                    doctor_decision_reason: item.doctor_decision_reason || '',
                    doctor_decision_status: item.doctor_decision_status || '',
                    candidate_status: item.candidate_status || '',
                    needs_update: item.needs_update === true,
                    sentence_meta: item.sentence_meta || {},
                });
            });
        });
        return rows;
    }

    private resolveContext() {
        this.encounterId = this.contextValue('encounter_id') || this.queryValue('encounter_id') || this.pathEncounterId();
    }

    private contextValue(key: string) {
        if (!this.context || typeof this.context !== 'object') return '';
        if (this.context[key]) return String(this.context[key]);
        if (this.context.encounter && this.context.encounter[key]) return String(this.context.encounter[key]);
        return '';
    }

    private queryValue(key: string) {
        try {
            const params = new URLSearchParams(window.location.search || '');
            return params.get(key) || '';
        } catch (e) {
            return '';
        }
    }

    private pathEncounterId() {
        try {
            const parts = (window.location.pathname || '').split('/').filter((part) => !!part);
            const index = parts.indexOf('clinic');
            if (index >= 0 && parts[index + 1]) return decodeURIComponent(parts[index + 1]);
            return parts.length ? decodeURIComponent(parts[parts.length - 1]) : '';
        } catch (e) {
            return '';
        }
    }

    private get workspacePreferenceKey() {
        return 'hanscribe:clinic:preferences:v2';
    }

    private get workspaceRecoveryKey() {
        return `hanscribe:clinic:recovery:v1:${this.encounterId || 'unknown'}`;
    }

    private loadWorkspacePreferences() {
        if (typeof window === 'undefined') return;
        try {
            const raw = window.localStorage.getItem(this.workspacePreferenceKey);
            const value = raw ? JSON.parse(raw) : {};
            const section = this.normalizedSoapSection(value.activeSoapSection || 'S');
            if (this.soapSectionOrder.indexOf(section) >= 0) this.activeSoapSection = section;
            if (value.layoutMode === 'chart' || value.layoutMode === 'balanced') this.workspaceLayoutMode = value.layoutMode;
            if (value.densityMode === 'all' || value.densityMode === 'focused') {
                this.workspaceDensityMode = value.densityMode;
                this.showAllSubjectiveInquiry = value.densityMode === 'all';
            }
            if (value.assistPanels && typeof value.assistPanels === 'object') {
                this.soapSectionAssistPanelOpenState = {
                    ...this.soapSectionAssistPanelOpenState,
                    ...value.assistPanels,
                };
            }
        } catch (e) {
            // 잘못된 이전 설정은 기본값으로 안전하게 무시한다.
        }
    }

    private saveWorkspacePreferences() {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(this.workspacePreferenceKey, JSON.stringify({
                activeSoapSection: this.activeSoapSection,
                layoutMode: this.workspaceLayoutMode,
                densityMode: this.workspaceDensityMode,
                assistPanels: this.soapSectionAssistPanelOpenState,
            }));
        } catch (e) {
            // 브라우저 저장소가 차단돼도 진료 입력 자체는 계속할 수 있다.
        }
    }

    private isWorkspaceQaMode() {
        if (typeof window === 'undefined') return false;
        try {
            return new URLSearchParams(window.location.search || '').get('workbench_qa') === '1';
        } catch (e) {
            return false;
        }
    }

    private createWorkspaceRecoverySnapshot(): WorkspaceRecoverySnapshot {
        const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value || {}));
        return {
            encounterId: this.encounterId,
            updatedAt: this.koreaIsoNow(),
            activeSoapSection: this.activeSoapSection,
            soapDrafts: clone(this.soapDrafts),
            objectiveFindingDrafts: clone(this.objectiveFindingDrafts),
            objectiveFindingManualText: String(this.objectiveFindingManualText || ''),
            treatmentPlanDrafts: clone(this.treatmentPlanDrafts),
        };
    }

    private persistWorkspaceRecovery() {
        if (typeof window === 'undefined' || !this.encounterId || this.clinicalReadOnly || this.isWorkspaceQaMode()) return;
        try {
            const snapshot = this.createWorkspaceRecoverySnapshot();
            window.localStorage.setItem(this.workspaceRecoveryKey, JSON.stringify(snapshot));
        } catch (e) {
            // 로컬 저장 실패는 서버 자동저장을 막지 않는다.
        }
    }

    private clearWorkspaceRecovery() {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.removeItem(this.workspaceRecoveryKey);
        } catch (e) {
            // 저장소 정리 실패는 사용자 작업에 영향을 주지 않는다.
        }
        this.recoveryAvailable = false;
        this.recoverySnapshot = null;
    }

    private loadWorkspaceRecovery() {
        if (typeof window === 'undefined' || !this.encounterId || this.isWorkspaceQaMode()) return;
        try {
            const raw = window.localStorage.getItem(this.workspaceRecoveryKey);
            const snapshot = raw ? JSON.parse(raw) as WorkspaceRecoverySnapshot : null;
            if (!snapshot || snapshot.encounterId !== this.encounterId || !snapshot.updatedAt) return;
            const ageMs = Date.now() - new Date(snapshot.updatedAt).getTime();
            if (!Number.isFinite(ageMs) || ageMs > 1000 * 60 * 60 * 24) {
                this.clearWorkspaceRecovery();
                return;
            }
            this.recoverySnapshot = snapshot;
            this.recoveryAvailable = true;
            void this.service.render();
        } catch (e) {
            this.clearWorkspaceRecovery();
        }
    }

    public async restoreWorkspaceRecovery() {
        const snapshot = this.recoverySnapshot;
        if (!snapshot || snapshot.encounterId !== this.encounterId || this.clinicalReadOnly) return;
        this.soapDrafts = JSON.parse(JSON.stringify(snapshot.soapDrafts || this.soapDrafts));
        this.objectiveFindingDrafts = JSON.parse(JSON.stringify(snapshot.objectiveFindingDrafts || {}));
        this.objectiveFindingManualText = String(snapshot.objectiveFindingManualText || '');
        this.treatmentPlanDrafts = JSON.parse(JSON.stringify(snapshot.treatmentPlanDrafts || {}));
        this.activeSoapSection = this.normalizedSoapSection(snapshot.activeSoapSection || 'S');
        Object.keys(this.soapDrafts).forEach((section) => {
            (this.soapDrafts[section] || []).forEach((item) => item.dirty = true);
        });
        this.recoveryAvailable = false;
        this.refreshClinicalChartViewState();
        this.showWorkspaceFeedback('복구된 내용을 확인한 뒤 저장해 주세요.', 'info');
        await this.service.render();
    }

    public async discardWorkspaceRecovery() {
        this.clearWorkspaceRecovery();
        await this.service.render();
    }

    private scheduleWorkspaceAutosave() {
        if (this.clinicalReadOnly || !this.encounterId || this.soapSaving) return;
        this.autosaveState = 'pending';
        if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
        this.recoveryTimer = setTimeout(() => this.persistWorkspaceRecovery(), 350);
        if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
        if (!this.autosaveEnabled || this.isWorkspaceQaMode()) return;
        this.autosaveTimer = setTimeout(() => void this.runWorkspaceAutosave(), 4000);
    }

    public async toggleWorkspaceAutosave() {
        if (this.clinicalReadOnly) return;
        this.autosaveEnabled = !this.autosaveEnabled;
        if (!this.autosaveEnabled && this.autosaveTimer) {
            clearTimeout(this.autosaveTimer);
            this.autosaveTimer = null;
        }
        if (this.autosaveEnabled && this.hasSoapDirty) this.scheduleWorkspaceAutosave();
        this.showWorkspaceFeedback(
            this.autosaveEnabled
                ? '자동 저장을 시작합니다. 최종 저장은 계속 별도 확인이 필요합니다.'
                : '자동 저장을 중지했습니다. 변경 내용은 이 브라우저에 임시 보관합니다.',
            'info'
        );
        await this.service.render();
    }

    private async runWorkspaceAutosave() {
        this.autosaveTimer = null;
        if (!this.hasSoapDirty || this.busy || this.clinicalReadOnly) {
            if (this.hasSoapDirty && !this.clinicalReadOnly) this.scheduleWorkspaceAutosave();
            return;
        }
        this.autosaveState = 'saving';
        await this.service.render();
        await this.saveSoapDraft(false, true);
    }

    public get autosaveStatusLabel() {
        if (!this.autosaveEnabled) return '자동 저장 꺼짐 · 변경 내용은 브라우저에 보관';
        if (this.autosaveState === 'saving') return '자동 저장 중';
        if (this.autosaveState === 'pending') return '자동 저장 대기';
        if (this.autosaveState === 'failed') return '자동 저장 실패';
        if (this.lastAutosavedAt) return `자동 저장 ${this.lastAutosavedAt}`;
        return '변경 시 자동 저장';
    }

    public async retryLastWorkspaceAction() {
        if (this.retryActionLabel === '임시 저장') {
            await this.saveSoapDraft(true);
        } else {
            await this.loadContext();
        }
    }

    private soapDraftRevisionValue(soap: WorkspaceMap) {
        const draft = soap && soap.draft && typeof soap.draft === 'object' ? soap.draft : {};
        return String(draft.updated || (soap && soap.updated) || '');
    }

    private isDevMode() {
        try {
            return document.cookie.split(';').some((item) => item.trim() === 'season-wiz-devmode=true')
                || window.localStorage.getItem('hanscribe_debug') === 'true';
        } catch (e) {
            return false;
        }
    }

    private asArray(value: any) {
        if (!value) return [];
        return Array.isArray(value) ? value : [value];
    }

    private async callApi(action: string, payload: any = {}) {
        try {
            const response: any = await wiz.call(action, payload);
            const result = this.unwrapResponse(response);
            this.requestId = result.requestId || this.requestId;
            return result;
        } catch (e) {
            return { ok: false, data: null, requestId: '', message: '요청 처리 중 오류가 발생했습니다.' };
        }
    }

    private async callApiWithRetry(action: string, payload: any = {}, retries: number = 1) {
        let result: any = null;
        for (let attempt = 0; attempt <= Math.max(0, retries); attempt += 1) {
            result = await this.callApi(action, payload);
            if (result && result.ok) return result;
            if (attempt < retries) {
                await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
            }
        }
        return result || { ok: false, data: null, requestId: '', message: '요청을 완료하지 못했습니다.' };
    }

    private async callApiWithTimeout(action: string, payload: any = {}, timeoutMs: number = 8000, timeoutMessage: string = '요청 응답 시간이 초과되었습니다.') {
        let timeoutId: any = null;
        const timeoutResult = new Promise((resolve) => {
            timeoutId = setTimeout(() => {
                resolve({ ok: false, data: null, requestId: '', message: timeoutMessage, timedOut: true });
            }, Math.max(1000, timeoutMs || 8000));
        });
        try {
            return await Promise.race([this.callApi(action, payload), timeoutResult]);
        } finally {
            if (timeoutId) clearTimeout(timeoutId);
        }
    }

    private unwrapResponse(response: any) {
        const payload = response && response.responseJSON ? response.responseJSON : response;
        const nested = payload && payload.data && payload.data.ok !== undefined ? payload.data : payload;
        const requestId = nested && nested.request_id ? nested.request_id : '';

        if (nested && nested.ok === true) {
            return { ok: true, data: nested.data || {}, requestId, message: '' };
        }
        if (payload && payload.code === 200) {
            return { ok: true, data: payload.data || {}, requestId, message: '' };
        }

        const error = nested && nested.error ? nested.error : null;
        const message = error && error.message ? error.message : '처리 결과를 확인하지 못했습니다.';
        return { ok: false, data: null, requestId, message };
    }
}

export default PortalHanscribeEncounterWorkspaceComponent;